import { createDocumentPreviewController, fetchDocumentPreview } from "./documentPreviewState.js";
import dictationComposer from './dictation/composer.js';
import MessageUI from "./MessageUI.js?v=45.0.25"
import EmptyUI from "./EmptyUI.js?v=45.0.0"
import ToolWorkspace from "./ToolWorkspace.js?v=1.0.14"
import {
    CHAT_DOCUMENT_LAZY_THRESHOLD,
    LAZY_DOCUMENT_CONTEXT_REPRESENTATION,
    isLazyCatalogDocument,
    mergeMonotonicDocumentProvenance,
    mergeMonotonicDocumentState,
    normalizeDocumentAvailability,
    privateDocumentScopeFromLocator,
    queueRecoverableDocuments,
    recoverableDocumentCount,
} from "./documentReinjection.js?v=1.1.1"
import {
    canonicalChatProvesLatestTurnCompleted,
    chatRunIdIsAtMost,
    completedChatIdentitiesFromMessages,
    consumeChunkedRealtimeUpdate,
    durableChatResponseId,
    escapePlaintextHtml,
    findChatRequestContextForMessage as findTrackedContextForMessage,
    isStaleInFlightChatUpdate,
    shouldHydrateInFlightChatText,
    hasUnobservedToolReceiptVersions,
    mergeVersionedToolReceipts,
    recordToolReceiptVersions,
    resolveTerminalActionState,
    sanitizeRichHtml,
    shouldDeferEmptySequencedTerminal,
    toolReceiptVersion,
    writeClipboardContent,
} from "./chatFrontendGuards.js?v=1.0.2"
import {
    classifyToolResultAccessFailure,
    createToolResultAccessError,
    toolResultAccessFeedback,
} from "./toolResultAccessFeedback.js?v=1.0.0"
import {
    createReviewApplySnapshot,
    getReviewFollowupPresentation,
    normalizeActiveReview,
    REVIEW_FOLLOWUP_ACTION,
    REVIEW_FOLLOWUP_DISMISS_LABEL,
} from "./reviewFollowup.js?v=1.0.1"

// Match the durable context-request limits.  This is only a client-side
// guard; the server remains the authority for every attachment and message.
const MAX_CONTEXT_RESUME_ATTACHMENTS = 20;
const MAX_CONTEXT_RESUME_MESSAGE_CHARACTERS = 4000;
const INITIAL_TOOL_RECEIPT_RECONCILIATION_DELAY_MS = 350;
const INITIAL_REQUEST_ACCEPTANCE_RECONCILIATION_DELAY_MS = 12000;
const CANONICAL_CHAT_REFRESH_TIMEOUT_MS = 10000;
const PERSISTED_WAITING_RECOVERY_MAX_DELAY_MS = 30000;
const PERSISTED_WAITING_RECOVERY_MAX_ATTEMPTS = 4;
const CHAT_COMPOSER_MAX_HEIGHT_PX = 200;
const TOOL_ACTION_MUTATION_TIMEOUT_MS = 30000;
const TOOL_ACTION_RECONCILIATION_TIMEOUT_MS = 10000;
const TOOL_RECEIPT_HYDRATION_MAX_ATTEMPTS = 4;
const CONTEXT_DOCUMENT_BINDING_MAX_ATTEMPTS = 4;
const CHAT_PROGRAMMATIC_SCROLL_TOLERANCE_PX = 2;
const CHAT_ARTIFACT_ENVELOPE_VERSION = '2';

const reviewApplySnapshotPayload = (source) => {
    const candidate = source && source.applySnapshot ? source.applySnapshot : source;
    const snapshot = createReviewApplySnapshot(candidate);
    if (!snapshot) return null;
    return {
        applySnapshot: snapshot,
        reviewId: snapshot.reviewId,
        materialState: snapshot.materialState,
        approvedFindingIds: [...snapshot.approvedFindingIds],
        approvedCount: snapshot.approvedCount,
        undecidedCount: snapshot.undecidedCount,
    };
};

const validReviewApplySnapshot = (source) => {
    const payload = reviewApplySnapshotPayload(source);
    if (!payload) return null;
    const { applySnapshot } = payload;
    if (
        !applySnapshot.reviewId
        || !applySnapshot.materialState
        || applySnapshot.approvedCount < 1
        || applySnapshot.approvedFindingIds.length !== applySnapshot.approvedCount
        || applySnapshot.approvedFindingIds.some(identifier => !identifier)
        || new Set(applySnapshot.approvedFindingIds).size !== applySnapshot.approvedFindingIds.length
    ) return null;
    return applySnapshot;
};

const TOOL_RECEIPT_RECONNECT_NOTICES = Object.freeze({
    checking: Object.freeze({
        tone: 'progress',
        title: 'Comprobando tu última acción',
        detail: 'Espera un momento antes de repetirla. Así evitamos aplicar el mismo cambio dos veces.',
        iconClass: 'anticon anticon-sync anticon-spin',
        retryable: false,
        role: 'status',
    }),
    syncing: Object.freeze({
        tone: 'progress',
        title: 'Recuperando la última actualización',
        detail: 'Tus resultados están guardados. Estamos poniéndolos al día en esta conversación.',
        iconClass: 'anticon anticon-sync anticon-spin',
        retryable: true,
        role: 'status',
    }),
    latest: Object.freeze({
        tone: 'progress',
        title: 'Falta una actualización',
        detail: 'Ya mostramos los resultados guardados. Estamos recuperando el cambio más reciente.',
        iconClass: 'anticon anticon-cloud-sync',
        retryable: true,
        role: 'status',
    }),
    offline: Object.freeze({
        tone: 'offline',
        title: 'Sin conexión a internet',
        detail: 'Revisa el Wi-Fi o los datos móviles. Volveremos a intentarlo cuando recuperes la conexión.',
        iconClass: 'anticon anticon-disconnect',
        retryable: true,
        role: 'alert',
    }),
    service: Object.freeze({
        tone: 'service',
        title: 'LEXIUS no está disponible por el momento',
        detail: 'Puede deberse a mantenimiento o a una interrupción temporal. Tus resultados siguen guardados.',
        iconClass: 'anticon anticon-warning',
        retryable: true,
        role: 'alert',
    }),
    network: Object.freeze({
        tone: 'offline',
        title: 'No pudimos comunicarnos con LEXIUS',
        detail: 'Comprueba tu conexión. Si otras páginas cargan con normalidad, vuelve a intentarlo en unos minutos.',
        iconClass: 'anticon anticon-cloud',
        retryable: true,
        role: 'alert',
    }),
    uncertain: Object.freeze({
        tone: 'attention',
        title: 'Aún estamos confirmando el cambio',
        detail: 'No repitas la acción por ahora: estamos comprobando si quedó guardada para evitar duplicados.',
        iconClass: 'anticon anticon-clock-circle',
        retryable: true,
        role: 'status',
    }),
    answer: Object.freeze({
        tone: 'progress',
        title: 'Recuperando la respuesta',
        detail: 'La respuesta está guardada y volverá a mostrarse en su lugar dentro de la conversación.',
        iconClass: 'anticon anticon-sync anticon-spin',
        retryable: true,
        role: 'status',
    }),
    context: Object.freeze({
        tone: 'progress',
        title: 'Recuperando la solicitud',
        detail: 'Estamos cargando los documentos necesarios para que puedas continuar la respuesta.',
        iconClass: 'anticon anticon-sync anticon-spin',
        retryable: true,
        role: 'status',
    }),
});

// Source bindings originate from server-side source resolution.  The client
// only preserves the opaque fields so an offline/cache/sync round trip does
// not turn a pinned source back into an unscoped path lookup.
const preserveSourceBinding = (entry) => {
    if (!entry || typeof entry !== 'object') return {};
    const binding = {};
    [
        'source_id',
        'source_revision_hash',
        'source_scope_kind',
        'source_scope_id',
        'source_kind',
    ].forEach((field) => {
        if (typeof entry[field] === 'string' && entry[field].trim()) {
            binding[field] = entry[field].trim();
        }
    });
    return binding;
};

export default {
    mixins: [dictationComposer],
    props: {
        session: Object,
    },
    provide() {
        return {
            dictationSubmissionBlocked: () => Boolean(this.dictationBusy || this.composerSendInFlight || this.contextResumeBusy
                || this.reviewIntentChecking || this.activeReviewSubmitChecking),
            dictationPublishTyping: value => this.publishTypingState(value),
            canFollowDocumentPreview: () => this.chatboxFollowingBottom === true && !this.chatViewportUserPointerDown,
            claimDocumentPreviewReveal: preview => this.claimDocumentPreviewReveal(preview),
            isProgrammaticChatScroll: element => this.chatViewportProgrammaticScroll === true
                && element && this.chatViewportProgrammaticScrollTarget !== null
                && Math.abs(Number(element.scrollTop) - Number(this.chatViewportProgrammaticScrollTarget))
                    <= CHAT_PROGRAMMATIC_SCROLL_TOLERANCE_PX,
            isChatViewportLayoutScroll: event => this.isChatViewportLayoutScroll(event),
            stopDocumentPreviewFollowing: () => {
                this.chatboxFollowingBottom = false;
                this.chatViewportUserScrollRevision = Number(this.chatViewportUserScrollRevision || 0) + 1;
                this.releaseProgrammaticChatScroll();
            },
            preserveDocumentPreviewPosition: () => { this.chatboxFollowingBottom = false; },
            retryDocumentPreview: previewId => this.ensureDocumentPreviewController().retry(previewId),
            setProgrammaticChatScrollTop: (element, scrollTop) => (
                this.setProgrammaticChatScrollTop(element, scrollTop)
            ),
            captureDocumentResponseViewport: () => this.captureChatViewportState(),
            restoreDocumentResponseViewport: snapshot => this.restoreChatViewportState(snapshot, { waitForLayout: false }),
        };
    },
    data() {
        return {
            message: null,
            currentSession: {
                uuid: null,
                title: null,
                data: [],
                created_at: null,
                updated_at: null,
                path: null,
                page_title: null,
                is_user_document: null,
                is_law: null,
                is_multi_document: false,
                page_title_html: null,
                suggestedQuestions: [],
                chat_documents_state: [],
                pending_document_injections: [],
                chat_pagination: null,
                showPopup: false,
                popupContent: '',
                is_call_log: false,
                is_pinned: false,
                is_collaborative: false,
                collaboration_role: null,
                owner_username: null,
                aiRunStatus: null,
                tool_receipts: [],
                tool_receipts_cursor: '',
            },
            replying: false,
            lastError: false,
            errorMessage: null,
            maximum_characters: 120000,
            showSuggestedQuestions: getaCookie("showSuggestedQuestions") === "true",
            loadingSuggestedQuestions: false,
            popupVisible: false,
            popupContentText: '',
            popupPosition: null,
            // Realtime (Ably) channel for remote updates to active chat
            remoteChatChannel: null,
            remoteChatSubscriptions: [],
            remotePresenceChannel: null,
            remotePresenceSubscriptions: [],
            remotePresenceClient: null,
            remotePresenceEntered: false,
            remotePresenceConnectionHandler: null,
            chatRealtimeGeneration: 0,
            chatRealtimeLifecyclePromise: null,
            pendingTypingState: false,
            presentMembers: [],
            invitationChannel: null,
            invitationSubscriptions: [],
            invitationRealtimeGeneration: 0,
            invitationRealtimeLifecyclePromise: null,
            invitationRealtimeRetryTimer: null,
            typingMembers: [],
            replyTarget: null,
            collaborationParticipants: [],
            pendingCollaborationInvitations: [],
            collaborationInviteEmail: '',
            collaborationInviteStatus: '',
            collaborationRemovalStatus: '',
            collaborationPushStatus: '',
            collaborationPushSupported: null,
            collaborationPushPermission: null,
            collaborationPushEnabled: false,
            collaborationPushBusy: false,
            collaborationPushVariant: 'muted',
            collaborationDialogVisible: false,
            deferredServerRefreshTimer: null,
            collaborationAiStatusDismissedKey: '',
            collaborationAiStatusVisibleKey: '',
            collaborationAiStatusDismissTimer: null,
            unseenIncomingCount: 0,
            showScrollToBottom: false,
            chatboxScrollElement: null,
            chatViewportRestoreVersion: 0,
            chatViewportUserScrollRevision: 0,
            chatViewportProgrammaticScrollRevision: 0,
            chatViewportProgrammaticScroll: false,
            chatViewportProgrammaticScrollTarget: null,
            chatViewportProgrammaticScrollReleaseTimer: null,
            chatboxFollowingBottom: null,
            chatViewportUserScrollIntentUntil: 0,
            chatViewportUserPointerDown: false,
            chatViewportPendingReplyReveals: [],
            longChatPageSize: 50,
            longChatTopFetchThreshold: 220,
            longChatLoadInFlight: false,
            longChatSuppressTopFetchUntil: 0,
            markReadTimer: null,
            markReadInFlight: false,
            pendingMarkReadUuid: '',
            mentionQuery: '',
            mentionStartIndex: -1,
            mentionSuggestionsVisible: false,
            mentionActiveIndex: 0,
            mentionHideTimer: null,
            hasCustomInstructions: false,
            chatRequestGeneration: 0,
            activeChatRequestContext: null,
            chatRequestContexts: [],
            persistedWaitingRecoveryTimer: null,
            persistedWaitingRecoveryAttempt: 0,
            persistedWaitingRecoveryFlight: null,
            persistedWaitingRecoveryGeneration: 0,
            persistedWaitingRecoverySnapshot: null,
            activeToolReceipt: null,
            activeReview: null,
            activeReviewLoading: false,
            activeReviewRequestController: null,
            activeReviewRequestGeneration: 0,
            activeReviewHydratedChatUuid: '',
            activeReviewLoadPromise: null,
            activeReviewLoadPromiseChatUuid: '',
            activeReviewLoadToken: null,
            activeReviewHydrationError: '',
            activeReviewSubmitChecking: false,
            activeReviewRefreshTimer: null,
            activeReviewReloadAfterFlight: false,
            unconfirmedNewChatUuid: '',
            reviewCheckpoint: null,
            reviewDeferredMessage: null,
            reviewIntentChecking: false,
            reviewApplyInFlight: null,
            reviewUpdateNotice: '',
            reviewUpdateNoticeTimer: null,
            reviewCompletionTimer: null,
            toolWorkspaceReturnFocus: null,
            toolActionKeys: {},
            toolActionMutationControllers: new Set(),
            contextResumeBusy: false,
            contextDocumentBindingState: 'idle',
            contextDocumentBindingError: '',
            // Local UUIDs are provisional until a chat endpoint has confirmed
            // them.  Avoid an initial /chat/tool-runs request for a local stub.
            toolReceiptServerConfirmedChats: {},
            toolReceiptDurableSignalChats: {},
            toolReceiptHydrationGeneration: 0,
            toolReceiptHydrationFlights: {},
            toolReceiptExpectedVersions: {},
            toolReceiptObservedVersions: {},
            toolReceiptHydrationRetryTimer: null,
            toolReceiptHydrationRetryAttempt: 0,
            toolReceiptReconnectStatus: '',
            toolReceiptReconnectRetrying: false,
            toolActionInlineStatus: '',
            toolActionInlineStatusToolRunId: '',
            documentAvailabilityNotice: null,
            documentAdditionCheckpoint: null,
            documentAdditionCheckpointRevision: 0,
            continuationProjectionFlights: {},
            continuationProjectionAppliedVersions: {},
            continuationProjectionRetryTimers: {},
            continuationProjectionRetryAttempts: {},
            continuationProjectionGeneration: 0,
            authorizedArtifactWakeups: {},
            documentPreviews: [],
            documentPreviewOwnership: [],
            documentResponseReadyNotice: null,
        }
    },

    components: {
        MessageUI,
        EmptyUI,
        ToolWorkspace
    },
    mounted() {
        this.ensureDocumentPreviewController();
        this.chatWindowElement = this.$el.closest('#chatModal');
        this.chatWindowElement?.addEventListener('chat-window-will-resize', this.captureWindowViewport);
        this.chatWindowElement?.addEventListener('chat-window-resize', this.restoreWindowViewport);
        window.addEventListener('lexius:document-preview-discovery', this.onDocumentPreviewDiscovery);
        window.addEventListener('beforeunload', this.confirmExit);
        window.addEventListener('calltranscriptadded', this.handleCallTranscriptAdded);
        window.addEventListener('server-chat-updated', this.onServerChatUpdated);
        window.addEventListener('server-chat-deleted', this.onServerChatDeleted);
        window.addEventListener('chat-instructions-updated', this.handleCustomInstructionsEvent);
        window.addEventListener('lexius-user-email-ready', this.attachInvitationRealtime);
        window.addEventListener('lexius-push-state-updated', this.handlePushNotificationStateEvent);
        window.addEventListener('online', this.handleToolReceiptConnectionOnline);
        window.addEventListener('offline', this.handleToolReceiptConnectionOffline);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        if (this.$el && typeof this.$el.addEventListener === 'function') {
            this.$el.addEventListener('click', this.handleDelegatedCollaborationReaction);
        }
        this.syncCustomInstructionsState();
        this.initializeDocsHelpPopover();
        if (!this.isDemoRecordingMode()) {
            this.loadPendingCollaborationInvitations();
            this.attachInvitationRealtime();
            this.handleChatParamsFromUrl();
            this.handleInvitationParamsFromUrl();
        }
        this.$nextTick(() => {
            this.attachChatboxScrollListener();
            this.reconcilePersistedWaitingSession();
            if (typeof window.refreshDemoRecordingTouchControls === 'function') {
                window.refreshDemoRecordingTouchControls();
            }
        });
    },

    beforeUnmount() {
        this._documentPreviewController?.dispose();
        this.chatWindowElement?.removeEventListener('chat-window-will-resize', this.captureWindowViewport);
        this.chatWindowElement?.removeEventListener('chat-window-resize', this.restoreWindowViewport);
        window.removeEventListener('lexius:document-preview-discovery', this.onDocumentPreviewDiscovery);
        window.removeEventListener('beforeunload', this.confirmExit);
        window.removeEventListener('calltranscriptadded', this.handleCallTranscriptAdded);
        window.removeEventListener('server-chat-updated', this.onServerChatUpdated);
        window.removeEventListener('server-chat-deleted', this.onServerChatDeleted);
        window.removeEventListener('chat-instructions-updated', this.handleCustomInstructionsEvent);
        window.removeEventListener('lexius-user-email-ready', this.attachInvitationRealtime);
        window.removeEventListener('lexius-push-state-updated', this.handlePushNotificationStateEvent);
        window.removeEventListener('online', this.handleToolReceiptConnectionOnline);
        window.removeEventListener('offline', this.handleToolReceiptConnectionOffline);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        if (this.$el && typeof this.$el.removeEventListener === 'function') {
            this.$el.removeEventListener('click', this.handleDelegatedCollaborationReaction);
        }
        this.detachChatboxScrollListener();
        this.releaseProgrammaticChatScroll();
        if (this.markReadTimer) {
            clearTimeout(this.markReadTimer);
            this.markReadTimer = null;
        }
        this.clearOpenChatUuid();
        clearTimeout(this.mentionHideTimer);
        this.detachChatRealtime();
        this.detachInvitationRealtime();
        this.clearCollaborationAiStatusDismissTimer();
        this.cancelPendingChatRequest(null, false);
        this.clearPersistedWaitingRecovery();
        this.cancelToolActionMutationRequests();
        this.cancelActiveReviewRequest();
        this.clearActiveReviewTimers();
        this.clearToolReceiptHydrationRetry();
        this.invalidateContinuationProjectionRecovery();
        if (this.deferredServerRefreshTimer) {
            clearTimeout(this.deferredServerRefreshTimer);
            this.deferredServerRefreshTimer = null;
        }
    },

    methods: {
        captureWindowViewport() {
            this.windowViewportSnapshot = this.captureChatViewportState();
        },
        restoreWindowViewport() {
            // Restore before the window's transform animation starts, while
            // message measurements reflect the new, unscaled layout.
            this.restoreChatViewportState(this.windowViewportSnapshot, { waitForLayout: false });
            this.windowViewportSnapshot = null;
        },
        ensureDocumentPreviewController() {
            if (!this._documentPreviewController) {
                this._documentPreviewController = createDocumentPreviewController({
                    fetchPreview: fetchDocumentPreview,
                    onChange: (previews, ownership = []) => {
                        this.documentPreviews = previews;
                        this.documentPreviewOwnership = ownership;
                        const receivedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
                        const controller = this._documentPreviewController;
                        const epoch = controller?.epoch;
                        this.$nextTick(() => {
                            this.updateScrollToBottomVisibility();
                            if (typeof requestAnimationFrame !== 'function') return;
                            requestAnimationFrame(() => {
                                if (!controller || controller.epoch !== epoch) return;
                                const elapsed = performance.now() - receivedAt;
                                controller.recordMetric('preview_to_paint_ms', elapsed);
                                if (elapsed > 50) controller.recordMetric('render_stall_ms', elapsed);
                            });
                        });
                    },
                });
                this._documentPreviewController.activate(this.currentSession && this.currentSession.uuid);
            }
            return this._documentPreviewController;
        },
        discoverDocumentPreviews(payload, chatUuid) {
            const controller = this.ensureDocumentPreviewController();
            const uuid = String(chatUuid || this.currentSession && this.currentSession.uuid || '');
            if (uuid !== String(this.currentSession && this.currentSession.uuid || '')) return;
            if (controller.chatUuid !== uuid) controller.activate(uuid);
            controller.discover(payload, uuid);
        },
        onDocumentPreviewDiscovery(event) {
            const detail = event && event.detail || {};
            this.discoverDocumentPreviews(detail, detail.chat_uuid);
        },
        openUserFilesShortcut() {
            this.$emit('openUserFiles');
        },
        isDemoRecordingMode() {
            if (typeof window === 'undefined') return false;
            if (window.__lexiusDemoRecording) return true;
            try {
                const params = new URLSearchParams(window.location.search || '');
                return params.get('demo_recording') === '1';
            } catch (e) {
                return false;
            }
        },
        getChatboxElement() {
            try {
                const ref = this.$refs && this.$refs.chatbox;
                if (Array.isArray(ref)) return ref[0] || null;
                if (ref && ref.$el) return ref.$el;
                return ref || this.chatboxScrollElement || null;
            } catch (e) {
                return null;
            }
        },
        attachChatboxScrollListener() {
            try {
                const el = this.getChatboxElement();
                if (!el || this.chatboxScrollElement === el) return;
                this.detachChatboxScrollListener();
                el.addEventListener('scroll', this.handleChatboxScroll, { passive: true });
                el.addEventListener('wheel', this.noteChatboxUserScrollIntent, { passive: true });
                el.addEventListener('touchstart', this.noteChatboxUserScrollIntent, { passive: true });
                el.addEventListener('touchmove', this.noteChatboxUserScrollIntent, { passive: true });
                el.addEventListener('pointerdown', this.noteChatboxUserScrollIntent, { passive: true });
                el.addEventListener('keydown', this.noteChatboxUserScrollIntent);
                document.addEventListener('pointerup', this.noteChatboxUserScrollIntent, { passive: true });
                document.addEventListener('pointercancel', this.noteChatboxUserScrollIntent, { passive: true });
                this.chatboxScrollElement = el;
                if (this.chatboxFollowingBottom === null) {
                    this.chatboxFollowingBottom = this.isChatboxAtBottom(el);
                }
            } catch (e) { }
        },
        detachChatboxScrollListener() {
            try {
                if (this.chatboxScrollElement) {
                    this.chatboxScrollElement.removeEventListener('scroll', this.handleChatboxScroll);
                    this.chatboxScrollElement.removeEventListener('wheel', this.noteChatboxUserScrollIntent);
                    this.chatboxScrollElement.removeEventListener('touchstart', this.noteChatboxUserScrollIntent);
                    this.chatboxScrollElement.removeEventListener('touchmove', this.noteChatboxUserScrollIntent);
                    this.chatboxScrollElement.removeEventListener('pointerdown', this.noteChatboxUserScrollIntent);
                    this.chatboxScrollElement.removeEventListener('keydown', this.noteChatboxUserScrollIntent);
                }
                document.removeEventListener('pointerup', this.noteChatboxUserScrollIntent);
                document.removeEventListener('pointercancel', this.noteChatboxUserScrollIntent);
            } catch (e) { }
            this.chatViewportUserPointerDown = false;
            this.chatboxScrollElement = null;
        },
        releaseProgrammaticChatScroll() {
            if (this.chatViewportProgrammaticScrollReleaseTimer) {
                clearTimeout(this.chatViewportProgrammaticScrollReleaseTimer);
                this.chatViewportProgrammaticScrollReleaseTimer = null;
            }
            this.chatViewportProgrammaticScroll = false;
            this.chatViewportProgrammaticScrollTarget = null;
        },
        setProgrammaticChatScrollTop(element, scrollTop) {
            if (!element) return;
            const nextScrollTop = Number.isFinite(Number(scrollTop)) ? Number(scrollTop) : 0;
            this.chatViewportProgrammaticScrollRevision = Number(this.chatViewportProgrammaticScrollRevision || 0) + 1;
            this.releaseProgrammaticChatScroll();
            this.chatViewportProgrammaticScroll = true;
            element.scrollTop = nextScrollTop;
            const appliedScrollTop = Number(element.scrollTop);
            const programmaticTarget = Number.isFinite(appliedScrollTop)
                ? appliedScrollTop
                : nextScrollTop;
            // Browsers clamp scrollTop to the current scroll range. Track the
            // applied position so that clamp-generated scroll events are not
            // mistaken for target-divergent user input.
            this.chatViewportProgrammaticScrollTarget = programmaticTarget;
            this.chatboxFollowingBottom = this.isChatboxAtBottom(element);

            const scheduleRelease = typeof requestAnimationFrame === 'function'
                ? requestAnimationFrame
                : (callback) => setTimeout(callback, 0);
            scheduleRelease(() => scheduleRelease(() => {
                if (this.chatViewportProgrammaticScrollTarget === programmaticTarget) {
                    this.releaseProgrammaticChatScroll();
                }
            }));
        },
        isChatboxAtBottom(element = null, tolerance = 2) {
            try {
                const el = element || this.getChatboxElement();
                if (!el) return true;
                const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
                return remaining <= tolerance;
            } catch (e) {
                return true;
            }
        },
        isChatboxFollowingBottom(element = null) {
            if (this.chatboxFollowingBottom === null) {
                this.chatboxFollowingBottom = this.isChatboxAtBottom(element);
            }
            return this.chatboxFollowingBottom === true;
        },
        isChatboxNearBottom(threshold = 150) {
            try {
                const el = this.getChatboxElement();
                if (!el) return true;
                const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
                return remaining <= threshold;
            } catch (e) {
                return true;
            }
        },
        rememberPendingAiReplyReveal(message) {
            if (!message) return null;
            const clientMessageId = String(
                message.client_message_id
                || this.messageReferenceId(message)
                || ''
            ).trim();
            const entry = {
                message,
                clientMessageId,
                chatUuid: String(this.currentSession && this.currentSession.uuid || ''),
                userScrollRevision: Number(this.chatViewportUserScrollRevision || 0),
                terminal: false,
            };
            const entries = Array.isArray(this.chatViewportPendingReplyReveals)
                ? this.chatViewportPendingReplyReveals
                : [];
            this.chatViewportPendingReplyReveals = [
                ...entries.filter(candidate => (
                    candidate && !this.pendingAiReplyRevealMatches(candidate, message)
                )),
                entry,
            ];
            return entry;
        },
        pendingAiReplyRevealMatches(entry, message) {
            if (!entry || !message) return false;
            if (entry.message === message) return true;
            const entryMessage = entry.message || {};
            const entryIds = new Set([
                entry.clientMessageId,
                entryMessage.client_message_id,
                entryMessage.message_id,
                entryMessage.response_message_id,
            ].map(value => String(value || '').trim()).filter(Boolean));
            return [
                message.client_message_id,
                message.message_id,
                message.response_message_id,
            ].some(value => entryIds.has(String(value || '').trim()));
        },
        pendingAiReplyRevealEntry(message) {
            const entries = Array.isArray(this.chatViewportPendingReplyReveals)
                ? this.chatViewportPendingReplyReveals
                : [];
            return entries.find(entry => this.pendingAiReplyRevealMatches(entry, message)) || null;
        },
        clearPendingAiReplyReveal(message) {
            const entries = Array.isArray(this.chatViewportPendingReplyReveals)
                ? this.chatViewportPendingReplyReveals
                : [];
            this.chatViewportPendingReplyReveals = entries.filter(entry => (
                entry && !this.pendingAiReplyRevealMatches(entry, message)
            ));
        },
        claimDocumentPreviewReveal(preview) {
            const responseId = String(preview?.response_id || preview?.response_message_id || '').trim();
            const entry = responseId ? this.pendingAiReplyRevealEntry({ message_id: responseId }) : null;
            if (!entry || entry.terminal === true
                || entry.chatUuid !== String(this.currentSession?.uuid || '')
                || Number(entry.userScrollRevision || 0) !== Number(this.chatViewportUserScrollRevision || 0)) {
                return false;
            }
            // The optimistic text placeholder intentionally stopped following.
            // Its later document may reveal itself once, provided the reader
            // has not scrolled or selected text in the meantime.
            entry.documentPreviewRevealed = true;
            return true;
        },
        hasUninterruptedPendingAiReplyReveal({ beforeDocumentPreview = false } = {}) {
            const currentChatUuid = String(this.currentSession && this.currentSession.uuid || '');
            const userScrollRevision = Number(this.chatViewportUserScrollRevision || 0);
            const entries = Array.isArray(this.chatViewportPendingReplyReveals)
                ? this.chatViewportPendingReplyReveals
                : [];
            return entries.some(entry => (
                entry
                && entry.terminal !== true
                && (!beforeDocumentPreview || entry.documentPreviewRevealed !== true)
                && entry.chatUuid === currentChatUuid
                && Number(entry.userScrollRevision || 0) === userScrollRevision
            ));
        },
        findChatMessageViewportNode(message, element = null) {
            try {
                if (!message) return null;
                const el = element || this.getChatboxElement();
                if (!el || typeof el.querySelectorAll !== 'function') return null;
                const messageId = this.messageReferenceId(message);
                return Array.from(el.querySelectorAll('[data-message-ref]')).find(node => (
                    String(node.getAttribute('data-message-ref') || '') === messageId
                )) || null;
            } catch (e) {
                return null;
            }
        },
        captureChatViewportState() {
            try {
                const el = this.getChatboxElement();
                if (!el) return null;
                const snapshot = {
                    chatUuid: String(this.currentSession && this.currentSession.uuid || ''),
                    wasFollowingBottom: this.isChatboxFollowingBottom(el),
                    userScrollRevision: Number(this.chatViewportUserScrollRevision || 0),
                    programmaticScrollRevision: Number(this.chatViewportProgrammaticScrollRevision || 0),
                    scrollTop: Number(el.scrollTop || 0),
                    anchorMessageId: '',
                    messageOffset: 0,
                    anchorInnerRef: '',
                    innerOffset: 0,
                };
                if (snapshot.wasFollowingBottom) return snapshot;

                const containerRect = el.getBoundingClientRect();
                const visibleAnchors = [];
                Array.from(el.querySelectorAll('[data-message-viewport-id]')).forEach((messageNode) => {
                    const messageRect = messageNode.getBoundingClientRect();
                    if (messageRect.bottom <= containerRect.top || messageRect.top >= containerRect.bottom) {
                        return;
                    }
                    const innerAnchors = Array.from(messageNode.querySelectorAll('[data-message-viewport-ref]'))
                        .map((node) => ({ node, rect: node.getBoundingClientRect() }))
                        .filter(({ rect }) => (
                            rect.bottom > containerRect.top && rect.top < containerRect.bottom
                        ));
                    if (innerAnchors.length) {
                        innerAnchors.forEach(({ node, rect }) => visibleAnchors.push({
                            messageNode,
                            node,
                            rect,
                        }));
                    } else {
                        visibleAnchors.push({
                            messageNode,
                            node: messageNode,
                            rect: messageRect,
                        });
                    }
                });
                const crossingAnchor = visibleAnchors
                    .filter(({ rect }) => rect.top <= containerRect.top && rect.bottom > containerRect.top)
                    .sort((left, right) => right.rect.top - left.rect.top)[0];
                const anchor = crossingAnchor || visibleAnchors
                    .sort((left, right) => left.rect.top - right.rect.top)[0];
                if (anchor) {
                    snapshot.anchorMessageId = String(
                        anchor.messageNode.getAttribute('data-message-viewport-id') || ''
                    );
                    snapshot.messageOffset = anchor.messageNode.getBoundingClientRect().top
                        - containerRect.top;
                    snapshot.anchorInnerRef = anchor.node === anchor.messageNode
                        ? ''
                        : String(anchor.node.getAttribute('data-message-viewport-ref') || '');
                    snapshot.innerOffset = anchor.node === anchor.messageNode
                        ? 0
                        : anchor.rect.top - containerRect.top;
                }
                return snapshot;
            } catch (e) {
                return null;
            }
        },
        waitForChatViewportLayout() {
            return new Promise((resolve) => {
                const scheduleFrame = typeof requestAnimationFrame === 'function'
                    ? requestAnimationFrame
                    : (callback) => setTimeout(callback, 0);
                scheduleFrame(() => scheduleFrame(resolve));
            });
        },
        async restoreChatViewportState(snapshot, { markIncoming = false, waitForLayout = true } = {}) {
            if (!snapshot) return false;
            const restoreVersion = (this.chatViewportRestoreVersion || 0) + 1;
            this.chatViewportRestoreVersion = restoreVersion;
            try {
                if (waitForLayout) {
                    await this.$nextTick();
                    await this.waitForChatViewportLayout();
                }
                if (restoreVersion !== this.chatViewportRestoreVersion) return false;
                if (
                    Number(snapshot.userScrollRevision || 0)
                    !== Number(this.chatViewportUserScrollRevision || 0)
                ) return false;
                // A preview may reveal itself after this snapshot was taken.
                // A delayed history refresh must not restore the old position
                // over that newer navigation, including a scroll to latest.
                if (
                    Number(snapshot.programmaticScrollRevision || 0)
                    !== Number(this.chatViewportProgrammaticScrollRevision || 0)
                ) return false;
                if (
                    !this.currentSession
                    || String(this.currentSession.uuid || '') !== String(snapshot.chatUuid || '')
                ) return false;

                const el = this.getChatboxElement();
                if (!el) return false;
                const pendingReplyPinned = (
                    typeof this.hasUninterruptedPendingAiReplyReveal === 'function'
                    && this.hasUninterruptedPendingAiReplyReveal({ beforeDocumentPreview: true })
                );
                // A document update can pin the viewport after this snapshot
                // was captured. Do not let an older restore resume following.
                if (snapshot.wasFollowingBottom && !pendingReplyPinned && this.chatboxFollowingBottom !== false) {
                    this.setProgrammaticChatScrollTop(
                        el,
                        Math.max(0, el.scrollHeight - el.clientHeight),
                    );
                    this.unseenIncomingCount = 0;
                } else {
                    let restoredAnchor = false;
                    if (snapshot.anchorMessageId) {
                        const messageNode = Array.from(el.querySelectorAll('[data-message-viewport-id]')).find((node) => (
                            String(node.getAttribute('data-message-viewport-id') || '') === snapshot.anchorMessageId
                        ));
                        const innerTarget = messageNode && snapshot.anchorInnerRef
                            ? Array.from(messageNode.querySelectorAll('[data-message-viewport-ref]')).find((node) => (
                                String(node.getAttribute('data-message-viewport-ref') || '')
                                === snapshot.anchorInnerRef
                            ))
                            : null;
                        const target = innerTarget || messageNode;
                        if (target) {
                            const targetOffset = innerTarget
                                ? Number(snapshot.innerOffset || 0)
                                : Number(snapshot.messageOffset || 0);
                            const delta = target.getBoundingClientRect().top
                                - el.getBoundingClientRect().top
                                - targetOffset;
                            const maxScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
                            const nextScrollTop = Math.max(0, Math.min(maxScrollTop, el.scrollTop + delta));
                            if (Math.abs(nextScrollTop - el.scrollTop) > 0.5) {
                                this.setProgrammaticChatScrollTop(el, nextScrollTop);
                            }
                            restoredAnchor = true;
                        }
                    }
                    if (!restoredAnchor) {
                        const maxScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
                        const nextScrollTop = Math.max(0, Math.min(maxScrollTop, Number(snapshot.scrollTop || 0)));
                        if (Math.abs(nextScrollTop - el.scrollTop) > 0.5) {
                            this.setProgrammaticChatScrollTop(el, nextScrollTop);
                        }
                    }
                    if (markIncoming && !pendingReplyPinned) {
                        this.unseenIncomingCount = Math.max(1, this.unseenIncomingCount || 0);
                    }
                }
                if (pendingReplyPinned) {
                    this.chatboxFollowingBottom = false;
                }
                this.updateScrollToBottomVisibility();
                return true;
            } catch (e) {
                return false;
            }
        },
        noteChatboxUserScrollIntent(event = null) {
            if (event && ['pointerup', 'pointercancel'].includes(event.type)) {
                if (this.chatViewportUserPointerDown) this.chatViewportUserScrollIntentUntil = Date.now() + 1000;
                this.chatViewportUserPointerDown = false;
                return;
            }
            if (event && event.type === 'pointerdown') this.chatViewportUserPointerDown = true;
            if (event && event.type === 'keydown') {
                const navigationKeys = new Set([
                    'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ',
                ]);
                if (!navigationKeys.has(String(event.key || ''))) return;
            }
            this.chatViewportUserScrollIntentUntil = Date.now() + 1000;
        },
        isChatViewportLayoutScroll(event) {
            return !!(
                event && event.isTrusted === true
                && !this.chatViewportUserPointerDown
                && Date.now() > Number(this.chatViewportUserScrollIntentUntil || 0)
                && typeof this.hasUninterruptedPendingAiReplyReveal === 'function'
                && this.hasUninterruptedPendingAiReplyReveal()
            );
        },
        handleChatboxScroll(event = null) {
            const element = event && event.currentTarget
                ? event.currentTarget
                : this.getChatboxElement();
            if (this.chatViewportProgrammaticScroll) {
                const target = this.chatViewportProgrammaticScrollTarget;
                const numericTarget = Number(target);
                const currentScrollTop = element ? Number(element.scrollTop) : Number.NaN;
                if (
                    target !== null
                    && target !== undefined
                    && Number.isFinite(numericTarget)
                    && Number.isFinite(currentScrollTop)
                    && Math.abs(currentScrollTop - numericTarget)
                        <= CHAT_PROGRAMMATIC_SCROLL_TOLERANCE_PX
                ) {
                    this.updateScrollToBottomVisibility();
                    return;
                }
                this.releaseProgrammaticChatScroll();
            }
            if (this.isChatViewportLayoutScroll(event)) {
                // Loading-state replacement and canonical reconciliation can
                // emit trusted scroll events without reader input. Preserve
                // the reply fence unless wheel/touch/pointer/key input shows
                // that the reader actually moved the viewport.
                this.updateScrollToBottomVisibility();
                return;
            }
            this.chatViewportUserScrollRevision = Number(this.chatViewportUserScrollRevision || 0) + 1;
            this.chatboxFollowingBottom = this.isChatboxAtBottom(element);
            if (this.chatboxFollowingBottom === true) {
                this.unseenIncomingCount = 0;
            }
            this.updateScrollToBottomVisibility();
            this.maybeLoadPreviousChatWindowFromScroll();
        },
        buildChatGetUrl(uuid, params = {}) {
            const query = new URLSearchParams();
            query.set('uuid', String(uuid || ''));
            // Recording targets must use turns from the full conversation,
            // including the initial review and the actual second query.
            query.set('chat_pagination', this.isDemoRecordingMode() ? '0' : '1');
            query.set('artifact_envelope', CHAT_ARTIFACT_ENVELOPE_VERSION);
            query.set('limit', String(params.limit || this.longChatPageSize || 50));
            if (params.before !== undefined && params.before !== null) {
                query.set('before', String(params.before));
            }
            if (params.start !== undefined && params.start !== null) {
                query.set('start', String(params.start));
            }
            return `/chats/get?${query.toString()}`;
        },
        parseChatPaginationNumber(value, fallback = null) {
            const parsed = Number.parseInt(String(value), 10);
            return Number.isFinite(parsed) ? parsed : fallback;
        },
        normalizeToolReceipt(raw) {
            if (!raw || typeof raw !== 'object') return null;
            const toolRunId = String(raw.tool_run_id || '').trim();
            const toolName = String(raw.tool_name || '').trim();
            const allowedTools = new Set([
                'solicitar_contexto',
                'leer_fuente',
                'crear_editar_archivo',
                'revisar_documento',
            ]);
            const allowedStatuses = new Set([
                'running', 'waiting_user', 'completed', 'partial', 'failed', 'cancelled',
                'expired', 'superseded',
            ]);
            if (!toolRunId || !allowedTools.has(toolName)) return null;
            const stateVersion = Math.max(1, Number.parseInt(String(raw.state_version || 1), 10) || 1);
            const eventSeq = Math.max(0, Number.parseInt(String(raw.event_seq || 0), 10) || 0);
            const ui = raw.ui_payload && typeof raw.ui_payload === 'object' ? raw.ui_payload : {};
            const rawRequestedDocuments = Array.isArray(ui.requested_documents)
                ? ui.requested_documents
                : (Array.isArray(ui.requests) ? ui.requests : []);
            const requestedDocuments = rawRequestedDocuments.map((item) => {
                if (typeof item === 'string') return item.slice(0, 300);
                if (!item || typeof item !== 'object') return null;
                const label = String(
                    item.label
                    || item.title
                    || item.documento
                    || item.name
                    || item.tipo
                    || item.description
                    || item.descripcion
                    || ''
                ).slice(0, 300);
                if (!label) return null;
                const priority = String(item.priority || item.prioridad || 'recomendado').toLowerCase() === 'necesario'
                    ? 'necesario'
                    : 'recomendado';
                const rawAcceptedTypes = Array.isArray(item.accepted_types)
                    ? item.accepted_types
                    : (Array.isArray(item.tipos_aceptados) ? item.tipos_aceptados : ['documento']);
                const acceptedTypes = [...new Set(rawAcceptedTypes.map((value) => {
                    const normalized = String(value || '').trim().toLowerCase();
                    return ({ document: 'documento', image: 'imagen', audio: 'audio', video: 'video' })[normalized]
                        || normalized;
                }).filter(value => ['documento', 'imagen', 'audio', 'video'].includes(value)))].slice(0, 4);
                return {
                    request_item_id: String(item.request_item_id || item.id || '').slice(0, 128),
                    label,
                    description: String(item.description || item.descripcion || item.detail || '').slice(0, 500),
                    priority,
                    accepted_types: acceptedTypes.length ? acceptedTypes : ['documento'],
                };
            }).filter(Boolean).slice(0, 10);
            let actions = Array.isArray(ui.actions)
                ? ui.actions.filter(item => typeof item === 'string').slice(0, 10)
                : [];
            const isWaitingContextRequest = String(raw.status || '') === 'waiting_user'
                && raw.result_ref
                && String(raw.result_ref.type || '') === 'context_request';
            if (isWaitingContextRequest && !actions.some(action => (
                ['omitir', 'continuar sin documentos'].includes(this.normalizeToolAction(action))
            ))) {
                actions = [...actions.slice(0, 9), 'Continuar sin documentos'];
            }
            return {
                tool_run_id: toolRunId,
                turn_id: String(raw.turn_id || ''),
                trigger_message_id: String(raw.trigger_message_id || ''),
                trigger_message_exists: typeof raw.trigger_message_exists === 'boolean'
                    ? raw.trigger_message_exists
                    : null,
                response_message_id: String(raw.response_message_id || ''),
                response_message_exists: typeof raw.response_message_exists === 'boolean'
                    ? raw.response_message_exists
                    : null,
                tool_name: toolName,
                status: allowedStatuses.has(String(raw.status || '')) ? String(raw.status) : 'running',
                state_version: stateVersion,
                event_seq: eventSeq,
                created_at: String(raw.created_at || ''),
                ui_payload: {
                    title: String(ui.title || '').slice(0, 300),
                    summary: String(ui.summary || '').slice(0, 1000),
                    preview: String(ui.preview || '').slice(0, 900),
                    warnings: Array.isArray(ui.warnings)
                        ? ui.warnings.filter(item => typeof item === 'string').slice(0, 20)
                        : [],
                    counts: ui.counts && typeof ui.counts === 'object' ? ui.counts : {},
                    actions,
                    requested_documents: requestedDocuments,
                },
                result_ref: raw.result_ref && typeof raw.result_ref === 'object'
                    ? {
                        type: String(raw.result_ref.type || ''),
                        id: String(raw.result_ref.id || ''),
                        ...(
                            String(raw.result_ref.type || '') === 'context_request'
                            ? {
                                resolution_epoch: Math.max(
                                    0,
                                    Number.parseInt(String(raw.result_ref.resolution_epoch || 0), 10) || 0,
                                ),
                            }
                            : {}
                        ),
                    }
                    : null,
                artifact_commit_synthetic: raw.artifact_commit_synthetic === true
                    && toolRunId.startsWith('artifact-version-'),
                error: raw.error && typeof raw.error === 'object'
                    ? { code: String(raw.error.code || ''), message: String(raw.error.message || '').slice(0, 500), retryable: raw.error.retryable === true }
                    : null,
            };
        },
        reconcileToolReceipts(rawReceipts, cursor = null, {
            viewportSnapshot = null,
            restoreViewport = true,
            markIncoming = false,
        } = {}) {
            if (!this.currentSession) return;
            const snapshot = restoreViewport
                ? (viewportSnapshot || this.captureChatViewportState())
                : null;
            const existing = Array.isArray(this.currentSession.tool_receipts)
                ? this.currentSession.tool_receipts
                : [];
            const normalized = [];
            (Array.isArray(rawReceipts) ? rawReceipts : []).forEach((raw) => {
                const receipt = this.normalizeToolReceipt(raw);
                if (!receipt) return;
                normalized.push(receipt);
            });
            const reviewProjectionAdvanced = normalized.some(receipt => (
                receipt
                && receipt.result_ref
                && receipt.result_ref.type === 'review'
                && toolReceiptVersion(existing, receipt.tool_run_id) < Number(receipt.state_version || 0)
            ));
            let receipts = mergeVersionedToolReceipts(existing, normalized);
            receipts.forEach((receipt) => {
                if (this.isAuthorizedArtifactWakeupReceipt(receipt)) {
                    receipt.artifact_commit_wakeup = true;
                }
            });
            const durableArtifactKeys = new Set(
                receipts
                    .filter(receipt => receipt && receipt.artifact_commit_synthetic !== true)
                    .map(receipt => this.artifactReceiptIdentityKey(receipt))
                    .filter(Boolean)
            );
            receipts = receipts.filter(receipt => (
                !receipt
                || receipt.artifact_commit_synthetic !== true
                || !durableArtifactKeys.has(this.artifactReceiptIdentityKey(receipt))
            ));
            receipts.forEach((receipt) => {
                const resultType = String(receipt && receipt.result_ref && receipt.result_ref.type || '');
                if (resultType === 'context_request' && receipt.status === 'waiting_user') {
                    this.ensureWaitingReceiptHost(receipt);
                } else if (
                    resultType === 'continuation'
                    && ['queued', 'leased', 'running', 'failed_retryable'].includes(String(receipt.status || ''))
                ) {
                    this.ensureContinuationActivityHost(receipt, receipt.result_ref.id);
                } else {
                    this.bindPausedBotToReceipt(receipt);
                }
                this.applyContextRequestReceiptState(receipt);
                this.applyContinuationReceiptState(receipt);
                this.applyReplacementConfirmationReceiptState(receipt);
            });
            const byId = new Map(receipts.map(item => [String(item.tool_run_id || ''), item]));
            try { this.$set(this.currentSession, 'tool_receipts', receipts); } catch (e) { this.currentSession.tool_receipts = receipts; }
            const activeId = String(this.activeToolReceipt && this.activeToolReceipt.tool_run_id || '');
            if (activeId && byId.has(activeId)) {
                const refreshed = byId.get(activeId);
                const requestedVersion = Number(this.activeToolReceipt.requested_artifact_version || 0);
                const reviewFocusMode = String(this.activeToolReceipt.review_focus_mode || '');
                const activeReview = this.activeToolReceipt.active_review || this.activeReview || null;
                const nextReceipt = requestedVersion > 0
                    ? { ...refreshed, requested_artifact_version: requestedVersion }
                    : refreshed;
                this.activeToolReceipt = {
                    ...nextReceipt,
                    ...(reviewFocusMode ? { review_focus_mode: reviewFocusMode } : {}),
                    ...(activeReview ? { active_review: activeReview } : {}),
                };
            }
            if (typeof cursor === 'string' && cursor) {
                try { this.$set(this.currentSession, 'tool_receipts_cursor', cursor); } catch (e) { this.currentSession.tool_receipts_cursor = cursor; }
            }
            if (snapshot) {
                this.restoreChatViewportState(snapshot, { markIncoming }).catch(() => {});
            }
            if (reviewProjectionAdvanced) {
                this.scheduleActiveReviewRefresh();
            }
        },
        rememberExpectedToolReceiptVersions(chatUuid, minimumVersions = {}) {
            const uuid = String(chatUuid || '').trim();
            if (!uuid || !minimumVersions || typeof minimumVersions !== 'object') return;
            const current = this.toolReceiptExpectedVersions[uuid] || {};
            Object.entries(minimumVersions).forEach(([entityId, rawVersion]) => {
                const id = String(entityId || '').trim();
                const version = Number.parseInt(String(rawVersion || 0), 10) || 0;
                if (!id || version <= 0) return;
                current[id] = Math.max(Number(current[id] || 0), version);
            });
            this.toolReceiptExpectedVersions[uuid] = current;
        },
        rememberObservedToolReceiptVersions(chatUuid, rawReceipts) {
            const uuid = String(chatUuid || '').trim();
            if (!uuid) return;
            this.toolReceiptObservedVersions[uuid] = recordToolReceiptVersions(
                this.toolReceiptObservedVersions[uuid] || {},
                rawReceipts,
            );
        },
        hasUnobservedToolReceiptVersions(chatUuid, session) {
            const uuid = String(chatUuid || '').trim();
            const expected = this.toolReceiptExpectedVersions[uuid] || {};
            const missing = hasUnobservedToolReceiptVersions(
                expected,
                session && Array.isArray(session.tool_receipts) ? session.tool_receipts : [],
                this.toolReceiptObservedVersions[uuid],
            );
            if (!Object.keys(expected).length) delete this.toolReceiptExpectedVersions[uuid];
            return missing;
        },
        toolReceiptHydrationDelayMs(attempt) {
            const override = typeof window !== 'undefined'
                ? window.__LEXIUS_TOOL_RECEIPT_RETRY_DELAYS_MS
                : null;
            if (Array.isArray(override) && override.length) {
                const candidate = Number(override[Math.min(attempt, override.length - 1)]);
                if (Number.isFinite(candidate) && candidate >= 0) return candidate;
            }
            const base = Math.min(2000, 100 * (2 ** Math.max(0, attempt)));
            return base + Math.floor(Math.random() * Math.min(125, Math.max(1, base / 4)));
        },
        waitForToolReceiptRetry(attempt) {
            return new Promise(resolve => setTimeout(resolve, this.toolReceiptHydrationDelayMs(attempt)));
        },
        async hydrateToolReceiptPages(session, uuid, { reset = false } = {}) {
            let cursor = reset ? '' : String(session.tool_receipts_cursor || '');
            const seenCursors = new Set();
            if (reset) delete this.toolReceiptObservedVersions[uuid];
            while (true) {
                const query = new URLSearchParams({ chat_uuid: uuid });
                if (cursor) query.set('after', cursor);
                const response = await fetch(`/chat/tool-runs?${query.toString()}`);
                if (!response.ok) {
                    throw createToolResultAccessError(toolResultAccessFeedback({
                        response,
                        resultType: 'result',
                    }));
                }
                const data = await response.json();
                if (
                    !this.currentSession
                    || this.currentSession !== session
                    || String(this.currentSession.uuid || '') !== uuid
                ) return false;
                this.discoverDocumentPreviews(data, uuid);
                this.toolReceiptServerConfirmedChats[uuid] = true;
                try { this.$set(session, 'tool_receipts_server_confirmed', true); } catch (e) {
                    session.tool_receipts_server_confirmed = true;
                }
                const nextCursor = typeof data.after === 'string' ? data.after : '';
                // Record every feed identity, including tools the UI does not
                // render, so hidden wake-ups cannot look like a missed update.
                this.rememberObservedToolReceiptVersions(uuid, data.receipts);
                // A cursor becomes durable locally only after its page has been
                // validated and merged. A later failed page cannot discard this
                // successfully applied projection.
                this.reconcileToolReceipts(data.receipts, nextCursor);
                if (!data.has_more || !nextCursor || nextCursor === cursor || seenCursors.has(nextCursor)) break;
                seenCursors.add(nextCursor);
                cursor = nextCursor;
            }
            return true;
        },
        async hydrateToolReceipts({ reset = false, minimumVersions = null } = {}) {
            const session = this.currentSession;
            const uuid = session && String(session.uuid || '');
            if (!uuid || session.is_call_log) return;
            this.rememberExpectedToolReceiptVersions(uuid, minimumVersions || {});

            const existingFlight = this.toolReceiptHydrationFlights[uuid];
            if (existingFlight && existingFlight.session === session) {
                // Coalesce callers onto one per-chat request chain. A reset that
                // arrives mid-flight becomes a follow-up pass rather than
                // invalidating the only response that may succeed.
                if (reset) existingFlight.resetRequested = true;
                if (minimumVersions && Object.keys(minimumVersions).length) {
                    existingFlight.minimumVersionRequested = true;
                }
                return existingFlight.promise;
            }
            if (existingFlight) {
                return existingFlight.promise.finally(() => this.hydrateToolReceipts({
                    reset,
                    minimumVersions,
                }));
            }

            const flight = {
                session,
                resetRequested: false,
                minimumVersionRequested: false,
                promise: null,
            };
            const run = async () => {
                let resetPass = reset;
                let consecutiveFailures = 0;
                let passCount = 0;
                let appliedAtLeastOnePass = false;
                while (passCount < (TOOL_RECEIPT_HYDRATION_MAX_ATTEMPTS * 3)) {
                    passCount += 1;
                    try {
                        const applied = await this.hydrateToolReceiptPages(session, uuid, { reset: resetPass });
                        if (!applied) return;
                        appliedAtLeastOnePass = true;
                        consecutiveFailures = 0;
                    } catch (error) {
                        if (
                            !this.currentSession
                            || this.currentSession !== session
                            || String(this.currentSession.uuid || '') !== uuid
                        ) return;
                        consecutiveFailures += 1;
                        if (consecutiveFailures < TOOL_RECEIPT_HYDRATION_MAX_ATTEMPTS) {
                            await this.waitForToolReceiptRetry(consecutiveFailures - 1);
                            continue;
                        }
                        const hasKnownDurableState = !!(
                            this.toolReceiptDurableSignalChats[uuid]
                            || (Array.isArray(session.tool_receipts) && session.tool_receipts.length > 0)
                            || this.hasUnobservedToolReceiptVersions(uuid, session)
                        );
                        if (hasKnownDurableState) {
                            const failureKind = classifyToolResultAccessFailure({ error });
                            this.toolReceiptReconnectStatus = ['offline', 'service', 'network'].includes(failureKind)
                                ? failureKind
                                : (appliedAtLeastOnePass ? 'latest' : 'syncing');
                            this.scheduleToolReceiptHydrationRetry(uuid);
                        }
                        return;
                    }

                    const followUpReset = flight.resetRequested;
                    const followUpMinimumVersion = flight.minimumVersionRequested;
                    flight.resetRequested = false;
                    flight.minimumVersionRequested = false;
                    const missingExpectedVersion = this.hasUnobservedToolReceiptVersions(uuid, session);
                    if (!followUpReset && !(followUpMinimumVersion && missingExpectedVersion)) {
                        if (missingExpectedVersion) {
                            // A successful feed is authoritative for this pass.
                            // Keep the expected version pending so a later
                            // realtime or lifecycle wake-up can catch it up,
                            // without polling an empty pre-chat projection.
                            this.clearToolReceiptHydrationRetry();
                            try { this.store(); } catch (e) { }
                            return;
                        }
                        this.clearToolReceiptHydrationRetry();
                        try { this.store(); } catch (e) { }
                        return;
                    }
                    resetPass = followUpReset;
                    await this.waitForToolReceiptRetry(Math.min(passCount - 1, 3));
                }
                this.clearToolReceiptHydrationRetry();
            };
            flight.promise = run().finally(() => {
                // Vue may proxy objects stored in component state, so comparing
                // the retrieved wrapper to this raw flight by identity can keep
                // a completed single-flight entry alive forever.
                delete this.toolReceiptHydrationFlights[uuid];
            });
            this.toolReceiptHydrationFlights[uuid] = flight;
            return flight.promise;
        },
        clearToolReceiptHydrationRetry() {
            if (this.toolReceiptHydrationRetryTimer) {
                clearTimeout(this.toolReceiptHydrationRetryTimer);
                this.toolReceiptHydrationRetryTimer = null;
            }
            this.toolReceiptHydrationRetryAttempt = 0;
            this.toolReceiptReconnectStatus = '';
            this.toolReceiptReconnectRetrying = false;
        },
        handleToolReceiptConnectionOffline() {
            if (!this.toolReceiptReconnectStatus) return;
            this.toolReceiptReconnectStatus = 'offline';
        },
        handleToolReceiptConnectionOnline() {
            this._documentPreviewController?.reconnect();
            this.reconcilePersistedWaitingSession();
            if (!['offline', 'network', 'service'].includes(this.toolReceiptReconnectStatus)) return;
            this.retryToolReceiptReconnect();
        },
        async retryToolReceiptReconnect() {
            if (this.toolReceiptReconnectRetrying) return;
            const uuid = String(this.currentSession && this.currentSession.uuid || '').trim();
            if (!uuid) return;
            if (this.toolReceiptHydrationRetryTimer) {
                clearTimeout(this.toolReceiptHydrationRetryTimer);
                this.toolReceiptHydrationRetryTimer = null;
            }
            this.toolReceiptHydrationRetryAttempt = 0;
            this.toolReceiptReconnectRetrying = true;
            this.toolReceiptReconnectStatus = 'syncing';
            try {
                await this.catchUpContinuationState({ resetReceipts: true });
            } catch (error) {
                const failureKind = classifyToolResultAccessFailure({ error });
                this.toolReceiptReconnectStatus = ['offline', 'service', 'network'].includes(failureKind)
                    ? failureKind
                    : 'syncing';
            } finally {
                this.toolReceiptReconnectRetrying = false;
            }
        },
        scheduleToolReceiptHydrationRetry(expectedUuid) {
            if (this.toolReceiptHydrationRetryTimer) return;
            const attempt = this.toolReceiptHydrationRetryAttempt + 1;
            this.toolReceiptHydrationRetryAttempt = attempt;
            const delayMs = Math.min(30000, 1000 * (2 ** Math.min(attempt - 1, 5)));
            this.toolReceiptHydrationRetryTimer = setTimeout(() => {
                this.toolReceiptHydrationRetryTimer = null;
                if (!this.currentSession || String(this.currentSession.uuid || '') !== String(expectedUuid || '')) {
                    this.clearToolReceiptHydrationRetry();
                    return;
                }
                this.hydrateToolReceipts({ reset: false });
            }, delayMs);
        },
        onRemoteToolUpdate(data) {
            if (!data) return;
            if (!this.currentSession || String(data.chat_uuid || '') !== String(this.currentSession.uuid || '')) return;
            if (data.event_type === 'review_state') {
                this.scheduleActiveReviewRefresh(0);
                return;
            }
            if (data.event_type !== 'tool_run') return;
            const entityId = String(data.entity_id || '');
            if (!entityId) return;
            this.toolReceiptServerConfirmedChats[String(this.currentSession.uuid || '')] = true;
            this.toolReceiptDurableSignalChats[String(this.currentSession.uuid || '')] = true;
            const current = (this.currentSession.tool_receipts || []).find(
                item => String(item.tool_run_id || '') === entityId
            );
            const incomingVersion = Number.parseInt(String(data.state_version || 0), 10) || 0;
            const observedVersion = toolReceiptVersion(
                this.toolReceiptObservedVersions[String(this.currentSession.uuid || '')],
                entityId,
            );
            if (Math.max(
                Number(current && current.state_version || 0),
                observedVersion,
            ) >= incomingVersion) return;
            this.hydrateToolReceipts({
                minimumVersions: { [entityId]: incomingVersion },
            });
        },
        clearInitialRequestAcceptanceReconciliation(context = null) {
            const trackedContext = context
                ? (this.getTrackedChatRequestContext(context) || context)
                : null;
            if (!trackedContext) return;
            if (trackedContext.initialAcceptanceReconciliationTimer) {
                clearTimeout(trackedContext.initialAcceptanceReconciliationTimer);
                trackedContext.initialAcceptanceReconciliationTimer = null;
            }
            if (context && context !== trackedContext) {
                context.initialAcceptanceReconciliationTimer = null;
            }
        },
        scheduleInitialRequestAcceptanceReconciliation(context) {
            const trackedContext = this.getTrackedChatRequestContext(context);
            if (!trackedContext || !trackedContext.uuid) return;
            this.clearInitialRequestAcceptanceReconciliation(trackedContext);
            const timer = setTimeout(() => {
                trackedContext.initialAcceptanceReconciliationTimer = null;
                if (!this.isChatRequestContextActive(trackedContext)) return;
                const placeholder = trackedContext.loadingMessage;
                Promise.resolve(this.refreshConversationFromServer(
                    trackedContext.uuid,
                    {
                        force: true,
                        preserveSuggestedQuestions: true,
                        timeoutMs: CANONICAL_CHAT_REFRESH_TIMEOUT_MS,
                    }
                )).catch(() => false).then(() => {
                    if (!this.isChatRequestContextActive(trackedContext)) return;
                    const snapshot = this.persistedWaitingRecoverySnapshot;
                    if (
                        placeholder
                        && snapshot
                        && ['active', 'paused', 'resolved'].includes(snapshot.verdict)
                    ) {
                        placeholder.awaitingInitialRequestAcceptance = false;
                        this.clearPersistedWaitingRecovery();
                        this.startAiRunStatus(placeholder, {
                            invocationText: '',
                        });
                    } else {
                        this.persistedWaitingRecoveryAttempt = Math.max(
                            1,
                            Number(this.persistedWaitingRecoveryAttempt || 0),
                        );
                        this.schedulePersistedWaitingRecovery(
                            this.currentSession,
                            trackedContext.uuid,
                        );
                    }
                    // Outcome remains correlated to the same request UUID.
                    // Recovery may poll/read canonical state, but it must not
                    // resend this turn automatically.
                    try { this.store(); } catch (e) { }
                });
            }, INITIAL_REQUEST_ACCEPTANCE_RECONCILIATION_DELAY_MS);
            trackedContext.initialAcceptanceReconciliationTimer = timer;
            if (context && context !== trackedContext) {
                context.initialAcceptanceReconciliationTimer = timer;
            }
        },
        clearInitialToolReceiptReconciliation(context = null) {
            const trackedContext = context
                ? (this.getTrackedChatRequestContext(context) || context)
                : null;
            if (!trackedContext) return;
            if (trackedContext.initialToolReceiptReconciliationTimer) {
                clearTimeout(trackedContext.initialToolReceiptReconciliationTimer);
                trackedContext.initialToolReceiptReconciliationTimer = null;
            }
            if (context && context !== trackedContext) {
                context.initialToolReceiptReconciliationTimer = null;
            }
        },
        scheduleInitialToolReceiptReconciliation(context) {
            const trackedContext = this.getTrackedChatRequestContext(context);
            if (!trackedContext || trackedContext.reconcileInitialToolReceipts !== true) return;
            const expectedUuid = String(trackedContext.uuid || '');
            if (!expectedUuid) return;
            const hydrate = () => {
                if (!this.isChatRequestContextActive(trackedContext)) return;
                if (!this.currentSession || String(this.currentSession.uuid || '') !== expectedUuid) return;
                this.hydrateToolReceipts({ reset: false });
            };
            // A deterministic context request can commit before this turn's
            // loading bubble has finished attaching its realtime listeners.
            // Reconcile the durable receipt once on acceptance, then once more
            // if the first read races the commit. This remains scoped to the
            // exact request rather than turning unmatched receipts into cards.
            hydrate();
            this.clearInitialToolReceiptReconciliation(trackedContext);
            const timer = setTimeout(() => {
                trackedContext.initialToolReceiptReconciliationTimer = null;
                hydrate();
            }, INITIAL_TOOL_RECEIPT_RECONCILIATION_DELAY_MS);
            trackedContext.initialToolReceiptReconciliationTimer = timer;
            if (context && context !== trackedContext) {
                context.initialToolReceiptReconciliationTimer = timer;
            }
        },
        normalizeToolAction(action) {
            return String(action || '')
                .trim()
                .toLocaleLowerCase('es-ES')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '');
        },
        normalizeToolActionDetail(detail) {
            if (typeof detail === 'string') return detail;
            try { return JSON.stringify(detail == null ? {} : detail); } catch (e) { return String(detail || ''); }
        },
        toolActionDetailFingerprint(detail) {
            const value = this.normalizeToolActionDetail(detail);
            let hash = 2166136261;
            for (let index = 0; index < value.length; index += 1) {
                hash ^= value.charCodeAt(index);
                hash = Math.imul(hash, 16777619);
            }
            return (hash >>> 0).toString(36);
        },
        toolActionIdempotencyKey(scope, receipt, detail = '') {
            const resource = String((receipt && receipt.result_ref && receipt.result_ref.id) || (receipt && receipt.tool_run_id) || 'resource');
            const fingerprint = this.toolActionDetailFingerprint(detail);
            const key = `${scope}:${resource}:${fingerprint}`;
            if (this.toolActionKeys[key]) return this.toolActionKeys[key];
            const suffix = window.crypto && typeof window.crypto.randomUUID === 'function'
                ? window.crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            const value = `${scope}:${fingerprint}:${suffix}`.slice(0, 128);
            this.toolActionKeys[key] = value;
            return value;
        },
        releaseToolActionIdempotencyKey(scope, receipt, detail = '') {
            const resource = String((receipt && receipt.result_ref && receipt.result_ref.id) || (receipt && receipt.tool_run_id) || 'resource');
            const fingerprint = this.toolActionDetailFingerprint(detail);
            delete this.toolActionKeys[`${scope}:${resource}:${fingerprint}`];
        },
        toolActionMutationTimeoutMs() {
            const override = Number(
                typeof window !== 'undefined' && window.__LEXIUS_TOOL_MUTATION_TIMEOUT_MS
            );
            return Number.isFinite(override) && override > 0
                ? override
                : TOOL_ACTION_MUTATION_TIMEOUT_MS;
        },
        cancelToolActionMutationRequests() {
            const controllers = this.toolActionMutationControllers;
            if (!controllers || typeof controllers.forEach !== 'function') return;
            controllers.forEach((controller) => {
                try { controller.abort(); } catch (e) { }
            });
            controllers.clear();
        },
        isToolActionChatCurrent(chatUuid) {
            return !!(
                chatUuid
                && this.currentSession
                && String(this.currentSession.uuid || '') === String(chatUuid)
            );
        },
        assertToolActionChatCurrent(chatUuid) {
            if (this.isToolActionChatCurrent(chatUuid)) return;
            const cancelled = new Error('La acción pertenece a otra conversación.');
            cancelled.name = 'MutationCancelledError';
            throw cancelled;
        },
        async fetchToolActionAttempt(url, options, timeoutMs) {
            const controller = new AbortController();
            let timedOut = false;
            const timeoutId = setTimeout(() => {
                timedOut = true;
                controller.abort();
            }, Math.max(1, Number(timeoutMs) || TOOL_ACTION_MUTATION_TIMEOUT_MS));
            this.toolActionMutationControllers.add(controller);
            try {
                return await fetch(url, { ...options, signal: controller.signal });
            } catch (error) {
                if (timedOut) {
                    const timeoutError = new Error('No pudimos confirmar todavía si la operación se guardó.');
                    timeoutError.name = 'MutationTimeoutError';
                    throw timeoutError;
                }
                if (error && error.name === 'AbortError') {
                    const cancelled = new Error('La acción se canceló al cambiar de conversación.');
                    cancelled.name = 'MutationCancelledError';
                    throw cancelled;
                }
                throw error;
            } finally {
                clearTimeout(timeoutId);
                this.toolActionMutationControllers.delete(controller);
            }
        },
        async fetchToolActionWithReconciliation(url, options, receipt, chatUuid) {
            this.assertToolActionChatCurrent(chatUuid);
            this.toolActionInlineStatus = '';
            this.toolActionInlineStatusToolRunId = '';
            try {
                const response = await this.fetchToolActionAttempt(
                    url,
                    options,
                    this.toolActionMutationTimeoutMs(),
                );
                this.assertToolActionChatCurrent(chatUuid);
                return response;
            } catch (error) {
                if (!error || error.name !== 'MutationTimeoutError') throw error;

                // The server may have committed after the browser lost the
                // response. Keep the caller's idempotency key in ``options``
                // and consult the exact durable receipt before one same-key
                // replay. Never label the action saved from a timeout alone.
                const previousVersion = Number(receipt && receipt.state_version || 0);
                const toolRunId = String(receipt && receipt.tool_run_id || '').trim();
                let durableCommitConfirmed = false;
                this.toolReceiptReconnectStatus = 'checking';
                if (toolRunId && chatUuid) {
                    try {
                        const query = new URLSearchParams({ chat_uuid: chatUuid });
                        const snapshotResponse = await this.fetchToolActionAttempt(
                            `/chat/tool-runs/${encodeURIComponent(toolRunId)}?${query.toString()}`,
                            { method: 'GET' },
                            TOOL_ACTION_RECONCILIATION_TIMEOUT_MS,
                        );
                        const snapshot = await snapshotResponse.json().catch(() => ({}));
                        const authoritative = snapshot && snapshot.receipt
                            && typeof snapshot.receipt === 'object'
                            ? snapshot.receipt
                            : snapshot;
                        durableCommitConfirmed = !!(
                            snapshotResponse.ok
                            && Number(authoritative && authoritative.state_version || 0) > previousVersion
                        );
                    } catch (reconciliationError) {
                        if (reconciliationError && reconciliationError.name === 'MutationCancelledError') {
                            throw reconciliationError;
                        }
                    }
                }
                this.assertToolActionChatCurrent(chatUuid);
                this.toolReceiptReconnectStatus = durableCommitConfirmed
                    ? 'syncing'
                    : 'uncertain';
                try {
                    const response = await this.fetchToolActionAttempt(
                        url,
                        options,
                        TOOL_ACTION_RECONCILIATION_TIMEOUT_MS,
                    );
                    this.assertToolActionChatCurrent(chatUuid);
                    return response;
                } catch (replayError) {
                    if (replayError && replayError.name === 'MutationCancelledError') {
                        throw replayError;
                    }
                    const outcomeUnknown = new Error(
                        'No pudimos confirmar el resultado. Reintenta: conservaremos la misma clave para evitar duplicados.'
                    );
                    outcomeUnknown.name = 'MutationOutcomeUnknownError';
                    throw outcomeUnknown;
                }
            }
        },
        artifactVersionForReceipt(receipt, explicitVersion = null) {
            const candidate = Number(
                explicitVersion
                || (receipt && receipt.ui_payload && receipt.ui_payload.counts && receipt.ui_payload.counts.version)
                || 0
            );
            return Number.isInteger(candidate) && candidate > 0 ? candidate : null;
        },
        artifactWakeupKey(artifactId, version, responseMessageId) {
            const normalizedArtifactId = String(artifactId || '').trim();
            const normalizedResponseId = String(responseMessageId || '').trim();
            const normalizedVersion = Number(version);
            if (
                !normalizedArtifactId
                || !normalizedResponseId
                || !Number.isInteger(normalizedVersion)
                || normalizedVersion <= 0
            ) return '';
            return `${normalizedArtifactId}|${normalizedVersion}|${normalizedResponseId}`;
        },
        artifactReceiptIdentityKey(receipt) {
            if (
                !receipt
                || !receipt.result_ref
                || receipt.result_ref.type !== 'document'
            ) return '';
            return this.artifactWakeupKey(
                receipt.result_ref.id,
                this.artifactVersionForReceipt(receipt),
                receipt.response_message_id,
            );
        },
        isAuthorizedArtifactWakeupReceipt(receipt) {
            const key = this.artifactReceiptIdentityKey(receipt);
            return !!(key && this.authorizedArtifactWakeups[key]);
        },
        rememberAuthorizedArtifactWakeup({ artifactId, version, responseMessageId }) {
            const key = this.artifactWakeupKey(artifactId, version, responseMessageId);
            if (!key) return '';
            const wakeup = {
                artifactId: String(artifactId),
                version: Number(version),
                responseMessageId: String(responseMessageId),
            };
            try { this.$set(this.authorizedArtifactWakeups, key, wakeup); } catch (e) {
                this.authorizedArtifactWakeups[key] = wakeup;
            }
            return key;
        },
        async onRemoteArtifactVersion(data) {
            const artifactId = String(data && data.artifact_id || '').trim();
            const responseMessageId = String(data && data.response_message_id || '').trim();
            const version = Number(data && data.version);
            if (!artifactId || !responseMessageId || !Number.isInteger(version) || version <= 0) return;

            const session = this.currentSession;
            const chatUuid = String(session && session.uuid || '');
            if (!session || !chatUuid) return;
            // Artifact commits are the durable boundary for applying review
            // recommendations. Refresh the compact state even when this
            // viewport does not currently contain the artifact's host bubble.
            this.scheduleActiveReviewRefresh(0);
            let responseMessage = this.findBotForTerminalError(responseMessageId, '');
            if (!responseMessage) {
                const context = this.findChatRequestContextForMessage({ response_message_id: responseMessageId });
                responseMessage = context && context.loadingMessage;
            }
            // A shared event may only wake the exact response that owns the
            // artifact. Never surface it as an uncorrelated conversation card.
            if (!responseMessage) {
                this.hydrateToolReceipts({ reset: false });
                return;
            }

            const query = new URLSearchParams({
                chat_uuid: chatUuid,
                version: String(version),
            });
            try {
                const projectionResponse = await fetch(
                    `/chat/document-artifacts/${encodeURIComponent(artifactId)}?${query.toString()}`,
                );
                const projection = await projectionResponse.json().catch(() => ({}));
                if (!projectionResponse.ok) return;
                // Dependency denial is intentionally returned as a successful
                // opaque projection. It is not proof that this viewer may see
                // (or even learn display metadata about) the artifact.
                if (projection && projection.available === false) return;
                if (String(projection && projection.artifact_id || '') !== artifactId) return;
                if (Number(projection && projection.selected_version) !== version) return;
                const hasAuthorizedContent = !!(
                    typeof projection.content_markdown === 'string'
                    || typeof projection.content_rendered_markdown === 'string'
                );
                if (!hasAuthorizedContent) return;
                if (
                    this.currentSession !== session
                    || String(this.currentSession && this.currentSession.uuid || '') !== chatUuid
                    || !Array.isArray(session.data)
                    || !session.data.includes(responseMessage)
                ) return;

                this.rememberAuthorizedArtifactWakeup({ artifactId, version, responseMessageId });
                this.toolReceiptServerConfirmedChats[chatUuid] = true;
                this.toolReceiptDurableSignalChats[chatUuid] = true;
                try { this.$set(session, 'tool_receipts_server_confirmed', true); } catch (e) {
                    session.tool_receipts_server_confirmed = true;
                }

                const existingReceipt = (Array.isArray(session.tool_receipts) ? session.tool_receipts : [])
                    .find(receipt => this.artifactReceiptIdentityKey(receipt) === (
                        this.artifactWakeupKey(artifactId, version, responseMessageId)
                    ));
                if (existingReceipt) {
                    try { this.$set(existingReceipt, 'artifact_commit_wakeup', true); } catch (e) {
                        existingReceipt.artifact_commit_wakeup = true;
                    }
                } else {
                    const syntheticReceipt = this.normalizeToolReceipt({
                        tool_run_id: `artifact-version-${artifactId}-v${version}-${responseMessageId}`,
                        tool_name: 'crear_editar_archivo',
                        status: 'completed',
                        state_version: version,
                        event_seq: version,
                        response_message_id: responseMessageId,
                        response_message_exists: true,
                        created_at: new Date().toISOString(),
                        ui_payload: {
                            title: 'Documento guardado',
                            summary: 'El documento se guardó en esta conversación.',
                            preview: '',
                            counts: { version },
                            actions: ['Abrir'],
                        },
                        result_ref: { type: 'document', id: artifactId },
                    });
                    if (syntheticReceipt) {
                        syntheticReceipt.artifact_commit_wakeup = true;
                        syntheticReceipt.artifact_commit_synthetic = true;
                        this.reconcileToolReceipts([syntheticReceipt]);
                    }
                }
                try { this.store(); } catch (e) { }
                // The projection proves this viewer may see the artifact. The
                // durable feed can now replace the temporary wake-up metadata
                // with the exact receipt when finalization catches up.
                this.hydrateToolReceipts({ reset: false });
            } catch (error) {
                // Authorization or connectivity failure leaves the event as an
                // opaque wake-up; no event field is ever rendered as content.
            }
        },
        openToolWorkspace(receipt, artifactVersion = null, options = {}) {
            if (!receipt || !receipt.result_ref) return;
            this.toolWorkspaceReturnFocus = document.activeElement;
            const pinnedVersion = this.artifactVersionForReceipt(receipt, artifactVersion);
            const selectedReceipt = pinnedVersion
                ? { ...receipt, requested_artifact_version: pinnedVersion }
                : receipt;
            this.activeToolReceipt = {
                ...selectedReceipt,
                ...(options.reviewFocusMode
                    ? { review_focus_mode: String(options.reviewFocusMode) }
                    : {}),
                ...(options.reviewApplySnapshot
                    ? { review_apply_snapshot: options.reviewApplySnapshot }
                    : {}),
                ...(this.activeReview ? { active_review: this.activeReview } : {}),
            };
        },
        cancelActiveReviewRequest() {
            this.activeReviewRequestGeneration += 1;
            if (this.activeReviewRequestController) {
                try { this.activeReviewRequestController.abort(); } catch (error) { }
                this.activeReviewRequestController = null;
            }
            this.activeReviewLoadPromise = null;
            this.activeReviewLoadPromiseChatUuid = '';
            this.activeReviewLoadToken = null;
            this.activeReviewLoading = false;
            this.activeReviewReloadAfterFlight = false;
        },
        clearActiveReviewTimers() {
            if (this.activeReviewRefreshTimer) {
                clearTimeout(this.activeReviewRefreshTimer);
                this.activeReviewRefreshTimer = null;
            }
            if (this.reviewUpdateNoticeTimer) {
                clearTimeout(this.reviewUpdateNoticeTimer);
                this.reviewUpdateNoticeTimer = null;
            }
            if (this.reviewCompletionTimer) {
                clearTimeout(this.reviewCompletionTimer);
                this.reviewCompletionTimer = null;
            }
        },
        showReviewUpdateNotice() {
            this.reviewUpdateNotice = 'Documento actualizado';
            if (this.reviewUpdateNoticeTimer) clearTimeout(this.reviewUpdateNoticeTimer);
            this.reviewUpdateNoticeTimer = setTimeout(() => {
                this.reviewUpdateNotice = '';
                this.reviewUpdateNoticeTimer = null;
            }, 3500);
        },
        scheduleActiveReviewRefresh(delay = 80) {
            if (this.activeReviewRefreshTimer) clearTimeout(this.activeReviewRefreshTimer);
            const expectedUuid = String(this.currentSession && this.currentSession.uuid || '');
            if (!expectedUuid) return;
            if (
                this.activeReviewLoadPromise
                && String(this.activeReviewLoadPromiseChatUuid || '') === expectedUuid
            ) {
                // A review-state wake-up carries no projection. If it arrives
                // during a same-chat GET, run one more authoritative read
                // after that flight settles instead of coalescing it away.
                this.activeReviewRefreshTimer = null;
                this.activeReviewReloadAfterFlight = true;
                return;
            }
            this.activeReviewRefreshTimer = setTimeout(() => {
                this.activeReviewRefreshTimer = null;
                if (String(this.currentSession && this.currentSession.uuid || '') !== expectedUuid) return;
                this.loadActiveReview();
            }, Math.max(0, Number(delay) || 0));
        },
        applyActiveReviewPayload(
            rawReview,
            { announceApplication = true, fromActiveReviewLoad = false } = {},
        ) {
            if (!fromActiveReviewLoad) {
                // Mutation/intent responses already contain the newer
                // authoritative projection. Fence any older GET or queued
                // refresh so it cannot overwrite this state on a slow client.
                if (this.activeReviewRefreshTimer) {
                    clearTimeout(this.activeReviewRefreshTimer);
                    this.activeReviewRefreshTimer = null;
                }
                if (this.activeReviewLoadPromise || this.activeReviewLoading) {
                    this.cancelActiveReviewRequest();
                } else {
                    this.activeReviewReloadAfterFlight = false;
                }
                const chatUuid = String(this.currentSession && this.currentSession.uuid || '');
                if (chatUuid) this.activeReviewHydratedChatUuid = chatUuid;
                this.activeReviewHydrationError = '';
            }
            const previous = this.activeReview && typeof this.activeReview === 'object'
                ? this.activeReview
                : null;
            const normalized = normalizeActiveReview(rawReview);
            this.activeReview = normalized ? { ...rawReview } : null;
            if (!normalized) {
                this.reviewApplyInFlight = null;
                this.reviewCheckpoint = null;
                return;
            }
            const applyFlight = this.reviewApplyInFlight;
            if (applyFlight) {
                const flightFindingIds = Array.isArray(applyFlight.approvedFindingIds)
                    ? applyFlight.approvedFindingIds
                    : [];
                const stillPendingIds = new Set(normalized.approvedPendingFindingIds);
                const flightWasApplied = flightFindingIds.some(findingId => (
                    !stillPendingIds.has(findingId)
                ));
                if (
                    applyFlight.reviewId !== normalized.reviewId
                    || applyFlight.materialState !== normalized.materialStateKey
                    || flightWasApplied
                ) {
                    this.reviewApplyInFlight = null;
                }
            }
            const previousReviewId = String(previous && (previous.review_id || previous.reviewId) || '');
            const previousAppliedAt = String(previous && previous.last_applied_at || '');
            const nextAppliedAt = String(rawReview && rawReview.last_applied_at || '');
            const previousAppliedCount = Number(previous && previous.applied_count || 0);
            const nextAppliedCount = Number(rawReview && rawReview.applied_count || 0);
            if (
                announceApplication
                && previousReviewId === normalized.reviewId
                && ((nextAppliedAt && nextAppliedAt !== previousAppliedAt)
                    || nextAppliedCount > previousAppliedCount)
            ) {
                this.showReviewUpdateNotice();
                this.reviewDeferredMessage = null;
                this.reviewCheckpoint = null;
            }
            const presentation = getReviewFollowupPresentation(this.activeReview);
            if (this.reviewCompletionTimer) {
                clearTimeout(this.reviewCompletionTimer);
                this.reviewCompletionTimer = null;
            }
            if (presentation.bar.visible && presentation.bar.transient) {
                const reviewId = normalized.reviewId;
                this.reviewCompletionTimer = setTimeout(() => {
                    this.reviewCompletionTimer = null;
                    if (normalizeActiveReview(this.activeReview)?.reviewId === reviewId) {
                        this.dismissActiveReview({ quiet: true, hideLocally: true });
                    }
                }, 3500);
            }
        },
        loadActiveReview({ announceApplication = true } = {}) {
            const chatUuid = String(this.currentSession && this.currentSession.uuid || '').trim();
            if (!chatUuid) {
                this.activeReview = null;
                this.activeReviewHydratedChatUuid = '';
                return Promise.resolve({ ok: true, chatUuid: '', stale: false });
            }
            if (
                this.unconfirmedNewChatUuid === chatUuid
                && !this.toolReceiptServerConfirmedChats?.[chatUuid]
                && this.currentSession.tool_receipts_server_confirmed !== true
            ) {
                // The local UUID precedes creation of the server chat. Leave
                // recovery unhydrated until persisted turn identities or a
                // successful scoped receipt read confirm that row exists.
                return Promise.resolve({ ok: false, chatUuid, stale: false, pendingAcceptance: true });
            }
            if (
                this.activeReviewLoadPromise
                && String(this.activeReviewLoadPromiseChatUuid || '') === chatUuid
            ) {
                return this.activeReviewLoadPromise;
            }
            this.cancelActiveReviewRequest();
            const generation = this.activeReviewRequestGeneration;
            const controller = typeof AbortController === 'function' ? new AbortController() : null;
            // Keep this token primitive. Vue proxies reactive objects, which
            // would break the identity check that clears the in-flight slot.
            const loadToken = generation;
            this.activeReviewRequestController = controller;
            this.activeReviewLoading = true;
            this.activeReviewHydratedChatUuid = '';
            this.activeReviewLoadPromiseChatUuid = chatUuid;
            this.activeReviewLoadToken = loadToken;
            const operation = Promise.resolve().then(async () => {
                try {
                    // Workspace opens rehydrate this state. Give every read a
                    // unique URL so a cached pre-decision projection cannot
                    // replace a newer finding-mutation response.
                    const nonce = (
                        globalThis.crypto
                        && typeof globalThis.crypto.randomUUID === 'function'
                    )
                        ? globalThis.crypto.randomUUID()
                        : `${Date.now()}-${generation}-${Math.random().toString(36).slice(2)}`;
                    const query = new URLSearchParams({
                        chat_uuid: chatUuid,
                        _review_state_nonce: nonce,
                    });
                    const response = await fetch(`/chat/document-reviews/active?${query.toString()}`, {
                        signal: controller ? controller.signal : undefined,
                        cache: 'no-store',
                    });
                    const data = await response.json().catch(() => ({}));
                    if (
                        generation !== this.activeReviewRequestGeneration
                        || String(this.currentSession && this.currentSession.uuid || '') !== chatUuid
                    ) return { ok: false, chatUuid, stale: true };
                    if (response.status === 404) {
                        this.applyActiveReviewPayload(null, {
                            announceApplication: false,
                            fromActiveReviewLoad: true,
                        });
                        this.activeReviewHydratedChatUuid = chatUuid;
                        this.activeReviewHydrationError = '';
                        return { ok: true, chatUuid, stale: false };
                    }
                    if (!response.ok) throw new Error(data.message || 'active_review_unavailable');
                    this.applyActiveReviewPayload(data.active_review, {
                        announceApplication,
                        fromActiveReviewLoad: true,
                    });
                    this.activeReviewHydratedChatUuid = chatUuid;
                    this.activeReviewHydrationError = '';
                    return { ok: true, chatUuid, stale: false };
                } catch (error) {
                    const stale = Boolean(
                        error && error.name === 'AbortError'
                        || generation !== this.activeReviewRequestGeneration
                        || String(this.currentSession && this.currentSession.uuid || '') !== chatUuid
                    );
                    if (!stale) {
                        console.warn('No se pudo cargar el seguimiento de la revisión.', error);
                    }
                    return { ok: false, chatUuid, stale, error };
                } finally {
                    if (generation === this.activeReviewRequestGeneration) {
                        this.activeReviewLoading = false;
                        this.activeReviewRequestController = null;
                    }
                    let reloadAfterFlight = false;
                    if (this.activeReviewLoadToken === loadToken) {
                        this.activeReviewLoadPromise = null;
                        this.activeReviewLoadPromiseChatUuid = '';
                        this.activeReviewLoadToken = null;
                        reloadAfterFlight = !!(
                            this.activeReviewReloadAfterFlight
                            && String(this.currentSession && this.currentSession.uuid || '') === chatUuid
                        );
                        this.activeReviewReloadAfterFlight = false;
                    }
                    if (reloadAfterFlight) {
                        this.scheduleActiveReviewRefresh(0);
                    }
                }
            });
            this.activeReviewLoadPromise = operation;
            return operation;
        },
        async ensureActiveReviewHydratedForMessage(expectedChatUuid, { force = false } = {}) {
            const chatUuid = String(expectedChatUuid || '').trim();
            if (!chatUuid) return { ready: true, stale: false };
            if (String(this.currentSession && this.currentSession.uuid || '') !== chatUuid) {
                return { ready: false, stale: true };
            }

            let result = null;
            if (
                this.activeReviewLoadPromise
                && String(this.activeReviewLoadPromiseChatUuid || '') === chatUuid
            ) {
                result = await this.activeReviewLoadPromise;
            } else if (
                !force
                && String(this.activeReviewHydratedChatUuid || '') === chatUuid
            ) {
                return { ready: true, stale: false };
            } else {
                result = await this.loadActiveReview({ announceApplication: false });
            }

            if (String(this.currentSession && this.currentSession.uuid || '') !== chatUuid) {
                return { ready: false, stale: true };
            }
            if (
                result
                && result.ok === true
                && String(this.activeReviewHydratedChatUuid || '') === chatUuid
            ) {
                this.activeReviewHydrationError = '';
                return { ready: true, stale: false };
            }
            if (result && result.stale) return { ready: false, stale: true };
            return { ready: false, stale: false };
        },
        async retryActiveReviewHydration() {
            if (this.activeReviewSubmitChecking) return;
            this.activeReviewHydrationError = '';
            await this.sendMessage();
        },
        findReviewReceipt(reviewId) {
            const target = String(reviewId || '');
            return (this.currentSession && this.currentSession.tool_receipts || []).find(receipt => (
                receipt
                && receipt.result_ref
                && receipt.result_ref.type === 'review'
                && String(receipt.result_ref.id || '') === target
            )) || null;
        },
        openReviewWorkspace(reviewId, mode = 'all', options = {}) {
            const normalized = normalizeActiveReview(this.activeReview);
            const targetReviewId = String(reviewId || normalized?.reviewId || '');
            if (!targetReviewId) return;
            const activeMatchesTarget = normalized && normalized.reviewId === targetReviewId;
            const receipt = this.findReviewReceipt(targetReviewId) || {
                tool_run_id: String(
                    activeMatchesTarget && this.activeReview && this.activeReview.tool_run_id
                    || `review-${targetReviewId}`
                ),
                tool_name: 'revisar_documento',
                status: 'completed',
                state_version: 1,
                event_seq: 0,
                ui_payload: { title: 'Revisión del documento', actions: [] },
                result_ref: { type: 'review', id: targetReviewId },
            };
            this.openToolWorkspace(receipt, null, {
                reviewFocusMode: mode === 'pending' ? 'pending' : 'all',
                reviewApplySnapshot: options.reviewApplySnapshot || null,
            });
        },
        async dismissActiveReview({ quiet = false, hideLocally = false } = {}) {
            const review = normalizeActiveReview(this.activeReview);
            const chatUuid = String(this.currentSession && this.currentSession.uuid || '');
            if (!review || !chatUuid) return;
            if (hideLocally) {
                this.applyActiveReviewPayload({
                    ...this.activeReview,
                    reminder_dismissed: true,
                }, { announceApplication: false });
            }
            try {
                const response = await fetch(
                    `/chat/document-reviews/${encodeURIComponent(review.reviewId)}/dismiss`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ chat_uuid: chatUuid }),
                    },
                );
                const data = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(data.message || 'dismiss_failed');
                this.applyActiveReviewPayload(data.active_review, { announceApplication: false });
            } catch (error) {
                if (!quiet) this.safeToolActionFailure('No se pudo ocultar el recordatorio. Inténtalo de nuevo.');
            }
        },
        reviewCheckpointForApply(originalMessage, snapshotSource = null) {
            const snapshotPayload = reviewApplySnapshotPayload(
                snapshotSource || this.activeReview,
            );
            if (!snapshotPayload) return null;
            if (snapshotPayload.approvedCount > 0) {
                return {
                    kind: 'approved_pending',
                    originalMessage,
                    copy: `Hay ${snapshotPayload.approvedCount} ${snapshotPayload.approvedCount === 1 ? 'recomendación aprobada' : 'recomendaciones aprobadas'} y ${snapshotPayload.undecidedCount} por decidir. ¿Qué quieres hacer?`,
                    ...snapshotPayload,
                };
            }
            return {
                kind: 'none_approved',
                originalMessage,
                copy: 'Todavía no has elegido qué recomendaciones aplicar.',
                ...snapshotPayload,
            };
        },
        reviewClarificationCheckpoint(kind, originalMessage, copy, snapshotSource) {
            const snapshotPayload = reviewApplySnapshotPayload(snapshotSource);
            if (!snapshotPayload) return null;
            return {
                kind,
                originalMessage,
                copy,
                ...snapshotPayload,
            };
        },
        reviewTargetClarificationCheckpoint(originalMessage, reviewId = '') {
            return {
                kind: 'target_review_unavailable',
                originalMessage,
                reviewId: String(reviewId || ''),
                copy: 'No pudimos confirmar qué revisión quieres usar. ¿Quieres continuar sin aplicar recomendaciones?',
            };
        },
        async resolveReviewIntent(originalMessage) {
            const presentation = getReviewFollowupPresentation(this.activeReview);
            const review = presentation.review;
            const chatUuid = String(this.currentSession && this.currentSession.uuid || '');
            if (!presentation.requiresIntentResolution || !chatUuid) {
                return { proceed: true, action: '' };
            }
            const originatingSnapshot = createReviewApplySnapshot(review);
            this.reviewIntentChecking = true;
            try {
                const response = await fetch('/chat/document-reviews/intent', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        chat_uuid: chatUuid,
                        review_id: review.reviewId,
                        material_state: originatingSnapshot && originatingSnapshot.materialState || '',
                        message: originalMessage,
                    }),
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    if (response.status === 409) {
                        await this.loadActiveReview({ announceApplication: false });
                        const refreshedPresentation = getReviewFollowupPresentation(this.activeReview);
                        if (refreshedPresentation.requiresIntentResolution) {
                            this.reviewCheckpoint = this.reviewClarificationCheckpoint(
                                'ambiguous_review',
                                originalMessage,
                                'La revisión se actualizó. ¿Quieres aplicar sus recomendaciones al documento?',
                                refreshedPresentation.review,
                            );
                            return { proceed: false, action: '' };
                        }
                        return { proceed: true, action: '' };
                    }
                    throw new Error(data.message || 'intent_unavailable');
                }
                const outcome = String(data.outcome || '');
                const targetReviewId = String(data.target_review_id || '').trim();
                const returnedReview = normalizeActiveReview(data.active_review);
                const nonAmbiguousOutcome = [
                    'apply_review_changes',
                    'other_document_edit',
                    'no_document_edit',
                ].includes(outcome);
                const targetDiffers = !!(
                    targetReviewId && targetReviewId !== review.reviewId
                );
                if (
                    targetDiffers
                    && (!returnedReview || returnedReview.reviewId !== targetReviewId)
                ) {
                    this.reviewCheckpoint = this.reviewTargetClarificationCheckpoint(
                        originalMessage,
                        review.reviewId,
                    );
                    return { proceed: false, action: '' };
                }

                let routingReview = review;
                let routingSnapshot = originatingSnapshot;
                const returnedProjectionMatches = !!(
                    returnedReview
                    && (
                        targetReviewId
                            ? returnedReview.reviewId === targetReviewId
                            : returnedReview.reviewId === review.reviewId
                    )
                );
                if (returnedProjectionMatches) {
                    // The intent response may have atomically reactivated an
                    // older review. Install that authorized projection before
                    // deriving any checkpoint, count, identifier, or action.
                    this.applyActiveReviewPayload(data.active_review, {
                        announceApplication: false,
                    });
                    routingReview = normalizeActiveReview(this.activeReview) || returnedReview;
                    routingSnapshot = createReviewApplySnapshot(routingReview);
                }
                if (
                    nonAmbiguousOutcome
                    && targetReviewId
                    && routingReview.reviewId !== targetReviewId
                ) {
                    this.reviewCheckpoint = this.reviewTargetClarificationCheckpoint(
                        originalMessage,
                        review.reviewId,
                    );
                    return { proceed: false, action: '' };
                }
                if (targetReviewId && !routingReview.editable) {
                    return { proceed: true, action: '', reviewId: '' };
                }
                if (outcome === 'apply_review_changes') {
                    if (routingReview.undecidedCount > 0) {
                        this.reviewCheckpoint = this.reviewCheckpointForApply(
                            originalMessage,
                            routingSnapshot,
                        );
                        return { proceed: false, action: '' };
                    }
                    return {
                        proceed: true,
                        action: 'apply_review_changes',
                        reviewId: routingReview.reviewId,
                        applySnapshot: routingSnapshot,
                    };
                }
                if (outcome === 'other_document_edit') {
                    return {
                        proceed: true,
                        action: 'other_document_edit',
                        reviewId: routingReview.reviewId,
                    };
                }
                if (outcome === 'no_document_edit') {
                    return {
                        proceed: true,
                        action: 'no_document_edit',
                        reviewId: routingReview.reviewId,
                    };
                }
                this.reviewCheckpoint = this.reviewClarificationCheckpoint(
                    data.ambiguity_scope === 'review' ? 'ambiguous_review' : 'ambiguous_document',
                    originalMessage,
                    data.ambiguity_scope === 'review'
                        ? '¿Quieres aplicar las recomendaciones de la revisión al documento?'
                        : '¿Quieres actualizar el documento o solo continuar la conversación?',
                    routingSnapshot,
                );
                return { proceed: false, action: '' };
            } catch (error) {
                this.reviewCheckpoint = this.reviewClarificationCheckpoint(
                    'ambiguous_document',
                    originalMessage,
                    '¿Quieres actualizar el documento o solo continuar la conversación?',
                    originatingSnapshot,
                );
                return { proceed: false, action: '' };
            } finally {
                this.reviewIntentChecking = false;
            }
        },
        async resolveExistingMessageReviewRouting(originalMessage) {
            const exactOriginalMessage = String(originalMessage == null ? '' : originalMessage);
            const chatUuid = String(this.currentSession && this.currentSession.uuid || '').trim();
            const originatingReview = normalizeActiveReview(this.activeReview);
            const originatingSnapshot = createReviewApplySnapshot(originatingReview);
            if (!chatUuid) {
                return {
                    proceed: true,
                    options: { reviewIntentResolved: true },
                };
            }
            if (this.activeReviewSubmitChecking || this.reviewIntentChecking) {
                return { proceed: false, options: {} };
            }

            this.activeReviewSubmitChecking = true;
            try {
                let hydration = null;
                try {
                    hydration = await this.ensureActiveReviewHydratedForMessage(
                        chatUuid,
                        { force: true },
                    );
                } catch (error) {
                    hydration = { ready: false, stale: false };
                }
                if (
                    String(this.currentSession && this.currentSession.uuid || '').trim() !== chatUuid
                    || (hydration && hydration.stale)
                ) {
                    return { proceed: false, options: {} };
                }
                if (!hydration || hydration.ready !== true) {
                    // Match ordinary sends: a transient projection read failure
                    // falls through without stale client routing. The backend's
                    // conversational guard can then recover on this turn.
                    this.activeReviewHydrationError = '';
                    return {
                        proceed: true,
                        options: { reviewIntentResolved: true },
                    };
                }

                const presentation = getReviewFollowupPresentation(this.activeReview);
                if (!presentation.requiresIntentResolution) {
                    return {
                        proceed: true,
                        options: { reviewIntentResolved: true },
                    };
                }

                const routing = await this.resolveReviewIntent(exactOriginalMessage);
                if (
                    String(this.currentSession && this.currentSession.uuid || '').trim() !== chatUuid
                    || !routing.proceed
                ) {
                    return { proceed: false, options: {} };
                }
                const applySnapshot = routing.action === 'apply_review_changes'
                    ? validReviewApplySnapshot(routing.applySnapshot)
                    : null;
                if (routing.action === 'apply_review_changes' && !applySnapshot) {
                    this.reviewCheckpoint = this.reviewClarificationCheckpoint(
                        'ambiguous_review',
                        exactOriginalMessage,
                        'La revisión se actualizó. ¿Quieres aplicar sus recomendaciones al documento?',
                        this.activeReview,
                    );
                    return { proceed: false, options: {} };
                }
                const originatingFindingIds = originatingSnapshot
                    ? [...originatingSnapshot.approvedFindingIds].sort()
                    : [];
                const refreshedFindingIds = applySnapshot
                    ? [...applySnapshot.approvedFindingIds].sort()
                    : [];
                const approvedSelectionChanged = !!(
                    routing.action === 'apply_review_changes'
                    && originatingSnapshot
                    && applySnapshot
                    && (
                        originatingSnapshot.reviewId !== applySnapshot.reviewId
                        || originatingSnapshot.materialState !== applySnapshot.materialState
                        || JSON.stringify(originatingFindingIds)
                            !== JSON.stringify(refreshedFindingIds)
                    )
                );
                if (approvedSelectionChanged) {
                    this.reviewCheckpoint = this.reviewClarificationCheckpoint(
                        'ambiguous_review',
                        exactOriginalMessage,
                        'La revisión se actualizó. ¿Quieres aplicar sus recomendaciones al documento?',
                        this.activeReview,
                    );
                    return { proceed: false, options: {} };
                }
                return {
                    proceed: true,
                    options: {
                        reviewIntentResolved: true,
                        reviewFollowupAction: routing.action,
                        reviewId: String(routing.reviewId || ''),
                        ...(applySnapshot ? {
                            reviewApplySnapshot: applySnapshot,
                            reviewMaterialState: applySnapshot.materialState,
                            reviewFindingIds: [...applySnapshot.approvedFindingIds],
                            reviewApprovedCount: applySnapshot.approvedCount,
                        } : {}),
                    },
                };
            } finally {
                if (
                    String(this.currentSession && this.currentSession.uuid || '').trim() === chatUuid
                ) {
                    this.activeReviewSubmitChecking = false;
                }
            }
        },
        async sendReviewMessage(originalMessage, action, {
            preserveDraft = false,
            reviewId = '',
            applySnapshot = null,
        } = {}) {
            const frozenApplySnapshot = action === 'apply_review_changes'
                ? validReviewApplySnapshot(applySnapshot)
                : null;
            const targetReviewId = String(
                frozenApplySnapshot && frozenApplySnapshot.reviewId || reviewId || '',
            );
            if (action && !targetReviewId) return;
            if (action === 'apply_review_changes' && !frozenApplySnapshot) {
                this.safeToolActionFailure('La revisión cambió. Vuelve a revisar las recomendaciones antes de aplicarlas.');
                return;
            }
            if (action && this.lastError) {
                // A clarification choice is an explicit new turn and may
                // supersede the failed replay that opened the checkpoint.
                this.clearLastChatError();
            }
            if (frozenApplySnapshot) {
                this.reviewApplyInFlight = {
                    chatUuid: String(this.currentSession && this.currentSession.uuid || ''),
                    reviewId: frozenApplySnapshot.reviewId,
                    materialState: frozenApplySnapshot.materialState,
                    approvedFindingIds: [...frozenApplySnapshot.approvedFindingIds],
                    approvedCount: frozenApplySnapshot.approvedCount,
                };
            }
            const priorDraft = this.message;
            this.message = String(originalMessage || '');
            try {
                await this.sendMessage({
                    reviewIntentResolved: true,
                    ...(action ? {
                        reviewFollowupAction: action,
                        reviewId: targetReviewId,
                    } : {}),
                    ...(frozenApplySnapshot ? {
                        reviewApplySnapshot: frozenApplySnapshot,
                        reviewMaterialState: frozenApplySnapshot.materialState,
                        reviewFindingIds: [...frozenApplySnapshot.approvedFindingIds],
                        reviewApprovedCount: frozenApplySnapshot.approvedCount,
                    } : {}),
                });
            } finally {
                if (
                    frozenApplySnapshot
                    && !this.replying
                    && !(typeof this.hasOutstandingChatWork === 'function' && this.hasOutstandingChatWork())
                ) {
                    this.reviewApplyInFlight = null;
                }
            }
            if (preserveDraft) {
                this.message = priorDraft;
                this.$nextTick(() => this.adjustTextareaHeightAndCheckLength());
            }
        },
        async sendExplicitReviewApply(originalMessage = '', snapshotSource = null) {
            const snapshot = validReviewApplySnapshot(snapshotSource);
            if (!snapshot) {
                this.safeToolActionFailure('La revisión cambió. Vuelve a revisar las recomendaciones antes de aplicarlas.');
                return;
            }
            const exactOriginal = String(originalMessage || '');
            const generatedMessage = snapshot.approvedCount === 1
                ? 'Aplica la recomendación aprobada de la revisión al documento.'
                : `Aplica las ${snapshot.approvedCount} recomendaciones aprobadas de la revisión al documento.`;
            this.reviewCheckpoint = null;
            await this.sendReviewMessage(
                exactOriginal || generatedMessage,
                'apply_review_changes',
                {
                    preserveDraft: !exactOriginal && !!String(this.message || ''),
                    reviewId: snapshot.reviewId,
                    applySnapshot: snapshot,
                },
            );
        },
        showReviewApplyConfirmation(snapshotSource = null, originalMessage = '') {
            const snapshot = validReviewApplySnapshot(snapshotSource);
            if (!snapshot) {
                this.safeToolActionFailure('La revisión cambió. Vuelve a revisar las recomendaciones antes de aplicarlas.');
                return false;
            }
            this.reviewCheckpoint = null;
            this.reviewDeferredMessage = {
                reviewId: snapshot.reviewId,
                message: String(originalMessage || ''),
            };
            this.openReviewWorkspace(snapshot.reviewId, 'all', {
                reviewApplySnapshot: snapshot,
            });
            return true;
        },
        async handleReviewAction(payload = {}) {
            const action = String(payload.id || payload.action || '');
            const reviewId = String(payload.reviewId || payload.review_id || '');
            if (
                action === REVIEW_FOLLOWUP_ACTION.REVIEW_PENDING
                || action === REVIEW_FOLLOWUP_ACTION.VIEW_REVIEW
            ) {
                const mode = payload.workspaceMode === 'pending' ? 'pending' : 'all';
                this.openReviewWorkspace(reviewId, mode);
                return;
            }
            if (action === REVIEW_FOLLOWUP_ACTION.APPLY_APPROVED || action === 'apply_review_changes') {
                this.showReviewApplyConfirmation(payload.applySnapshot);
            }
        },
        handleReviewStateChanged(activeReview = null) {
            if (activeReview && typeof activeReview === 'object') {
                this.applyActiveReviewPayload(activeReview);
            } else {
                this.scheduleActiveReviewRefresh(0);
            }
        },
        async handleWorkspaceReviewAction(payload = {}) {
            if (String(payload.action || '') !== 'apply_review_changes') return;
            const snapshot = validReviewApplySnapshot(payload.applySnapshot);
            if (!snapshot) {
                this.safeToolActionFailure('La revisión cambió. Vuelve a revisar las recomendaciones antes de aplicarlas.');
                return;
            }
            const deferredState = this.reviewDeferredMessage;
            const deferred = deferredState
                && String(deferredState.reviewId || '') === snapshot.reviewId
                ? String(deferredState.message || '')
                : '';
            this.reviewDeferredMessage = null;
            this.closeToolWorkspace();
            await this.sendExplicitReviewApply(deferred, snapshot);
        },
        reviewCheckpointReviewPending() {
            const checkpoint = this.reviewCheckpoint;
            if (!checkpoint) return;
            this.reviewDeferredMessage = {
                reviewId: String(checkpoint.reviewId || ''),
                message: String(checkpoint.originalMessage || ''),
            };
            this.reviewCheckpoint = null;
            this.openReviewWorkspace(checkpoint.reviewId, 'pending');
        },
        reviewCheckpointApplyApproved() {
            const checkpoint = this.reviewCheckpoint;
            const originalMessage = String(checkpoint && checkpoint.originalMessage || '');
            this.showReviewApplyConfirmation(
                checkpoint && checkpoint.applySnapshot,
                originalMessage,
            );
        },
        async reviewCheckpointContinue(action) {
            const checkpoint = this.reviewCheckpoint;
            const originalMessage = String(checkpoint && checkpoint.originalMessage || '');
            const reviewId = String(checkpoint && checkpoint.reviewId || '');
            this.reviewCheckpoint = null;
            await this.sendReviewMessage(originalMessage, action, { reviewId });
        },
        reviewCheckpointApplyReview() {
            const checkpoint = this.reviewCheckpoint;
            const originalMessage = String(checkpoint && checkpoint.originalMessage || '');
            const snapshot = checkpoint && checkpoint.applySnapshot;
            this.reviewCheckpoint = null;
            if (validReviewApplySnapshot(snapshot)) {
                this.showReviewApplyConfirmation(snapshot, originalMessage);
                return;
            }
            if (snapshot && snapshot.undecidedCount > 0) {
                this.reviewCheckpoint = this.reviewCheckpointForApply(originalMessage, snapshot);
                return;
            }
            this.safeToolActionFailure('La revisión cambió. Vuelve a revisar las recomendaciones antes de aplicarlas.');
        },
        safeToolActionFailure(message, toolRunId = '') {
            const safeMessage = String(message || 'No se pudo completar esta acción. Inténtalo de nuevo.');
            this.toolActionInlineStatus = safeMessage;
            this.toolActionInlineStatusToolRunId = String(toolRunId || '');
        },
        async getArtifactProjectionForReceipt(receipt, artifactVersion = null) {
            if (!receipt || !receipt.result_ref || receipt.result_ref.type !== 'document') {
                throw new Error('artifact_unavailable');
            }
            const chatUuid = String(this.currentSession && this.currentSession.uuid || '');
            if (!chatUuid) throw new Error('chat_unavailable');
            const query = new URLSearchParams({ chat_uuid: chatUuid });
            const pinnedVersion = this.artifactVersionForReceipt(receipt, artifactVersion);
            if (pinnedVersion) query.set('version', String(pinnedVersion));
            const response = await fetch(
                `/chat/document-artifacts/${encodeURIComponent(String(receipt.result_ref.id))}?${query.toString()}`,
            );
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw createToolResultAccessError(toolResultAccessFeedback({
                    response,
                    resultType: 'document',
                    retryable: data.retryable === true,
                }));
            }
            return data;
        },
        downloadBlob(blob, filename) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = String(filename || 'documento').replace(/[^\w\-. áéíóúüñ]/gi, '-');
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        },
        async downloadArtifactForReceipt(receipt, format, artifactVersion = null) {
            try {
                const projection = await this.getArtifactProjectionForReceipt(receipt, artifactVersion);
                const version = Number(projection.selected_version || this.artifactVersionForReceipt(receipt, artifactVersion) || 0);
                if (!version) throw new Error('artifact_version_unavailable');
                const query = new URLSearchParams({
                    chat_uuid: String(this.currentSession && this.currentSession.uuid || ''),
                    format,
                });
                const response = await fetch(
                    `/chat/document-artifacts/${encodeURIComponent(String(receipt.result_ref.id))}/versions/${version}/export?${query.toString()}`,
                );
                if (!response.ok) {
                    throw createToolResultAccessError(toolResultAccessFeedback({
                        response,
                        resultType: 'document',
                    }));
                }
                const stem = String(projection.filename || 'documento.md').replace(/\.md$/i, '');
                this.downloadBlob(await response.blob(), `${stem}.${format}`);
                return true;
            } catch (error) {
                const feedback = toolResultAccessFeedback({ error, resultType: 'document' });
                this.safeToolActionFailure(`${feedback.title}. ${feedback.message}`);
                return false;
            }
        },
        async copyArtifactForReceipt(receipt, artifactVersion = null, artifactClipboardContent = '') {
            try {
                let content = String(artifactClipboardContent || '');
                if (!content) {
                    const projection = await this.getArtifactProjectionForReceipt(receipt, artifactVersion);
                    content = String(
                        projection.content_export_markdown
                        || projection.content_rendered_markdown
                        || projection.content_markdown
                        || ''
                    );
                }
                if (!content) throw new Error('clipboard_unavailable');
                let html;
                try {
                    const processed = typeof window.processMarkdownContent === 'function'
                        ? window.processMarkdownContent(content)
                        : content;
                    const rendered = window.marked && typeof window.marked.parse === 'function'
                        ? window.marked.parse(processed)
                        : escapePlaintextHtml(content);
                    const transformed = typeof window.processConvertedHTML === 'function'
                        ? window.processConvertedHTML(rendered, content)
                        : rendered;
                    html = sanitizeRichHtml(transformed, content, {
                        FORBID_TAGS: [
                            'script', 'style', 'iframe', 'object', 'embed', 'form', 'input',
                            'img', 'picture', 'source', 'video', 'audio', 'track',
                        ],
                        FORBID_ATTR: [
                            'style', 'src', 'srcset', 'poster', 'background', 'onerror', 'onclick',
                        ],
                    });
                    const clipboardDocument = new DOMParser().parseFromString(html, 'text/html');
                    clipboardDocument.querySelectorAll('strong, b').forEach(element => {
                        element.style.fontWeight = '700';
                    });
                    html = clipboardDocument.body.innerHTML;
                } catch (error) {
                    html = escapePlaintextHtml(content);
                }
                await writeClipboardContent({
                    clipboard: navigator.clipboard,
                    ClipboardItemClass: window.ClipboardItem,
                    BlobClass: window.Blob,
                    html,
                    plaintext: content,
                });
                return true;
            } catch (error) {
                if (!error || !error.toolResultAccessFeedback) {
                    this.safeToolActionFailure('No se pudo copiar el documento. Selecciona el texto para copiarlo manualmente.');
                    return false;
                }
                const feedback = toolResultAccessFeedback({ error, resultType: 'document' });
                this.safeToolActionFailure(`${feedback.title}. ${feedback.message}`);
                return false;
            }
        },
        async resolveContextReceipt(receipt, resolution) {
            if (!receipt || !receipt.result_ref || receipt.result_ref.type !== 'context_request' || this.contextResumeBusy) return;
            this.contextResumeBusy = true;
            const chatUuid = String(this.currentSession && this.currentSession.uuid || '');
            const scope = `context-${resolution}`;
            const expectedResolutionEpoch = Math.max(
                0,
                Number.parseInt(String(receipt.result_ref.resolution_epoch || 0), 10) || 0,
            );
            const operationDetail = {
                context_request_id: String(receipt.result_ref.id || ''),
                expected_resolution_epoch: expectedResolutionEpoch,
                resolution,
                source_ids: [],
                source_selections: [],
                attached_documents: [],
                resume_message: '',
            };
            try {
                const response = await this.fetchToolActionWithReconciliation(
                    `/chat/context-requests/${encodeURIComponent(String(receipt.result_ref.id))}/resolve`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            chat_uuid: chatUuid,
                            resolution,
                            source_ids: [],
                            source_selections: [],
                            expected_resolution_epoch: expectedResolutionEpoch,
                            idempotency_key: this.toolActionIdempotencyKey(scope, receipt, operationDetail),
                        }),
                    },
                    receipt,
                    chatUuid,
                );
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    const mismatch = String(data.error || data.code || '') === 'idempotency_payload_mismatch';
                    const failure = new Error(data.message || 'context_action_failed');
                    if (mismatch) failure.name = 'IdempotencyPayloadMismatchError';
                    throw failure;
                }
                if (!this.isToolActionChatCurrent(chatUuid)) return null;
                this.releaseToolActionIdempotencyKey(scope, receipt, operationDetail);
                if (resolution === 'skip') {
                    this.markPausedBotResuming(receipt, data && data.continuation_job_id);
                } else {
                    this.bindPausedBotToReceipt(receipt, data && data.continuation_job_id);
                }
                this.startContinuationProjectionCatchUp(receipt, data && data.continuation_job_id);
                this.catchUpContinuationState({ resetReceipts: false });
                return data;
            } catch (error) {
                if (error && error.name === 'MutationCancelledError') return null;
                this.safeToolActionFailure(
                    error && error.name === 'IdempotencyPayloadMismatchError'
                        ? 'La selección cambió desde el intento anterior. Revisa la solicitud y vuelve a enviarla.'
                        : resolution === 'skip'
                        ? 'No se pudo continuar sin documentos. Inténtalo de nuevo.'
                        : 'No se pudo cancelar la solicitud. Inténtalo de nuevo.'
                );
                return null;
            } finally {
                this.contextResumeBusy = false;
            }
        },
        findWaitingContextReceipt() {
            const receipts = this.currentSession && Array.isArray(this.currentSession.tool_receipts)
                ? this.currentSession.tool_receipts
                : [];
            for (let index = receipts.length - 1; index >= 0; index -= 1) {
                const receipt = receipts[index];
                if (!receipt || receipt.status !== 'waiting_user') continue;
                if (!receipt.result_ref || receipt.result_ref.type !== 'context_request') continue;
                if (!String(receipt.result_ref.id || '').trim()) continue;
                // Do not revive an older receipt just because another answer
                // happens to be paused. The request must still own a visible
                // paused assistant placeholder in this chat.
                if (!this.findPausedBotForReceipt(receipt)) continue;
                return receipt;
            }
            return null;
        },
        contextRequestItemsForReceipt(receipt) {
            const ui = receipt && receipt.ui_payload && typeof receipt.ui_payload === 'object'
                ? receipt.ui_payload
                : {};
            const rawItems = Array.isArray(ui.requested_documents)
                ? ui.requested_documents
                : (Array.isArray(ui.requests) ? ui.requests : []);
            return rawItems.map((item) => {
                if (!item || typeof item !== 'object') return null;
                const requestItemId = String(item.request_item_id || item.id || '').trim().slice(0, 128);
                if (!requestItemId) return null;
                const rawAcceptedTypes = Array.isArray(item.accepted_types)
                    ? item.accepted_types
                    : (Array.isArray(item.tipos_aceptados) ? item.tipos_aceptados : ['documento']);
                const acceptedTypes = [...new Set(rawAcceptedTypes.map((type) => {
                    const normalized = String(type || '').trim().toLocaleLowerCase('es-ES');
                    return ({ document: 'documento', image: 'imagen' })[normalized] || normalized;
                }).filter(type => ['documento', 'imagen', 'audio', 'video'].includes(type)))];
                return {
                    request_item_id: requestItemId,
                    priority: String(item.priority || item.prioridad || '').toLocaleLowerCase('es-ES') === 'necesario'
                        ? 'necesario'
                        : 'recomendado',
                    accepted_types: acceptedTypes.length ? acceptedTypes : ['documento'],
                };
            }).filter(Boolean);
        },
        contextAttachmentCategory(document) {
            const explicitKind = String(document && document.source_kind || '').trim().toLocaleLowerCase('es-ES');
            if (explicitKind === 'image' || explicitKind === 'imagen') return 'imagen';
            if (explicitKind === 'audio') return 'audio';
            if (explicitKind === 'video') return 'video';
            const locator = `${String(document && document.path || '')} ${String(document && document.page_title || '')}`
                .toLocaleLowerCase('es-ES');
            const extensionMatches = [...locator.matchAll(/\.([a-z0-9]+)(?=[^a-z0-9]|$)/g)];
            const extension = extensionMatches.length
                ? extensionMatches[extensionMatches.length - 1][1]
                : '';
            if (['png', 'jpg', 'jpeg', 'webp', 'heic', 'gif', 'bmp', 'tif', 'tiff'].includes(extension)) return 'imagen';
            if (['aac', 'aiff', 'flac', 'm4a', 'mp3', 'oga', 'ogg', 'opus', 'wav', 'wma'].includes(extension)) return 'audio';
            if (['3g2', '3gp', 'avi', 'm4v', 'mkv', 'mov', 'mp4', 'mpeg', 'mpg', 'webm', 'wmv'].includes(extension)) return 'video';
            return 'documento';
        },
        bindContextAttachmentsToRequestItems(receipt, attachedDocuments) {
            const requestItems = this.contextRequestItemsForReceipt(receipt);
            if (!requestItems.length) {
                return {
                    attachments: attachedDocuments,
                    missingNecessary: [],
                    hasUnbound: false,
                };
            }
            const covered = new Set();
            let hasUnbound = false;
            const attachments = attachedDocuments.map((document) => {
                const category = this.contextAttachmentCategory(document);
                const compatible = requestItems.filter(item => item.accepted_types.includes(category));
                const chosen = compatible.find(item => (
                    item.priority === 'necesario' && !covered.has(item.request_item_id)
                )) || compatible.find(item => !covered.has(item.request_item_id)) || compatible[0];
                if (!chosen) {
                    hasUnbound = true;
                    return { ...document, request_item_id: '' };
                }
                covered.add(chosen.request_item_id);
                return { ...document, request_item_id: chosen.request_item_id };
            });
            return {
                attachments,
                missingNecessary: requestItems.filter(item => (
                    item.priority === 'necesario' && !covered.has(item.request_item_id)
                )),
                hasUnbound,
            };
        },
        appendVisibleContextResumeMessage(receipt, continuationJobId, payload) {
            if (!this.currentSession || !Array.isArray(this.currentSession.data)) return null;
            const messageId = String(payload && payload.message_id || '').trim();
            const text = String(payload && payload.content || '').trim();
            if (!messageId || !text) return null;

            const messages = this.currentSession.data;
            let message = messages.find((item) => (
                item && item.sender === 'user' && String(item.message_id || '').trim() === messageId
            ));
            const pausedBot = this.bindPausedBotToReceipt(receipt, continuationJobId);
            if (!message) {
                const serverTimestamp = String(payload && payload.timestamp || '').trim();
                const parsedTimestamp = serverTimestamp ? Date.parse(serverTimestamp) : NaN;
                message = {
                    text,
                    sender: 'user',
                    timestamp: Number.isNaN(parsedTimestamp) ? Date.now() : parsedTimestamp,
                    server_timestamp: serverTimestamp,
                    message_id: messageId,
                    turn_id: String(payload && payload.turn_id || '').trim(),
                    sender_email: this.getCurrentUserEmail(),
                    sender_display_name: this.getCurrentUserEmail(),
                    chat_context_resume_message: true,
                };
                const pausedIndex = pausedBot ? messages.indexOf(pausedBot) : -1;
                if (pausedIndex >= 0) messages.splice(pausedIndex, 0, message);
                else messages.push(message);
                this.reserveWindowedChatIndexes(message);
            }
            if (pausedBot) {
                this.annotateAiReplyAnchor(pausedBot, message);
                try { this.$set(pausedBot, 'resumingContext', true); } catch (e) { pausedBot.resumingContext = true; }
                try { this.$set(pausedBot, 'continuationNeedsAction', false); } catch (e) { pausedBot.continuationNeedsAction = false; }
                this.repositionAiReplyAfterAnchor(pausedBot);
            }
            return message;
        },
        contextDocumentBindingDelayMs(attempt) {
            const override = typeof window !== 'undefined'
                ? window.__LEXIUS_CONTEXT_BINDING_RETRY_DELAYS_MS
                : null;
            if (Array.isArray(override) && override.length) {
                const candidate = Number(override[Math.min(attempt, override.length - 1)]);
                if (Number.isFinite(candidate) && candidate >= 0) return candidate;
            }
            const base = Math.min(1200, 150 * (2 ** Math.max(0, attempt)));
            return base + Math.floor(Math.random() * Math.min(100, Math.max(1, base / 3)));
        },
        async reconcileWaitingContextReceipt() {
            for (let attempt = 0; attempt < CONTEXT_DOCUMENT_BINDING_MAX_ATTEMPTS; attempt += 1) {
                const existing = this.findWaitingContextReceipt();
                if (existing) return existing;
                await this.hydrateToolReceipts({ reset: attempt > 0 });
                const hydrated = this.findWaitingContextReceipt();
                if (hydrated) return hydrated;
                if (attempt + 1 < CONTEXT_DOCUMENT_BINDING_MAX_ATTEMPTS) {
                    await new Promise(resolve => setTimeout(
                        resolve,
                        this.contextDocumentBindingDelayMs(attempt),
                    ));
                }
            }
            return null;
        },
        retryContextDocumentBinding() {
            if (this.contextResumeBusy || this.dictationBusy || this.dictationEditorBusy || this.composerSendInFlight) return;
            this.resumeWaitingContextWithPendingDocuments(String(this.message || '').trim());
        },
        async resumeWaitingContextWithPendingDocuments(outgoingText) {
            if (this.dictationBusy || this.dictationEditorBusy) return true;
            const attachedDocuments = this.getPendingContextDocumentsPayload(
                this.currentSession,
                { includeCatalogBindings: false },
            );
            if (!attachedDocuments.length) return false;
            let receipt = this.findWaitingContextReceipt();
            const hasPausedBot = !!(
                this.currentSession
                && Array.isArray(this.currentSession.data)
                && this.currentSession.data.some((message) => (
                    message && message.sender === 'bot' && message.waitingForUser === true
                ))
            );
            if (!receipt && !hasPausedBot) return false;
            // Once a paused response and pending documents coexist, this send
            // is unambiguously a context binding attempt. Never let a delayed
            // receipt turn it into an unrelated new AI generation.
            if (this.contextResumeBusy) return true;
            this.contextResumeBusy = true;
            this.contextDocumentBindingError = '';

            try {
                if (!receipt) {
                    this.contextDocumentBindingState = 'binding_context_documents';
                    receipt = await this.reconcileWaitingContextReceipt();
                }
                if (!receipt) {
                    this.contextDocumentBindingState = 'failed';
                    this.contextDocumentBindingError = (
                        'Todavía no pudimos vincular estos documentos con la solicitud pausada. ' +
                        'Tu mensaje y los archivos siguen aquí.'
                    );
                    return true;
                }
                if (attachedDocuments.length > MAX_CONTEXT_RESUME_ATTACHMENTS) {
                    this.contextDocumentBindingState = 'failed';
                    this.contextDocumentBindingError = (
                        `Esta solicitud puede continuar con hasta ${MAX_CONTEXT_RESUME_ATTACHMENTS} documentos a la vez. ` +
                        'Reduce los adjuntos y vuelve a intentarlo.'
                    );
                    return true;
                }
                if (String(outgoingText || '').length > MAX_CONTEXT_RESUME_MESSAGE_CHARACTERS) {
                    this.contextDocumentBindingState = 'failed';
                    this.contextDocumentBindingError = (
                        'Para continuar con estos documentos, reduce el mensaje a 4.000 caracteres o menos.'
                    );
                    return true;
                }

                const chatUuid = String(this.currentSession && this.currentSession.uuid || '');
                if (!chatUuid) {
                    this.contextDocumentBindingState = 'failed';
                    this.contextDocumentBindingError = 'No pudimos identificar esta conversación. Tus archivos siguen adjuntos.';
                    return true;
                }
                const scope = 'context-attached-documents';
                const normalizedResumeMessage = String(outgoingText || '').replace(/\r\n?/g, '\n').trim();
                const unboundAttachments = attachedDocuments.map((document) => ({
                    path: String(document.path || '').trim(),
                    page_title: String(document.page_title || '').slice(0, 300),
                    is_user_document: document.is_user_document === true,
                    source_kind: String(document.source_kind || '').trim().slice(0, 64),
                }));
                const binding = this.bindContextAttachmentsToRequestItems(receipt, unboundAttachments);
                if (binding.hasUnbound) {
                    this.contextDocumentBindingState = 'failed';
                    this.contextDocumentBindingError = (
                        'Uno de los archivos no tiene un tipo aceptado para esta solicitud. ' +
                        'Tus archivos siguen aquí.'
                    );
                    return true;
                }
                const canonicalAttachments = binding.attachments.map((document) => {
                    const canonical = {
                        path: document.path,
                        page_title: document.page_title,
                        is_user_document: document.is_user_document,
                    };
                    if (document.request_item_id) canonical.request_item_id = document.request_item_id;
                    return canonical;
                });
                const operationDetail = {
                    context_request_id: String(receipt.result_ref.id || ''),
                    expected_resolution_epoch: Math.max(
                        0,
                        Number.parseInt(String(receipt.result_ref.resolution_epoch || 0), 10) || 0,
                    ),
                    resolution: 'selected',
                    source_ids: [],
                    source_selections: [],
                    attached_documents: canonicalAttachments,
                    resume_message: normalizedResumeMessage,
                };
                this.contextDocumentBindingState = 'resolving_context_documents';
                const response = await this.fetchToolActionWithReconciliation(
                    `/chat/context-requests/${encodeURIComponent(String(receipt.result_ref.id))}/resolve`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            chat_uuid: chatUuid,
                            resolution: 'selected',
                            source_ids: [],
                            source_selections: [],
                            attached_documents: canonicalAttachments,
                            resume_message: normalizedResumeMessage,
                            expected_resolution_epoch: operationDetail.expected_resolution_epoch,
                            idempotency_key: this.toolActionIdempotencyKey(scope, receipt, operationDetail),
                        }),
                    },
                    receipt,
                    chatUuid,
                );
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    const mismatch = String(data.error || data.code || '') === 'idempotency_payload_mismatch';
                    const failure = new Error(data.message || 'context_attachment_resume_failed');
                    if (mismatch) failure.name = 'IdempotencyPayloadMismatchError';
                    throw failure;
                }
                if (!this.isToolActionChatCurrent(chatUuid)) return true;
                const resumeMessage = data && data.resume_message;
                if (!resumeMessage || !String(resumeMessage.message_id || '').trim()
                    || !String(resumeMessage.content || '').trim()) {
                    throw new Error('context_attachment_resume_not_confirmed');
                }

                const visibleMessage = this.appendVisibleContextResumeMessage(
                    receipt,
                    data && data.continuation_job_id,
                    resumeMessage,
                );
                if (!visibleMessage) throw new Error('context_attachment_resume_not_visible');
                this.releaseToolActionIdempotencyKey(scope, receipt, operationDetail);
                this.clearPendingContextDocuments();
                this.message = null;
                this.publishTypingState(false);
                this.replying = true;
                this.loadingSuggestedQuestions = true;
                this.contextDocumentBindingState = 'idle';
                this.contextDocumentBindingError = '';
                this.store();
                this.startContinuationProjectionCatchUp(receipt, data && data.continuation_job_id);
                this.catchUpContinuationState({ resetReceipts: false });
                return true;
            } catch (error) {
                if (error && error.name === 'MutationCancelledError') return true;
                this.contextDocumentBindingState = 'failed';
                this.contextDocumentBindingError = error && error.name === 'IdempotencyPayloadMismatchError'
                    ? 'Los documentos o el mensaje cambiaron desde el intento anterior. Revísalos y vuelve a intentarlo; no se eliminó nada.'
                    : (
                        'No se pudieron usar los documentos para continuar. Tu mensaje sigue listo para enviar; ' +
                        'revisa la solicitud de documentos o inténtalo de nuevo.'
                    );
                return true;
            } finally {
                this.contextResumeBusy = false;
            }
        },
        async transitionContinuationReceipt(receipt, action) {
            if (!receipt || !receipt.result_ref || receipt.result_ref.type !== 'continuation') return;
            const chatUuid = String(this.currentSession && this.currentSession.uuid || '');
            const scope = `continuation-${action}`;
            try {
                const response = await this.fetchToolActionWithReconciliation(
                    `/chat/continuations/${encodeURIComponent(String(receipt.result_ref.id))}/${action}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            chat_uuid: chatUuid,
                            idempotency_key: this.toolActionIdempotencyKey(scope, receipt),
                        }),
                    },
                    receipt,
                    chatUuid,
                );
                if (!response.ok) throw new Error('continuation_action_failed');
                if (!this.isToolActionChatCurrent(chatUuid)) return;
                this.releaseToolActionIdempotencyKey(scope, receipt);
                if (action === 'retry') {
                    this.markPausedBotResuming(receipt, receipt.result_ref.id);
                }
                this.hydrateToolReceipts();
            } catch (error) {
                if (error && error.name === 'MutationCancelledError') return;
                this.safeToolActionFailure('No se pudo actualizar la respuesta. Inténtalo de nuevo.');
            }
        },
        async resolveReplacementConfirmation(receipt, action) {
            if (
                !receipt || !receipt.result_ref
                || receipt.result_ref.type !== 'replacement_confirmation'
                || !['confirm', 'cancel'].includes(action)
            ) return;
            if (
                action === 'confirm'
                && !window.confirm(
                    '¿Confirmas este reemplazo completo? Se guardará como una nueva versión reversible.'
                )
            ) return;
            const chatUuid = String(this.currentSession && this.currentSession.uuid || '');
            const scope = `replacement-confirmation-${action}`;
            try {
                const response = await this.fetchToolActionWithReconciliation(
                    `/chat/document-replacement-confirmations/${encodeURIComponent(String(receipt.result_ref.id))}/${action}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            chat_uuid: chatUuid,
                            idempotency_key: this.toolActionIdempotencyKey(scope, receipt),
                        }),
                    },
                    receipt,
                    chatUuid,
                );
                if (!response.ok) throw new Error('replacement_confirmation_failed');
                if (!this.isToolActionChatCurrent(chatUuid)) return;
                this.releaseToolActionIdempotencyKey(scope, receipt);
                this.hydrateToolReceipts();
            } catch (error) {
                if (error && error.name === 'MutationCancelledError') return;
                this.safeToolActionFailure(
                    action === 'confirm'
                        ? 'No se pudo confirmar el reemplazo. Actualiza el documento e inténtalo de nuevo.'
                        : 'No se pudo cancelar el reemplazo. Inténtalo de nuevo.',
                    receipt.tool_run_id,
                );
            }
        },
        handleToolWorkspaceResolved(data = {}) {
            this.markPausedBotResuming(
                this.activeToolReceipt,
                data && data.continuation_job_id,
            );
            this.startContinuationProjectionCatchUp(
                this.activeToolReceipt,
                data && data.continuation_job_id,
            );
            this.catchUpContinuationState({ resetReceipts: false });
        },
        async handleToolAction({
            action,
            receipt,
            artifactVersion,
            artifactClipboardContent,
            complete,
        } = {}) {
            const settleArtifactAction = successful => {
                if (typeof complete === 'function') complete(successful);
            };
            if (!receipt || !receipt.result_ref) {
                settleArtifactAction(false);
                return;
            }
            const normalized = this.normalizeToolAction(action);
            if (normalized === 'artifact-open' || normalized === 'abrir' || normalized === 'abrir documento'
                || normalized === 'seleccionar documentos' || normalized === 'seleccionar otros documentos'
                || normalized === 'abrir pasajes'
                || normalized === 'ver revision') {
                this.openToolWorkspace(receipt, artifactVersion);
                return;
            }
            if (normalized === 'artifact-export-pdf' || normalized === 'descargar pdf' || normalized === 'pdf') {
                settleArtifactAction(await this.downloadArtifactForReceipt(receipt, 'pdf', artifactVersion));
                return;
            }
            if (normalized === 'artifact-export-docx' || normalized === 'descargar word' || normalized === 'descargar docx') {
                settleArtifactAction(await this.downloadArtifactForReceipt(receipt, 'docx', artifactVersion));
                return;
            }
            if (normalized === 'copiar') {
                if (receipt.result_ref.type === 'document') {
                    settleArtifactAction(await this.copyArtifactForReceipt(
                        receipt,
                        artifactVersion,
                        artifactClipboardContent,
                    ));
                } else {
                    this.openToolWorkspace(receipt, artifactVersion);
                    settleArtifactAction(true);
                }
                return;
            }
            if (normalized === 'descargar') {
                if (receipt.result_ref.type === 'document') {
                    await this.downloadArtifactForReceipt(receipt, 'docx', artifactVersion);
                } else {
                    this.openToolWorkspace(receipt, artifactVersion);
                }
                return;
            }
            if (normalized === 'omitir' || normalized === 'continuar sin documentos') {
                await this.resolveContextReceipt(receipt, 'skip');
                return;
            }
            if (normalized === 'cancelar' || normalized === 'cancelar solicitud') {
                await this.resolveContextReceipt(receipt, 'cancel');
                return;
            }
            if (normalized === 'detener') {
                await this.transitionContinuationReceipt(receipt, 'stop');
                return;
            }
            if (normalized === 'reintentar respuesta') {
                await this.transitionContinuationReceipt(receipt, 'retry');
                return;
            }
            if (normalized === 'confirmar reemplazo') {
                await this.resolveReplacementConfirmation(receipt, 'confirm');
                return;
            }
            if (normalized === 'cancelar reemplazo') {
                await this.resolveReplacementConfirmation(receipt, 'cancel');
                return;
            }
            this.openToolWorkspace(receipt, artifactVersion);
        },
        closeToolWorkspace() {
            this.activeToolReceipt = null;
            this.reviewDeferredMessage = null;
            this.$nextTick(() => {
                const target = this.toolWorkspaceReturnFocus;
                this.toolWorkspaceReturnFocus = null;
                if (target && document.contains(target) && typeof target.focus === 'function') {
                    target.focus({ preventScroll: true });
                }
            });
        },
        normalizeChatPaginationPayload(data) {
            try {
                const source = data && data.pagination;
                const enabled = source && (
                    source.enabled === true
                    || source.enabled === 1
                    || String(source.enabled).toLowerCase() === 'true'
                );
                if (!enabled) return null;
                const total = this.parseChatPaginationNumber(source.total, null);
                const start = this.parseChatPaginationNumber(source.start, null);
                const end = this.parseChatPaginationNumber(source.end, null);
                if (total === null || start === null || end === null) return null;
                const safeTotal = Math.max(0, total);
                const safeStart = Math.max(0, Math.min(start, safeTotal));
                const safeEnd = Math.max(safeStart, Math.min(end, safeTotal));
                const rawLimit = this.parseChatPaginationNumber(source.limit, this.longChatPageSize || 50);
                const limit = Math.max(1, rawLimit || this.longChatPageSize || 50);
                const anchorIndex = this.parseChatPaginationNumber(source.anchor_index, null);
                return {
                    enabled: true,
                    total: safeTotal,
                    start: safeStart,
                    end: safeEnd,
                    limit,
                    has_previous: source.has_previous === true || source.has_previous === 1 || String(source.has_previous).toLowerCase() === 'true',
                    has_next: source.has_next === true || source.has_next === 1 || String(source.has_next).toLowerCase() === 'true',
                    anchor: typeof source.anchor === 'string' ? source.anchor : '',
                    anchor_index: anchorIndex !== null && anchorIndex >= 0 ? anchorIndex : null,
                    threshold: this.parseChatPaginationNumber(source.threshold, 50),
                    loading_previous: false,
                    anchor_applied: false,
                    initial_anchor_pending: true,
                };
            } catch (e) {
                return null;
            }
        },
        setSessionChatPagination(session, pagination) {
            if (!session) return;
            if (typeof this.$set === 'function') {
                this.$set(session, 'chat_pagination', pagination || null);
            } else {
                session.chat_pagination = pagination || null;
            }
        },
        hasOptimisticWindowTailBeyond(session, total) {
            try {
                const safeTotal = this.parseChatPaginationNumber(total, null);
                if (safeTotal === null) return false;
                const messages = session && Array.isArray(session.data) ? session.data : [];
                return messages.some((message) => {
                    if (!message || typeof message !== 'object') return false;
                    const index = this.parseChatPaginationNumber(message._chat_absolute_index, null);
                    if (index === null || index < safeTotal) return false;
                    return message.isLoading === true || !String(message.server_timestamp || '').trim();
                });
            } catch (e) {
                return false;
            }
        },
        getNextWindowedChatIndex(session = this.currentSession) {
            const pagination = session && session.chat_pagination;
            if (!pagination || !pagination.enabled) return null;
            const candidates = [
                this.parseChatPaginationNumber(pagination.end, 0),
                this.parseChatPaginationNumber(pagination.total, 0),
            ].filter((value) => value !== null && Number.isFinite(value));
            const messages = Array.isArray(session.data) ? session.data : [];
            messages.forEach((message) => {
                if (!message || typeof message !== 'object') return;
                const index = this.parseChatPaginationNumber(message._chat_absolute_index, null);
                if (index !== null && index >= 0) candidates.push(index + 1);
            });
            return Math.max(0, ...candidates);
        },
        reserveWindowedChatIndexes(messages, session = this.currentSession) {
            const pagination = session && session.chat_pagination;
            if (!pagination || !pagination.enabled) return false;
            const list = Array.isArray(messages) ? messages : [messages];
            let nextIndex = this.getNextWindowedChatIndex(session);
            if (nextIndex === null) return false;
            let changed = false;
            list.forEach((message) => {
                if (!message || typeof message !== 'object') return;
                const existingIndex = this.parseChatPaginationNumber(message._chat_absolute_index, null);
                if (existingIndex !== null && existingIndex >= 0) {
                    nextIndex = Math.max(nextIndex, existingIndex + 1);
                    return;
                }
                try { this.$set(message, '_chat_absolute_index', nextIndex); } catch (e) { message._chat_absolute_index = nextIndex; }
                nextIndex += 1;
                changed = true;
            });
            if (!changed) return false;
            const total = Math.max(
                this.parseChatPaginationNumber(pagination.total, 0) || 0,
                nextIndex
            );
            const start = Math.min(
                this.parseChatPaginationNumber(pagination.start, 0) || 0,
                total
            );
            const end = Math.min(
                Math.max(this.parseChatPaginationNumber(pagination.end, 0) || 0, nextIndex),
                total
            );
            this.setSessionChatPagination(session, {
                ...pagination,
                total,
                start,
                end,
                has_previous: start > 0,
                has_next: false,
                loading_previous: false,
                initial_anchor_pending: false,
            });
            return true;
        },
        applyWindowedEditIndexes(messages, baseIndex, session = this.currentSession) {
            const pagination = session && session.chat_pagination;
            const parsedBaseIndex = this.parseChatPaginationNumber(baseIndex, null);
            if (!pagination || !pagination.enabled || parsedBaseIndex === null || parsedBaseIndex < 0) return false;
            const list = Array.isArray(messages) ? messages : [messages];
            let assigned = 0;
            list.forEach((message) => {
                if (!message || typeof message !== 'object') return;
                try { this.$set(message, '_chat_absolute_index', parsedBaseIndex + assigned); } catch (e) { message._chat_absolute_index = parsedBaseIndex + assigned; }
                assigned += 1;
            });
            if (!assigned) return false;
            const end = parsedBaseIndex + assigned;
            const start = Math.min(
                this.parseChatPaginationNumber(pagination.start, parsedBaseIndex) || 0,
                end
            );
            this.setSessionChatPagination(session, {
                ...pagination,
                total: Math.max(end, start),
                start,
                end,
                has_previous: start > 0,
                has_next: false,
                loading_previous: false,
                initial_anchor_pending: false,
            });
            return true;
        },
        applyChatPaginationFromPayload(session, data, { preserveAnchorState = false } = {}) {
            if (!session) return null;
            const pagination = this.normalizeChatPaginationPayload(data);
            if (!pagination) {
                this.setSessionChatPagination(session, null);
                return null;
            }
            const existing = session.chat_pagination || {};
            pagination.loading_previous = false;
            if (preserveAnchorState) {
                pagination.anchor_applied = !!existing.anchor_applied;
                pagination.initial_anchor_pending = !!existing.initial_anchor_pending;
                if (existing.enabled) {
                    const existingTotal = this.parseChatPaginationNumber(existing.total, pagination.total);
                    if (this.hasOptimisticWindowTailBeyond(session, pagination.total)) {
                        pagination.total = Math.max(pagination.total, existingTotal || 0);
                    }
                    const existingStart = this.parseChatPaginationNumber(existing.start, pagination.start);
                    const existingEnd = this.parseChatPaginationNumber(existing.end, pagination.end);
                    const safeTotal = Math.max(0, pagination.total);
                    pagination.start = Math.min(Math.min(existingStart, pagination.start), safeTotal);
                    pagination.end = Math.min(Math.max(existingEnd, pagination.end), safeTotal);
                    if (pagination.start > pagination.end) pagination.start = pagination.end;
                    pagination.has_previous = pagination.start > 0;
                    pagination.has_next = pagination.end < pagination.total;
                }
            }
            this.setSessionChatPagination(session, pagination);
            return pagination;
        },
        reconcileChatPaginationWithServerPayload(session, data) {
            const serverPagination = this.normalizeChatPaginationPayload(data);
            const pagination = session && session.chat_pagination;
            if (!serverPagination || !pagination || !pagination.enabled) return;
            if (this.hasOptimisticWindowTailBeyond(session, serverPagination.total)) return;
            const total = serverPagination.total;
            const start = Math.min(
                this.parseChatPaginationNumber(pagination.start, serverPagination.start) || 0,
                total
            );
            const end = Math.min(
                this.parseChatPaginationNumber(pagination.end, serverPagination.end) || 0,
                total
            );
            this.setSessionChatPagination(session, {
                ...pagination,
                total,
                start: Math.min(start, end),
                end,
                has_previous: Math.min(start, end) > 0,
                has_next: end < total,
                loading_previous: false,
            });
        },
        isLongChatWindowed(session = this.currentSession) {
            return !!(session && session.chat_pagination && session.chat_pagination.enabled);
        },
        chatPaginationMessageKey(message) {
            if (!message || typeof message !== 'object') return '';
            if (message._chat_absolute_index !== undefined && message._chat_absolute_index !== null) {
                return `idx:${message._chat_absolute_index}`;
            }
            return [
                message.sender || '',
                message.server_timestamp || message.timestamp || '',
                message.text || message.content || '',
            ].join('|');
        },
        chatPaginationComparableMessageKey(message) {
            if (!message || typeof message !== 'object') return '';
            const sender = String(message.sender || '').trim();
            const text = String(message.text || message.content || '').replace(/\s+/g, ' ').trim();
            if (!sender || !text) return '';
            const participant = sender === 'user'
                ? String(message.sender_email || message.sender_username || message.sender_display_name || '').trim().toLowerCase()
                : sender;
            return `${sender}|${participant}|${text}`;
        },
        chatPaginationServerMessageKey(message) {
            const serverTimestamp = String(message && message.server_timestamp || '').trim();
            if (!serverTimestamp) return '';
            const comparableKey = this.chatPaginationComparableMessageKey(message);
            return comparableKey ? `${serverTimestamp}|${comparableKey}` : '';
        },
        mergeWindowedDuplicateMessage(existing, incoming) {
            const replacement = { ...(existing || {}), ...(incoming || {}) };
            [
                'researchActivityHistory',
                'contextDocuments',
                'reactions',
            ].forEach((field) => {
                if (Array.isArray(existing && existing[field]) && !Array.isArray(incoming && incoming[field])) {
                    replacement[field] = existing[field];
                }
            });
            return replacement;
        },
        dedupeWindowedChatMessages(messages) {
            const deduped = [];
            const hasServerTimestamp = (message) => !!String(message && message.server_timestamp || '').trim();
            messages.forEach((message) => {
                if (!message) return;
                const comparableKey = this.chatPaginationComparableMessageKey(message);
                const serverKey = this.chatPaginationServerMessageKey(message);
                const incomingHasServerTimestamp = hasServerTimestamp(message);
                if (serverKey) {
                    const serverMatchIndex = deduped.findIndex((existing) => (
                        this.chatPaginationServerMessageKey(existing) === serverKey
                    ));
                    if (serverMatchIndex >= 0) {
                        deduped[serverMatchIndex] = this.mergeWindowedDuplicateMessage(
                            deduped[serverMatchIndex],
                            message
                        );
                        return;
                    }
                }
                if (comparableKey && incomingHasServerTimestamp) {
                    const optimisticIndex = deduped.findIndex((existing) => (
                        this.chatPaginationComparableMessageKey(existing) === comparableKey
                        && (!hasServerTimestamp(existing) || existing.isLoading === true)
                    ));
                    if (optimisticIndex >= 0) {
                        deduped[optimisticIndex] = this.mergeWindowedDuplicateMessage(
                            deduped[optimisticIndex],
                            message
                        );
                        return;
                    }
                }
                if (
                    comparableKey
                    && !incomingHasServerTimestamp
                    && deduped.some((existing) => (
                        this.chatPaginationComparableMessageKey(existing) === comparableKey
                        && hasServerTimestamp(existing)
                    ))
                ) {
                    return;
                }
                deduped.push(message);
            });
            return deduped;
        },
        mergeWindowedChatMessages(existingMessages, incomingMessages) {
            const existing = Array.isArray(existingMessages) ? existingMessages : [];
            const incoming = Array.isArray(incomingMessages) ? incomingMessages : [];
            if (!existing.length) return incoming;
            if (!incoming.length) return existing;
            const mergedByKey = new Map();
            const insertionOrder = [];
            const putMessage = (message) => {
                const key = this.chatPaginationMessageKey(message);
                if (!key) {
                    insertionOrder.push(Symbol('message'));
                    return;
                }
                if (!mergedByKey.has(key)) {
                    insertionOrder.push(key);
                }
                mergedByKey.set(key, message);
            };
            existing.forEach(putMessage);
            incoming.forEach(putMessage);
            const merged = insertionOrder
                .map((key) => mergedByKey.get(key))
                .filter(Boolean);
            const deduped = this.dedupeWindowedChatMessages(merged);
            const allIndexed = deduped.every((message) => (
                message
                && message._chat_absolute_index !== undefined
                && message._chat_absolute_index !== null
                && Number.isFinite(Number(message._chat_absolute_index))
            ));
            if (allIndexed) {
                deduped.sort((left, right) => Number(left._chat_absolute_index) - Number(right._chat_absolute_index));
            }
            return deduped;
        },
        shouldLoadPreviousChatWindow() {
            try {
                const session = this.currentSession;
                const pagination = session && session.chat_pagination;
                if (!pagination || !pagination.enabled || !pagination.has_previous) return false;
                if (pagination.loading_previous || this.longChatLoadInFlight) return false;
                if (Date.now() < (this.longChatSuppressTopFetchUntil || 0)) return false;
                const el = this.getChatboxElement();
                if (!el) return false;
                return el.scrollTop <= (this.longChatTopFetchThreshold || 220);
            } catch (e) {
                return false;
            }
        },
        maybeLoadPreviousChatWindowFromScroll() {
            if (this.shouldLoadPreviousChatWindow()) {
                this.loadPreviousChatWindow();
            }
        },
        async loadPreviousChatWindow() {
            const session = this.currentSession;
            const pagination = session && session.chat_pagination;
            if (!session || !session.uuid || !pagination || !pagination.enabled || !pagination.has_previous) return;
            if (pagination.loading_previous || this.longChatLoadInFlight) return;
            const before = this.parseChatPaginationNumber(pagination.start, null);
            if (before === null || before <= 0) return;
            const uuid = String(session.uuid);
            const el = this.getChatboxElement();
            const previousHeight = el ? el.scrollHeight : 0;
            const previousTop = el ? el.scrollTop : 0;
            this.longChatLoadInFlight = true;
            this.setSessionChatPagination(session, { ...pagination, loading_previous: true });
            try {
                const limit = pagination.limit || this.longChatPageSize || 50;
                const response = await fetch(this.buildChatGetUrl(uuid, { before, limit }));
                if (!response.ok) return;
                const data = await response.json();
                if (!this.currentSession || String(this.currentSession.uuid || '') !== uuid) return;
                const incomingPagination = this.normalizeChatPaginationPayload(data);
                const incomingMessages = this.normalizeConversationPayload(data) || [];
                const currentMessages = Array.isArray(this.currentSession.data) ? this.currentSession.data : [];
                const seen = new Set(currentMessages.map((message) => this.chatPaginationMessageKey(message)));
                const prepended = [];
                incomingMessages.forEach((message) => {
                    const key = this.chatPaginationMessageKey(message);
                    if (!key || seen.has(key)) return;
                    seen.add(key);
                    prepended.push(message);
                });
                if (prepended.length) {
                    this.currentSession.data = prepended.concat(currentMessages);
                }
                const activePagination = this.currentSession.chat_pagination || pagination;
                if (incomingPagination) {
                    const currentEnd = this.parseChatPaginationNumber(activePagination.end, incomingPagination.end);
                    this.setSessionChatPagination(this.currentSession, {
                        ...activePagination,
                        total: incomingPagination.total,
                        start: incomingPagination.start,
                        end: currentEnd,
                        limit: incomingPagination.limit || activePagination.limit || this.longChatPageSize || 50,
                        has_previous: incomingPagination.has_previous,
                        has_next: !!activePagination.has_next || currentEnd < incomingPagination.total,
                        threshold: incomingPagination.threshold,
                        loading_previous: false,
                        anchor_applied: true,
                        initial_anchor_pending: false,
                    });
                }
                this.store();
                this.$nextTick(() => {
                    try {
                        const activeEl = this.getChatboxElement();
                        if (!activeEl || !this.currentSession || String(this.currentSession.uuid || '') !== uuid) return;
                        activeEl.scrollTop = previousTop + Math.max(0, activeEl.scrollHeight - previousHeight);
                        this.updateScrollToBottomVisibility();
                    } catch (e) { }
                });
            } catch (e) {
                // Best-effort lazy loading; keep the already-rendered window usable.
            } finally {
                this.longChatLoadInFlight = false;
                if (this.currentSession && String(this.currentSession.uuid || '') === uuid && this.currentSession.chat_pagination) {
                    this.currentSession.chat_pagination.loading_previous = false;
                }
            }
        },
        scrollToLongChatAnchor({ force = false } = {}) {
            const session = this.currentSession;
            const pagination = session && session.chat_pagination;
            if (!pagination || !pagination.enabled) return;
            if (!force && pagination.anchor_applied) return;
            const anchorIndex = this.parseChatPaginationNumber(pagination.anchor_index, null);
            const applyScroll = () => {
                try {
                    const el = this.getChatboxElement();
                    if (!el) return;
                    let didScroll = false;
                    if (anchorIndex !== null) {
                        const target = el.querySelector(`[data-chat-index="${anchorIndex}"]`);
                        if (target) {
                            const containerRect = el.getBoundingClientRect();
                            const targetRect = target.getBoundingClientRect();
                            this.longChatSuppressTopFetchUntil = Date.now() + 700;
                            el.scrollTop = Math.max(0, el.scrollTop + targetRect.top - containerRect.top - 12);
                            didScroll = true;
                        }
                    }
                    if (!didScroll && !pagination.has_next) {
                        this.longChatSuppressTopFetchUntil = Date.now() + 700;
                        el.scrollTop = el.scrollHeight;
                    }
                    pagination.anchor_applied = true;
                    pagination.initial_anchor_pending = false;
                    this.updateScrollToBottomVisibility();
                } catch (e) { }
            };
            this.$nextTick(applyScroll);
        },
        updateScrollToBottomVisibility() {
            try {
                if (this.readyDocumentResponse && this.isDocumentResponseVisible(this.readyDocumentResponse)) {
                    this.documentResponseReadyNotice = null;
                }
                if (this.isDemoRecordingMode()) {
                    this.showScrollToBottom = false;
                    return;
                }
                this.showScrollToBottom = !this.isChatboxNearBottom()
                    && !this.showCollaborativeNewMessagesCue;
            } catch (e) {
                this.showScrollToBottom = false;
            }
        },
        scrollChatboxToBottom({ smooth = false, resetUnseen = true } = {}) {
            try {
                const el = this.getChatboxElement();
                if (!el) return;
                this.chatboxFollowingBottom = true;
                if (typeof el.scrollTo === 'function') {
                    el.scrollTo({
                        top: el.scrollHeight,
                        behavior: smooth ? 'smooth' : 'auto',
                    });
                } else {
                    el.scrollTop = el.scrollHeight;
                }
                if (resetUnseen) {
                    this.unseenIncomingCount = 0;
                }
                this.showScrollToBottom = false;
            } catch (e) { }
        },
        schedulePendingAiReplyReveal(message, { onlyIfFollowing = false } = {}) {
            if (!message) return false;
            if (onlyIfFollowing && !this.isChatboxFollowingBottom()) return false;
            const entry = this.rememberPendingAiReplyReveal(message);
            if (!entry) return false;
            this.$nextTick(() => {
                if (
                    entry.terminal === true
                    || entry.chatUuid !== String(this.currentSession && this.currentSession.uuid || '')
                ) return;
                // Vue's DOM insertion can produce a browser scroll event on
                // mobile before this next tick. That is layout adjustment,
                // not user intent; begin the user-scroll fence from the
                // position we are about to reveal.
                entry.userScrollRevision = Number(this.chatViewportUserScrollRevision || 0);
                this.revealPendingAiReply(message);
            });
            return true;
        },
        revealPendingAiReply(message = null) {
            try {
                const el = this.getChatboxElement();
                if (!el) return;
                let nextScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
                const messageNode = this.findChatMessageViewportNode(message, el);
                if (messageNode && typeof messageNode.getBoundingClientRect === 'function') {
                    const containerRect = el.getBoundingClientRect();
                    const messageRect = messageNode.getBoundingClientRect();
                    if (messageRect.bottom > containerRect.bottom) {
                        nextScrollTop = el.scrollTop + messageRect.bottom - containerRect.bottom;
                    } else if (messageRect.top < containerRect.top) {
                        nextScrollTop = el.scrollTop + messageRect.top - containerRect.top;
                    } else {
                        nextScrollTop = el.scrollTop;
                    }
                }
                this.setProgrammaticChatScrollTop(
                    el,
                    Math.max(0, nextScrollTop),
                );
                if (message && !this.pendingAiReplyRevealEntry(message)) {
                    this.rememberPendingAiReplyReveal(message);
                }
                // Revealing the optimistic reply is a one-time transition,
                // not permission to follow a long answer to its end. Matching
                // programmatic scroll events are suppressed, while a real
                // user scroll can still opt back into bottom-following.
                this.chatboxFollowingBottom = false;
                this.unseenIncomingCount = 0;
                this.showScrollToBottom = false;
            } catch (e) { }
        },
        async settlePendingAiReplyReveal(message, {
            finalize = true,
            revealAnswerStart = true,
        } = {}) {
            const entry = this.pendingAiReplyRevealEntry(message);
            if (!entry) return false;
            try {
                await this.$nextTick();
                await this.waitForChatViewportLayout();
                if (
                    entry.chatUuid !== String(this.currentSession && this.currentSession.uuid || '')
                    || Number(entry.userScrollRevision || 0)
                        !== Number(this.chatViewportUserScrollRevision || 0)
                ) {
                    return false;
                }

                const el = this.getChatboxElement();
                if (!el) return false;
                if (revealAnswerStart && !entry.documentPreviewRevealed) {
                    const messageNode = this.findChatMessageViewportNode(message, el);
                    const responseBlock = messageNode
                        && messageNode.querySelector('md-block.bot-response');
                    const firstAnswerNode = responseBlock
                        ? Array.from(responseBlock.children || []).find(node => (
                            String(node.textContent || '').trim()
                        )) || responseBlock
                        : null;
                    if (firstAnswerNode && typeof firstAnswerNode.getBoundingClientRect === 'function') {
                        const containerRect = el.getBoundingClientRect();
                        const answerRect = firstAnswerNode.getBoundingClientRect();
                        const responseRect = responseBlock
                            && typeof responseBlock.getBoundingClientRect === 'function'
                            ? responseBlock.getBoundingClientRect()
                            : answerRect;
                        if (
                            responseRect.bottom > containerRect.bottom + 2
                            || answerRect.top < containerRect.top - 2
                            || answerRect.top >= containerRect.bottom
                            || answerRect.bottom > containerRect.bottom + 2
                        ) {
                            const maxScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
                            const nextScrollTop = Math.max(
                                0,
                                Math.min(
                                    maxScrollTop,
                                    el.scrollTop + answerRect.top - containerRect.top,
                                ),
                            );
                            this.setProgrammaticChatScrollTop(el, nextScrollTop);
                        }
                    }
                }

                if (finalize) {
                    entry.terminal = true;
                }
                this.chatboxFollowingBottom = this.isChatboxAtBottom(el);
                this.unseenIncomingCount = 0;
                this.updateScrollToBottomVisibility();
                return true;
            } catch (e) {
                return false;
            } finally {
                if (finalize) {
                    this.clearPendingAiReplyReveal(message);
                }
            }
        },
        settlePendingAiReplyAfterTerminal(
            message,
            terminalRestore,
            canonicalRestore = null,
            options = {},
        ) {
            const hasCanonicalRestore = !!canonicalRestore;
            const immediateSettlement = Promise.resolve(terminalRestore)
                .catch(() => false)
                .then(() => this.settlePendingAiReplyReveal(message, {
                    ...options,
                    finalize: !hasCanonicalRestore,
                }));
            if (!hasCanonicalRestore) return immediateSettlement;
            return Promise.allSettled([
                immediateSettlement,
                Promise.resolve(canonicalRestore),
            ]).then(() => this.settlePendingAiReplyReveal(message, {
                ...options,
                finalize: true,
            }));
        },
        isDocumentResponseVisible(message) {
            const host = this.getChatboxElement();
            if (!message || !host) return false;
            const target = this.findChatMessageViewportNode(message, host)
                ?.querySelector('[data-tour="chat-response-content"]');
            if (!target) return false;
            const hostRect = host.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            // A long response can span the viewport without either edge being visible.
            return hostRect.bottom > hostRect.top && targetRect.bottom > targetRect.top
                && targetRect.bottom > hostRect.top && targetRect.top < hostRect.bottom;
        },
        notifyDocumentResponseReady(message) {
            const messageId = this.messageReferenceId(message);
            if (!this.currentSession?.data.some(item => this.messageReferenceId(item) === messageId)) return;
            this.documentResponseReadyNotice = this.isDocumentResponseVisible(message) ? null : {
                chatUuid: String(this.currentSession.uuid || ''),
                messageId,
            };
        },
        scrollToDocumentResponse() {
            const message = this.readyDocumentResponse;
            const host = this.getChatboxElement();
            const target = this.findChatMessageViewportNode(message, host)
                ?.querySelector('[data-tour="chat-response-content"]');
            if (!message || !host || !target) return;
            // Explicit navigation supersedes pending completion/history restores.
            this.chatViewportUserScrollRevision = Number(this.chatViewportUserScrollRevision || 0) + 1;
            this.clearPendingAiReplyReveal(message);
            this.releaseProgrammaticChatScroll();
            this.chatboxFollowingBottom = false;
            const top = host.scrollTop + target.getBoundingClientRect().top - host.getBoundingClientRect().top - 8;
            const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            host.scrollTo({ top: Math.max(0, top), behavior: reducedMotion ? 'instant' : 'smooth' });
            target.focus({ preventScroll: true });
            this.documentResponseReadyNotice = null;
        },
        scrollToNewMessages() {
            this.scrollChatboxToBottom({ smooth: true, resetUnseen: true });
            this.markCurrentChatReadDebounced();
        },
        scrollToLatestMessages() {
            // Visit the current document end immediately; later preview
            // updates preserve this position instead of following new text.
            this.scrollChatboxToBottom({ smooth: !this.documentPreviews?.length });
            this.showScrollToBottom = false;
        },
        isDocumentVisible() {
            try {
                return document.visibilityState === 'visible';
            } catch (e) {
                return true;
            }
        },
        clearOpenChatUuid() {
            try {
                if (window.__lexiusOpenChatUuid) {
                    window.__lexiusOpenChatUuid = '';
                }
            } catch (e) { }
        },
        updateOpenChatUuid() {
            try {
                const uuid = this.currentSession && this.currentSession.uuid
                    ? String(this.currentSession.uuid)
                    : '';
                window.__lexiusOpenChatUuid = uuid;
            } catch (e) { }
        },
        handleVisibilityChange() {
            if (this.isDocumentVisible()) {
                const hadOutstandingChatWork = this.hasOutstandingChatWork();
                this.catchUpContinuationState({ resetReceipts: false });
                this.markCurrentChatReadDebounced();
                // Returning to the tab must not pull the viewport while an AI
                // answer is still changing underneath the reader.
                if (
                    this.isChatboxFollowingBottom()
                    && !hadOutstandingChatWork
                    && !this.hasOutstandingChatWork()
                ) {
                    this.$nextTick(() => this.scrollChatboxToBottom({ smooth: false, resetUnseen: true }));
                }
            }
        },
        isMessageFromCurrentUser(payload = {}) {
            try {
                const senderEmail = String(payload.sender_email || '').trim().toLowerCase();
                const currentEmail = String(window.user_email || '').trim().toLowerCase();
                if (senderEmail && currentEmail && senderEmail === currentEmail) {
                    return true;
                }
            } catch (e) { }
            return false;
        },
        handleIncomingMessageVisibility({
            remote = true,
            payload = null,
            pendingAiReply = null,
        } = {}) {
            // Collaborative messages may arrive while an answer is streaming.
            // Keep them visible in the transcript, but never let their arrival
            // opt the viewport back into following the growing response.
            if (this.hasOutstandingChatWork()) {
                const shouldRevealPendingReply = !!pendingAiReply
                    && this.schedulePendingAiReplyReveal(pendingAiReply, {
                        onlyIfFollowing: true,
                    });
                if (this.isDocumentVisible()) {
                    this.markCurrentChatReadDebounced();
                }
                this.$nextTick(() => {
                    const pendingReplyPinned = !!(
                        shouldRevealPendingReply
                        && this.hasUninterruptedPendingAiReplyReveal()
                    );
                    if (!pendingReplyPinned && !this.isChatboxFollowingBottom()) {
                        this.unseenIncomingCount = Math.min(999, (this.unseenIncomingCount || 0) + 1);
                    }
                    this.updateScrollToBottomVisibility();
                });
                return;
            }
            if (!remote || this.isMessageFromCurrentUser(payload || {})) {
                this.$nextTick(() => {
                    this.scrollChatboxToBottom({ smooth: false, resetUnseen: true });
                    this.updateScrollToBottomVisibility();
                });
                return;
            }
            const wasFollowingBottom = this.isChatboxFollowingBottom();
            const isVisible = this.isDocumentVisible();
            if (isVisible) {
                this.markCurrentChatReadDebounced();
            }
            this.$nextTick(() => {
                if (wasFollowingBottom && isVisible) {
                    this.scrollChatboxToBottom({ smooth: true, resetUnseen: true });
                } else {
                    this.unseenIncomingCount = Math.min(999, (this.unseenIncomingCount || 0) + 1);
                }
                this.updateScrollToBottomVisibility();
            });
        },
        handleIncomingAiResponseVisibility(viewportSnapshot = null) {
            // AI text, reasoning, and durable tool receipts can all change the
            // response height. Capture the pre-update viewport synchronously,
            // then restore it after Vue has committed the new layout. This
            // keeps a terminal realtime update and its later canonical refresh
            // from making independent decisions against different DOM trees.
            const snapshot = viewportSnapshot || this.captureChatViewportState();
            if (this.isDocumentVisible()) {
                this.markCurrentChatReadDebounced();
            }
            return this.restoreChatViewportState(snapshot, { markIncoming: true });
        },
        markCurrentChatReadDebounced() {
            try {
                if (!this.currentSession || !this.currentSession.uuid || !this.isCollaborativeChat) return;
                if (!this.isDocumentVisible()) return;
                const uuid = String(this.currentSession.uuid);
                this.pendingMarkReadUuid = uuid;
                if (this.markReadTimer) {
                    clearTimeout(this.markReadTimer);
                }
                this.markReadTimer = setTimeout(() => {
                    this.markReadTimer = null;
                    this.markCollaborativeChatRead(this.pendingMarkReadUuid);
                }, 250);
            } catch (e) { }
        },
        async markCollaborativeChatRead(uuid) {
            const chatUuid = String(uuid || '').trim();
            if (!chatUuid || this.markReadInFlight) return;
            this.markReadInFlight = true;
            try {
                await fetch('/chats/collaboration/read', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ uuid: chatUuid }),
                });
                try {
                    this.$emit('sessionPatched', chatUuid, { unread_count: 0 });
                } catch (e) { }
            } catch (e) {
                // Best effort only; the next list refresh or message event can retry.
            } finally {
                this.markReadInFlight = false;
                if (
                    this.pendingMarkReadUuid
                    && this.pendingMarkReadUuid !== chatUuid
                    && this.currentSession
                    && String(this.currentSession.uuid || '') === String(this.pendingMarkReadUuid)
                ) {
                    this.markCurrentChatReadDebounced();
                }
            }
        },
        syncCustomInstructionsState() {
            try {
                const value = (window.chatCustomInstructions || '');
                this.hasCustomInstructions = value.trim().length > 0;
            } catch (e) {
                this.hasCustomInstructions = false;
            }
        },
        initializeDocsHelpPopover() {
            this.$nextTick(() => {
                window.setTimeout(() => {
                    try {
                        if (typeof bootstrap === 'undefined' || !bootstrap.Popover) {
                            return;
                        }

                        const root = this.$el || document;
                        const helpButton = root.querySelector('.help-button-docs');
                        if (!helpButton) {
                            return;
                        }

                        const helpContent = (
                            helpButton.getAttribute('data-content') ||
                            helpButton.getAttribute('data-bs-content') ||
                            helpButton.getAttribute('title') ||
                            helpButton.getAttribute('data-bs-original-title') ||
                            ''
                        ).trim();

                        if (!helpContent) {
                            return;
                        }

                        if (bootstrap.Tooltip && typeof bootstrap.Tooltip.getInstance === 'function') {
                            const tooltipInstance = bootstrap.Tooltip.getInstance(helpButton);
                            if (tooltipInstance) {
                                tooltipInstance.dispose();
                            }
                        }

                        if (typeof bootstrap.Popover.getInstance === 'function') {
                            const popoverInstance = bootstrap.Popover.getInstance(helpButton);
                            if (popoverInstance) {
                                popoverInstance.dispose();
                            }
                        }

                        helpButton.setAttribute('data-bs-content', helpContent);
                        helpButton.setAttribute('data-bs-trigger', 'focus');
                        helpButton.setAttribute('data-bs-html', 'true');

                        new bootstrap.Popover(helpButton, {
                            trigger: 'focus',
                            html: true,
                            content: helpContent,
                            container: 'body',
                        });
                    } catch (e) {
                        console.warn('Could not initialize the chat documents help popover.', e);
                    }
                }, 0);
            });
        },
        handleCustomInstructionsEvent(event) {
            try {
                if (event && event.detail && typeof event.detail.instructions === 'string') {
                    this.hasCustomInstructions = event.detail.instructions.trim().length > 0;
                    return;
                }
            } catch (e) {
                // Fallback handled below
            }
            this.syncCustomInstructionsState();
        },
        handleCustomInstructionsIndicatorClick(event) {
            try {
                if (event && typeof event.preventDefault === 'function') {
                    event.preventDefault();
                }
            } catch (e) { }

            try {
                const jq = window.$ || window.jQuery;
                if (jq) {
                    jq(document).trigger('close-chat-modal');
                }
            } catch (e) { }

            window.setTimeout(() => {
                const jq = window.$ || window.jQuery;
                if (!jq) {
                    return;
                }

                const themeBar = jq('.custom-theme-bar');
                if (themeBar.length) {
                    themeBar.stop(true, true).fadeIn('fast');
                }

                const card = jq('#chatInstructionsCard');
                const textarea = jq('#chatCustomInstructions');

                if (card.length) {
                    card.removeClass('highlight-custom-instructions');
                    try {
                        void card.get(0).offsetWidth;
                    } catch (e) { }
                    card.addClass('highlight-custom-instructions');
                    window.setTimeout(() => {
                        try {
                            card.removeClass('highlight-custom-instructions');
                        } catch (e) { }
                    }, 5000);
                }

                if (textarea.length) {
                    try {
                        textarea.get(0).scrollIntoView({ behavior: 'smooth', block: 'center' });
                    } catch (e) {
                        try {
                            textarea.get(0).scrollIntoView();
                        } catch (scrollError) { }
                    }
                    try {
                        textarea.trigger('focus');
                    } catch (e) { }
                } else if (card.length) {
                    try {
                        card.get(0).scrollIntoView({ behavior: 'smooth', block: 'center' });
                    } catch (e) {
                        try {
                            card.get(0).scrollIntoView();
                        } catch (scrollError) { }
                    }
                }
            }, 200);
        },
        getCurrentUserEmail() {
            try {
                return String(window.user_email || '').trim().toLowerCase();
            } catch (e) {
                return '';
            }
        },
        getCurrentUsername() {
            try {
                return String(window.user_name || window.user_id || '').trim();
            } catch (e) {
                return '';
            }
        },
        isSafeDocumentHref(path) {
            const value = String(path || '').trim();
            if (!value) return false;
            try {
                const parsed = new URL(value, window.location.origin);
                if (parsed.protocol && !['http:', 'https:'].includes(parsed.protocol)) {
                    return false;
                }
            } catch (e) {
                return false;
            }
            return !/^\s*(javascript|data|vbscript):/i.test(value);
        },
        documentHref(docOrPath) {
            const doc = (docOrPath && typeof docOrPath === 'object') ? docOrPath : { path: docOrPath };
            const path = String(doc.path || '').trim();
            if (!this.isSafeDocumentHref(path)) return '';
            const isUserDocument = this.coerceBoolean(doc.is_user_document) === true || path.includes('/get_user_upload');
            if (isUserDocument && typeof buildUserUploadHref === 'function') {
                const hasCorporateScope = path.includes('scope=corporate');
                return buildUserUploadHref(
                    path,
                    doc.page_title || doc.title || 'Documento',
                    hasCorporateScope ? 'corporate' : null,
                    hasCorporateScope ? '' : (this.currentSession && this.currentSession.uuid)
                );
            }
            return path;
        },
        async sha256Hex(value) {
            const text = String(value || '').trim().toLowerCase();
            if (!text || !window.crypto || !window.crypto.subtle || !window.TextEncoder) {
                return '';
            }
            const bytes = new TextEncoder().encode(text);
            const digest = await window.crypto.subtle.digest('SHA-256', bytes);
            return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
        },
        isAiInvocationText(text) {
            const rawText = String(text || '');
            const normalized = rawText.trim().toLowerCase();
            if (!normalized) return false;
            if (/(^|[^A-Za-z0-9_])@(lexius|abogado|ai)(?=$|[^A-Za-z0-9_.-])/i.test(rawText)) {
                return true;
            }
            return normalized === '/ai'
                || normalized === '/ia'
                || normalized.startsWith('/ai ')
                || normalized.startsWith('/ia ');
        },
        openCollaborationDialog() {
            if (!this.currentSession || this.currentSession.is_call_log) {
                return;
            }
            this.collaborationDialogVisible = true;
            this.collaborationInviteStatus = '';
            this.collaborationRemovalStatus = '';
            try { this.loadCollaborationState(); } catch (e) { }
            this.refreshCollaborationPushNotifications();
            this.$nextTick(() => {
                try {
                    const root = this.$el || document;
                    const input = root.querySelector('.collaboration-dialog input[type="email"]');
                    if (input && this.canInviteCollaborators) {
                        input.focus();
                    }
                } catch (e) { }
            });
        },
        closeCollaborationDialog() {
            this.collaborationDialogVisible = false;
            this.collaborationInviteStatus = '';
            this.collaborationRemovalStatus = '';
        },
        ensurePushNotificationApi() {
            if (typeof window.lexiusEnablePushNotifications === 'function') {
                return true;
            }
            try {
                if (typeof window.initializeAlerts === 'function') {
                    window.initializeAlerts();
                    window.alertsInitialized = true;
                } else if (typeof initializeAlerts === 'function') {
                    initializeAlerts();
                    window.alertsInitialized = true;
                }
            } catch (e) { }
            return typeof window.lexiusEnablePushNotifications === 'function';
        },
        handlePushNotificationStateEvent(event) {
            try {
                this.applyCollaborationPushState((event && event.detail) || {});
            } catch (e) { }
        },
        applyCollaborationPushState(state) {
            const pushState = state && typeof state === 'object' ? state : {};
            if (typeof pushState.supported === 'boolean') {
                this.collaborationPushSupported = pushState.supported;
            }
            if (typeof pushState.hasSubscription === 'boolean') {
                this.collaborationPushEnabled = pushState.hasSubscription;
            }
            this.collaborationPushPermission = pushState.permission || this.collaborationPushPermission || null;
            this.collaborationPushBusy = pushState.busy === true;
            this.collaborationPushVariant = pushState.variant || (this.collaborationPushEnabled ? 'success' : 'muted');
            if (pushState.message) {
                this.collaborationPushStatus = pushState.message;
            } else if (this.collaborationPushEnabled) {
                this.collaborationPushStatus = 'Notificaciones activas en este dispositivo.';
            } else if (this.collaborationPushSupported === false) {
                this.collaborationPushStatus = 'Tu navegador no soporta notificaciones push.';
            } else if (this.collaborationPushPermission === 'denied') {
                this.collaborationPushStatus = 'Las notificaciones están bloqueadas en este navegador.';
            } else {
                this.collaborationPushStatus = 'Activa las notificaciones para recibir avisos de actividad colaborativa en este dispositivo.';
            }
        },
        async refreshCollaborationPushNotifications() {
            if (!this.ensurePushNotificationApi()) {
                this.applyCollaborationPushState({
                    supported: false,
                    message: 'No se pudo inicializar el servicio de notificaciones en este navegador.',
                    variant: 'warning',
                });
                return;
            }
            try {
                if (typeof window.lexiusRefreshPushState === 'function') {
                    const state = await window.lexiusRefreshPushState();
                    this.applyCollaborationPushState(state);
                    return;
                }
                if (typeof window.lexiusGetPushNotificationState === 'function') {
                    this.applyCollaborationPushState(window.lexiusGetPushNotificationState());
                }
            } catch (e) {
                this.applyCollaborationPushState({
                    message: 'No se pudo revisar el estado de las notificaciones.',
                    variant: 'danger',
                });
            }
        },
        async enableCollaborationPushNotifications() {
            if (this.collaborationPushEnabled) {
                this.collaborationPushStatus = 'Las notificaciones ya están activas en este dispositivo.';
                this.collaborationPushVariant = 'success';
                return;
            }
            this.collaborationPushBusy = true;
            this.collaborationPushVariant = 'muted';
            this.collaborationPushStatus = 'Solicitando permiso para activar notificaciones...';
            try {
                if (this.ensurePushNotificationApi() && typeof window.lexiusEnablePushNotifications === 'function') {
                    const state = await window.lexiusEnablePushNotifications();
                    this.applyCollaborationPushState(state);
                    await this.refreshCollaborationPushNotifications();
                    return;
                }
                this.applyCollaborationPushState({
                    supported: false,
                    message: 'No se pudo inicializar el servicio de notificaciones en este navegador.',
                    variant: 'warning',
                });
            } catch (e) {
                this.applyCollaborationPushState({
                    message: 'No se pudieron activar las notificaciones. Intenta de nuevo.',
                    variant: 'danger',
                });
            } finally {
                this.collaborationPushBusy = false;
            }
        },
        async loadPendingCollaborationInvitations() {
            if (this.isDemoRecordingMode()) return;
            try {
                const response = await fetch('/chats/collaboration/invitations');
                if (!response.ok) return;
                const data = await response.json();
                this.pendingCollaborationInvitations = Array.isArray(data.invitations) ? data.invitations : [];
            } catch (e) { }
        },
        async handleInvitationParamsFromUrl() {
            if (this.isDemoRecordingMode()) return;
            try {
                const params = new URLSearchParams(window.location.search || '');
                const invitationUuid = params.get('chat_invitation');
                const token = params.get('token') || '';
                if (!invitationUuid) return;
                await this.loadPendingCollaborationInvitations();
                const pendingIndex = this.pendingCollaborationInvitations.findIndex(invitation => (
                    invitation && String(invitation.invitation_uuid || '') === String(invitationUuid)
                ));
                if (pendingIndex === -1) return;
                if (token) {
                    const pendingInvitation = {
                        ...this.pendingCollaborationInvitations[pendingIndex],
                        token,
                    };
                    this.pendingCollaborationInvitations.splice(pendingIndex, 1, pendingInvitation);
                }
                this.openChatModalForInvitation();
                params.delete('chat_invitation');
                params.delete('token');
                const nextQuery = params.toString();
                const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash || ''}`;
                window.history.replaceState({}, document.title, nextUrl);
            } catch (e) { }
        },
        async handleChatParamsFromUrl() {
            if (this.isDemoRecordingMode()) return;

            let params;
            try {
                params = new URLSearchParams(window.location.search || '');
            } catch (e) {
                return;
            }

            const chatUuid = String(params.get('chat') || '').trim();
            if (!chatUuid) return;

            const removeChatParam = () => {
                params.delete('chat');
                const nextQuery = params.toString();
                const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash || ''}`;
                window.history.replaceState({}, document.title, nextUrl);
                try {
                    if (typeof window.lexiusClearPostAuthContinuation === 'function') {
                        window.lexiusClearPostAuthContinuation('chat');
                    }
                } catch (e) { }
            };

            if (!/^[A-Za-z0-9_-]{1,128}$/.test(chatUuid)) {
                removeChatParam();
                return;
            }

            try {
                const result = await this.switchSession(
                    chatUuid,
                    null,
                    null,
                    null,
                    null,
                    { forceServerFetch: true, requireServerSuccess: true }
                );

                if (result && result.ok === true
                    && this.currentSession
                    && String(this.currentSession.uuid || '') === chatUuid) {
                    this.openChatModalForInvitation();
                    try { this.$emit('sessionAdded'); } catch (e) { }
                    try { this.$emit('sessionActivated', chatUuid); } catch (e) { }
                    removeChatParam();
                    return;
                }

                if (result && result.status === 401) {
                    const loginParams = new URLSearchParams();
                    if (window.user_email) loginParams.set('email', window.user_email);
                    loginParams.set('chat', chatUuid);
                    window.location.href = `/login?${loginParams.toString()}`;
                    return;
                }

                if (result && (result.status === 403 || result.status === 404)) {
                    alert('No se encontró el chat o ya no tienes acceso a él.');
                    removeChatParam();
                }
            } catch (e) {
                console.warn('No se pudo abrir el chat indicado por la notificación.', e);
            }
        },
        openChatModalForInvitation() {
            try {
                const modalEl = document.getElementById('chatModal');
                if (!modalEl) return;
                if (window.bootstrap && window.bootstrap.Modal) {
                    const modal = window.bootstrap.Modal.getInstance(modalEl) || new window.bootstrap.Modal(modalEl);
                    modal.show();
                    return;
                }
                if (window.$ && typeof window.$(modalEl).modal === 'function') {
                    window.$(modalEl).modal('show');
                }
            } catch (e) { }
        },
        queueInvitationRealtimeLifecycle(operation) {
            const previous = this.invitationRealtimeLifecyclePromise
                && typeof this.invitationRealtimeLifecyclePromise.then === 'function'
                ? this.invitationRealtimeLifecyclePromise
                : Promise.resolve();
            const run = previous.catch(() => { }).then(operation);
            const settled = run.catch(() => { });
            this.invitationRealtimeLifecyclePromise = settled;
            return settled;
        },
        takeInvitationRealtimeSnapshot() {
            const snapshot = {
                channel: this.invitationChannel,
                subscriptions: Array.isArray(this.invitationSubscriptions)
                    ? this.invitationSubscriptions.slice()
                    : [],
            };
            if (snapshot.channel && typeof snapshot.channel.unsubscribe === 'function') {
                try {
                    if (snapshot.subscriptions.length) {
                        snapshot.subscriptions.forEach(({ eventName, handler }) => {
                            snapshot.channel.unsubscribe(eventName, handler);
                        });
                    }
                } catch (e) { }
            }
            this.invitationChannel = null;
            this.invitationSubscriptions = [];
            return snapshot;
        },
        async cleanupInvitationRealtimeSnapshot(snapshot) {
            if (!snapshot || !snapshot.channel) return;
            await this.detachRealtimeChannel(snapshot.channel);
        },
        attachInvitationRealtime() {
            if (this.isDemoRecordingMode()) return Promise.resolve();
            const generation = Number(this.invitationRealtimeGeneration || 0) + 1;
            this.invitationRealtimeGeneration = generation;
            if (this.invitationRealtimeRetryTimer) {
                clearTimeout(this.invitationRealtimeRetryTimer);
                this.invitationRealtimeRetryTimer = null;
            }
            const previous = this.takeInvitationRealtimeSnapshot();
            return this.queueInvitationRealtimeLifecycle(async () => {
                await this.cleanupInvitationRealtimeSnapshot(previous);
                if (generation !== Number(this.invitationRealtimeGeneration || 0)) return;

                if (!window.ably_realtime_client && typeof initializeAbly === 'function') {
                    await initializeAbly();
                }
                if (generation !== Number(this.invitationRealtimeGeneration || 0)) return;
                if (!window.ably_realtime_client) return;
                const userEmail = this.getCurrentUserEmail();
                if (!userEmail) {
                    this.invitationRealtimeRetryTimer = window.setTimeout(() => {
                        this.invitationRealtimeRetryTimer = null;
                        if (generation === Number(this.invitationRealtimeGeneration || 0)) {
                            this.attachInvitationRealtime();
                        }
                    }, 1000);
                    return;
                }
                const hashedEmail = await this.sha256Hex(userEmail);
                if (!hashedEmail || generation !== Number(this.invitationRealtimeGeneration || 0)) return;

                const channel = window.ably_realtime_client.channels.get(`collab-invites-${hashedEmail}`);
                const refresh = () => { this.loadPendingCollaborationInvitations(); };
                const subscriptions = [
                    { eventName: 'invitation_created', handler: refresh },
                    { eventName: 'invitation_accepted', handler: refresh },
                    { eventName: 'invitation_rejected', handler: refresh },
                    {
                        eventName: 'participant_removed',
                        handler: (message) => {
                            try { this.handleParticipantRemoved(message && message.data ? message.data : {}); } catch (e) { }
                        },
                    },
                    {
                        eventName: 'participant_left',
                        handler: (message) => {
                            try { this.handleParticipantLeft(message && message.data ? message.data : {}); } catch (e) { }
                        },
                    },
                ];
                const releaseLocalSubscriptions = async () => {
                    subscriptions.forEach(({ eventName, handler }) => {
                        try { channel.unsubscribe(eventName, handler); } catch (e) { }
                    });
                    await this.detachRealtimeChannel(channel);
                };
                try {
                    for (const subscription of subscriptions) {
                        const result = channel.subscribe(subscription.eventName, subscription.handler);
                        if (result && typeof result.then === 'function') await result;
                        if (generation !== Number(this.invitationRealtimeGeneration || 0)) break;
                    }
                } catch (error) {
                    await releaseLocalSubscriptions();
                    return;
                }
                if (generation !== Number(this.invitationRealtimeGeneration || 0)) {
                    await releaseLocalSubscriptions();
                    return;
                }
                this.invitationChannel = channel;
                this.invitationSubscriptions = subscriptions;
            });
        },
        detachInvitationRealtime() {
            this.invitationRealtimeGeneration = Number(this.invitationRealtimeGeneration || 0) + 1;
            if (this.invitationRealtimeRetryTimer) {
                clearTimeout(this.invitationRealtimeRetryTimer);
                this.invitationRealtimeRetryTimer = null;
            }
            const snapshot = this.takeInvitationRealtimeSnapshot();
            return this.queueInvitationRealtimeLifecycle(
                () => this.cleanupInvitationRealtimeSnapshot(snapshot)
            );
        },
        async respondToCollaborationInvitation(invitation, action) {
            try {
                if (!invitation || !invitation.invitation_uuid) return;
                const response = await fetch('/chats/collaboration/invitations/respond', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        invitation_uuid: invitation.invitation_uuid,
                        token: invitation.token || '',
                        action,
                    }),
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    alert(data.message || 'No se pudo responder la invitación.');
                    return;
                }
                await this.loadPendingCollaborationInvitations();
                const accepted = action === 'accept';
                const chatUuid = data.invitation && data.invitation.chat_uuid;
                if (accepted && chatUuid) {
                    await this.switchSession(chatUuid, null, null, null, null, { forceServerFetch: true });
                    try { this.$emit('sessionAdded'); } catch (e) { }
                    try { this.$emit('sessionActivated', chatUuid); } catch (e) { }
                }
            } catch (e) {
                alert('No se pudo responder la invitación.');
            }
        },
        async loadCollaborationState() {
            if (this.isDemoRecordingMode()) {
                this.collaborationParticipants = [];
                return;
            }
            try {
                const uuid = this.currentSession && this.currentSession.uuid;
                if (!uuid) return;
                const response = await fetch(`/chats/collaboration/participants?uuid=${encodeURIComponent(uuid)}`);
                if (!this.currentSession || String(this.currentSession.uuid || '') !== String(uuid)) {
                    return;
                }
                if (!response.ok) {
                    this.collaborationParticipants = [];
                    return;
                }
                const data = await response.json();
                if (!this.currentSession || String(this.currentSession.uuid || '') !== String(uuid)) {
                    return;
                }
                this.collaborationParticipants = Array.isArray(data.participants) ? data.participants : [];
            } catch (e) {
                this.collaborationParticipants = [];
            }
        },
        participantDisplayName(participant) {
            if (!participant || typeof participant !== 'object') return 'Participante';
            return participant.display_name || participant.email || participant.username || 'Participante';
        },
        avatarInitial(value, fallback = 'P') {
            const cleaned = String(value || '').trim().replace(/^@+/, '');
            const char = Array.from(cleaned)[0] || fallback || 'P';
            return String(char).toUpperCase();
        },
        participantInitial(participant) {
            if (!participant || typeof participant !== 'object') return 'P';
            const label = this.participantDisplayName(participant);
            const handle = this.buildMentionHandle(
                participant.mention_handle,
                label,
                participant.username,
                participant.email
            );
            return this.avatarInitial(label || handle || 'Participante', 'P');
        },
        buildMentionHandle(...values) {
            for (const value of values) {
                let candidate = String(value || '').trim();
                if (!candidate) continue;
                if (candidate.includes('@')) {
                    candidate = candidate.split('@')[0];
                }
                try {
                    candidate = candidate.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                } catch (e) { }
                candidate = candidate
                    .replace(/^@+/, '')
                    .replace(/\s+/g, '')
                    .replace(/[^A-Za-z0-9_.-]/g, '');
                if (candidate) {
                    return candidate.slice(0, 48);
                }
            }
            return '';
        },
        participantMentionSuggestion(participant) {
            if (!participant || typeof participant !== 'object') return null;
            const status = String(participant.status || 'active').trim().toLowerCase();
            if (status === 'removed' || status === 'rejected') return null;
            if (this.isCurrentUserParticipant(participant)) return null;

            const label = this.participantDisplayName(participant);
            const email = String(participant.email || '').trim();
            const username = String(participant.username || '').trim();
            const handle = this.buildMentionHandle(
                participant.mention_handle,
                label,
                username,
                email
            );
            if (!handle) return null;
            const detail = email && email.toLowerCase() !== String(label || '').trim().toLowerCase()
                ? email
                : (status === 'pending' ? 'Invitación pendiente' : 'Colaborador');
            return {
                id: `human-${email || username || handle}`,
                type: 'human',
                label,
                handle,
                mention: `@${handle}`,
                detail,
                status,
                initial: this.avatarInitial(label || handle || 'Participante', 'P'),
                searchText: `${label} ${email} ${username} ${handle}`.toLowerCase(),
            };
        },
        findActiveMention(text, cursor) {
            const value = String(text || '');
            const caret = Math.max(0, Math.min(Number(cursor) || 0, value.length));
            const beforeCaret = value.slice(0, caret);
            const mentionStart = beforeCaret.lastIndexOf('@');
            if (mentionStart < 0) return null;

            const previous = mentionStart > 0 ? value.charAt(mentionStart - 1) : '';
            if (previous && /[A-Za-z0-9_]/.test(previous)) return null;

            const query = beforeCaret.slice(mentionStart + 1);
            if (/\s/.test(query) || /[^A-Za-z0-9_.-]/.test(query)) return null;
            return { start: mentionStart, query };
        },
        updateMentionSuggestions(event) {
            if (!this.isCollaborativeChat) {
                this.hideMentionSuggestions();
                return;
            }
            clearTimeout(this.mentionHideTimer);
            const textarea = (event && event.target) || this.$refs.messageInput;
            const text = textarea && typeof textarea.value === 'string'
                ? textarea.value
                : String(this.message || '');
            const cursor = textarea && typeof textarea.selectionStart === 'number'
                ? textarea.selectionStart
                : text.length;
            const activeMention = this.findActiveMention(text, cursor);
            if (!activeMention) {
                this.hideMentionSuggestions();
                return;
            }
            this.mentionStartIndex = activeMention.start;
            this.mentionQuery = activeMention.query;
            this.mentionActiveIndex = 0;
            this.mentionSuggestionsVisible = true;
            if (!Array.isArray(this.collaborationParticipants) || !this.collaborationParticipants.length) {
                try { this.loadCollaborationState(); } catch (e) { }
            }
        },
        hideMentionSuggestions() {
            clearTimeout(this.mentionHideTimer);
            this.mentionHideTimer = null;
            this.mentionSuggestionsVisible = false;
            this.mentionQuery = '';
            this.mentionStartIndex = -1;
            this.mentionActiveIndex = 0;
        },
        scheduleHideMentionSuggestions() {
            clearTimeout(this.mentionHideTimer);
            this.mentionHideTimer = setTimeout(() => {
                this.hideMentionSuggestions();
            }, 120);
        },
        handleMentionSuggestionKeydown(event) {
            if (!this.showMentionSuggestions) return false;
            const suggestions = this.mentionSuggestions;
            if (!suggestions.length) return false;
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.mentionActiveIndex = (this.mentionActiveIndex + 1) % suggestions.length;
                return true;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.mentionActiveIndex = (this.mentionActiveIndex - 1 + suggestions.length) % suggestions.length;
                return true;
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault();
                this.selectMentionSuggestion(suggestions[this.mentionActiveIndex] || suggestions[0]);
                return true;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                this.hideMentionSuggestions();
                return true;
            }
            return false;
        },
        selectMentionSuggestion(suggestion) {
            if (!suggestion || !suggestion.mention) return;
            const textarea = this.$refs.messageInput;
            const text = String(this.message || '');
            const cursor = textarea && typeof textarea.selectionStart === 'number'
                ? textarea.selectionStart
                : text.length;
            const start = this.mentionStartIndex >= 0 ? this.mentionStartIndex : cursor;
            const insertion = `${suggestion.mention} `;
            this.message = `${text.slice(0, start)}${insertion}${text.slice(cursor)}`;
            this.hideMentionSuggestions();
            this.$nextTick(() => {
                try {
                    const input = this.$refs.messageInput;
                    if (!input) return;
                    const nextCursor = start + insertion.length;
                    input.focus();
                    input.setSelectionRange(nextCursor, nextCursor);
                    this.adjustTextareaHeightAndCheckLength({ target: input });
                } catch (e) { }
            });
        },
        isCurrentUserParticipant(participant) {
            if (!participant || typeof participant !== 'object') return false;
            const currentEmail = this.getCurrentUserEmail();
            const currentUsername = this.getCurrentUsername().toLowerCase();
            const email = String(participant.email || '').trim().toLowerCase();
            const username = String(participant.username || '').trim().toLowerCase();
            return !!(
                (currentEmail && email && currentEmail === email)
                || (currentUsername && username && currentUsername === username)
            );
        },
        participantPresence(participant) {
            if (!participant || typeof participant !== 'object') {
                return { online: false, typing: false };
            }
            const email = String(participant.email || '').trim().toLowerCase();
            const username = String(participant.username || '').trim().toLowerCase();
            const match = (this.presentMembers || []).find((item) => {
                const itemEmail = String(item.email || '').trim().toLowerCase();
                const itemUsername = String(item.username || '').trim().toLowerCase();
                return !!(
                    (email && itemEmail && email === itemEmail)
                    || (username && itemUsername && username === itemUsername)
                );
            });
            return {
                online: !!match,
                typing: !!(match && match.typing === true),
            };
        },
        participantBadges(participant) {
            const badges = [];
            if (!participant || typeof participant !== 'object') return badges;
            if (participant.role === 'owner') badges.push('Admin');
            if (this.isCurrentUserParticipant(participant)) badges.push('Tú');
            if (participant.status === 'pending') badges.push('Pendiente');
            const presence = this.participantPresence(participant);
            if (presence.typing) badges.push('Escribiendo');
            else if (presence.online) badges.push('En línea');
            return badges;
        },
        canRemoveParticipant(participant) {
            if (!participant || typeof participant !== 'object') return false;
            if (!this.canInviteCollaborators) return false;
            if (participant.role === 'owner') return false;
            if (this.isCurrentUserParticipant(participant)) return false;
            return participant.status === 'active' || participant.status === 'pending';
        },
        showCollaborationNotice(message) {
            const text = String(message || '').trim();
            if (!text) return;
            const safeText = text
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
            try {
                const toastElement = $('#chatToast');
                if (toastElement && toastElement.length) {
                    toastElement.find('.chatToastText').html(`
                        <div class="chatToastIcon">
                            <i class="anticon anticon-team"></i>
                        </div>
                        <p>${safeText}</p>
                    `);
                    toastElement.show();
                    const toast = new bootstrap.Toast(toastElement);
                    toast.show();
                    toastElement.on('hidden.bs.toast', function () {
                        $(this).css('display', 'none');
                    });
                    return;
                }
            } catch (e) { }
            try { alert(text); } catch (e) { }
        },
        isParticipantRemovalForCurrentUser(payload) {
            if (!payload || typeof payload !== 'object') return false;
            const currentEmail = this.getCurrentUserEmail();
            const currentUsername = this.getCurrentUsername().toLowerCase();
            const email = String(payload.participant_email || '').trim().toLowerCase();
            const username = String(payload.participant_username || '').trim().toLowerCase();
            return !!(
                (currentEmail && email && currentEmail === email)
                || (currentUsername && username && currentUsername === username)
            );
        },
        handleParticipantRemoved(payload) {
            if (!payload || typeof payload !== 'object') return;
            const chatUuid = String(payload.chat_uuid || '').trim();
            const isForCurrentUser = this.isParticipantRemovalForCurrentUser(payload);
            if (isForCurrentUser) {
                this.showCollaborationNotice('Te removieron de este chat colaborativo. Ya no tienes acceso a sus mensajes ni documentos.');
                try { this.$emit('collaborationRemoved', chatUuid); } catch (e) { }
                if (this.currentSession && String(this.currentSession.uuid || '') === chatUuid) {
                    try { this.detachChatRealtime(); } catch (e) { }
                    this.collaborationDialogVisible = false;
                    this.collaborationParticipants = [];
                    this.presentMembers = [];
                    this.typingMembers = [];
                    this.replying = false;
                    this.lastError = false;
                    this.errorMessage = null;
                    this.currentSession = {
                        uuid: null,
                        title: null,
                        data: [],
                        created_at: null,
                        updated_at: null,
                        path: null,
                        page_title: null,
                        is_user_document: null,
                        is_law: null,
                        is_multi_document: false,
                        page_title_html: null,
                        suggestedQuestions: [],
                        showPopup: false,
                        popupContent: '',
                        is_call_log: false,
                        is_pinned: false,
                        is_collaborative: false,
                        collaboration_role: null,
                        owner_username: null,
                        aiRunStatus: null,
                        chat_documents_state: [],
                        pending_document_injections: [],
                        chat_pagination: null,
                    };
                    try { this.$emit('sessionActivated', null); } catch (e) { }
                }
                return;
            }
            if (this.currentSession && String(this.currentSession.uuid || '') === chatUuid) {
                this.loadCollaborationState();
                this.showCollaborationNotice(`${payload.participant_display_name || 'Un participante'} ya no forma parte del chat.`);
            }
        },
        handleParticipantLeft(payload) {
            if (!payload || typeof payload !== 'object') return;
            const chatUuid = String(payload.chat_uuid || '').trim();
            const isForCurrentUser = this.isParticipantRemovalForCurrentUser(payload);
            if (isForCurrentUser) {
                this.showCollaborationNotice('Abandonaste este chat colaborativo.');
                try { this.$emit('collaborationRemoved', chatUuid); } catch (e) { }
                if (this.currentSession && String(this.currentSession.uuid || '') === chatUuid) {
                    try { this.detachChatRealtime(); } catch (e) { }
                    this.collaborationDialogVisible = false;
                    this.collaborationParticipants = [];
                    this.presentMembers = [];
                    this.typingMembers = [];
                    this.replying = false;
                    this.lastError = false;
                    this.errorMessage = null;
                    this.currentSession = {
                        uuid: null,
                        title: null,
                        data: [],
                        created_at: null,
                        updated_at: null,
                        path: null,
                        page_title: null,
                        is_user_document: null,
                        is_law: null,
                        is_multi_document: false,
                        page_title_html: null,
                        suggestedQuestions: [],
                        showPopup: false,
                        popupContent: '',
                        is_call_log: false,
                        is_pinned: false,
                        is_collaborative: false,
                        collaboration_role: null,
                        owner_username: null,
                        aiRunStatus: null,
                        chat_documents_state: [],
                        pending_document_injections: [],
                        chat_pagination: null,
                    };
                    try { this.$emit('sessionActivated', null); } catch (e) { }
                }
                return;
            }
            if (this.currentSession && String(this.currentSession.uuid || '') === chatUuid) {
                this.loadCollaborationState();
                this.showCollaborationNotice(`${payload.participant_display_name || 'Un participante'} abandonó el chat.`);
            }
        },
        async leaveCollaborationChat() {
            if (!this.canLeaveCollaborationChat || !this.currentSession || !this.currentSession.uuid) return;
            const confirmed = window.confirm('¿Abandonar este chat colaborativo? Ya no aparecerá en tu lista de chats.');
            if (!confirmed) return;
            this.collaborationRemovalStatus = 'Abandonando el chat...';
            const chatUuid = String(this.currentSession.uuid);
            try {
                const response = await fetch('/chats/collaboration/participants/leave', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ uuid: chatUuid }),
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    this.collaborationRemovalStatus = data.message || 'No se pudo abandonar el chat.';
                    return;
                }
                this.handleParticipantLeft((data && data.participant) || { chat_uuid: chatUuid });
            } catch (e) {
                this.collaborationRemovalStatus = 'No se pudo abandonar el chat.';
            }
        },
        async removeCollaborator(participant) {
            if (!this.canRemoveParticipant(participant)) return;
            const label = this.participantDisplayName(participant);
            const confirmed = window.confirm(`¿Remover a ${label} de este chat colaborativo? Ya no podrá ver mensajes nuevos ni usar los documentos compartidos del chat.`);
            if (!confirmed) return;
            this.collaborationRemovalStatus = `Removiendo a ${label}...`;
            try {
                const response = await fetch('/chats/collaboration/participants/remove', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        uuid: this.currentSession.uuid,
                        email: participant.email || '',
                        username: participant.username || '',
                    }),
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    this.collaborationRemovalStatus = data.message || 'No se pudo remover el participante.';
                    return;
                }
                this.collaborationRemovalStatus = `${label} fue removido del chat.`;
                await this.loadCollaborationState();
            } catch (e) {
                this.collaborationRemovalStatus = 'No se pudo remover el participante.';
            }
        },
        patchCurrentSessionReactive(patch) {
            if (!this.currentSession || !patch || typeof patch !== 'object') return;
            Object.keys(patch).forEach((key) => {
                try {
                    if (typeof this.$set === 'function') {
                        this.$set(this.currentSession, key, patch[key]);
                    } else {
                        this.currentSession[key] = patch[key];
                    }
                } catch (e) {
                    this.currentSession[key] = patch[key];
                }
            });
        },
        presentDocumentAvailabilityNotice(notice, session = this.currentSession) {
            if (!notice || typeof notice !== 'object') {
                this.documentAvailabilityNotice = null;
                return true;
            }
            if (session && session !== this.currentSession) return false;
            const tone = ['info', 'warning', 'error'].includes(String(notice.tone || ''))
                ? String(notice.tone)
                : 'info';
            this.documentAvailabilityNotice = {
                tone,
                title: String(notice.title || '').trim(),
                detail: String(notice.detail || '').trim(),
                failedTitles: Array.isArray(notice.failedTitles)
                    ? notice.failedTitles.map(value => String(value || '').trim()).filter(Boolean)
                    : [],
            };
            return true;
        },
        dismissDocumentAvailabilityNotice() {
            this.documentAvailabilityNotice = null;
        },
        captureDocumentAdditionCheckpoint(
            session = this.currentSession,
            { preserveExisting = false } = {},
        ) {
            if (!session || typeof session !== 'object') return false;
            const uuid = String(session.uuid || '');
            const existing = this.documentAdditionCheckpoint;
            const existingIsInFlight = !!(
                existing
                && Array.isArray(this.chatRequestContexts)
                && this.chatRequestContexts.some(context => (
                    context
                    && context.cancelled !== true
                    && context.completed !== true
                    && Number(context.documentAdditionCheckpointVersion)
                        === Number(existing.version)
                ))
            );
            if (
                preserveExisting
                && existing
                && existing.session === session
                && String(existing.uuid || '') === uuid
                && !existingIsInFlight
            ) return true;
            this.documentAdditionCheckpointRevision = Math.max(
                0,
                Number(this.documentAdditionCheckpointRevision || 0),
            ) + 1;
            const version = this.documentAdditionCheckpointRevision;
            this.documentAdditionCheckpoint = {
                uuid,
                session,
                version,
                snapshot: {
                    path: Array.isArray(session.path) ? session.path.slice() : session.path,
                    page_title: Array.isArray(session.page_title) ? session.page_title.slice() : session.page_title,
                    is_user_document: Array.isArray(session.is_user_document)
                        ? session.is_user_document.slice()
                        : session.is_user_document,
                    is_law: session.is_law,
                    is_multi_document: !!session.is_multi_document,
                    page_title_html: session.page_title_html || null,
                    chat_documents_state: Array.isArray(session.chat_documents_state)
                        ? session.chat_documents_state.map(entry => ({ ...entry }))
                        : [],
                    pending_document_injections: Array.isArray(session.pending_document_injections)
                        ? session.pending_document_injections.map(entry => ({ ...entry }))
                        : [],
                },
            };
            return true;
        },
        currentDocumentAdditionCheckpointVersion(session = this.currentSession) {
            const checkpoint = this.documentAdditionCheckpoint;
            if (!checkpoint || checkpoint.session !== session) return null;
            if (String(checkpoint.uuid || '') !== String(session && session.uuid || '')) return null;
            const version = Number(checkpoint.version);
            return Number.isInteger(version) && version > 0 ? version : null;
        },
        clearDocumentAdditionCheckpoint(uuid = null, expectedVersion = undefined) {
            if (!this.documentAdditionCheckpoint) return false;
            if (
                uuid
                && String(this.documentAdditionCheckpoint.uuid || '') !== String(uuid)
            ) return false;
            if (
                expectedVersion !== undefined
                && Number(this.documentAdditionCheckpoint.version) !== Number(expectedVersion)
            ) return false;
            this.documentAdditionCheckpoint = null;
            return true;
        },
        rollbackDocumentAdditionCheckpoint(uuid = null, expectedVersion = undefined) {
            const checkpoint = this.documentAdditionCheckpoint;
            if (!checkpoint || !checkpoint.snapshot) return false;
            if (uuid && String(checkpoint.uuid || '') !== String(uuid)) return false;
            if (
                expectedVersion !== undefined
                && Number(checkpoint.version) !== Number(expectedVersion)
            ) return false;
            if (
                !this.currentSession
                || checkpoint.session !== this.currentSession
                || String(this.currentSession.uuid || '') !== String(checkpoint.uuid || '')
            ) {
                this.documentAdditionCheckpoint = null;
                return false;
            }
            this.patchCurrentSessionReactive({
                ...checkpoint.snapshot,
                chat_documents_state: checkpoint.snapshot.chat_documents_state.map(entry => ({ ...entry })),
                pending_document_injections: checkpoint.snapshot.pending_document_injections.map(entry => ({ ...entry })),
            });
            this.emitCurrentSessionDocumentPatch(this.currentSession);
            try { this.store(); } catch (e) { }
            this.documentAdditionCheckpoint = null;
            return true;
        },
        applyAuthoritativeDocumentMetadata(rawMetadata, session = this.currentSession) {
            if (
                !rawMetadata
                || typeof rawMetadata !== 'object'
                || Array.isArray(rawMetadata)
                || !session
                || session !== this.currentSession
            ) return false;

            const currentUuid = String(session.uuid || '').trim();
            const metadataUuid = String(
                rawMetadata.uuid || rawMetadata.chat_uuid || ''
            ).trim();
            if (metadataUuid && (!currentUuid || metadataUuid !== currentUuid)) return false;
            if (!Object.prototype.hasOwnProperty.call(rawMetadata, 'chat_documents_state')) {
                return false;
            }

            const canonicalState = this.normalizeChatDocumentsStatePayload(
                rawMetadata.chat_documents_state
            );
            const pendingProjection = {
                chat_documents_state: [],
                pending_document_injections: Array.isArray(session.pending_document_injections)
                    ? session.pending_document_injections.map(entry => ({ ...entry }))
                    : [],
            };
            const previousPending = this.normalizePendingDocumentInjections(pendingProjection)
                .map(entry => ({ ...entry }));
            const canonicalLocatorKeys = new Set(canonicalState.map((entry) => (
                this.buildDocumentKey(
                    entry.path,
                    entry.is_user_document,
                    entry.source_scope_kind || entry.scope || entry.user_document_scope || '',
                )
            )));
            const canonicalKeys = new Set(
                canonicalState.flatMap(entry => [
                    entry.key,
                    entry.doc_id,
                    entry.source_id,
                ]).map(value => String(value || '').trim()).filter(Boolean)
            );
            const pendingDocumentInjections = previousPending.filter((entry) => {
                const key = String(entry.key || '').trim();
                if (key && canonicalKeys.has(key)) return true;
                const locatorKey = this.buildDocumentKey(
                    entry.path,
                    entry.is_user_document,
                    entry.source_scope_kind || entry.scope || entry.user_document_scope || '',
                );
                return canonicalLocatorKeys.has(locatorKey);
            });

            const projection = {};
            this.applySessionDocumentRows(canonicalState, projection);
            this.patchCurrentSessionReactive({
                ...projection,
                chat_documents_state: canonicalState,
                pending_document_injections: pendingDocumentInjections,
                chat_documents_state_status: String(
                    rawMetadata.chat_documents_state_status || 'valid'
                ),
                chat_documents_state_persistable: Object.prototype.hasOwnProperty.call(
                    rawMetadata,
                    'chat_documents_state_persistable',
                )
                    ? this.coerceBoolean(rawMetadata.chat_documents_state_persistable) !== false
                    : true,
            });
            this.emitCurrentSessionDocumentPatch(session);
            try { this.store(); } catch (e) { }
            return true;
        },
        applyDocumentAvailabilityFeedback(rawAvailability, session = this.currentSession) {
            if (!session || session !== this.currentSession) return null;
            const localState = this.normalizeChatDocumentsStatePayload(
                session.chat_documents_state
            );
            const availability = normalizeDocumentAvailability(rawAvailability, localState);
            if (!availability) return null;
            const rawPrunedCount = Number(rawAvailability && rawAvailability.pruned_count);
            const prunedCount = Number.isInteger(rawPrunedCount) && rawPrunedCount > 0
                ? rawPrunedCount
                : 0;

            const failedIds = new Set(
                availability.failed.map(item => String(item.doc_id || '').trim()).filter(Boolean)
            );
            if (failedIds.size) {
                const remainingState = localState.filter((entry) => ![
                    entry.doc_id,
                    entry.source_id,
                    entry.key,
                ].some(value => failedIds.has(String(value || '').trim())));
                if (remainingState.length !== localState.length) {
                    session.chat_documents_state = remainingState;
                    this.applySessionDocumentRows(remainingState, session);
                    this.normalizePendingDocumentInjections(session);
                    this.emitCurrentSessionDocumentPatch(session);
                    try { this.store(); } catch (e) { }
                }
            }

            const availableTotal = availability.available_total === null
                ? this.normalizeChatDocumentsStatePayload(session.chat_documents_state).length
                : availability.available_total;
            // Lazy catalog bookkeeping is internal; only actionable failures
            // should surface in the persistent chat notice.
            const shouldShow = availability.failed.length > 0 || prunedCount > 0;
            if (!shouldShow) return availability;

            const countParts = [];
            if (availability.added !== null) {
                countParts.push(`${availability.added} ${availability.added === 1 ? 'documento agregado' : 'documentos agregados'}`);
            }
            if (availability.already_present) {
                countParts.push(`${availability.already_present} ya ${availability.already_present === 1 ? 'estaba' : 'estaban'} en el chat`);
            }
            const failedTitles = availability.failed.map(item => item.title || 'Documento no disponible');
            const unavailableCount = Math.max(prunedCount, availability.failed.length);
            const failureDetail = unavailableCount
                ? ` ${unavailableCount === 1 ? 'Un documento no quedó disponible' : `${unavailableCount} documentos no quedaron disponibles`}. Puedes volver a agregarlos después de revisar su acceso.`
                : '';
            this.presentDocumentAvailabilityNotice({
                tone: 'warning',
                title: `${availableTotal} ${availableTotal === 1 ? 'documento disponible' : 'documentos disponibles'}`,
                detail: `${countParts.length ? `${countParts.join('; ')}.` : ''}${failureDetail}`.trim(),
                failedTitles,
            }, session);
            return availability;
        },
        getCollaborationSessionTitle() {
            const candidates = [
                this.currentSession && this.currentSession.title,
                this.currentSession && this.currentSession.page_title,
                this.message,
            ];
            for (const candidate of candidates) {
                if (Array.isArray(candidate)) {
                    const joined = candidate.map((item) => String(item || '').trim()).filter(Boolean).join(', ');
                    if (joined) return joined;
                    continue;
                }
                const value = String(candidate || '').trim();
                if (value) return value;
            }
            return 'Chat colaborativo';
        },
        ensureCollaborationSessionUuid() {
            if (!this.currentSession || this.currentSession.is_call_log) return false;
            if (this.currentSession.uuid) {
                window.chat_uuid = this.currentSession.uuid;
                return true;
            }

            this.currentSession.uuid = this.generateUUID();
            this.currentSession.title = this.getCollaborationSessionTitle();
            this.currentSession.created_at = this.currentSession.created_at || Date.now();
            this.currentSession.updated_at = Date.now();
            window.chat_uuid = this.currentSession.uuid;
            this.ensureSessionDocumentState(this.currentSession);
            this.synchronizeSessionDocumentMetadataFromState(this.currentSession);
            this.normalizePendingDocumentInjections(this.currentSession);
            this.store();
            try { this.$emit('sessionActivated', this.currentSession.uuid); } catch (e) { }
            try { this.$emit('sessionAdded'); } catch (e) { }
            try { this.attachChatRealtime(); } catch (e) { }
            return true;
        },
        buildCollaborationInvitePayload(email) {
            const payload = {
                uuid: this.currentSession.uuid,
                email,
                create_if_missing: true,
                title: this.getCollaborationSessionTitle(),
                path: this.currentSession.path,
                page_title: this.currentSession.page_title,
                is_user_document: this.currentSession.is_user_document,
                chat_documents_state: this.serializeChatDocumentsState(this.currentSession.chat_documents_state),
            };
            return payload;
        },
        getPersonalDocumentsSharedByInvite() {
            const docs = [];
            try {
                const groups = this.documentGroups || {};
                if (Array.isArray(groups.all)) {
                    groups.all.forEach((doc) => {
                        if (doc && doc.is_user_document) docs.push(doc);
                    });
                }
            } catch (e) { }
            if (!docs.length && this.currentSession && this.currentSession.is_user_document) {
                const paths = Array.isArray(this.currentSession.path) ? this.currentSession.path : [this.currentSession.path];
                const titles = Array.isArray(this.currentSession.page_title) ? this.currentSession.page_title : [this.currentSession.page_title];
                const userFlags = Array.isArray(this.currentSession.is_user_document)
                    ? this.currentSession.is_user_document
                    : paths.map(() => this.currentSession.is_user_document);
                paths.forEach((path, index) => {
                    if (!userFlags[index]) return;
                    docs.push({
                        path,
                        page_title: titles[index] || path,
                        is_user_document: true,
                    });
                });
            }
            const seen = new Set();
            return docs.filter((doc) => {
                const key = String(doc.key || doc.path || doc.page_title || '').trim();
                if (!key || seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        },
        confirmPersonalDocumentSharingForInvite(email) {
            if (this.isCollaborativeChat) return true;
            const personalDocs = this.getPersonalDocumentsSharedByInvite();
            if (!personalDocs.length) return true;
            const names = personalDocs
                .map((doc) => String(doc.page_title || doc.path || '').trim())
                .filter(Boolean)
                .slice(0, 5);
            const suffix = personalDocs.length > names.length
                ? `\n- ${personalDocs.length - names.length} archivo(s) adicional(es)`
                : '';
            const list = names.length ? `\n\n- ${names.join('\n- ')}${suffix}` : '';
            return window.confirm(
                `Vas a compartir contenido de tu biblioteca personal con ${email}. ` +
                `Al enviar la invitación, ${personalDocs.length === 1 ? 'este archivo quedará disponible' : 'estos archivos quedarán disponibles'} para los miembros del chat como contexto compartido.${list}`
            );
        },
        async inviteCollaborator() {
            const email = String(this.collaborationInviteEmail || '').trim().toLowerCase();
            if (!email) return;
            if (!this.confirmPersonalDocumentSharingForInvite(email)) return;
            if (!this.ensureCollaborationSessionUuid()) return;
            this.collaborationInviteStatus = 'Enviando invitación...';
            try {
                const response = await fetch('/chats/collaboration/invite', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(this.buildCollaborationInvitePayload(email)),
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    this.collaborationInviteStatus = data.message || 'No se pudo enviar la invitación.';
                    return;
                }
                this.collaborationInviteEmail = '';
                this.collaborationInviteStatus = data.invitation && data.invitation.already_pending
                    ? 'Ya existe una invitación pendiente para ese correo.'
                    : 'Invitación enviada.';
                this.patchCurrentSessionReactive({
                    is_collaborative: true,
                    collaboration_role: this.currentSession.collaboration_role || 'owner',
                });
                try {
                    this.$emit('sessionPatched', this.currentSession.uuid, {
                        is_collaborative: true,
                        collaboration_role: this.currentSession.collaboration_role || 'owner',
                    });
                } catch (e) { }
                await this.loadCollaborationState();
            } catch (e) {
                this.collaborationInviteStatus = 'No se pudo enviar la invitación.';
            }
        },
        getMessageKeyForDuplicate(message) {
            if (!message) return '';
            const ts = message.timestamp == null ? '' : String(message.timestamp);
            const sender = String(message.sender_username || message.sender_email || message.sender_display_name || '').trim().toLowerCase();
            const text = String(message.text || '').trim();
            return `${ts}|${sender}|${text}`;
        },
        hashMessageSeed(seed) {
            const text = String(seed || '');
            let hash = 0;
            for (let i = 0; i < text.length; i += 1) {
                hash = ((hash << 5) - hash) + text.charCodeAt(i);
                hash |= 0;
            }
            return Math.abs(hash).toString(36);
        },
        canonicalMessageFallbackId(message) {
            if (!message || typeof message !== 'object') return '';
            const senderType = message.sender === 'bot' ? 'assistant' : (message.sender || 'message');
            const absoluteIndex = this.parseChatPaginationNumber(message._chat_absolute_index, null);
            const turnId = String(message.turn_id || '').trim();
            const serverTimestamp = String(message.server_timestamp || '').trim();
            const sender = String(
                message.sender_email
                || message.sender_username
                || message.sender_display_name
                || senderType
            ).trim();
            const text = String(message.text || message.content || '').slice(0, 160);
            const stablePosition = turnId || absoluteIndex !== null
                ? `${turnId}|${absoluteIndex === null ? '' : String(absoluteIndex)}`
                : `${serverTimestamp}|${sender}|${text}`;
            const seed = [
                'canonical',
                senderType,
                stablePosition,
            ].join('|');
            return `msg-${senderType}-${this.hashMessageSeed(seed)}`;
        },
        markMessageAsOptimistic(message) {
            if (!message || typeof message !== 'object') return message;
            try { this.$set(message, '_is_optimistic', true); } catch (e) { message._is_optimistic = true; }
            this.messageReferenceId(message);
            return message;
        },
        messageReferenceId(message) {
            if (!message) return '';
            if (message.message_id) return String(message.message_id);
            if (message.client_message_id) return String(message.client_message_id);
            const senderType = message.sender === 'bot' ? 'assistant' : (message.sender || 'message');
            // A message created in this client needs a persistent identity
            // before the server assigns its durable id. Only explicitly
            // marked optimistic messages get a random client id; canonical
            // payloads use their deterministic fallback below.
            if (message._is_optimistic === true) {
                let randomPart = '';
                try {
                    randomPart = globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function'
                        ? globalThis.crypto.randomUUID()
                        : '';
                } catch (e) { }
                if (!randomPart) {
                    randomPart = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
                }
                const clientId = `client-${senderType}-${randomPart}`;
                try { this.$set(message, 'client_message_id', clientId); } catch (e) { message.client_message_id = clientId; }
                try { this.$set(message, 'message_id', clientId); } catch (e) { message.message_id = clientId; }
                return clientId;
            }
            return this.canonicalMessageFallbackId(message);
        },
        canonicalMessageIdentityValues(message) {
            if (!message || typeof message !== 'object') return [];
            return [...new Set([
                message.message_id,
                message.response_message_id,
                message.client_request_id,
                this.messageReferenceId(message),
            ].map(value => String(value || '').trim()).filter(Boolean))];
        },
        canonicalMessageText(message) {
            return String(message && (message.text || message.content) || '')
                .replace(/\s+/g, ' ')
                .trim();
        },
        canonicalMessageMatchScore(existing, incoming) {
            if (!existing || !incoming || existing.sender !== incoming.sender) return -1;
            if (existing.sender === 'bot') {
                const existingResponseId = durableChatResponseId(existing.response_message_id || existing.message_id);
                const incomingResponseId = durableChatResponseId(incoming.response_message_id || incoming.message_id);
                if (existingResponseId && incomingResponseId && existingResponseId !== incomingResponseId) return -1;
            }
            const existingIds = new Set(this.canonicalMessageIdentityValues(existing));
            const incomingIds = this.canonicalMessageIdentityValues(incoming);
            if (incomingIds.some(id => existingIds.has(id))) return 100;

            const existingTurnId = String(existing.turn_id || '').trim();
            const incomingTurnId = String(incoming.turn_id || '').trim();
            if (existingTurnId && incomingTurnId && existingTurnId === incomingTurnId) return 90;

            const existingTimestamp = String(existing.server_timestamp || '').trim();
            const incomingTimestamp = String(incoming.server_timestamp || '').trim();
            if (existingTimestamp && incomingTimestamp && existingTimestamp === incomingTimestamp) {
                return this.canonicalMessageText(existing) === this.canonicalMessageText(incoming) ? 80 : 75;
            }

            const existingAbsoluteIndex = this.parseChatPaginationNumber(existing._chat_absolute_index, null);
            const incomingAbsoluteIndex = this.parseChatPaginationNumber(incoming._chat_absolute_index, null);
            if (existingAbsoluteIndex !== null && incomingAbsoluteIndex !== null
                && existingAbsoluteIndex === incomingAbsoluteIndex) {
                return 70;
            }

            const sameText = this.canonicalMessageText(existing)
                && this.canonicalMessageText(existing) === this.canonicalMessageText(incoming);
            if (sameText && (!existingTimestamp || existing.isLoading === true)) return 60;
            return -1;
        },
        findCanonicalMessageMatch(existingMessages, incoming, consumedIndexes, incomingIndex) {
            const existing = Array.isArray(existingMessages) ? existingMessages : [];
            let bestIndex = -1;
            let bestScore = -1;
            existing.forEach((candidate, index) => {
                if (consumedIndexes.has(index)) return;
                const score = this.canonicalMessageMatchScore(candidate, incoming);
                if (score < 0) return;
                if (
                    score > bestScore
                    || (score === bestScore && bestIndex >= 0
                        && Math.abs(index - incomingIndex) < Math.abs(bestIndex - incomingIndex))
                ) {
                    bestScore = score;
                    bestIndex = index;
                }
            });
            return bestIndex;
        },
        isUnboundOptimisticAssistantPlaceholder(message) {
            if (!message || message.sender !== 'bot') return false;
            // A response that already has a durable server identity must only
            // reconcile through that identity. This fallback is exclusively
            // for the acceptance/recovery gap before it was received.
            if (String(message.response_message_id || '').trim()) return false;
            const messageId = String(message.message_id || '').trim();
            return message.isLoading === true
                || message.awaitingInitialRequestAcceptance === true
                || this.isProvisionalLocalAssistantMessageId(messageId);
        },
        optimisticAssistantAnchorsMessage(placeholder, anchorMessage) {
            if (!placeholder || !anchorMessage) return false;
            const placeholderAnchorId = String(
                placeholder.ai_response_anchor_message_id || ''
            ).trim();
            const anchorIds = [
                anchorMessage.message_id,
                anchorMessage.client_message_id,
                anchorMessage.client_request_id,
            ].map(value => String(value || '').trim()).filter(Boolean);
            const placeholderRequestId = String(placeholder.client_request_id || '').trim();
            if (placeholderRequestId && anchorIds.includes(placeholderRequestId)) return true;
            if (placeholderAnchorId && anchorIds.includes(placeholderAnchorId)) return true;

            const placeholderTimestamp = String(
                placeholder.ai_response_anchor_timestamp || ''
            ).trim();
            const anchorTimestamp = String(anchorMessage.server_timestamp || '').trim();
            return !!(placeholderTimestamp && anchorTimestamp && placeholderTimestamp === anchorTimestamp);
        },
        findCanonicalOptimisticAssistantMatch(
            existingMessages,
            desiredMessages,
            reconciledMessages,
            consumedIndexes,
            incomingIndex,
        ) {
            let anchorIndex = incomingIndex - 1;
            while (anchorIndex >= 0 && desiredMessages[anchorIndex]?.sender !== 'user') {
                anchorIndex -= 1;
            }
            if (anchorIndex < 0) return -1;
            const canonicalAnchor = reconciledMessages[anchorIndex] || desiredMessages[anchorIndex];
            if (!canonicalAnchor || canonicalAnchor.sender !== 'user') return -1;

            const candidates = [];
            (Array.isArray(existingMessages) ? existingMessages : []).forEach((message, index) => {
                if (consumedIndexes.has(index)) return;
                if (!this.isUnboundOptimisticAssistantPlaceholder(message)) return;
                if (this.optimisticAssistantAnchorsMessage(message, canonicalAnchor)) {
                    candidates.push(index);
                }
            });
            return candidates.length === 1 ? candidates[0] : -1;
        },
        mergeCanonicalResearchActivityHistory(existingHistory, incomingHistory) {
            if (!Array.isArray(existingHistory)) {
                return Array.isArray(incomingHistory) ? incomingHistory.slice() : existingHistory;
            }
            if (!Array.isArray(incomingHistory)) return existingHistory;
            const indexesByKey = new Map();
            existingHistory.forEach((activity, index) => {
                const key = this.researchActivityDedupeKey(activity);
                if (!key) return;
                const indexes = indexesByKey.get(key) || [];
                indexes.push(index);
                indexesByKey.set(key, indexes);
            });
            const consumedIndexes = new Set();
            const reconciled = incomingHistory.map((activity) => {
                const key = this.researchActivityDedupeKey(activity);
                const candidateIndexes = key ? indexesByKey.get(key) : [];
                const existingIndex = Array.isArray(candidateIndexes)
                    ? candidateIndexes.find(index => !consumedIndexes.has(index))
                    : undefined;
                if (existingIndex === undefined) {
                    return activity;
                }
                consumedIndexes.add(existingIndex);
                const target = existingHistory[existingIndex];
                if (target && typeof target === 'object' && activity && typeof activity === 'object') {
                    Object.keys(activity).forEach((field) => {
                        try { this.$set(target, field, activity[field]); } catch (e) { target[field] = activity[field]; }
                    });
                }
                return target || activity;
            });
            // The server chain is canonical. Reorder/trim the existing
            // reactive array instead of replacing it so a BotMessage retains
            // its local selection while stale activities actually disappear.
            existingHistory.splice(0, existingHistory.length, ...reconciled);
            return existingHistory;
        },
        clearCanonicalAssistantTransientState(message) {
            if (!message || message.sender !== 'bot') return message;
            const authoritativeState = {
                isLoading: false,
                failed: message.generation_failed === true,
                retryable: message.generation_failed === true && message.retryable !== false,
                cancelled: false,
                waitingForUser: false,
                resumingContext: false,
                continuationNeedsAction: false,
                awaitingInitialRequestAcceptance: false,
                terminalRecoveryInFlight: false,
                _is_optimistic: false,
            };
            Object.keys(authoritativeState).forEach((field) => {
                try { this.$set(message, field, authoritativeState[field]); } catch (e) { message[field] = authoritativeState[field]; }
            });
            return message;
        },
        hasRecoverableGenerationError(runStatus = {}) {
            if (!runStatus || typeof runStatus !== 'object') return false;
            const status = String(runStatus.status || runStatus.state || '').trim().toLowerCase();
            const phase = String(runStatus.phase || '').trim().toLowerCase();
            const reportsFailure = !!(
                runStatus.error === true
                || ['error', 'failed', 'failed_retryable'].includes(status)
                || ['error', 'failed', 'failed_retryable'].includes(phase)
            );
            return reportsFailure && runStatus.recoverable !== false;
        },
        clearCanonicalAssistantFailureFlags(session, canonicalMessages = []) {
            if (!session || !Array.isArray(session.data)) return;
            const canonicalAssistants = (Array.isArray(canonicalMessages) ? canonicalMessages : [])
                .filter(message => (
                    message
                    && message.sender === 'bot'
                    && String(message.text || '').trim()
                ));
            const canonicalObjects = new Set(canonicalAssistants);
            const canonicalIds = new Set(canonicalAssistants
                .flatMap(message => this.canonicalMessageIdentityValues(message))
                .map(value => String(value || '').trim())
                .filter(Boolean));
            session.data.forEach((message) => {
                if (!message || message.sender !== 'bot') return;
                const isCanonicalObject = canonicalObjects.has(message);
                const hasCanonicalIdentity = this.canonicalMessageIdentityValues(message)
                    .some(value => canonicalIds.has(String(value || '').trim()));
                if (isCanonicalObject || hasCanonicalIdentity) {
                    this.clearCanonicalAssistantTransientState(message);
                }
            });
        },
        canonicalRepairTargetProjection(session, canonicalMessages = []) {
            const localMessages = session && Array.isArray(session.data)
                ? session.data
                : [];
            const canonical = Array.isArray(canonicalMessages) ? canonicalMessages : [];
            const isTrueFlag = value => (
                value === true
                || value === 1
                || String(value || '').trim().toLowerCase() === 'true'
            );
            let canonicalTriggerIndex = -1;
            for (let index = canonical.length - 1; index >= 0; index -= 1) {
                const message = canonical[index];
                if (
                    message
                    && message.sender === 'user'
                    && !isTrueFlag(message.human_only)
                    && !isTrueFlag(message.ai_queued)
                    && !String(message.ai_queue_status || '').trim()
                    && !isTrueFlag(message.chat_document_injection)
                    && !isTrueFlag(message.document_injection)
                    && !isTrueFlag(message.chat_context_resume_message)
                    && !isTrueFlag(message.context_resume_message)
                    && !isTrueFlag(message.context_resume)
                ) {
                    canonicalTriggerIndex = index;
                    break;
                }
            }

            const consumedLocalIndexes = new Set();
            let localTriggerIndex = -1;
            canonical.forEach((message, index) => {
                const matchIndex = this.findCanonicalMessageMatch(
                    localMessages,
                    message,
                    consumedLocalIndexes,
                    index,
                );
                if (matchIndex < 0) return;
                consumedLocalIndexes.add(matchIndex);
                if (index === canonicalTriggerIndex) localTriggerIndex = matchIndex;
            });

            // Canonical rows provide the only settled assistant evidence. Keep
            // every unmatched local tail row so a newer user/loading/failed
            // turn prevents an older server response from clearing its error.
            const projected = canonical.slice();
            const localTailStart = localTriggerIndex >= 0 ? localTriggerIndex + 1 : 0;
            localMessages.forEach((message, index) => {
                if (index < localTailStart || consumedLocalIndexes.has(index)) return;
                if (message && message.sender === 'bot') {
                    projected.push({ ...message, failed: true });
                    return;
                }
                projected.push(message);
            });
            return projected;
        },
        repairStaleChatErrorFromCanonical(session, canonicalMessages, serverData = {}) {
            if (!session || !Array.isArray(canonicalMessages)) return false;
            const runStatus = serverData && serverData.run_status
                && typeof serverData.run_status === 'object'
                ? serverData.run_status
                : {};
            const completionEvidence = {
                messages: canonicalMessages,
                inProgress: !!(serverData && (
                    serverData.in_progress === true
                    || serverData.run_state_indeterminate === true
                )),
                hasRecoverableGenerationError: this.hasRecoverableGenerationError(runStatus),
                pagination: this.normalizeChatPaginationPayload(serverData),
            };
            if (!canonicalChatProvesLatestTurnCompleted(completionEvidence)) return false;
            const targetProjection = this.canonicalRepairTargetProjection(
                session,
                canonicalMessages,
            );
            if (!canonicalChatProvesLatestTurnCompleted({
                ...completionEvidence,
                messages: targetProjection,
            })) return false;

            this.clearCanonicalAssistantFailureFlags(session, canonicalMessages);
            session.lastError = false;
            session.errorMessage = null;
            if (session === this.currentSession) {
                this.lastError = false;
                this.errorMessage = null;
            }
            return true;
        },
        repairStaleChatErrorFromRealtimeCompletion(message) {
            if (
                !message
                || message.sender !== 'bot'
                || message.isLoading === true
                || message.cancelled === true
                || message.waitingForUser === true
                || message.resumingContext === true
                || !String(message.text || '').trim()
                || !this.currentSession
                || !Array.isArray(this.currentSession.data)
            ) return false;

            const target = this.currentSession.data.includes(message)
                ? message
                : this.findAiAnswerMatch(message);
            if (!target) return false;
            const projectedMessages = this.currentSession.data.map(candidate => (
                candidate === target
                    ? {
                        ...candidate,
                        isLoading: false,
                        failed: false,
                        retryable: false,
                        cancelled: false,
                        waitingForUser: false,
                        resumingContext: false,
                    }
                    : candidate
            ));
            const provesLatestTurnCompleted = canonicalChatProvesLatestTurnCompleted({
                messages: projectedMessages,
                inProgress: false,
                hasRecoverableGenerationError: false,
                pagination: this.currentSession.chat_pagination || null,
            });
            if (!provesLatestTurnCompleted) return false;

            this.clearCanonicalAssistantTransientState(target);
            this.clearLastChatError();
            return true;
        },
        reconcileCanonicalMessageFields(existing, incoming) {
            if (!existing || !incoming) return existing || incoming;
            const canonicalFields = { ...incoming };
            canonicalFields.researchActivityHistory = this.mergeCanonicalResearchActivityHistory(
                existing.researchActivityHistory,
                incoming.researchActivityHistory,
            );
            ['contextDocuments', 'reactions'].forEach((field) => {
                if (!Array.isArray(incoming[field]) && Array.isArray(existing[field])) {
                    canonicalFields[field] = existing[field];
                }
            });
            Object.keys(canonicalFields).forEach((field) => {
                if (canonicalFields[field] === undefined) return;
                if (field === 'client_message_id' && existing.client_message_id) return;
                if (
                    ['message_id', 'client_message_id', 'client_request_id', 'response_message_id', 'server_timestamp', 'turn_id'].includes(field)
                    && !String(canonicalFields[field] || '').trim()
                ) return;
                try { this.$set(existing, field, canonicalFields[field]); } catch (e) { existing[field] = canonicalFields[field]; }
            });
            if (existing.sender === 'user') {
                try { this.$set(existing, '_is_optimistic', false); } catch (e) { existing._is_optimistic = false; }
            }
            // Canonical rows settle the optimistic state, including a saved
            // generation failure when the response did not complete.
            this.clearCanonicalAssistantTransientState(existing);
            return existing;
        },
        reconcileConversationMessagesInPlace(incomingMessages, {
            preserveLoadingPlaceholders = false,
            preserveWaitingPlaceholders = false,
            preservePendingTurnRequestId = '',
        } = {}) {
            if (!this.currentSession || !Array.isArray(this.currentSession.data)) return [];
            const existingMessages = this.currentSession.data.slice();
            const desiredMessages = Array.isArray(incomingMessages) ? incomingMessages : [];
            const consumedIndexes = new Set();
            const reconciled = [];
            desiredMessages.forEach((incoming, incomingIndex) => {
                let matchIndex = this.findCanonicalMessageMatch(
                    existingMessages,
                    incoming,
                    consumedIndexes,
                    incomingIndex,
                );
                if (matchIndex < 0 && incoming && incoming.sender === 'bot') {
                    matchIndex = this.findCanonicalOptimisticAssistantMatch(
                        existingMessages,
                        desiredMessages,
                        reconciled,
                        consumedIndexes,
                        incomingIndex,
                    );
                }
                if (matchIndex < 0) {
                    reconciled.push(incoming);
                    return;
                }
                consumedIndexes.add(matchIndex);
                reconciled.push(
                    this.reconcileCanonicalMessageFields(existingMessages[matchIndex], incoming)
                );
            });

            const pendingRequestId = String(preservePendingTurnRequestId || '').trim();
            if (pendingRequestId) {
                existingMessages.forEach((message, index) => {
                    if (
                        consumedIndexes.has(index)
                        || !message
                        || message.sender !== 'user'
                        || String(message.client_request_id || '').trim() !== pendingRequestId
                    ) return;
                    reconciled.splice(Math.min(index, reconciled.length), 0, message);
                    consumedIndexes.add(index);
                });
            }

            if (preserveLoadingPlaceholders || preserveWaitingPlaceholders) {
                existingMessages.forEach((message, index) => {
                    const preserveLoading = preserveLoadingPlaceholders && message && message.isLoading === true;
                    const preserveWaiting = preserveWaitingPlaceholders && message && (
                        message.waitingForUser === true || message.resumingContext === true
                    );
                    if (consumedIndexes.has(index) || (!preserveLoading && !preserveWaiting)) return;
                    const anchorId = String(message.ai_response_anchor_message_id || '').trim();
                    const anchorIndex = anchorId
                        ? reconciled.findIndex(item => this.messageReferenceId(item) === anchorId)
                        : -1;
                    const insertIndex = anchorIndex >= 0
                        ? this.aiReplyInsertIndexAfterAnchor(reconciled, message, anchorIndex)
                        : reconciled.length;
                    reconciled.splice(insertIndex, 0, message);
                    consumedIndexes.add(index);
                });
            }

            this.currentSession.data.splice(0, this.currentSession.data.length, ...reconciled);
            return this.currentSession.data;
        },
        findLatestUserMessageForAiInvocation(invocationText = '') {
            const messages = this.currentSession && Array.isArray(this.currentSession.data)
                ? this.currentSession.data
                : [];
            const normalizedText = String(invocationText || '').trim();
            for (let i = messages.length - 1; i >= 0; i--) {
                const message = messages[i];
                if (!message || message.sender !== 'user') continue;
                if (normalizedText && String(message.text || '').trim() !== normalizedText) continue;
                return message;
            }
            return null;
        },
        findMessageIndexByAnchor({ messageId = '', timestamp = '', text = '', sender = 'user' } = {}, exclude = null) {
            const messages = this.currentSession && Array.isArray(this.currentSession.data)
                ? this.currentSession.data
                : [];
            const normalizedMessageId = String(messageId || '').trim();
            const normalizedTimestamp = String(timestamp || '').trim();
            const normalizedText = String(text || '').trim();
            const normalizedSender = String(sender || '').trim();
            for (let i = messages.length - 1; i >= 0; i--) {
                const message = messages[i];
                if (!message || message === exclude) continue;
                if (normalizedSender && message.sender !== normalizedSender) continue;
                if (normalizedMessageId && this.messageReferenceId(message) === normalizedMessageId) return i;
                const messageTimestamp = String(message.server_timestamp || message.timestamp || '').trim();
                if (normalizedTimestamp && messageTimestamp === normalizedTimestamp) return i;
                if (normalizedText && String(message.text || '').trim() === normalizedText) return i;
            }
            return -1;
        },
        getAiReplyAnchorMessage(options = {}, invocationText = '') {
            if (options && options.queuedMessage) {
                const queuedMessage = options.queuedMessage;
                const directIndex = this.currentSession && Array.isArray(this.currentSession.data)
                    ? this.currentSession.data.indexOf(queuedMessage)
                    : -1;
                if (directIndex >= 0) return queuedMessage;
                const queuedIndex = this.findMessageIndexByAnchor({
                    messageId: queuedMessage.message_id,
                    timestamp: queuedMessage.server_timestamp || queuedMessage.timestamp,
                    text: queuedMessage.text,
                    sender: 'user',
                });
                if (queuedIndex >= 0) return this.currentSession.data[queuedIndex];
            }
            return this.findLatestUserMessageForAiInvocation(invocationText);
        },
        annotateAiReplyAnchor(botMessage, anchorMessage) {
            if (!botMessage || !anchorMessage) return;
            const anchorId = this.messageReferenceId(anchorMessage);
            const anchorTimestamp = String(anchorMessage.server_timestamp || anchorMessage.timestamp || '').trim();
            const anchorText = String(anchorMessage.text || anchorMessage.content || '').trim();
            if (anchorId) {
                try { this.$set(botMessage, 'ai_response_anchor_message_id', anchorId); } catch (e) { botMessage.ai_response_anchor_message_id = anchorId; }
            }
            if (anchorTimestamp) {
                try { this.$set(botMessage, 'ai_response_anchor_timestamp', anchorTimestamp); } catch (e) { botMessage.ai_response_anchor_timestamp = anchorTimestamp; }
            }
            if (anchorText) {
                try { this.$set(botMessage, 'ai_response_anchor_text', anchorText); } catch (e) { botMessage.ai_response_anchor_text = anchorText; }
            }
        },
        insertAiReplyAfterAnchor(botMessage, options = {}, invocationText = '') {
            if (!this.currentSession || !Array.isArray(this.currentSession.data) || !botMessage) return false;
            const anchorMessage = this.getAiReplyAnchorMessage(options, invocationText);
            this.annotateAiReplyAnchor(botMessage, anchorMessage);
            if (!anchorMessage) {
                this.currentSession.data.push(botMessage);
                return false;
            }
            const anchorIndex = this.currentSession.data.indexOf(anchorMessage);
            if (anchorIndex < 0) {
                this.currentSession.data.push(botMessage);
                return false;
            }
            this.currentSession.data.splice(anchorIndex + 1, 0, botMessage);
            return true;
        },
        aiReplyInsertIndexAfterAnchor(messages, botMessage, anchorIndex) {
            let insertIndex = anchorIndex + 1;
            const responseId = durableChatResponseId(botMessage.response_message_id || botMessage.message_id);
            const turnId = String(botMessage.turn_id || '').trim();
            if (!responseId || !turnId) return insertIndex;
            // Retries reuse their user anchor and turn, but follow earlier failed attempts.
            while (insertIndex < messages.length) {
                const candidate = messages[insertIndex];
                const failedResponseId = candidate && durableChatResponseId(candidate.response_message_id || candidate.message_id);
                if (candidate !== botMessage && !(
                    candidate && candidate.sender === 'bot' && candidate.generation_failed === true
                    && String(candidate.turn_id || '').trim() === turnId
                    && failedResponseId && failedResponseId !== responseId
                )) break;
                insertIndex += 1;
            }
            return insertIndex;
        },
        repositionAiReplyAfterAnchor(botMessage) {
            if (!this.currentSession || !Array.isArray(this.currentSession.data) || !botMessage) return botMessage;
            const messages = this.currentSession.data;
            const currentIndex = messages.indexOf(botMessage);
            if (currentIndex < 0) return botMessage;
            const anchorIndex = this.findMessageIndexByAnchor({
                messageId: botMessage.ai_response_anchor_message_id,
                timestamp: botMessage.ai_response_anchor_timestamp,
                text: botMessage.ai_response_anchor_text,
                sender: 'user',
            }, botMessage);
            if (anchorIndex < 0) return botMessage;
            let insertIndex = this.aiReplyInsertIndexAfterAnchor(messages, botMessage, anchorIndex);
            if (currentIndex < insertIndex) insertIndex -= 1;
            if (insertIndex === currentIndex) return botMessage;
            messages.splice(currentIndex, 1);
            messages.splice(insertIndex, 0, botMessage);
            return botMessage;
        },
        normalizeReplyToPayload(replyTo) {
            if (!replyTo || typeof replyTo !== 'object') return null;
            const snippet = String(replyTo.snippet || '').trim();
            if (!snippet) return null;
            const senderTypeRaw = String(replyTo.sender_type || replyTo.sender || '').trim().toLowerCase();
            const senderType = senderTypeRaw === 'bot' ? 'assistant' : (senderTypeRaw || 'user');
            return {
                message_id: String(replyTo.message_id || '').trim().slice(0, 160),
                sender_type: ['user', 'assistant'].includes(senderType) ? senderType : 'user',
                sender_label: String(replyTo.sender_label || '').trim().slice(0, 120),
                snippet: snippet.slice(0, 240),
                timestamp: String(replyTo.timestamp || '').trim().slice(0, 80),
            };
        },
        firstNameFromSender(message) {
            if (!message || typeof message !== 'object') return '';
            const email = String(message.sender_email || '').trim();
            const displayName = String(message.sender_display_name || '').trim();
            const displayNameIsEmail = displayName && displayName.toLowerCase() === email.toLowerCase();
            if (displayName && !displayNameIsEmail && !displayName.includes('@')) {
                return displayName.split(/\s+/).filter(Boolean)[0] || displayName;
            }
            if (email && email.includes('@')) {
                const localPart = email.split('@')[0].split(/[._-]+/).filter(Boolean)[0] || email.split('@')[0];
                return localPart ? localPart.charAt(0).toUpperCase() + localPart.slice(1) : '';
            }
            return String(message.sender_username || '').trim();
        },
        buildReplyReference(message) {
            if (!this.isCollaborativeChat || !message || message.sender === 'system') return null;
            const text = String(message.text || message.content || '').replace(/\s+/g, ' ').trim();
            if (!text) return null;
            const senderType = message.sender === 'bot' ? 'assistant' : 'user';
            const senderLabel = senderType === 'assistant'
                ? 'Abogado Virtual'
                : (this.firstNameFromSender(message) || 'Participante');
            return this.normalizeReplyToPayload({
                message_id: this.messageReferenceId(message),
                sender_type: senderType,
                sender_label: senderLabel,
                snippet: text,
                timestamp: String(message.server_timestamp || message.timestamp || ''),
            });
        },
        selectReplyTarget(message) {
            const reply = this.buildReplyReference(message);
            if (!reply) return;
            this.replyTarget = reply;
            this.$nextTick(() => {
                try {
                    const textarea = this.$refs.messageInput;
                    if (textarea && typeof textarea.focus === 'function') {
                        textarea.focus({ preventScroll: true });
                    }
                } catch (e) { }
            });
        },
        clearReplyTarget() {
            this.replyTarget = null;
        },
        scrollToMessageReference(replyTo) {
            const reply = this.normalizeReplyToPayload(replyTo);
            if (!reply) return;
            this.$nextTick(() => {
                try {
                    const candidates = Array.from(this.$el.querySelectorAll('[data-message-ref]'));
                    let target = candidates.find((el) => el.getAttribute('data-message-ref') === reply.message_id);
                    if (!target && reply.snippet) {
                        const targetType = reply.sender_type === 'assistant' ? 'bot' : 'user';
                        const normalizedSnippet = reply.snippet.toLowerCase();
                        target = candidates.find((el) => {
                            const sender = el.getAttribute('data-message-sender') || '';
                            const text = (el.textContent || '').replace(/\s+/g, ' ').toLowerCase();
                            return sender === targetType && text.includes(normalizedSnippet.slice(0, 80).toLowerCase());
                        });
                    }
                    if (!target) return;
                    target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                    target.classList.add('collaboration-message-highlight');
                    setTimeout(() => {
                        try { target.classList.remove('collaboration-message-highlight'); } catch (e) { }
                    }, 1800);
                } catch (e) { }
            });
        },
        findLoadingBotIndex() {
            const messages = this.currentSession && Array.isArray(this.currentSession.data)
                ? this.currentSession.data
                : [];
            for (let i = messages.length - 1; i >= 0; i--) {
                const item = messages[i];
                if (item && item.sender === 'bot' && item.isLoading === true) return i;
            }
            return -1;
        },
        findLoadingBotIndexForRun(runId = null) {
            const messages = this.currentSession && Array.isArray(this.currentSession.data)
                ? this.currentSession.data
                : [];
            const normalizedRunId = runId !== undefined && runId !== null ? String(runId) : '';
            if (normalizedRunId) {
                for (let i = messages.length - 1; i >= 0; i--) {
                    const item = messages[i];
                    if (!item || item.sender !== 'bot' || item.isLoading !== true) continue;
                    if (item.run_id !== undefined && item.run_id !== null && String(item.run_id) === normalizedRunId) {
                        return i;
                    }
                }
                if (this.isCompletedSessionRunId(normalizedRunId)) {
                    // A completed prior run must not seize the empty placeholder
                    // created for the next turn. That paints the previous answer
                    // into the new bubble while reasoning is still in progress.
                    return -1;
                }
                for (let i = 0; i < messages.length; i++) {
                    const item = messages[i];
                    if (!item || item.sender !== 'bot' || item.isLoading !== true) continue;
                    if (item.run_id === undefined || item.run_id === null || item.run_id === '') {
                        // /chat_stream has not reserved this turn yet. A tagged
                        // leftover from the previous answer must not inherit it.
                        if (item.awaitingInitialRequestAcceptance === true) continue;
                        return i;
                    }
                }
                // A tagged event must never claim another active reply merely
                // because it is the most recent loading placeholder.  Numeric
                // live-run ids and UUID continuation ids are separate
                // namespaces, so only an exact (or truly unbound) match is
                // safe here.
                return -1;
            }
            return this.findLoadingBotIndex();
        },
        completedSessionIdentities(currentMessage = null) {
            return completedChatIdentitiesFromMessages(
                this.currentSession && Array.isArray(this.currentSession.data)
                    ? this.currentSession.data
                    : [],
                currentMessage,
            );
        },
        isCompletedSessionRunId(runId = null) {
            const incoming = runId !== undefined && runId !== null ? String(runId) : '';
            if (!incoming) return false;
            const identities = this.completedSessionIdentities();
            const lastCompleted = this.currentSession && this.currentSession.last_completed_run_id;
            if (chatRunIdIsAtMost(incoming, lastCompleted) || chatRunIdIsAtMost(incoming, identities.lastCompletedRunId)) {
                return true;
            }
            return identities.runIds.includes(incoming);
        },
        isStaleRemoteChatUpdate(data = {}, loadingBot = null) {
            const identities = this.completedSessionIdentities(loadingBot);
            return isStaleInFlightChatUpdate({
                incomingRunId: data && data.run_id,
                reservedRunId: loadingBot && loadingBot.run_id,
                lastCompletedRunId: (
                    this.currentSession && this.currentSession.last_completed_run_id
                ) || identities.lastCompletedRunId,
                incomingResponseMessageId: data && data.response_message_id,
                reservedResponseMessageId: loadingBot && (
                    loadingBot.response_message_id || loadingBot.message_id
                ),
                completedResponseMessageIds: identities.responseMessageIds,
                completedRunIds: identities.runIds,
                completedMessageTexts: identities.texts,
                incomingText: data && (typeof data.text === 'string' ? data.text : data.message),
            });
        },
        setBotResponseIdentity(botMessage, responseMessageId = '', continuationJobId = '') {
            if (!botMessage) return;
            const responseId = String(responseMessageId || '').trim();
            const continuationId = String(continuationJobId || '').trim();
            if (responseId) {
                try { this.$set(botMessage, 'response_message_id', responseId); } catch (e) { botMessage.response_message_id = responseId; }
                try { this.$set(botMessage, 'message_id', responseId); } catch (e) { botMessage.message_id = responseId; }
            }
            if (continuationId) {
                try { this.$set(botMessage, 'continuation_job_id', continuationId); } catch (e) { botMessage.continuation_job_id = continuationId; }
            }
        },
        findWaitingBotIndexForResponseMessage(responseMessageId = '') {
            const responseId = String(responseMessageId || '').trim();
            if (!responseId || !this.currentSession || !Array.isArray(this.currentSession.data)) return -1;
            const messages = this.currentSession.data;
            for (let i = messages.length - 1; i >= 0; i--) {
                const item = messages[i];
                if (!item || item.sender !== 'bot' || item.waitingForUser !== true) continue;
                if (String(item.message_id || '').trim() === responseId
                    || String(item.response_message_id || '').trim() === responseId) {
                    return i;
                }
            }
            return -1;
        },
        async reconcileEmptySequencedTerminal(data, placeholder) {
            if (!placeholder || placeholder.terminalRecoveryInFlight === true) return;
            const chatUuid = String(this.currentSession && this.currentSession.uuid || '');
            if (!chatUuid) return;
            try { this.$set(placeholder, 'terminalRecoveryInFlight', true); } catch (e) { placeholder.terminalRecoveryInFlight = true; }
            const incomingResponseId = String(data && data.response_message_id || '').trim();
            const incomingRunId = String(data && data.run_id || '').trim();
            try {
                let authoritativeText = null;
                let suggestedQuestions = null;

                // Prefer the committed conversation row because it is durable
                // and can identify the exact collaborative response.
                if (incomingResponseId) {
                    const response = await fetch(this.buildChatGetUrl(chatUuid, { limit: 100 }));
                    if (response.ok) {
                        const payload = await response.json().catch(() => ({}));
                        const match = (Array.isArray(payload.conversation) ? payload.conversation : []).find(item => (
                            item
                            && item.role === 'assistant'
                            && String(item.message_id || '').trim() === incomingResponseId
                            && typeof item.content === 'string'
                        ));
                        if (match && match.content.length > 0) authoritativeText = match.content;
                        if (Array.isArray(payload.suggested_questions)) {
                            suggestedQuestions = payload.suggested_questions;
                        }
                    }
                }

                // The status snapshot covers the short interval between the
                // terminal wakeup and conversation-row visibility.
                if (authoritativeText === null) {
                    const response = await fetch('/check_chat_status', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ uuid: chatUuid }),
                    });
                    if (response.ok) {
                        const status = await response.json().catch(() => ({}));
                        const statusRunId = String(status && status.run_id || '').trim();
                        const matchesRun = !incomingRunId || !statusRunId || incomingRunId === statusRunId;
                        const isTerminal = !!(status && (status.done || status.is_complete || status.cancelled));
                        if (
                            matchesRun
                            && isTerminal
                            && typeof status.message === 'string'
                            && status.message.length > 0
                        ) {
                            authoritativeText = status.message;
                            suggestedQuestions = Array.isArray(status.suggested_questions)
                                ? status.suggested_questions
                                : suggestedQuestions;
                        }
                    }
                }

                if (
                    authoritativeText === null
                    || !this.currentSession
                    || String(this.currentSession.uuid || '') !== chatUuid
                    || !Array.isArray(this.currentSession.data)
                    || !this.currentSession.data.includes(placeholder)
                    || placeholder.isLoading !== true
                ) return;

                placeholder.text = authoritativeText;
                placeholder.isLoading = false;
                this.setBotResponseIdentity(placeholder, incomingResponseId);
                if (incomingRunId) placeholder.run_id = incomingRunId;
                if (Array.isArray(suggestedQuestions)) {
                    this.currentSession.suggestedQuestions = suggestedQuestions;
                }
                this.handleMessageCompleted(placeholder);
            } catch (error) {
                // Keep the placeholder loading. Its BotMessage recovery poll
                // remains active and may still obtain the authoritative status.
            } finally {
                if (placeholder) {
                    try { this.$set(placeholder, 'terminalRecoveryInFlight', false); } catch (e) { placeholder.terminalRecoveryInFlight = false; }
                }
            }
        },
        findPausedBotForReceipt(receipt) {
            if (!receipt || !this.currentSession || !Array.isArray(this.currentSession.data)) return null;
            const responseId = String(receipt.response_message_id || '').trim();
            if (responseId) {
                const directIndex = this.findWaitingBotIndexForResponseMessage(responseId);
                if (directIndex >= 0) return this.currentSession.data[directIndex];
            }
            const triggerId = String(receipt.trigger_message_id || '').trim();
            if (!responseId || !triggerId) return null;
            const candidates = [];
            for (let i = this.currentSession.data.length - 1; i >= 0; i--) {
                const item = this.currentSession.data[i];
                if (!item || item.sender !== 'bot' || item.waitingForUser !== true) continue;
                if (String(item.ai_response_anchor_message_id || '').trim() !== triggerId) continue;
                const boundResponseId = String(
                    item.response_message_id || item.message_id || ''
                ).trim();
                const hasDurableResponseId = !!(
                    boundResponseId
                    && !this.isProvisionalLocalAssistantMessageId(boundResponseId)
                );
                // A replay for an older response must not seize a newer paused
                // run that happens to share the same durable user-message
                // anchor. Only an unbound/provisional local placeholder may
                // adopt the receipt's durable response identity.
                if (hasDurableResponseId && boundResponseId !== responseId) continue;
                candidates.push(item);
            }
            return candidates.length === 1 ? candidates[0] : null;
        },
        isProvisionalLocalUserMessageId(messageId = '') {
            return /^(?:msg|client)-user-[a-z0-9-]+$/i.test(String(messageId || '').trim());
        },
        isProvisionalLocalAssistantMessageId(messageId = '') {
            return /^(?:msg|client)-assistant-[a-z0-9-]+$/i.test(String(messageId || '').trim());
        },
        adoptDurableTriggerAnchorForReceipt(botMessage, receipt) {
            if (!botMessage || !receipt || !this.currentSession || !Array.isArray(this.currentSession.data)) {
                return false;
            }
            const responseId = String(receipt.response_message_id || '').trim();
            const triggerId = String(receipt.trigger_message_id || '').trim();
            const provisionalAnchorId = String(botMessage.ai_response_anchor_message_id || '').trim();
            const boundResponseId = String(
                botMessage.response_message_id || botMessage.message_id || ''
            ).trim();
            if (
                !responseId
                || !triggerId
                || responseId !== boundResponseId
                || !this.isProvisionalLocalUserMessageId(provisionalAnchorId)
            ) {
                return false;
            }
            const matchingAnchors = this.currentSession.data.filter(message => (
                message
                && message.sender === 'user'
                && String(message.message_id || '').trim() === provisionalAnchorId
            ));
            // Only a single, local provisional anchor for the exact reserved
            // assistant response may adopt the server's durable trigger id.
            // Existing server ids and unloaded/offscreen receipts stay intact.
            if (matchingAnchors.length !== 1) return false;
            const anchor = matchingAnchors[0];
            try { this.$set(anchor, 'message_id', triggerId); } catch (e) { anchor.message_id = triggerId; }
            const turnId = String(receipt.turn_id || '').trim();
            if (turnId && !String(anchor.turn_id || '').trim()) {
                try { this.$set(anchor, 'turn_id', turnId); } catch (e) { anchor.turn_id = turnId; }
            }
            try { this.$set(botMessage, 'ai_response_anchor_message_id', triggerId); } catch (e) { botMessage.ai_response_anchor_message_id = triggerId; }
            return true;
        },
        findActiveChatRequestContextForReceipt(receipt) {
            if (!receipt || !this.currentSession) return null;
            const responseId = String(receipt.response_message_id || '').trim();
            if (!responseId) return null;
            const candidates = (this.chatRequestContexts || []).filter(context => (
                context
                && this.isChatRequestContextActive(context)
                && String(context.responseMessageId || '').trim() === responseId
            ));
            if (candidates.length !== 1) return null;
            const context = candidates[0];
            const expectedTriggerId = String(context.triggerMessageId || '').trim();
            const receiptTriggerId = String(receipt.trigger_message_id || '').trim();
            if (expectedTriggerId && expectedTriggerId !== receiptTriggerId) return null;
            return context;
        },
        completeChatRequestContextForReceipt(receipt) {
            const context = this.findActiveChatRequestContextForReceipt(receipt);
            if (context) this.completeChatRequestContext(context);
            return context;
        },
        findReservedLoadingBotForWaitingReceipt(receipt) {
            if (!receipt || !this.currentSession || !Array.isArray(this.currentSession.data)) return null;
            const isContextRequest = !!(
                receipt.tool_name === 'solicitar_contexto'
                && receipt.result_ref
                && receipt.result_ref.type === 'context_request'
            );
            if (!isContextRequest && !this.isReplacementConfirmationReceipt(receipt)) return null;
            const responseId = String(receipt.response_message_id || '').trim();
            const context = this.findActiveChatRequestContextForReceipt(receipt);
            if (!context) return null;
            let loadingMessage = context.loadingMessage;
            if (
                !loadingMessage
                || loadingMessage.sender !== 'bot'
                || loadingMessage.isLoading !== true
                || !this.currentSession.data.includes(loadingMessage)
            ) {
                const inSessionMatches = this.currentSession.data.filter(message => (
                    message
                    && message.sender === 'bot'
                    && message.isLoading === true
                    && (
                        String(message.message_id || '').trim() === responseId
                        || String(message.response_message_id || '').trim() === responseId
                    )
                ));
                if (inSessionMatches.length !== 1) return null;
                loadingMessage = inSessionMatches[0];
                // Server refreshes replace message objects. Keep the active
                // request bound to the exact durable response clone so a
                // receipt-first terminal can retire it.
                context.loadingMessage = loadingMessage;
            }
            return loadingMessage;
        },
        removeDuplicateLoadingBotsForResponse(responseMessageId, completedResponse = null) {
            if (!this.currentSession || !Array.isArray(this.currentSession.data)) return 0;
            const responseId = String(responseMessageId || '').trim();
            if (!responseId) return 0;
            const messages = this.currentSession.data;
            let removed = 0;
            for (let index = messages.length - 1; index >= 0; index--) {
                const message = messages[index];
                if (
                    !message
                    || message === completedResponse
                    || message.sender !== 'bot'
                    || (message.isLoading !== true && message.resumingContext !== true)
                    || (
                        String(message.message_id || '').trim() !== responseId
                        && String(message.response_message_id || '').trim() !== responseId
                    )
                ) continue;
                this.completeChatRequestContextForMessage(message);
                messages.splice(index, 1);
                removed += 1;
            }
            return removed;
        },
        reserveAcceptedChatResponseIdentity(context, data) {
            const trackedContext = this.getTrackedChatRequestContext(context);
            const reservedLoadingMessage = trackedContext && trackedContext.loadingMessage;
            if (
                !trackedContext
                || !this.isChatRequestContextActive(trackedContext)
                || !this.currentSession
                || !Array.isArray(this.currentSession.data)
                || !reservedLoadingMessage
                || reservedLoadingMessage.sender !== 'bot'
                || !this.currentSession.data.includes(reservedLoadingMessage)
            ) {
                return false;
            }
            const responseId = String(data && data.response_message_id || '').trim();
            const triggerId = String(data && data.trigger_message_id || '').trim();
            const turnId = String(data && data.turn_id || '').trim();
            const runId = String(data && data.run_id || '').trim();
            if (responseId) {
                trackedContext.responseMessageId = responseId;
                this.setBotResponseIdentity(reservedLoadingMessage, responseId);
            }
            if (triggerId) trackedContext.triggerMessageId = triggerId;
            if (turnId) trackedContext.turnId = turnId;
            if (runId) {
                trackedContext.runId = runId;
                try { this.$set(reservedLoadingMessage, 'run_id', runId); } catch (e) { reservedLoadingMessage.run_id = runId; }
            }
            // The accepted response belongs to this exact placeholder, so it
            // can reserve identity early. The same strict provisional-anchor
            // check below is used when the durable receipt arrives first.
            if (responseId && triggerId) {
                this.adoptDurableTriggerAnchorForReceipt(reservedLoadingMessage, {
                    response_message_id: responseId,
                    trigger_message_id: triggerId,
                    turn_id: turnId,
                });
            }
            return !!responseId;
        },
        bindPausedBotToReceipt(receipt, continuationJobId = '') {
            if (!receipt || !this.currentSession || !Array.isArray(this.currentSession.data)) return null;
            const responseId = String(receipt.response_message_id || '').trim();
            let pausedBot = this.findPausedBotForReceipt(receipt);
            const reservedLoadingBot = !pausedBot
                ? this.findReservedLoadingBotForWaitingReceipt(receipt)
                : null;
            pausedBot = pausedBot || reservedLoadingBot;
            if (!pausedBot) return null;
            this.setBotResponseIdentity(pausedBot, responseId, continuationJobId);
            this.adoptDurableTriggerAnchorForReceipt(pausedBot, receipt);
            if (reservedLoadingBot && receipt.status === 'waiting_user') {
                reservedLoadingBot.isLoading = false;
                reservedLoadingBot.waitingForUser = true;
                reservedLoadingBot.resumingContext = false;
                reservedLoadingBot.cancelled = false;
                this.handleMessageWaitingForContext(reservedLoadingBot);
            }
            return pausedBot;
        },
        ensureWaitingReceiptHost(receipt) {
            let pausedBot = this.bindPausedBotToReceipt(receipt);
            if (pausedBot || !receipt || !this.currentSession || !Array.isArray(this.currentSession.data)) {
                return pausedBot;
            }
            const responseId = String(receipt.response_message_id || '').trim();
            if (!responseId) return null;
            const triggerId = String(receipt.trigger_message_id || '').trim();
            const turnId = String(receipt.turn_id || '').trim();
            pausedBot = this.findBotForTerminalError(responseId, triggerId);
            if (
                pausedBot
                && pausedBot.isLoading !== true
                && pausedBot.waitingForUser !== true
                && pausedBot.resumingContext !== true
                && String(pausedBot.text || '').trim()
            ) return null;
            if (!pausedBot) {
                pausedBot = {
                    sender: 'bot',
                    text: '',
                    timestamp: Date.now(),
                    _is_optimistic: true,
                    isLoading: false,
                    waitingForUser: true,
                    resumingContext: false,
                    continuationNeedsAction: false,
                    cancelled: false,
                    failed: false,
                    researchActivityHistory: [],
                    ai_response_anchor_message_id: triggerId,
                };
                const anchorIndex = this.findMessageIndexByAnchor({
                    messageId: triggerId,
                    sender: 'user',
                });
                this.currentSession.data.splice(
                    anchorIndex >= 0 ? anchorIndex + 1 : this.currentSession.data.length,
                    0,
                    pausedBot,
                );
                this.reserveWindowedChatIndexes(pausedBot);
            }
            this.setBotResponseIdentity(pausedBot, responseId);
            if (triggerId) {
                try { this.$set(pausedBot, 'ai_response_anchor_message_id', triggerId); } catch (e) { pausedBot.ai_response_anchor_message_id = triggerId; }
            }
            if (turnId) {
                try { this.$set(pausedBot, 'turn_id', turnId); } catch (e) { pausedBot.turn_id = turnId; }
            }
            pausedBot.isLoading = false;
            pausedBot.waitingForUser = true;
            pausedBot.resumingContext = false;
            pausedBot.cancelled = false;
            pausedBot.failed = false;
            return pausedBot;
        },
        ensureContinuationActivityHost(receipt, continuationJobId = '') {
            const continuationId = String(continuationJobId || '').trim();
            let pausedBot = this.bindPausedBotToReceipt(receipt, continuationId);
            if (pausedBot || !continuationId) return pausedBot;
            if (!receipt || !this.currentSession || !Array.isArray(this.currentSession.data)) return null;

            const responseId = String(receipt.response_message_id || '').trim();
            if (!responseId) return null;
            const triggerId = String(receipt.trigger_message_id || '').trim();
            const turnId = String(receipt.turn_id || '').trim();
            // The continuation receipt carries an exact durable response id.
            // Do not let an unrelated loading reply that merely shares the
            // trigger anchor adopt that identity.
            pausedBot = this.findBotForTerminalError(responseId, '');
            if (
                pausedBot
                && pausedBot.isLoading !== true
                && pausedBot.waitingForUser !== true
                && pausedBot.resumingContext !== true
                && String(pausedBot.text || '').trim()
            ) {
                // A persisted answer with this response identity is already
                // authoritative. A delayed resolution must not turn it back
                // into a live continuation placeholder.
                return null;
            }
            if (!pausedBot) {
                pausedBot = {
                    sender: 'bot',
                    text: '',
                    timestamp: Date.now(),
                    _is_optimistic: true,
                    isLoading: false,
                    waitingForUser: true,
                    resumingContext: false,
                    continuationNeedsAction: false,
                    cancelled: false,
                    failed: false,
                    researchActivityHistory: [],
                    ai_response_anchor_message_id: triggerId,
                };
                const anchorIndex = this.findMessageIndexByAnchor({
                    messageId: triggerId,
                    sender: 'user',
                });
                this.currentSession.data.splice(
                    anchorIndex >= 0 ? anchorIndex + 1 : this.currentSession.data.length,
                    0,
                    pausedBot,
                );
                this.reserveWindowedChatIndexes(pausedBot);
            }

            this.setBotResponseIdentity(pausedBot, responseId, continuationId);
            if (triggerId) {
                try { this.$set(pausedBot, 'ai_response_anchor_message_id', triggerId); } catch (e) { pausedBot.ai_response_anchor_message_id = triggerId; }
            }
            if (turnId) {
                try { this.$set(pausedBot, 'turn_id', turnId); } catch (e) { pausedBot.turn_id = turnId; }
            }
            pausedBot.isLoading = false;
            pausedBot.waitingForUser = true;
            pausedBot.cancelled = false;
            pausedBot.failed = false;
            return pausedBot;
        },
        markPausedBotResuming(receipt, continuationJobId = '') {
            const continuationId = String(continuationJobId || '').trim();
            const pausedBot = this.ensureContinuationActivityHost(receipt, continuationId);
            if (!pausedBot || !continuationId) return pausedBot;
            try { this.$set(pausedBot, 'resumingContext', true); } catch (e) { pausedBot.resumingContext = true; }
            try { this.$set(pausedBot, 'continuationNeedsAction', false); } catch (e) { pausedBot.continuationNeedsAction = false; }
            this.replying = true;
            this.loadingSuggestedQuestions = true;
            this.store();
            return pausedBot;
        },
        isTerminalContextRequestReceipt(receipt) {
            return !!(
                receipt
                && receipt.result_ref
                && receipt.result_ref.type === 'context_request'
                && ['failed', 'cancelled', 'expired', 'superseded'].includes(receipt.status)
            );
        },
        applyContextRequestReceiptState(receipt) {
            if (!this.isTerminalContextRequestReceipt(receipt)) return;
            const pausedBot = this.findPausedBotForReceipt(receipt)
                || this.findReservedLoadingBotForWaitingReceipt(receipt);
            this.completeChatRequestContextForReceipt(receipt);
            const activeId = String(this.activeToolReceipt && this.activeToolReceipt.tool_run_id || '');
            if (activeId && activeId === String(receipt.tool_run_id || '')) {
                this.closeToolWorkspace();
            }
            this.contextResumeBusy = false;
            this.contextDocumentBindingState = 'idle';
            this.contextDocumentBindingError = '';
            if (!pausedBot) {
                this.finalizeChatUiAfterTerminal();
                return;
            }

            pausedBot.isLoading = false;
            pausedBot.waitingForUser = false;
            pausedBot.resumingContext = false;
            pausedBot.continuationNeedsAction = false;
            this.completeChatRequestContextForMessage(pausedBot);
            const messages = this.currentSession && Array.isArray(this.currentSession.data)
                ? this.currentSession.data
                : [];
            if (!String(pausedBot.text || '').trim()) {
                const index = messages.indexOf(pausedBot);
                if (index >= 0) messages.splice(index, 1);
            }
            try { this.store(); } catch (e) { }
            this.finalizeChatUiAfterTerminal();
        },
        isReplacementConfirmationReceipt(receipt) {
            if (!receipt || receipt.tool_name !== 'crear_editar_archivo') return false;
            if (
                receipt.result_ref
                && receipt.result_ref.type === 'replacement_confirmation'
            ) return true;
            const counts = receipt.ui_payload && receipt.ui_payload.counts;
            return Number(counts && counts.replacement_confirmation) === 1;
        },
        applyReplacementConfirmationReceiptState(receipt) {
            if (!this.isReplacementConfirmationReceipt(receipt)) return;
            if (receipt.status === 'waiting_user') {
                this.bindPausedBotToReceipt(receipt);
                return;
            }
            if (!['completed', 'failed', 'cancelled', 'expired'].includes(receipt.status)) return;
            if (
                this.toolActionInlineStatusToolRunId
                && this.toolActionInlineStatusToolRunId === String(receipt.tool_run_id || '')
            ) {
                this.toolActionInlineStatus = '';
                this.toolActionInlineStatusToolRunId = '';
            }
            const pausedBot = this.findPausedBotForReceipt(receipt)
                || this.findReservedLoadingBotForWaitingReceipt(receipt);
            this.completeChatRequestContextForReceipt(receipt);
            const activeId = String(this.activeToolReceipt && this.activeToolReceipt.tool_run_id || '');
            if (activeId && activeId === String(receipt.tool_run_id || '')) {
                this.closeToolWorkspace();
            }
            if (!pausedBot) {
                this.finalizeChatUiAfterTerminal();
                return;
            }
            pausedBot.isLoading = false;
            pausedBot.waitingForUser = false;
            pausedBot.resumingContext = false;
            pausedBot.continuationNeedsAction = false;
            this.completeChatRequestContextForMessage(pausedBot);
            const messages = this.currentSession && Array.isArray(this.currentSession.data)
                ? this.currentSession.data
                : [];
            if (!String(pausedBot.text || '').trim()) {
                const index = messages.indexOf(pausedBot);
                if (index >= 0) messages.splice(index, 1);
            }
            try { this.store(); } catch (e) { }
            this.finalizeChatUiAfterTerminal();
        },
        continuationProjectionVersion(projection, receipt) {
            return Math.max(
                Number(projection && projection.receipt_version || 0),
                Number(projection && projection.state_version || 0),
                Number(receipt && receipt.state_version || 0),
            );
        },
        canonicalAssistantFromProjection(projection, receipt) {
            const raw = projection && (
                projection.assistant_message
                || projection.canonical_assistant_message
                || projection.message
            );
            if (!raw || typeof raw !== 'object') return null;
            const content = typeof raw.content === 'string'
                ? raw.content
                : (typeof raw.text === 'string' ? raw.text : '');
            const responseMessageId = String(
                raw.message_id
                || raw.response_message_id
                || (projection && projection.response_message_id)
                || (receipt && receipt.response_message_id)
                || ''
            ).trim();
            if (!responseMessageId || !content) return null;
            return {
                ...raw,
                content,
                message_id: responseMessageId,
            };
        },
        async fetchContinuationFallbackProjection(chatUuid, receipt) {
            const responseMessageId = String(receipt && receipt.response_message_id || '').trim();
            if (!chatUuid || !responseMessageId) return null;
            const response = await fetch(this.buildChatGetUrl(chatUuid, { limit: 100 }));
            if (!response.ok) return null;
            const payload = await response.json().catch(() => ({}));
            const conversation = Array.isArray(payload.conversation) ? payload.conversation : [];
            const chains = Array.isArray(payload.reasoning_chains) ? payload.reasoning_chains : [];
            let assistantIndex = 0;
            let canonical = null;
            let reasoningActivities = [];
            for (const message of conversation) {
                if (!message || message.role !== 'assistant') continue;
                const chain = Array.isArray(chains[assistantIndex]) ? chains[assistantIndex] : [];
                assistantIndex += 1;
                if (String(message.message_id || '').trim() !== responseMessageId) continue;
                canonical = message;
                reasoningActivities = chain;
                break;
            }
            if (!canonical || typeof canonical.content !== 'string' || !canonical.content) return null;
            return {
                continuation_job_id: String(receipt.result_ref && receipt.result_ref.id || ''),
                status: 'completed',
                response_message_id: responseMessageId,
                assistant_message: canonical,
                reasoning_activities: reasoningActivities,
                receipt_version: Number(receipt.state_version || 0),
            };
        },
        clearContinuationProjectionRetry(continuationJobId) {
            const id = String(continuationJobId || '').trim();
            if (!id) return;
            const timer = this.continuationProjectionRetryTimers[id];
            if (timer) clearTimeout(timer);
            delete this.continuationProjectionRetryTimers[id];
            delete this.continuationProjectionRetryAttempts[id];
        },
        clearContinuationProjectionRetries() {
            Object.keys(this.continuationProjectionRetryTimers || {}).forEach((id) => {
                this.clearContinuationProjectionRetry(id);
            });
        },
        invalidateContinuationProjectionRecovery() {
            this.continuationProjectionGeneration = Number(this.continuationProjectionGeneration || 0) + 1;
            this.clearContinuationProjectionRetries();
            // Fetch cannot be reliably cancelled on every supported browser.
            // Detach old flights so a reactivated session can start a fresh
            // canonical read; each detached response is fenced by generation.
            this.continuationProjectionFlights = {};
        },
        startContinuationProjectionCatchUp(contextReceipt, continuationJobId) {
            const id = String(continuationJobId || '').trim();
            if (!id) return;
            const continuationReceipt = {
                ...(contextReceipt || {}),
                status: 'running',
                result_ref: { type: 'continuation', id },
            };
            this.reconcileContinuationProjection(continuationReceipt, { force: true });
        },
        scheduleContinuationProjectionRetry(receipt, chatUuid) {
            const continuationJobId = String(receipt && receipt.result_ref && receipt.result_ref.id || '').trim();
            if (!continuationJobId || this.continuationProjectionRetryTimers[continuationJobId]) return;
            const attempt = Number(this.continuationProjectionRetryAttempts[continuationJobId] || 0) + 1;
            this.continuationProjectionRetryAttempts[continuationJobId] = attempt;
            const delayMs = Math.min(30000, 1000 * (2 ** Math.min(attempt - 1, 5)));
            this.continuationProjectionRetryTimers[continuationJobId] = setTimeout(() => {
                delete this.continuationProjectionRetryTimers[continuationJobId];
                if (!this.currentSession || String(this.currentSession.uuid || '') !== String(chatUuid || '')) return;
                const current = (this.currentSession.tool_receipts || []).find(item => (
                    item
                    && item.result_ref
                    && item.result_ref.type === 'continuation'
                    && String(item.result_ref.id || '') === continuationJobId
                )) || receipt;
                if (current) this.reconcileContinuationProjection(current, { force: true });
            }, delayMs);
        },
        applyCanonicalContinuationProjection(projection, receipt) {
            const canonical = this.canonicalAssistantFromProjection(projection, receipt);
            if (!canonical || !this.currentSession || !Array.isArray(this.currentSession.data)) return false;
            const responseMessageId = String(canonical.message_id || '').trim();
            const continuationJobId = String(
                projection && projection.continuation_job_id
                || (receipt.result_ref && receipt.result_ref.id)
                || ''
            ).trim();
            let target = this.findBotForTerminalError(
                responseMessageId,
                String(receipt.trigger_message_id || ''),
            );
            if (!target) {
                target = {
                    sender: 'bot',
                    text: '',
                    timestamp: Date.now(),
                    _is_optimistic: true,
                    isLoading: false,
                    researchActivityHistory: [],
                };
                const anchorIndex = this.findMessageIndexByAnchor({
                    messageId: String(receipt.trigger_message_id || ''),
                    sender: 'user',
                });
                this.currentSession.data.splice(
                    anchorIndex >= 0 ? anchorIndex + 1 : this.currentSession.data.length,
                    0,
                    target,
                );
                this.reserveWindowedChatIndexes(target);
            }
            const wasAlreadyCanonical = !!(
                target.waitingForUser !== true
                && target.resumingContext !== true
                && target.isLoading !== true
                && String(target.text || '') === canonical.content
            );
            target.text = canonical.content;
            target.isLoading = false;
            target.waitingForUser = false;
            target.resumingContext = false;
            target.continuationNeedsAction = false;
            target.cancelled = false;
            target.failed = false;
            if (canonical.turn_id) target.turn_id = String(canonical.turn_id);
            if (canonical.timestamp) {
                target.server_timestamp = String(canonical.timestamp);
                const timestamp = Date.parse(canonical.timestamp);
                if (!Number.isNaN(timestamp)) target.timestamp = timestamp;
            }
            this.setBotResponseIdentity(target, responseMessageId, continuationJobId);
            const reasoningActivities = Array.isArray(projection && projection.reasoning_activities)
                ? projection.reasoning_activities
                : (Array.isArray(canonical.reasoning_activities) ? canonical.reasoning_activities : []);
            this.mergeReasoningActivitiesIntoMessage(target, reasoningActivities);
            this.repositionAiReplyAfterAnchor(target);
            if (receipt) {
                receipt.status = 'completed';
                receipt.state_version = Math.max(
                    Number(receipt.state_version || 0),
                    Number(projection && projection.receipt_version || 0),
                );
            }
            this.clearContinuationProjectionRetry(continuationJobId);
            this.toolReceiptReconnectStatus = '';
            if (wasAlreadyCanonical) {
                try { this.store(); } catch (e) { }
                this.completeChatRequestContextForMessage(target);
                this.finalizeChatUiAfterTerminal();
            } else {
                // The projection already contains the canonical persisted
                // assistant row and its exact reasoning chain. A general chat
                // refresh may still expose the pre-continuation snapshot and
                // must not replace this newer terminal projection.
                this.handleMessageCompleted(target, { refreshConversation: false });
            }
            return true;
        },
        applyContinuationFailureProjection(projection, receipt) {
            const rawFailure = projection && (projection.failure || projection.failure_state);
            const failure = rawFailure && typeof rawFailure === 'object'
                ? rawFailure
                : {};
            if (receipt && Object.keys(failure).length) {
                receipt.error = {
                    code: String(failure.code || ''),
                    message: String(failure.message || '').slice(0, 500),
                    retryable: failure.retryable === true,
                };
            }
            const pausedBot = this.findPausedBotForReceipt(receipt);
            if (pausedBot) {
                pausedBot.resumingContext = false;
                pausedBot.continuationNeedsAction = true;
            }
            this.completeChatRequestContextForMessage(pausedBot);
            this.finalizeChatUiAfterTerminal();
            try { this.store(); } catch (e) { }
        },
        applyContinuationSourceActionProjection(projection, continuationReceipt) {
            const rawReceipt = projection && projection.receipt;
            const contextReceipt = this.normalizeToolReceipt(rawReceipt);
            if (
                !contextReceipt
                || contextReceipt.status !== 'waiting_user'
                || !contextReceipt.result_ref
                || contextReceipt.result_ref.type !== 'context_request'
                || !String(contextReceipt.result_ref.id || '').trim()
            ) return false;

            // The continuation has ended, but the original context request was
            // reopened with a fresh resolution epoch. Replace the continuation
            // receipt with that canonical request projection before exposing
            // the picker again; realtime delivery is only a wake-up hint.
            this.reconcileToolReceipts([rawReceipt]);
            const durableReceipt = (this.currentSession.tool_receipts || []).find(item => (
                item && String(item.tool_run_id || '') === String(contextReceipt.tool_run_id || '')
            )) || contextReceipt;
            if (
                durableReceipt.status !== 'waiting_user'
                || !durableReceipt.result_ref
                || durableReceipt.result_ref.type !== 'context_request'
                || String(durableReceipt.result_ref.id || '') !== String(contextReceipt.result_ref.id || '')
            ) {
                // A newer hydration already superseded this projection. The
                // versioned merge above applied that state; never regress the
                // bubble or picker using this late source-action response.
                const staleContinuationJobId = String(
                    projection.continuation_job_id
                    || (continuationReceipt && continuationReceipt.result_ref || {}).id
                    || ''
                );
                this.clearContinuationProjectionRetry(staleContinuationJobId);
                if (
                    durableReceipt.result_ref
                    && durableReceipt.result_ref.type === 'continuation'
                    && String(durableReceipt.result_ref.id || '').trim()
                ) {
                    // Its first reconciliation coalesced onto the stale flight
                    // that is still unwinding. Schedule a fresh read after the
                    // flight releases so the newer continuation cannot stall.
                    this.scheduleContinuationProjectionRetry(
                        durableReceipt,
                        String(this.currentSession && this.currentSession.uuid || ''),
                    );
                }
                return true;
            }
            const responseMessageId = String(
                durableReceipt.response_message_id
                || (continuationReceipt && continuationReceipt.response_message_id)
                || (projection && projection.response_message_id)
                || ''
            ).trim();
            const triggerMessageId = String(
                durableReceipt.trigger_message_id
                || (continuationReceipt && continuationReceipt.trigger_message_id)
                || ''
            ).trim();
            let pausedBot = this.findBotForTerminalError(responseMessageId, triggerMessageId);
            if (!pausedBot && this.currentSession && Array.isArray(this.currentSession.data)) {
                pausedBot = {
                    sender: 'bot',
                    text: '',
                    timestamp: Date.now(),
                    _is_optimistic: true,
                    isLoading: false,
                    waitingForUser: true,
                    resumingContext: false,
                    continuationNeedsAction: false,
                    researchActivityHistory: [],
                    ai_response_anchor_message_id: triggerMessageId,
                };
                const anchorIndex = this.findMessageIndexByAnchor({
                    messageId: triggerMessageId,
                    sender: 'user',
                });
                this.currentSession.data.splice(
                    anchorIndex >= 0 ? anchorIndex + 1 : this.currentSession.data.length,
                    0,
                    pausedBot,
                );
                this.reserveWindowedChatIndexes(pausedBot);
            }
            if (!pausedBot) return false;

            this.setBotResponseIdentity(pausedBot, responseMessageId);
            try { this.$set(pausedBot, 'continuation_job_id', ''); } catch (e) { pausedBot.continuation_job_id = ''; }
            pausedBot.isLoading = false;
            pausedBot.waitingForUser = true;
            pausedBot.resumingContext = false;
            pausedBot.continuationNeedsAction = false;
            pausedBot.cancelled = false;
            pausedBot.failed = false;
            if (
                this.activeToolReceipt
                && String(this.activeToolReceipt.tool_run_id || '') === String(durableReceipt.tool_run_id || '')
            ) this.closeToolWorkspace();
            this.contextResumeBusy = false;
            this.contextDocumentBindingState = 'idle';
            this.contextDocumentBindingError = '';
            this.toolReceiptReconnectStatus = '';
            this.clearContinuationProjectionRetry(
                String(projection.continuation_job_id || (continuationReceipt.result_ref || {}).id || '')
            );
            this.handleMessageWaitingForContext(pausedBot);
            return true;
        },
        async reconcileContinuationProjection(receipt, { force = false } = {}) {
            if (!receipt || !receipt.result_ref || receipt.result_ref.type !== 'continuation') return null;
            const continuationJobId = String(receipt.result_ref.id || '').trim();
            const chatUuid = String(this.currentSession && this.currentSession.uuid || '').trim();
            if (!continuationJobId || !chatUuid) return null;
            const session = this.currentSession;
            const projectionGeneration = Number(this.continuationProjectionGeneration || 0);
            const existing = this.continuationProjectionFlights[continuationJobId];
            if (existing) return existing;
            const appliedVersion = Number(this.continuationProjectionAppliedVersions
                && this.continuationProjectionAppliedVersions[continuationJobId] || 0);
            const receiptVersion = Number(receipt.state_version || 0);
            const settled = this.findBotForTerminalError(
                String(receipt.response_message_id || ''),
                String(receipt.trigger_message_id || ''),
            );
            if (
                !force
                && receipt.status === 'completed'
                && appliedVersion >= receiptVersion
                && settled
                && settled.waitingForUser !== true
                && settled.resumingContext !== true
            ) return null;

            const request = (async () => {
                let projection = null;
                try {
                    const query = new URLSearchParams({ chat_uuid: chatUuid });
                    const response = await fetch(
                        `/chat/continuations/${encodeURIComponent(continuationJobId)}?${query.toString()}`
                    );
                    if (response.ok) projection = await response.json().catch(() => null);
                } catch (error) { }
                if (
                    !projection
                    || String(projection.continuation_job_id || continuationJobId) !== continuationJobId
                ) {
                    try {
                        projection = await this.fetchContinuationFallbackProjection(chatUuid, receipt);
                    } catch (error) {
                        projection = null;
                    }
                }
                if (
                    !this.currentSession
                    || this.currentSession !== session
                    || String(this.currentSession.uuid || '') !== chatUuid
                    || Number(this.continuationProjectionGeneration || 0) !== projectionGeneration
                ) return null;
                if (!projection) {
                    this.toolReceiptReconnectStatus = 'answer';
                    this.scheduleContinuationProjectionRetry(receipt, chatUuid);
                    return null;
                }
                this.discoverDocumentPreviews(projection, chatUuid);
                const status = String(projection.job_status || projection.status || receipt.status || '').toLowerCase();
                if (status === 'completed') {
                    const applied = this.applyCanonicalContinuationProjection(projection, receipt);
                    if (!applied) {
                        this.toolReceiptReconnectStatus = 'answer';
                        this.scheduleContinuationProjectionRetry(receipt, chatUuid);
                        return projection;
                    }
                } else if (status === 'source_action_required') {
                    const applied = this.applyContinuationSourceActionProjection(projection, receipt);
                    if (!applied) {
                        this.toolReceiptReconnectStatus = 'context';
                        this.scheduleContinuationProjectionRetry(receipt, chatUuid);
                        return projection;
                    }
                } else if (['failed', 'failed_terminal', 'cancelled', 'expired', 'superseded', 'outcome_unknown'].includes(status)) {
                    receipt.status = status === 'cancelled' ? 'cancelled' : 'failed';
                    this.applyContinuationFailureProjection(projection, receipt);
                    this.clearContinuationProjectionRetry(continuationJobId);
                } else if (['queued', 'leased', 'running', 'failed_retryable'].includes(status)) {
                    this.markPausedBotResuming(receipt, continuationJobId);
                    // The initial post-resolution read commonly observes a
                    // non-terminal job. Keep polling the canonical projection
                    // so a tab heals even if every terminal realtime hint is
                    // dropped and the general chat snapshot stays paused.
                    this.scheduleContinuationProjectionRetry(receipt, chatUuid);
                }
                const version = this.continuationProjectionVersion(projection, receipt);
                if (!this.continuationProjectionAppliedVersions) this.continuationProjectionAppliedVersions = {};
                this.continuationProjectionAppliedVersions[continuationJobId] = Math.max(appliedVersion, version);
                return projection;
            })().finally(() => {
                if (this.continuationProjectionFlights[continuationJobId] === request) {
                    delete this.continuationProjectionFlights[continuationJobId];
                }
            });
            this.continuationProjectionFlights[continuationJobId] = request;
            return request;
        },
        async catchUpContinuationState({ resetReceipts = false } = {}) {
            const session = this.currentSession;
            const chatUuid = String(session && session.uuid || '');
            if (!chatUuid) return;
            await this.hydrateToolReceipts({ reset: resetReceipts });
            if (!this.currentSession || this.currentSession !== session) return;
            const receipts = Array.isArray(session.tool_receipts) ? session.tool_receipts : [];
            receipts.forEach((receipt) => {
                this.bindPausedBotToReceipt(receipt);
                this.applyContextRequestReceiptState(receipt);
                if (receipt && receipt.result_ref && receipt.result_ref.type === 'continuation') {
                    this.reconcileContinuationProjection(receipt, { force: true });
                }
            });
        },
        applyContinuationReceiptState(receipt) {
            if (
                !receipt
                || !receipt.result_ref
                || receipt.result_ref.type !== 'continuation'
            ) return;
            if (receipt.status === 'completed') {
                this.reconcileContinuationProjection(receipt);
                return;
            }
            const pausedBot = this.findPausedBotForReceipt(receipt);
            if (!pausedBot) {
                if (receipt.status === 'failed' || receipt.status === 'cancelled') {
                    this.finalizeChatUiAfterTerminal();
                    this.reconcileContinuationProjection(receipt);
                }
                return;
            }
            if (receipt.status === 'running') {
                this.markPausedBotResuming(receipt, receipt.result_ref.id);
                return;
            }
            if (receipt.status !== 'failed' && receipt.status !== 'cancelled') return;
            try { this.$set(pausedBot, 'resumingContext', false); } catch (e) { pausedBot.resumingContext = false; }
            try { this.$set(pausedBot, 'continuationNeedsAction', true); } catch (e) { pausedBot.continuationNeedsAction = true; }
            this.store();
            this.completeChatRequestContextForMessage(pausedBot);
            this.finalizeChatUiAfterTerminal();
            this.reconcileContinuationProjection(receipt);
        },
        findLatestBotMessage() {
            const messages = this.currentSession && Array.isArray(this.currentSession.data)
                ? this.currentSession.data
                : [];
            for (let i = messages.length - 1; i >= 0; i--) {
                const item = messages[i];
                if (item && item.sender === 'bot') return item;
            }
            return null;
        },
        hasActiveAiRun() {
            const status = this.currentSession && this.currentSession.aiRunStatus
                ? this.currentSession.aiRunStatus
                : {};
            return !!(
                this.replying
                || this.findLoadingBotIndex() >= 0
                || status.state === 'running'
            );
        },
        researchActivityDedupeKey(activity) {
            if (!activity || typeof activity !== 'object') return '';
            const activitySeq = Number(activity.activity_seq);
            if (Number.isInteger(activitySeq) && activitySeq > 0) {
                return [
                    String(activity.continuation_job_id || activity.run_id || ''),
                    String(activity.response_message_id || ''),
                    String(activitySeq),
                ].join('|');
            }
            const explicitId = String(activity.id || '').trim();
            if (explicitId) return `id|${explicitId}`;
            return [
                String(activity.continuation_job_id || activity.run_id || ''),
                String(activity.response_message_id || ''),
                String(activity.created_at || activity.timestamp || ''),
                String(activity.status || ''),
                String(activity.message || ''),
                String(activity.query || '')
            ].join('|');
        },
        mergeReasoningActivitiesIntoMessage(message, activities) {
            if (!message || !Array.isArray(activities) || activities.length === 0) return false;
            if (!Array.isArray(message.researchActivityHistory)) {
                try { this.$set(message, 'researchActivityHistory', []); } catch (e) { message.researchActivityHistory = []; }
            }
            const existingKeys = new Set(
                message.researchActivityHistory
                    .map((activity) => this.researchActivityDedupeKey(activity))
                    .filter(Boolean)
            );
            let added = false;
            activities.forEach((activity) => {
                if (!activity || !activity.message) return;
                const key = this.researchActivityDedupeKey(activity);
                if (!key || existingKeys.has(key)) return;
                existingKeys.add(key);
                message.researchActivityHistory.push({
                    ...activity,
                    // Keep recovery and realtime entries addressable by the
                    // same deterministic fields. BotMessage derives the
                    // fallback entry id instead of persisting a random one.
                    timestamp: activity.created_at || activity.timestamp || 0,
                });
                added = true;
            });
            return added;
        },
        applyRunSnapshotToPlaceholder(placeholder, runStatus = {}, reasoningActivities = []) {
            if (!placeholder) return placeholder;
            const status = runStatus && typeof runStatus === 'object' ? runStatus : {};
            const runId = status.run_id !== undefined && status.run_id !== null ? String(status.run_id) : '';
            const snapshotText = typeof status.message === 'string'
                ? status.message
                : (typeof status.text === 'string' ? status.text : '');
            const identities = this.completedSessionIdentities(placeholder);
            const snapshotDone = !!(
                status.done
                || status.is_complete
                || String(status.status || '') === 'done'
            );
            if (
                runId
                && !isStaleInFlightChatUpdate({
                    incomingRunId: runId,
                    reservedRunId: placeholder.run_id,
                    lastCompletedRunId: (
                        this.currentSession && this.currentSession.last_completed_run_id
                    ) || identities.lastCompletedRunId,
                    incomingResponseMessageId: status.response_message_id,
                    reservedResponseMessageId: placeholder.response_message_id || placeholder.message_id,
                    completedResponseMessageIds: identities.responseMessageIds,
                    completedRunIds: identities.runIds,
                    completedMessageTexts: identities.texts,
                    incomingText: snapshotText,
                })
            ) {
                try { this.$set(placeholder, 'run_id', runId); } catch (e) { placeholder.run_id = runId; }
            }
            if (shouldHydrateInFlightChatText({
                placeholderText: placeholder.text,
                snapshotText,
                snapshotDone,
                incomingRunId: runId,
                reservedRunId: placeholder.run_id,
                lastCompletedRunId: (
                    this.currentSession && this.currentSession.last_completed_run_id
                ) || identities.lastCompletedRunId,
                incomingResponseMessageId: status.response_message_id,
                reservedResponseMessageId: placeholder.response_message_id || placeholder.message_id,
                completedResponseMessageIds: identities.responseMessageIds,
                completedRunIds: identities.runIds,
                completedMessageTexts: identities.texts,
                incomingText: snapshotText,
            })) {
                placeholder.text = snapshotText;
            }
            this.mergeReasoningActivitiesIntoMessage(placeholder, reasoningActivities);
            return placeholder;
        },
        captureInFlightAiPlaceholder() {
            if (!this.currentSession || !Array.isArray(this.currentSession.data)) return null;
            const index = this.findLoadingBotIndex();
            if (index < 0) return null;
            const message = this.currentSession.data[index];
            const clone = this.cloneChatMessageForUi(message);
            if (!clone) return null;
            clone._preserve_index = index;
            const previousMessage = this.currentSession.data[index - 1] || null;
            const nextMessage = this.currentSession.data[index + 1] || null;
            if (previousMessage) clone._preserve_after_message_id = this.messageReferenceId(previousMessage);
            if (nextMessage) clone._preserve_before_message_id = this.messageReferenceId(nextMessage);
            return clone;
        },
        preserveInFlightAiPlaceholderAfterRefresh(preserved, runStatus = {}, reasoningActivities = []) {
            if (!preserved || !this.currentSession || !Array.isArray(this.currentSession.data)) return null;
            const messages = this.currentSession.data;
            const runId = (
                runStatus && runStatus.run_id !== undefined && runStatus.run_id !== null
            ) ? String(runStatus.run_id) : String(preserved.run_id || '');
            let target = null;
            if (runId) {
                const index = this.findLoadingBotIndexForRun(runId);
                target = index >= 0 ? messages[index] : null;
            }
            if (!target) {
                const preservedIds = new Set([
                    preserved.client_message_id,
                    preserved.message_id,
                    preserved.response_message_id,
                    preserved.client_request_id,
                    preserved.ai_response_anchor_message_id,
                ].map(value => String(value || '').trim()).filter(Boolean));
                const matches = messages.filter(message => (
                    message
                    && message.sender === 'bot'
                    && message.isLoading === true
                    && [
                        message.client_message_id,
                        message.message_id,
                        message.response_message_id,
                        message.client_request_id,
                        message.ai_response_anchor_message_id,
                    ].map(value => String(value || '').trim()).some(id => id && preservedIds.has(id))
                ));
                if (matches.length === 1) target = matches[0];
            }
            if (!target) {
                target = this.cloneChatMessageForUi(preserved);
                if (!target) return null;
                target.sender = 'bot';
                target.isLoading = true;
                target.cancelled = false;
                const afterMessageId = String(
                    target.ai_response_anchor_message_id
                    || target._preserve_after_message_id
                    || ''
                ).trim();
                let insertIndex = messages.length;
                if (afterMessageId) {
                    const anchorIndex = messages.findIndex((message) => (
                        message && this.messageReferenceId(message) === afterMessageId
                    ));
                    if (anchorIndex >= 0) insertIndex = this.aiReplyInsertIndexAfterAnchor(messages, target, anchorIndex);
                } else if (Number.isFinite(Number(target._preserve_index))) {
                    insertIndex = Math.min(Math.max(Number(target._preserve_index), 0), messages.length);
                }
                messages.splice(insertIndex, 0, target);
            }
            this.applyRunSnapshotToPlaceholder(target, runStatus, reasoningActivities);
            this.repositionAiReplyAfterAnchor(target);
            this.startAiRunStatus(target, {
                invocationText: (this.currentSession.aiRunStatus || {}).invocationText || '',
                runId: runId || target.run_id || null,
            });
            return target;
        },
        insertMessageByServerTimestamp(message) {
            if (!this.currentSession || !Array.isArray(this.currentSession.data) || !message) return;
            const messages = this.currentSession.data;
            const incomingTime = message.server_timestamp ? Date.parse(message.server_timestamp) : NaN;
            if (Number.isNaN(incomingTime)) {
                messages.push(message);
                return;
            }
            for (let i = 0; i < messages.length; i++) {
                const existing = messages[i];
                if (!existing || existing.isLoading === true || !existing.server_timestamp) continue;
                if (existing.ai_queued === true && message.ai_queued !== true) continue;
                const existingTime = Date.parse(existing.server_timestamp);
                if (!Number.isNaN(existingTime) && existingTime > incomingTime) {
                    messages.splice(i, 0, message);
                    return;
                }
            }
            messages.push(message);
        },
        getQueuedAiRequests() {
            const messages = this.currentSession && Array.isArray(this.currentSession.data)
                ? this.currentSession.data
                : [];
            return messages.filter((message) => (
                message
                && message.sender === 'user'
                && message.ai_queued === true
                && String(message.ai_queue_status || 'queued') === 'queued'
            ));
        },
        captureQueuedAiRequests() {
            try {
                const messages = this.currentSession && Array.isArray(this.currentSession.data)
                    ? this.currentSession.data
                    : [];
                return messages
                    .filter((message) => (
                        message
                        && message.sender === 'user'
                        && message.ai_queued === true
                        && ['queued', 'running'].includes(String(message.ai_queue_status || 'queued'))
                    ))
                    .map((message) => this.cloneChatMessageForUi(message))
                    .filter(Boolean);
            } catch (e) {
                return [];
            }
        },
        captureServerAckedHumanOnlyMessages() {
            try {
                const messages = this.currentSession && Array.isArray(this.currentSession.data)
                    ? this.currentSession.data
                    : [];
                return messages
                    .filter((message) => (
                        message
                        && message.sender === 'user'
                        && message.human_only === true
                        && message.ai_queued !== true
                        && String(message.server_timestamp || '').trim()
                    ))
                    .map((message) => this.cloneChatMessageForUi(message))
                    .filter(Boolean);
            } catch (e) {
                return [];
            }
        },
        mergeQueuedAiRequests(normalizedMessages, queuedMessages) {
            const normalized = Array.isArray(normalizedMessages) ? normalizedMessages : [];
            if (!Array.isArray(queuedMessages) || queuedMessages.length === 0) return normalized;
            queuedMessages.forEach((queued) => {
                if (!queued || queued.ai_queued !== true) return;
                const key = this.getMessageKeyForDuplicate(queued);
                const exists = normalized.some((message) => {
                    if (!message) return false;
                    if (key && this.getMessageKeyForDuplicate(message) === key) return true;
                    return (
                        message.sender === 'user'
                        && String(message.text || '').trim() === String(queued.text || '').trim()
                        && String(message.sender_email || '').trim().toLowerCase() === String(queued.sender_email || '').trim().toLowerCase()
                    );
                });
                if (!exists) {
                    normalized.push(queued);
                }
            });
            return normalized;
        },
        mergeMissingHumanOnlyMessages(normalizedMessages, humanOnlyMessages) {
            const normalized = Array.isArray(normalizedMessages) ? normalizedMessages : [];
            if (!Array.isArray(humanOnlyMessages) || humanOnlyMessages.length === 0) return normalized;
            humanOnlyMessages.forEach((humanMessage) => {
                if (!humanMessage || humanMessage.human_only !== true || humanMessage.ai_queued === true) return;
                const serverTimestamp = String(humanMessage.server_timestamp || '').trim();
                if (!serverTimestamp) return;
                const sender = String(humanMessage.sender_email || humanMessage.sender_username || '').trim().toLowerCase();
                const text = String(humanMessage.text || '').trim();
                const key = this.getMessageKeyForDuplicate(humanMessage);
                const exists = normalized.some((message) => {
                    if (!message) return false;
                    if (key && this.getMessageKeyForDuplicate(message) === key) return true;
                    return (
                        message.sender === 'user'
                        && String(message.server_timestamp || '').trim() === serverTimestamp
                        && String(message.text || '').trim() === text
                        && String(message.sender_email || message.sender_username || '').trim().toLowerCase() === sender
                    );
                });
                if (exists) return;

                const incomingTime = Date.parse(serverTimestamp);
                if (Number.isNaN(incomingTime)) {
                    normalized.push(humanMessage);
                    return;
                }
                for (let i = 0; i < normalized.length; i++) {
                    const existing = normalized[i];
                    if (!existing || existing.isLoading === true || !existing.server_timestamp) continue;
                    const existingTime = Date.parse(existing.server_timestamp);
                    if (!Number.isNaN(existingTime) && existingTime > incomingTime) {
                        normalized.splice(i, 0, humanMessage);
                        return;
                    }
                }
                normalized.push(humanMessage);
            });
            return normalized;
        },
        cloneChatMessageForUi(message) {
            if (!message || typeof message !== 'object') return null;
            const clone = { ...message };
            if (Array.isArray(message.researchActivityHistory)) {
                clone.researchActivityHistory = message.researchActivityHistory.slice();
            }
            if (Array.isArray(message.contextDocuments)) {
                clone.contextDocuments = message.contextDocuments.slice();
            }
            if (Array.isArray(message.reactions)) {
                clone.reactions = message.reactions.slice();
            }
            this.messageReferenceId(clone);
            return clone;
        },
        cloneChatMessageForUiWithPosition(message) {
            const clone = this.cloneChatMessageForUi(message);
            if (!clone || !this.currentSession || !Array.isArray(this.currentSession.data)) return clone;
            const messages = this.currentSession.data;
            let messageIndex = messages.indexOf(message);
            if (messageIndex < 0) {
                const messageId = this.messageReferenceId(message);
                const messageText = String(message.text || message.content || '').replace(/\s+/g, ' ').trim();
                const messageSender = String(message.sender || '').trim();
                messageIndex = messages.findIndex((candidate) => {
                    if (!candidate) return false;
                    if (messageSender && candidate.sender !== messageSender) return false;
                    if (messageId && this.messageReferenceId(candidate) === messageId) return true;
                    const candidateText = String(candidate.text || candidate.content || '').replace(/\s+/g, ' ').trim();
                    return !!messageText && candidateText === messageText;
                });
            }
            if (messageIndex >= 0) {
                clone._preserve_index = messageIndex;
                const previousMessage = messages[messageIndex - 1] || null;
                const nextMessage = messages[messageIndex + 1] || null;
                if (previousMessage) {
                    clone._preserve_after_message_id = this.messageReferenceId(previousMessage);
                    clone._preserve_after_text = String(previousMessage.text || previousMessage.content || '').trim();
                }
                if (nextMessage) {
                    clone._preserve_before_message_id = this.messageReferenceId(nextMessage);
                    clone._preserve_before_text = String(nextMessage.text || nextMessage.content || '').trim();
                }
            }
            return clone;
        },
        findAiAnswerMatch(answer) {
            if (!answer || !this.currentSession || !Array.isArray(this.currentSession.data)) return null;
            const answerId = String(answer.message_id || '').trim();
            const answerServerTimestamp = String(answer.server_timestamp || '').trim();
            return this.currentSession.data.find((message) => {
                if (!message || message.sender !== 'bot') return false;
                const messageId = String(message.message_id || '').trim();
                if (answerId && messageId && messageId === answerId) return true;
                const messageServerTimestamp = String(message.server_timestamp || '').trim();
                return !!answerServerTimestamp && messageServerTimestamp === answerServerTimestamp;
            }) || null;
        },
        findAiAnswerAfterPreservedAnchor(answer) {
            if (!answer || !this.currentSession || !Array.isArray(this.currentSession.data)) return null;
            const anchorIndex = this.findMessageIndexByAnchor({
                messageId: answer.ai_response_anchor_message_id,
                timestamp: answer.ai_response_anchor_timestamp,
                text: answer.ai_response_anchor_text,
                sender: 'user',
            });
            if (anchorIndex < 0) return null;
            const messages = this.currentSession.data;
            for (let i = anchorIndex + 1; i < messages.length; i++) {
                const message = messages[i];
                if (!message) continue;
                if (message.sender === 'user') break;
                if (
                    message.sender === 'bot'
                    && message.isLoading !== true
                    && String(message.text || '').trim()
                ) {
                    return message;
                }
            }
            return null;
        },
        findAiAnswerAtPreservedIndex(answer) {
            if (!answer || !this.currentSession || !Array.isArray(this.currentSession.data)) return null;
            const preservedIndex = Number(answer._preserve_index);
            if (!Number.isFinite(preservedIndex) || preservedIndex < 0) return null;
            const messages = this.currentSession.data;
            const message = messages[preservedIndex];
            if (
                message
                && message.sender === 'bot'
                && message.isLoading !== true
                && String(message.text || '').trim()
            ) {
                return message;
            }
            for (let i = Math.min(Math.floor(preservedIndex), messages.length - 1); i >= 0; i--) {
                const candidate = messages[i];
                if (!candidate) continue;
                if (candidate.sender === 'user') break;
                if (
                    candidate.sender === 'bot'
                    && candidate.isLoading !== true
                    && String(candidate.text || '').trim()
                ) {
                    return candidate;
                }
            }
            return null;
        },
        captureRecentCompletedAiAnswer() {
            if (!this.currentSession || !Array.isArray(this.currentSession.data)) return null;
            const status = this.currentSession.aiRunStatus || {};
            const completedAt = Math.max(Number(this._lastAiCompletionAt || 0), Number(status.completedAt || 0));
            const recentlyCompleted = (Date.now() - completedAt) < 30000;
            if (!recentlyCompleted) return null;
            const messages = this.currentSession.data;
            let candidate = null;
            if (status.answerMessageId) {
                candidate = messages.find((message) => (
                    message
                    && message.sender === 'bot'
                    && this.messageReferenceId(message) === status.answerMessageId
                )) || null;
            }
            if (!candidate) {
                for (let i = messages.length - 1; i >= 0; i--) {
                    const message = messages[i];
                    if (
                        message
                        && message.sender === 'bot'
                        && !message.isLoading
                        && !message.cancelled
                        && String(message.text || '').trim()
                    ) {
                        candidate = message;
                        break;
                    }
                }
            }
            if (!candidate) return null;
            const clone = this.cloneChatMessageForUiWithPosition(candidate);
            if (!clone) return null;
            return clone;
        },
        preserveCompletedAiAnswerAfterRefresh(answer) {
            if (
                !answer
                || answer.isLoading === true
                || answer.waitingForUser === true
                || !String(answer.text || '').trim()
                || !this.currentSession
                || !Array.isArray(this.currentSession.data)
            ) return null;
            const messages = this.currentSession.data;
            const resolveInsertIndex = (exclude = null) => {
                const anchorIndex = this.findMessageIndexByAnchor({
                    messageId: answer.ai_response_anchor_message_id,
                    timestamp: answer.ai_response_anchor_timestamp,
                    text: answer.ai_response_anchor_text,
                    sender: 'user',
                }, exclude);
                if (anchorIndex >= 0) return anchorIndex + 1;

                const afterMessageId = String(answer._preserve_after_message_id || '').trim();
                if (afterMessageId) {
                    const afterIndex = messages.findIndex((message) => (
                        message
                        && message !== exclude
                        && this.messageReferenceId(message) === afterMessageId
                    ));
                    if (afterIndex >= 0) return afterIndex + 1;
                }
                const afterText = String(answer._preserve_after_text || '').trim();
                if (afterText) {
                    const normalizedAfterText = afterText.replace(/\s+/g, ' ');
                    const afterIndex = messages.findIndex((message) => (
                        message
                        && message !== exclude
                        && this.canonicalMessageText(message) === normalizedAfterText
                    ));
                    if (afterIndex >= 0) return afterIndex + 1;
                }

                const beforeMessageId = String(answer._preserve_before_message_id || '').trim();
                if (beforeMessageId) {
                    const beforeIndex = messages.findIndex((message) => (
                        message
                        && message !== exclude
                        && this.messageReferenceId(message) === beforeMessageId
                    ));
                    if (beforeIndex >= 0) return beforeIndex;
                }
                const beforeText = String(answer._preserve_before_text || '').trim();
                if (beforeText) {
                    const normalizedBeforeText = beforeText.replace(/\s+/g, ' ');
                    const beforeIndex = messages.findIndex((message) => (
                        message
                        && message !== exclude
                        && this.canonicalMessageText(message) === normalizedBeforeText
                    ));
                    if (beforeIndex >= 0) return beforeIndex;
                }

                const preservedIndex = Number(answer._preserve_index);
                if (Number.isFinite(preservedIndex) && preservedIndex >= 0) {
                    return Math.min(preservedIndex, messages.length);
                }
                return messages.length;
            };
            let target = this.findAiAnswerMatch(answer);
            if (!target) {
                target = this.findAiAnswerAfterPreservedAnchor(answer);
            }
            if (!target) {
                target = this.findAiAnswerAtPreservedIndex(answer);
            }
            if (!target) {
                target = this.cloneChatMessageForUi(answer);
                if (!target) return null;
                target.isLoading = false;
                target.cancelled = false;
                messages.splice(resolveInsertIndex(), 0, target);
            } else {
                target.isLoading = false;
                target.cancelled = false;
            }
            this.completeAiRunStatus(target, { cancelled: false, touchCompletion: false, revealStatus: false });
            return target;
        },
        setAiRunStatus(status) {
            if (!this.currentSession) return;
            const normalized = status && typeof status === 'object' ? status : null;
            if (!this.isTransientCollaborationAiStatus(normalized && normalized.state)) {
                this.collaborationAiStatusDismissedKey = '';
            }
            this.patchCurrentSessionReactive({ aiRunStatus: normalized });
        },
        isTransientCollaborationAiStatus(state) {
            return ['answered', 'cancelled', 'error'].includes(String(state || ''));
        },
        isRecentCollaborationAiAnswer(status = {}) {
            const completedAt = Number(status && status.completedAt || 0);
            if (!Number.isFinite(completedAt) || completedAt <= 0) return false;
            const ageMs = Date.now() - completedAt;
            return ageMs >= 0 && ageMs < 120000;
        },
        collaborationAiStatusKey(status = {}) {
            const sessionId = this.currentSession && this.currentSession.uuid ? String(this.currentSession.uuid) : '';
            const state = String(status.state || '');
            const messageId = String(status.answerMessageId || status.messageId || '');
            const completedAt = String(status.completedAt || '');
            const message = String(status.message || '').slice(0, 120);
            const preview = String(status.answerPreview || status.preview || '').slice(0, 120);
            const detail = String(status.detail || '').slice(0, 120);
            return [sessionId, state, messageId, completedAt, message, preview, detail].join('|');
        },
        collaborationAiStatusStableKey(status = {}) {
            const sessionId = this.currentSession && this.currentSession.uuid ? String(this.currentSession.uuid) : '';
            const state = String(status.state || '');
            const messageId = String(status.answerMessageId || status.messageId || '');
            const completedAt = String(status.completedAt || '');
            return [sessionId, state, messageId || completedAt].join('|');
        },
        acknowledgeCollaborationAiStatus(status = null) {
            if (!this.currentSession) return;
            const targetStatus = status || this.collaborationAiStatus || {};
            const completedAt = Number(targetStatus.completedAt || (this.currentSession.aiRunStatus || {}).completedAt || 0);
            const currentAckAt = Number(this.currentSession.aiRunStatusAckAt || 0);
            const ackAt = Math.max(
                Number.isFinite(currentAckAt) ? currentAckAt : 0,
                Number.isFinite(completedAt) ? completedAt : 0,
                Date.now()
            );
            this.patchCurrentSessionReactive({ aiRunStatusAckAt: ackAt });
            try { this.store(); } catch (e) { }
        },
        clearCollaborationAiStatusDismissTimer() {
            if (this.collaborationAiStatusDismissTimer) {
                clearTimeout(this.collaborationAiStatusDismissTimer);
                this.collaborationAiStatusDismissTimer = null;
            }
        },
        scheduleCollaborationAiStatusDismiss(status) {
            if (!status || !this.isTransientCollaborationAiStatus(status.state)) {
                this.clearCollaborationAiStatusDismissTimer();
                return;
            }
            const statusKey = status.statusKey || this.collaborationAiStatusKey(status);
            const stableKey = status.stableKey || this.collaborationAiStatusStableKey(status);
            if (!statusKey || statusKey === this.collaborationAiStatusDismissedKey) return;
            this.clearCollaborationAiStatusDismissTimer();
            this.collaborationAiStatusDismissTimer = setTimeout(() => {
                const currentStatus = this.collaborationAiStatus;
                const currentStableKey = currentStatus
                    ? (currentStatus.stableKey || this.collaborationAiStatusStableKey(currentStatus))
                    : '';
                if (currentStatus && currentStableKey === stableKey) {
                    this.collaborationAiStatusDismissedKey = currentStatus.statusKey || statusKey;
                    this.acknowledgeCollaborationAiStatus(currentStatus);
                }
                this.clearCollaborationAiStatusDismissTimer();
            }, 7000);
        },
        dismissCollaborationAiStatus() {
            const status = this.collaborationAiStatus;
            if (!status) return;
            const statusKey = status.statusKey || this.collaborationAiStatusKey(status);
            if (!statusKey) return;
            this.clearCollaborationAiStatusDismissTimer();
            this.collaborationAiStatusDismissedKey = statusKey;
            if (this.isTransientCollaborationAiStatus(status.state)) {
                this.acknowledgeCollaborationAiStatus(status);
            }
        },
        isCurrentChatBeingViewed() {
            try {
                if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                    return false;
                }
                const modal = typeof document !== 'undefined' ? document.getElementById('chatModal') : null;
                if (!modal) return true;
                return modal.classList.contains('show') && modal.getAttribute('aria-hidden') !== 'true';
            } catch (e) {
                return false;
            }
        },
        startAiRunStatus(message = null, { invocationText = '', runId = null } = {}) {
            if (!this.currentSession || !this.isCollaborativeChat) return;
            const target = message || this.findLatestBotMessage();
            const messageId = target ? this.messageReferenceId(target) : '';
            this.setAiRunStatus({
                state: 'running',
                startedAt: Date.now(),
                invocationText: String(invocationText || '').trim().slice(0, 240),
                runId: runId ? String(runId) : null,
                messageId,
            });
        },
        completeAiRunStatus(message, { cancelled = false, touchCompletion = true, revealStatus = true } = {}) {
            if (!message || !this.currentSession || message.sender !== 'bot') return;
            let target = message;
            const messages = Array.isArray(this.currentSession.data) ? this.currentSession.data : null;
            if (!messages) return;
            const previousStatus = this.currentSession.aiRunStatus || {};
            if (messages.indexOf(target) < 0 && !cancelled) {
                let existing = this.findAiAnswerMatch(target);
                if (!existing && previousStatus.answerMessageId) {
                    const previousAnswerId = String(previousStatus.answerMessageId);
                    existing = messages.find((candidate) => (
                        candidate
                        && candidate.sender === 'bot'
                        && this.messageReferenceId(candidate) === previousAnswerId
                    )) || null;
                }
                if (existing) {
                    target = existing;
                } else if (String(target.text || '').trim()) {
                    const clone = this.cloneChatMessageForUi(target);
                    if (!clone) return;
                    clone.sender = 'bot';
                    clone.isLoading = false;
                    clone.cancelled = false;
                    messages.push(clone);
                    target = clone;
                }
            }
            const messageId = this.messageReferenceId(target);
            const completedRunId = target && target.run_id !== undefined && target.run_id !== null && target.run_id !== ''
                ? String(target.run_id)
                : (previousStatus.runId !== undefined && previousStatus.runId !== null ? String(previousStatus.runId) : null);
            if (completedRunId) {
                try { this.$set(this.currentSession, 'last_completed_run_id', completedRunId); } catch (e) { this.currentSession.last_completed_run_id = completedRunId; }
            }
            if (!this.isCollaborativeChat) return;
            const text = String(target.text || '').replace(/\s+/g, ' ').trim();
            const previousCompletedAt = Number(previousStatus.completedAt || 0);
            const sameAnsweredMessage = !!(
                !cancelled
                && previousStatus
                && previousStatus.state === 'answered'
                && (
                    (
                        previousStatus.answerMessageId
                        && messageId
                        && String(previousStatus.answerMessageId) === String(messageId)
                    )
                    || (
                        String(previousStatus.answerPreview || '').replace(/\s+/g, ' ').trim()
                        && String(previousStatus.answerPreview || '').replace(/\s+/g, ' ').trim() === text.slice(0, 180)
                    )
                )
            );
            const completedAt = sameAnsweredMessage && previousCompletedAt > 0
                ? previousCompletedAt
                : (touchCompletion ? Date.now() : (
                    previousCompletedAt
                    || Number(this._lastAiCompletionAt || 0)
                    || Date.now()
                ));
            if (touchCompletion && !sameAnsweredMessage) {
                this._lastAiCompletionAt = completedAt;
            }
            const previousStatusKey = previousStatus && previousStatus.state === 'answered'
                ? this.collaborationAiStatusKey(previousStatus)
                : '';
            const nextStatus = {
                state: cancelled ? 'cancelled' : 'answered',
                completedAt,
                answerMessageId: messageId,
                answerPreview: text.slice(0, 180),
                runId: completedRunId,
                lastCompletedRunId: completedRunId,
            };
            const nextStatusKey = this.collaborationAiStatusKey(nextStatus);
            if (!this.isTransientCollaborationAiStatus(previousStatus.state)) {
                this.collaborationAiStatusDismissedKey = '';
            }
            if (!cancelled && revealStatus !== false) {
                if (this.isCurrentChatBeingViewed()) {
                    this.collaborationAiStatusVisibleKey = nextStatusKey;
                } else {
                    this.collaborationAiStatusVisibleKey = '';
                }
            } else if (
                !cancelled
                && revealStatus === false
                && previousStatusKey
                && this.collaborationAiStatusVisibleKey === previousStatusKey
            ) {
                this.collaborationAiStatusVisibleKey = nextStatusKey;
            }
            this.setAiRunStatus(nextStatus);
        },
        markAiRunError(message = '') {
            if (!this.isCollaborativeChat) return;
            this.setAiRunStatus({
                state: 'error',
                completedAt: Date.now(),
                message: String(message || 'No se pudo completar la respuesta del Abogado Virtual.').slice(0, 240),
            });
        },
        isRecoverableTerminalErrorStatus(runStatus = {}) {
            if (!runStatus || typeof runStatus !== 'object') return false;
            return (
                (runStatus.error === true || String(runStatus.status || '') === 'error')
                && runStatus.recoverable !== false
                && !!String(runStatus.response_message_id || '').trim()
                && !!String(runStatus.trigger_message_id || '').trim()
            );
        },
        findBotForTerminalError(responseMessageId = '', triggerMessageId = '') {
            if (!this.currentSession || !Array.isArray(this.currentSession.data)) return null;
            const responseId = String(responseMessageId || '').trim();
            const triggerId = String(triggerMessageId || '').trim();
            if (!responseId) return null;
            for (let index = this.currentSession.data.length - 1; index >= 0; index -= 1) {
                const message = this.currentSession.data[index];
                if (!message || message.sender !== 'bot') continue;
                if (
                    String(message.message_id || '').trim() === responseId
                    || String(message.response_message_id || '').trim() === responseId
                ) {
                    return message;
                }
            }
            if (!triggerId) return null;
            for (let index = this.currentSession.data.length - 1; index >= 0; index -= 1) {
                const message = this.currentSession.data[index];
                if (!message || message.sender !== 'bot' || message.isLoading !== true) continue;
                if (String(message.ai_response_anchor_message_id || '').trim() === triggerId) {
                    return message;
                }
            }
            return null;
        },
        ensureTerminalErrorPlaceholder(runStatus = {}) {
            if (!this.isRecoverableTerminalErrorStatus(runStatus)) return null;
            if (!this.currentSession || !Array.isArray(this.currentSession.data)) return null;
            const responseId = String(runStatus.response_message_id || '').trim();
            const triggerId = String(runStatus.trigger_message_id || '').trim();
            const turnId = String(runStatus.turn_id || '').trim();
            let target = this.findBotForTerminalError(responseId, triggerId);

            // A persisted answer with the same reserved id is authoritative;
            // never replace it with a late failure event.
            if (
                target
                && target.isLoading !== true
                && target.failed !== true
                && String(target.text || '').trim()
            ) {
                return null;
            }
            if (!target) {
                target = {
                    sender: 'bot',
                    text: '',
                    timestamp: Date.now(),
                    _is_optimistic: true,
                    isLoading: false,
                    cancelled: false,
                    failed: true,
                    retryable: true,
                    researchActivityHistory: [],
                };
                const anchorIndex = this.findMessageIndexByAnchor({
                    messageId: triggerId,
                    sender: 'user',
                });
                this.currentSession.data.splice(
                    anchorIndex >= 0 ? anchorIndex + 1 : this.currentSession.data.length,
                    0,
                    target,
                );
            }
            this.setBotResponseIdentity(target, responseId);
            if (triggerId) {
                try { this.$set(target, 'ai_response_anchor_message_id', triggerId); } catch (e) { target.ai_response_anchor_message_id = triggerId; }
            }
            if (turnId) {
                try { this.$set(target, 'turn_id', turnId); } catch (e) { target.turn_id = turnId; }
            }
            if (runStatus.run_id !== undefined && runStatus.run_id !== null && runStatus.run_id !== '') {
                try { this.$set(target, 'run_id', String(runStatus.run_id)); } catch (e) { target.run_id = String(runStatus.run_id); }
            }
            target.text = String(
                runStatus.error_message
                || runStatus.message
                || 'No pudimos completar la respuesta. Por favor, inténtalo de nuevo.'
            );
            target.isLoading = false;
            target.cancelled = false;
            target.waitingForUser = false;
            target.failed = true;
            target.retryable = runStatus.recoverable !== false;
            this.repositionAiReplyAfterAnchor(target);
            return target;
        },
        ensureAiLoadingPlaceholder({
            invocationText = '',
            wasCancelled = false,
            runStatus = null,
            reasoningActivities = [],
        } = {}) {
            if (!this.currentSession || !Array.isArray(this.currentSession.data)) return null;
            let index = this.findLoadingBotIndex();
            let placeholder = index >= 0 ? this.currentSession.data[index] : null;
            if (!placeholder) {
                placeholder = {
                    sender: 'bot',
                    text: '',
                    timestamp: Date.now(),
                    _is_optimistic: true,
                    isLoading: !wasCancelled,
                    cancelled: !!wasCancelled,
                    researchActivityHistory: [],
                };
                this.currentSession.data.push(placeholder);
            }
            if (wasCancelled) {
                placeholder.isLoading = false;
                placeholder.cancelled = true;
                this.completeAiRunStatus(placeholder, { cancelled: true });
                this.completeChatRequestContextForMessage(placeholder);
                this.finalizeChatUiAfterTerminal();
            } else {
                this.applyRunSnapshotToPlaceholder(placeholder, runStatus, reasoningActivities);
                placeholder.isLoading = true;
                placeholder.cancelled = false;
                this.replying = true;
                this.lastError = false;
                this.errorMessage = null;
                this.startAiRunStatus(placeholder, {
                    invocationText,
                    runId: placeholder.run_id || (runStatus && runStatus.run_id) || null,
                });
            }
            return placeholder;
        },
        syncAiRunStatusFromServer({
            inProgress = false,
            wasCancelled = false,
            forceFinalize = false,
            revealCompletedStatus = true,
            runStatus = null,
            reasoningActivities = [],
        } = {}) {
            if (!this.currentSession || this.currentSession.is_call_log) return;
            if (this.isRecoverableTerminalErrorStatus(runStatus)) {
                const failedMessage = this.ensureTerminalErrorPlaceholder(runStatus);
                if (failedMessage) {
                    this.completeChatRequestContextForMessage(failedMessage);
                    this.lastError = true;
                    this.errorMessage = failedMessage.text;
                    this.markAiRunError(failedMessage.text);
                    this.finalizeChatUiAfterTerminal();
                }
                return;
            }
            const hasRealtime = (window.sync_enabled === true) || (typeof window.ably_realtime_client !== 'undefined');
            if (inProgress && hasRealtime) {
                this.ensureAiLoadingPlaceholder({ runStatus, reasoningActivities });
                return;
            }
            if (wasCancelled) {
                this.ensureAiLoadingPlaceholder({ wasCancelled: true });
                return;
            }
            const status = this.currentSession.aiRunStatus || {};
            const recentlyCompleted = (
                (Date.now() - (this._lastAiCompletionAt || 0)) < 15000
                || this.isRecentCollaborationAiAnswer(status)
            );
            if (status.state === 'running' || forceFinalize || recentlyCompleted) {
                const latestBot = this.findLatestBotMessage();
                if (latestBot && !latestBot.isLoading) {
                    this.completeAiRunStatus(latestBot, {
                        cancelled: !!latestBot.cancelled,
                        revealStatus: revealCompletedStatus,
                        touchCompletion: false,
                    });
                    this.completeChatRequestContextForMessage(latestBot);
                    this.finalizeChatUiAfterTerminal();
                }
            }
        },
        scrollToAiStatusMessage() {
            const status = this.collaborationAiStatus;
            if (!status || !status.messageId) return;
            if (status.state === 'answered') {
                this.acknowledgeCollaborationAiStatus(status);
            }
            const senderType = status.state === 'queued' ? 'user' : 'assistant';
            this.scrollToMessageReference({
                message_id: status.messageId,
                sender_type: senderType,
                sender_label: status.state === 'queued' ? 'Solicitud a Lexius' : 'Abogado Virtual',
                snippet: status.preview || status.title || 'Abogado Virtual',
            });
        },
        applyQueuedAiFields(target, source = {}) {
            if (!target || !source || source.ai_queued !== true) return;
            try { this.$set(target, 'ai_queued', true); } catch (e) { target.ai_queued = true; }
            try { this.$set(target, 'ai_queue_status', source.ai_queue_status || 'queued'); } catch (e) { target.ai_queue_status = source.ai_queue_status || 'queued'; }
            try { this.$set(target, 'ai_queue_reason', source.ai_queue_reason || 'Ya hay una respuesta del Abogado Virtual en curso.'); } catch (e) { target.ai_queue_reason = source.ai_queue_reason || 'Ya hay una respuesta del Abogado Virtual en curso.'; }
            if (source.ai_queue_position !== undefined && source.ai_queue_position !== null) {
                try { this.$set(target, 'ai_queue_position', source.ai_queue_position); } catch (e) { target.ai_queue_position = source.ai_queue_position; }
            }
            try { this.$set(target, 'human_only', true); } catch (e) { target.human_only = true; }
        },
        handleQueuedAiRequest(data = {}, loadingMessage = null, invocationText = '') {
            if (!this.currentSession || !Array.isArray(this.currentSession.data)) return;
            const payload = data.queued_message || data.message_payload || {};
            const queuedText = String(payload.text || invocationText || '').trim();
            if (!queuedText) return;
            if (loadingMessage) {
                const loadingIndex = this.currentSession.data.indexOf(loadingMessage);
                if (loadingIndex >= 0) {
                    this.currentSession.data.splice(loadingIndex, 1);
                }
            }
            let target = null;
            for (let i = this.currentSession.data.length - 1; i >= 0; i--) {
                const candidate = this.currentSession.data[i];
                if (candidate && candidate.sender === 'user' && String(candidate.text || '').trim() === queuedText) {
                    target = candidate;
                    break;
                }
            }
            if (!target) {
                target = {
                    sender: 'user',
                    text: queuedText,
                    timestamp: payload.timestamp ? Date.parse(payload.timestamp) || Date.now() : Date.now(),
                    server_timestamp: payload.timestamp || '',
                    sender_username: payload.sender_username || '',
                    sender_email: payload.sender_email || '',
                    sender_display_name: payload.sender_display_name || payload.sender_email || payload.sender_username || '',
                    human_only: true,
                };
                this.currentSession.data.push(target);
            }
            this.reserveWindowedChatIndexes(target);
            if (payload.timestamp) {
                target.timestamp = Date.parse(payload.timestamp) || target.timestamp || Date.now();
                target.server_timestamp = payload.timestamp;
            }
            if (payload.sender_username) target.sender_username = payload.sender_username;
            if (payload.sender_email) target.sender_email = payload.sender_email;
            if (payload.sender_display_name) target.sender_display_name = payload.sender_display_name;
            if (payload.message_id) {
                try { this.$set(target, 'message_id', String(payload.message_id)); } catch (e) { target.message_id = String(payload.message_id); }
            }
            if (payload.turn_id) {
                try { this.$set(target, 'turn_id', String(payload.turn_id)); } catch (e) { target.turn_id = String(payload.turn_id); }
            }
            if (payload.client_request_id) {
                try { this.$set(target, 'client_request_id', String(payload.client_request_id)); } catch (e) { target.client_request_id = String(payload.client_request_id); }
            }
            try { this.$set(target, '_is_optimistic', false); } catch (e) { target._is_optimistic = false; }
            this.applyQueuedAiFields(target, {
                ai_queued: true,
                ai_queue_status: payload.ai_queue_status || data.ai_queue_status || 'queued',
                ai_queue_reason: payload.ai_queue_reason || data.ai_queue_reason || data.message || '',
                ai_queue_position: payload.ai_queue_position || data.ai_queue_position,
            });
            this.messageReferenceId(target);
            this.chatResponseReceived = true;
            this.currentSession.suggestedQuestions = [];
            this.lastError = false;
            this.errorMessage = null;
            this.store();
            this.finalizeChatUiAfterTerminal();
            this.settlePendingAiReplyAfterTerminal(
                loadingMessage,
                this.handleIncomingAiResponseVisibility(),
                null,
                { revealAnswerStart: false },
            ).catch(() => {});
        },
        runQueuedAiRequest(message) {
            if (!message || message.ai_queued !== true) return;
            if (this.replying || this.findLoadingBotIndex() >= 0) {
                alert('Lexius todavía está respondiendo. Esta solicitud seguirá visible en cola.');
                return;
            }
            const text = String(message.text || '').trim();
            if (!text) return;
            try { this.$set(message, 'ai_queue_status', 'running'); } catch (e) { message.ai_queue_status = 'running'; }
            this.store();
            this.reply(text, {
                processQueuedAiRequest: true,
                queuedMessageTimestamp: String(message.server_timestamp || message.timestamp || '').trim(),
                queuedMessage: message,
                clientRequestId: String(message.client_request_id || '').trim(),
            });
        },
        restoreQueuedAiRequest(message, reason = '') {
            if (!message || message.ai_queued !== true) return;
            try { this.$set(message, 'ai_queue_status', 'queued'); } catch (e) { message.ai_queue_status = 'queued'; }
            const fallbackReason = 'No se pudo procesar esta solicitud. Inténtalo de nuevo cuando Lexius termine.';
            try { this.$set(message, 'ai_queue_reason', String(reason || message.ai_queue_reason || fallbackReason)); } catch (e) { message.ai_queue_reason = String(reason || message.ai_queue_reason || fallbackReason); }
            this.store();
        },
        addGroupMessageToSession(payload, options = {}) {
            try {
                const remote = !(options && options.remote === false);
                if (!this.currentSession || !this.currentSession.uuid) return;
                if (payload && payload.chat_uuid && String(payload.chat_uuid) !== String(this.currentSession.uuid)) return;
                const text = payload && typeof payload.text === 'string' ? payload.text : '';
                if (!text) return;
                const message = {
                    sender: 'user',
                    text,
                    timestamp: payload.timestamp ? Date.parse(payload.timestamp) || payload.timestamp : Date.now(),
                    server_timestamp: payload.timestamp || '',
                    sender_username: payload.sender_username || '',
                    sender_email: payload.sender_email || '',
                    sender_display_name: payload.sender_display_name || payload.sender_email || payload.sender_username || 'Participante',
                    human_only: true,
                };
                this.applyQueuedAiFields(message, payload);
                const replyTo = this.normalizeReplyToPayload(payload.reply_to || payload.replyTo);
                if (replyTo) {
                    message.reply_to = replyTo;
                }
                const contextDocuments = this.normalizeMessageContextDocumentsPayload(
                    payload.context_documents || payload.contextDocuments || payload.chat_context_documents
                );
                if (contextDocuments.length) {
                    message.contextDocuments = contextDocuments;
                }
                this.messageReferenceId(message);
                const key = this.getMessageKeyForDuplicate(message);
                const messages = this.currentSession.data || [];
                const exists = messages.some((existing) => this.getMessageKeyForDuplicate(existing) === key);
                if (exists) return;
                let mergeTarget = null;
                const incomingSender = String(message.sender_email || message.sender_username || '').trim().toLowerCase();
                for (let i = messages.length - 1; i >= 0; i--) {
                    const existing = messages[i];
                    if (!existing || existing.sender !== 'user') continue;
                    if (String(existing.server_timestamp || '').trim()) continue;
                    const existingSender = String(existing.sender_email || existing.sender_username || '').trim().toLowerCase();
                    if (incomingSender && existingSender && incomingSender !== existingSender) continue;
                    if (String(existing.text || '').trim() !== text) continue;
                    mergeTarget = existing;
                    break;
                }
                if (mergeTarget) {
                    mergeTarget.timestamp = message.timestamp;
                    mergeTarget.server_timestamp = message.server_timestamp;
                    mergeTarget.sender_username = message.sender_username;
                    mergeTarget.sender_email = message.sender_email;
                    mergeTarget.sender_display_name = message.sender_display_name;
                    mergeTarget.human_only = message.human_only;
                    this.applyQueuedAiFields(mergeTarget, payload);
                    if (message.reply_to) mergeTarget.reply_to = message.reply_to;
                    if (message.contextDocuments) mergeTarget.contextDocuments = message.contextDocuments;
                    this.messageReferenceId(mergeTarget);
                    this.reserveWindowedChatIndexes(mergeTarget);
                    this.patchCurrentSessionReactive({ is_collaborative: true });
                    this.store();
                    this.handleIncomingMessageVisibility({ remote, payload });
                    return;
                }
                this.insertMessageByServerTimestamp(message);
                this.reserveWindowedChatIndexes(message);
                this.patchCurrentSessionReactive({ is_collaborative: true });
                this.store();
                this.handleIncomingMessageVisibility({ remote, payload });
            } catch (e) { }
        },
        applyMessageReaction(payload) {
            try {
                if (!payload || !this.currentSession || !Array.isArray(this.currentSession.data)) return;
                if (!['agree', 'disagree'].includes(payload.reaction)) return;
                if (payload.chat_uuid && String(payload.chat_uuid) !== String(this.currentSession.uuid || '')) return;
                const targetTs = String(payload.message_timestamp || '');
                if (!targetTs) return;
                const message = this.currentSession.data.find((item) => (
                    String(item.server_timestamp || '') === targetTs
                    || String(item.timestamp || '') === targetTs
                ));
                if (!message) return;
                if (!Array.isArray(message.reactions)) {
                    try { this.$set(message, 'reactions', []); } catch (e) { message.reactions = []; }
                }
                const payloadReactionUuid = String(payload.reaction_uuid || '');
                const reactionKey = `${payload.reactor_username || ''}|${payload.reaction || ''}`;
                const isSameReaction = (item) => (
                    !!(
                        item
                        && (
                            (payloadReactionUuid && String(item.reaction_uuid || '') === payloadReactionUuid)
                            || `${item.reactor_username || ''}|${item.reaction || ''}` === reactionKey
                        )
                    )
                );
                if (payload.removed === true) {
                    const nextReactions = message.reactions.filter((item) => !isSameReaction(item));
                    try { this.$set(message, 'reactions', nextReactions); } catch (e) { message.reactions = nextReactions; }
                    this.store();
                    return;
                }
                const exists = message.reactions.some(isSameReaction);
                if (!exists) {
                    message.reactions.push({
                        reaction_uuid: payload.reaction_uuid,
                        reaction: payload.reaction,
                        reactor_username: payload.reactor_username,
                        reactor_email: payload.reactor_email,
                        reactor_display_name: payload.reactor_display_name || payload.reactor_email || '',
                    });
                }
                this.store();
            } catch (e) { }
        },
        handleDelegatedCollaborationReaction(event) {
            try {
                const target = event && event.target;
                if (!target || typeof target.closest !== 'function') return;
                const button = target.closest('button[data-collaboration-reaction]');
                if (!button || !this.$el || !this.$el.contains(button)) return;
                const reaction = String(button.getAttribute('data-collaboration-reaction') || '').trim();
                if (!['agree', 'disagree'].includes(reaction)) return;
                const messageEl = button.closest('[data-message-ref]');
                const messageRef = messageEl ? String(messageEl.getAttribute('data-message-ref') || '') : '';
                if (!messageRef || !this.currentSession || !Array.isArray(this.currentSession.data)) return;
                const message = this.currentSession.data.find((item) => (
                    item
                    && item.sender === 'user'
                    && this.messageReferenceId(item) === messageRef
                ));
                if (!message) return;
                event.preventDefault();
                this.reactToMessage({ message, reaction });
            } catch (e) { }
        },
        async reactToMessage({ message, reaction }) {
            try {
                if (!message || !this.currentSession || !this.currentSession.uuid) return;
                if (!['agree', 'disagree'].includes(reaction)) return;
                const response = await fetch('/chats/collaboration/reactions', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        uuid: this.currentSession.uuid,
                        message_timestamp: String(message.server_timestamp || message.timestamp || ''),
                        message_sender_username: message.sender_username || '',
                        reaction,
                    }),
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    alert(data.message || 'No se pudo guardar la reacción.');
                    return;
                }
                if (data.reaction) {
                    this.applyMessageReaction(data.reaction);
                }
            } catch (e) {
                alert('No se pudo guardar la reacción.');
            }
        },
        async sendCollaborationHumanMessage(text) {
            try {
                const contextDocuments = this.getOutgoingMessageContextDocuments(this.currentSession);
                const response = await fetch('/chats/collaboration/messages', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        uuid: this.currentSession.uuid,
                        message: text,
                        context_documents: contextDocuments,
                        reply_to: this.replyTarget || null,
                    }),
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    alert(data.message || 'No se pudo enviar el mensaje.');
                    return false;
                }
                if (data.message) {
                    this.addGroupMessageToSession(data.message, { remote: false });
                }
                if (contextDocuments.length) {
                    this.clearPendingContextDocuments(
                        contextDocuments,
                        this.currentSession,
                    );
                    this.store();
                }
                this.loadingSuggestedQuestions = false;
                this.message = null;
                this.clearReplyTarget();
                this.publishTypingState(false);
                return true;
            } catch (e) {
                alert('No se pudo enviar el mensaje.');
                return false;
            }
        },
        buildPresencePayload(isTyping = false) {
            const email = this.getCurrentUserEmail();
            const username = this.getCurrentUsername();
            return {
                email,
                username,
                display_name: username || email,
                typing: !!isTyping,
                chat_uuid: this.currentSession && this.currentSession.uuid ? String(this.currentSession.uuid) : '',
            };
        },
        summarizePresenceMembers(members) {
            const currentEmail = this.getCurrentUserEmail();
            const currentUsername = this.getCurrentUsername().toLowerCase();
            const byParticipant = new Map();
            for (const member of Array.isArray(members) ? members : []) {
                const data = member && member.data ? member.data : {};
                const email = String(data.email || '').trim().toLowerCase();
                const username = String(data.username || '').trim().toLowerCase();
                if ((email && email === currentEmail) || (username && username === currentUsername)) continue;
                const key = email ? `email:${email}` : (username ? `user:${username}` : String(member.connectionId || ''));
                if (!key) continue;
                const displayName = String(data.display_name || data.email || 'Participante').trim();
                const existing = byParticipant.get(key) || {
                    display_name: displayName || 'Participante',
                    email,
                    username,
                    typing: false,
                };
                existing.typing = existing.typing || data.typing === true;
                byParticipant.set(key, existing);
            }
            return Array.from(byParticipant.values());
        },
        publishTypingState(isTyping = true) {
            try {
                this.pendingTypingState = !!isTyping;
                if (!isTyping) {
                    clearTimeout(this._typingStopTimer);
                    this._typingStopTimer = null;
                }
                if (!this.remotePresenceChannel || !this.remotePresenceChannel.presence) return;
                if (typeof this.remotePresenceChannel.presence.update !== 'function') return;
                const payload = this.buildPresencePayload(this.pendingTypingState);
                const method = this.remotePresenceEntered ? 'update' : 'enter';
                if (typeof this.remotePresenceChannel.presence[method] !== 'function') return;
                const presenceChannel = this.remotePresenceChannel;
                const updateResult = this.remotePresenceChannel.presence[method](payload);
                if (
                    updateResult
                    && typeof updateResult.then === 'function'
                    && typeof updateResult.catch === 'function'
                ) {
                    updateResult
                        .then(() => {
                            if (this.remotePresenceChannel === presenceChannel) {
                                this.remotePresenceEntered = true;
                            }
                        })
                        .catch(() => {
                            if (this.remotePresenceChannel === presenceChannel) {
                                this.remotePresenceEntered = false;
                            }
                        });
                } else {
                    this.remotePresenceEntered = true;
                }
                if (isTyping) {
                    clearTimeout(this._typingStopTimer);
                    this._typingStopTimer = setTimeout(() => {
                        try { this.publishTypingState(false); } catch (e) { }
                    }, 2500);
                }
            } catch (e) { }
        },
        // Detects the synthetic intro message injected for document chats
        isDocumentIntroMessage(content) {
            try {
                if (!content || typeof content !== 'string') return false;
                const s = content.trim();
                // Multi-document intro prefix
                if (s.startsWith('Quiero chatear acerca de múltiples archivos:')) return true;
                // User document intro title line variants
                if (s.startsWith('**Nombre del documento subido a LEXIUS') || s.startsWith('**Nombre de ')) return true;
                // Jurisprudence single-doc intro header
                if (s.startsWith('**Sentencia:**')) return true;
                // Presence of the canonical "Texto completo ..." headers used in intros (anywhere in text)
                const sLower = s.toLowerCase();
                if (sLower.includes('**texto completo del documento:**') ||
                    sLower.includes('**texto completo de la sentencia:**') ||
                    sLower.includes('**texto completo del texto legal:**') ||
                    sLower.includes('**texto completo de ')) {
                    return true;
                }
                return false;
            } catch (e) {
                return false;
            }
        },
        firstDefined(...values) {
            for (const value of values) {
                if (value !== undefined) {
                    return value;
                }
            }
            return undefined;
        },
        coerceBoolean(value) {
            if (value === undefined) return undefined;
            if (value === null) return null;
            if (typeof value === 'boolean') return value;
            if (typeof value === 'number') return value !== 0;
            if (typeof value === 'string') {
                const trimmed = value.trim().toLowerCase();
                if (!trimmed) return false;
                if (["true", "1", "sí", "si", "yes"].includes(trimmed)) return true;
                if (["false", "0", "no"].includes(trimmed)) return false;
                if (["null", "none", "undefined"].includes(trimmed)) return null;
                if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
                    try {
                        const parsed = JSON.parse(value);
                        return this.coerceBoolean(parsed);
                    } catch (e) {
                        return true;
                    }
                }
                return true;
            }
            if (Array.isArray(value)) {
                return value.some((item) => this.coerceBoolean(item));
            }
            if (typeof value === 'object') return true;
            return Boolean(value);
        },
        isSerializedDocumentList(value) {
            // JSON string lists can be cut mid-value by legacy VARCHAR columns.
            if (typeof value !== 'string' || !/^\s*\[\s*"/.test(value)) return false;
            const text = value.trim();
            let inString = false;
            let escaped = false;
            let expectsValue = true;
            for (let index = 1; index < text.length; index++) {
                const character = text[index];
                if (inString) {
                    if (escaped) escaped = false;
                    else if (character === '\\') escaped = true;
                    else if (character === '"') inString = false;
                } else if (/\s/.test(character)) {
                    continue;
                } else if (expectsValue && character === '"') {
                    inString = true;
                    expectsValue = false;
                } else if (!expectsValue && character === ',') {
                    expectsValue = true;
                } else if (!expectsValue && character === ']') {
                    // A suffix after the list belongs to a filename such as
                    // ["Anexo"].pdf, not serialized document metadata.
                    return index === text.length - 1;
                } else {
                    return false;
                }
            }
            return true;
        },
        normalizePathPayload(raw) {
            if (raw === undefined) return undefined;
            if (raw === null || raw === '') return null;
            if (Array.isArray(raw)) return raw;
            if (typeof raw === 'string') {
                const trimmed = raw.trim();
                if (!trimmed) return null;
                if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                    try {
                        const parsed = JSON.parse(trimmed);
                        if (Array.isArray(parsed)) return parsed;
                    } catch (e) { }
                }
                return trimmed;
            }
            if (typeof raw === 'object') return raw;
            return String(raw);
        },
        normalizePageTitlePayload(raw, isMulti) {
            if (raw === undefined) return undefined;
            if (raw === null) return null;
            if (Array.isArray(raw)) {
                return isMulti ? raw : (raw.length ? raw[0] : null);
            }
            if (typeof raw === 'string') {
                const trimmed = raw.trim();
                if (!trimmed) return null;
                if (isMulti && trimmed.startsWith('[') && trimmed.endsWith(']')) {
                    try {
                        const parsed = JSON.parse(trimmed);
                        if (Array.isArray(parsed)) return parsed;
                    } catch (e) { }
                }
                return trimmed;
            }
            return isMulti ? [String(raw)] : String(raw);
        },
        normalizeIsUserDocumentPayload(raw, isMulti) {
            if (raw === undefined) return undefined;
            if (raw === null || raw === '') return null;
            if (Array.isArray(raw)) {
                const mapped = raw.map((item) => this.coerceBoolean(item));
                return isMulti ? mapped : (mapped.length ? mapped[0] : null);
            }
            if (typeof raw === 'string') {
                const trimmed = raw.trim();
                if (!trimmed) return null;
                if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                    try {
                        const parsed = JSON.parse(trimmed);
                        if (Array.isArray(parsed)) {
                            const mapped = parsed.map((item) => this.coerceBoolean(item));
                            return isMulti ? mapped : (mapped.length ? mapped[0] : null);
                        }
                    } catch (e) { }
                }
                if (trimmed === 'null') return null;
                return this.coerceBoolean(trimmed);
            }
            if (isMulti && typeof raw === 'object') {
                try {
                    const mapped = Object.values(raw).map((item) => this.coerceBoolean(item));
                    return isMulti ? mapped : (mapped.length ? mapped[0] : null);
                } catch (e) {
                    return null;
                }
            }
            return this.coerceBoolean(raw);
        },
        normalizeMultiDocumentSession(session) {
            if (!session || !Array.isArray(session.path)) return;

            session.is_multi_document = true;
            const pathCount = session.path.length;

            if (!Array.isArray(session.page_title)) {
                session.page_title = Array(pathCount).fill(session.page_title || '');
            } else if (session.page_title.length !== pathCount) {
                const fixedTitles = session.page_title.slice(0, pathCount);
                while (fixedTitles.length < pathCount) {
                    fixedTitles.push('');
                }
                session.page_title = fixedTitles;
            }

            let isUserDocs = session.is_user_document;
            if (!Array.isArray(isUserDocs)) {
                isUserDocs = Array(pathCount).fill(isUserDocs);
            }

            session.is_user_document = session.path.map((docPath, index) => {
                const value = index < isUserDocs.length ? isUserDocs[index] : undefined;
                if (typeof docPath === 'string' && docPath.includes('/get_user_upload')) {
                    return true;
                }
                let normalized = this.coerceBoolean(value);
                if (normalized === undefined || normalized === null) {
                    if (typeof docPath === 'string' && docPath.includes('/get_user_upload')) {
                        return true;
                    }
                    return false;
                }
                return normalized;
            });
        },
        normalizeNonUserDocumentIdentityPath(pathValue) {
            let normalizedPath = String(pathValue || '').trim();
            if (!normalizedPath) return normalizedPath;
            if (normalizedPath.startsWith('./')) normalizedPath = normalizedPath.slice(2);
            normalizedPath = normalizedPath.replace(/^\/+/, '');

            const documentsFolder = (typeof DOCUMENTS_FOLDER === 'string' ? DOCUMENTS_FOLDER.trim() : '')
                .replace(/^\/+/, '')
                .replace(/\/+$/, '');
            if (
                documentsFolder &&
                (normalizedPath === documentsFolder || normalizedPath.startsWith(`${documentsFolder}/`))
            ) {
                normalizedPath = normalizedPath.slice(documentsFolder.length).replace(/^\/+/, '');
            }

            return normalizedPath;
        },
        buildDocumentKey(pathValue, isUserDocument, scopeHint = '', sourceBinding = null) {
            const originalPath = String(pathValue || '').trim();
            let normalizedPath = '';
            try {
                normalizedPath = decodeURIComponent(originalPath);
            } catch (e) {
                normalizedPath = originalPath;
            }

            // A /get_user_upload locator is authoritative evidence that this is
            // a private document. During first-response persistence, a stale
            // scalar flag can briefly disagree with the newly attached path.
            // Letting that false flag participate in identity creates a second
            // "initial" row for the same upload.
            const effectiveIsUserDocument = !!isUserDocument
                || normalizedPath.includes('/get_user_upload');

            if (effectiveIsUserDocument) {
                let filename = '';
                try {
                    const parsed = new URL(normalizedPath, window.location.origin);
                    filename = parsed.searchParams.get('filename') || '';
                } catch (e) { }
                if (!filename && normalizedPath.includes('filename=')) {
                    try {
                        filename = normalizedPath.split('filename=', 2)[1].split('&', 1)[0];
                    } catch (e) {
                        filename = '';
                    }
                }
                normalizedPath = (filename || normalizedPath.split('/').pop() || '').trim();
            } else {
                let extracted = normalizedPath;
                try {
                    const parsed = new URL(normalizedPath, window.location.origin);
                    const pathname = (parsed.pathname || '').trim();
                    const canonicalQuery = Array.from(parsed.searchParams.entries())
                        .sort(([keyA, valueA], [keyB, valueB]) => {
                            if (keyA === keyB) return String(valueA).localeCompare(String(valueB));
                            return keyA.localeCompare(keyB);
                        })
                        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                        .join('&');
                    const pathParam = parsed.searchParams.get('path');
                    if (pathParam) {
                        try {
                            extracted = decodeURIComponent(pathParam);
                        } catch (e) {
                            extracted = pathParam;
                        }
                    } else if (canonicalQuery) {
                        extracted = pathname && pathname !== '/'
                            ? `${pathname}?${canonicalQuery}`
                            : `?${canonicalQuery}`;
                    } else {
                        if (pathname && pathname !== '/') extracted = pathname;
                    }
                } catch (e) {
                    if (normalizedPath.includes('path=')) {
                        try {
                            extracted = normalizedPath.split('path=', 2)[1].split('&', 1)[0];
                            extracted = decodeURIComponent(extracted);
                        } catch (err) {
                            extracted = normalizedPath;
                        }
                    }
                }
                normalizedPath = String(extracted || '').trim();
                normalizedPath = this.normalizeNonUserDocumentIdentityPath(normalizedPath);
            }

            if (effectiveIsUserDocument) {
                const scope = privateDocumentScopeFromLocator(originalPath, scopeHint);
                const binding = sourceBinding && typeof sourceBinding === 'object'
                    ? sourceBinding
                    : {};
                const sourceId = String(binding.source_id || '').trim();
                const sourceScopeId = String(binding.source_scope_id || '').trim();
                const sourceRevision = String(binding.source_revision_hash || '').trim();
                const sourceKind = String(binding.source_kind || '').trim();
                if (
                    ['personal', 'corporate'].includes(scope)
                    && sourceId
                    && sourceScopeId
                    && sourceRevision
                    && sourceKind
                ) return `source|${sourceId}`;
                return scope === 'corporate'
                    ? `1|corporate|${normalizedPath}`
                    : `1|${normalizedPath}`;
            }
            return `0|${normalizedPath}`;
        },
        buildDocumentReconcileIdentity(
            pathValue,
            isUserDocument,
            scopeHint = '',
            sourceBinding = null,
        ) {
            return this.buildDocumentKey(
                pathValue,
                isUserDocument,
                scopeHint,
                sourceBinding,
            );
        },
        normalizeChatDocumentsStatePayload(rawState) {
            if (!Array.isArray(rawState)) return [];
            const normalized = [];
            const indexByKey = new Map();
            const unboundIndexByLocator = new Map();
            for (const entry of rawState) {
                if (!entry || typeof entry !== 'object') continue;
                const path = (entry.path == null ? '' : String(entry.path)).trim();
                // Older clients cached the entire (possibly truncated) list as a document.
                if (!path || this.isSerializedDocumentList(path)) continue;
                const coercedIsUser = this.coerceBoolean(entry.is_user_document);
                const isUser = path.includes('/get_user_upload')
                    ? true
                    : (coercedIsUser === undefined || coercedIsUser === null ? false : !!coercedIsUser);
                const scopeHint = entry.source_scope_kind || entry.scope || entry.user_document_scope;
                const locatorKey = this.buildDocumentKey(path, isUser, scopeHint);
                const key = this.buildDocumentKey(
                    path,
                    isUser,
                    scopeHint,
                    entry,
                );
                const normalizedEntry = {
                    doc_id: entry.doc_id || key,
                    path,
                    page_title: entry.page_title == null ? '' : String(entry.page_title),
                    is_user_document: isUser,
                    ...preserveSourceBinding(entry),
                    ...(isLazyCatalogDocument(entry)
                        ? { context_representation: LAZY_DOCUMENT_CONTEXT_REPRESENTATION }
                        : {}),
                    added_during: entry.added_during === 'added_later' ? 'added_later' : 'initial',
                    is_out_of_context: !!entry.is_out_of_context,
                    pending_reinjection: !!entry.pending_reinjection,
                    injection_message_id: entry.injection_message_id || null,
                    out_of_context_at: entry.out_of_context_at || null,
                    last_injected_at: entry.last_injected_at || null,
                    key,
                };
                let existingIndex = indexByKey.get(key);
                if (!Number.isInteger(existingIndex) && key !== locatorKey) {
                    existingIndex = unboundIndexByLocator.get(locatorKey);
                }
                if (Number.isInteger(existingIndex)) {
                    normalized[existingIndex] = mergeMonotonicDocumentProvenance(
                        normalized[existingIndex],
                        normalizedEntry,
                    );
                    indexByKey.set(key, existingIndex);
                    if (key !== locatorKey) unboundIndexByLocator.delete(locatorKey);
                    continue;
                }
                indexByKey.set(key, normalized.length);
                if (key === locatorKey) {
                    unboundIndexByLocator.set(locatorKey, normalized.length);
                }
                normalized.push(normalizedEntry);
            }
            return normalized;
        },
        normalizeMessageContextDocumentsPayload(rawDocs) {
            if (!Array.isArray(rawDocs)) return [];
            const normalized = [];
            const seen = new Set();

            for (const item of rawDocs) {
                if (!item || typeof item !== 'object') continue;
                const path = (item.path == null ? '' : String(item.path)).trim();
                if (!path) continue;
                if (!this.isSafeDocumentHref(path)) continue;
                const pageTitle = item.page_title == null ? '' : String(item.page_title).trim();
                const coercedIsUser = this.coerceBoolean(item.is_user_document);
                const isUser = path.includes('/get_user_upload')
                    ? true
                    : (coercedIsUser === undefined || coercedIsUser === null ? false : !!coercedIsUser);
                const key = this.buildDocumentKey(path, isUser);
                if (seen.has(key)) continue;
                seen.add(key);
                normalized.push({
                    key,
                    path,
                    page_title: pageTitle,
                    is_user_document: isUser,
                });
            }

            return normalized;
        },
        getSessionDocumentRows(session = this.currentSession) {
            if (!session) return [];
            const paths = Array.isArray(session.path) ? session.path : (session.path ? [session.path] : []);
            const titles = Array.isArray(session.page_title) ? session.page_title : (paths.length ? [session.page_title] : []);
            const normalizedState = this.normalizeChatDocumentsStatePayload(
                Array.isArray(session.chat_documents_state) ? session.chat_documents_state : []
            );
            const stateByKey = new Map();
            for (const item of normalizedState) {
                if (!item || !item.key) continue;
                if (!stateByKey.has(item.key)) {
                    stateByKey.set(item.key, item);
                }
            }
            let isUsers = [];
            if (Array.isArray(session.is_user_document)) {
                isUsers = session.is_user_document;
            } else {
                isUsers = Array(paths.length).fill(session.is_user_document);
            }

            const rows = [];
            for (let i = 0; i < paths.length; i++) {
                const pathValue = (paths[i] == null ? '' : String(paths[i])).trim();
                if (!pathValue) continue;
                const fallbackIsUser = this.coerceBoolean(isUsers[i]);
                const inferredIsUser = pathValue.includes('/get_user_upload')
                    ? true
                    : (fallbackIsUser === undefined || fallbackIsUser === null ? false : !!fallbackIsUser);
                const alignedState = normalizedState[i];
                const alignedStateIsUser = alignedState
                    ? !!alignedState.is_user_document
                    : null;
                const stateMatchesLegacyRow = !!(
                    alignedState
                    && String(alignedState.path || '').trim() === pathValue
                    && alignedStateIsUser === inferredIsUser
                );
                const scopeHint = stateMatchesLegacyRow
                    ? alignedState.source_scope_kind
                        || alignedState.scope
                        || alignedState.user_document_scope
                        || ''
                    : '';
                const key = this.buildDocumentKey(
                    pathValue,
                    inferredIsUser,
                    scopeHint,
                    stateMatchesLegacyRow ? alignedState : null,
                );
                const stateMatch = stateByKey.get(key);
                let isUser = false;
                if (stateMatch) {
                    isUser = !!stateMatch.is_user_document;
                } else {
                    isUser = inferredIsUser;
                }
                rows.push({
                    doc_id: key,
                    path: pathValue,
                    page_title: titles[i] == null ? '' : String(titles[i]),
                    is_user_document: isUser,
                    key,
                });
            }
            return rows;
        },
        ensureSessionDocumentState(session = this.currentSession) {
            if (!session || typeof session !== 'object') return [];

            const normalizedState = this.normalizeChatDocumentsStatePayload(session.chat_documents_state);
            const legacyValues = [session.path, session.page_title].flat();
            if (legacyValues.some(value => this.isSerializedDocumentList(value))) {
                // Recover before legacy rows can create bogus "initial" documents.
                // Without canonical state, retain only intact individual paths and
                // suppress list-shaped titles; never infer paths from a partial list.
                const recoveredRows = normalizedState.length
                    ? normalizedState
                    : this.getSessionDocumentRows(session)
                        .filter(row => !this.isSerializedDocumentList(row.path))
                        .map(row => ({
                            ...row,
                            page_title: this.isSerializedDocumentList(row.page_title) ? '' : row.page_title,
                        }));
                this.applySessionDocumentRows(recoveredRows, session);
            }
            const stateMap = new Map(normalizedState.map((entry) => [entry.key, entry]));
            const rows = this.getSessionDocumentRows(session);

            for (const row of rows) {
                if (stateMap.has(row.key)) continue;
                const created = {
                    doc_id: row.doc_id || row.key,
                    path: row.path,
                    page_title: row.page_title || '',
                    is_user_document: !!row.is_user_document,
                    added_during: 'initial',
                    is_out_of_context: false,
                    pending_reinjection: false,
                    injection_message_id: null,
                    out_of_context_at: null,
                    last_injected_at: null,
                    key: row.key,
                };
                normalizedState.push(created);
                stateMap.set(row.key, created);
            }

            // Keep the same order as the visible document list, then append residual state rows.
            const ordered = [];
            const pushed = new Set();
            for (const row of rows) {
                const st = stateMap.get(row.key);
                if (!st || pushed.has(st.key)) continue;
                ordered.push(st);
                pushed.add(st.key);
            }
            for (const item of normalizedState) {
                if (!item || pushed.has(item.key)) continue;
                ordered.push(item);
                pushed.add(item.key);
            }

            session.chat_documents_state = ordered;
            return session.chat_documents_state;
        },
        synchronizeSessionDocumentMetadataFromState(session = this.currentSession) {
            if (!session || typeof session !== 'object') return;
            const rows = this.getSessionDocumentRows(session);
            const normalizedState = this.normalizeChatDocumentsStatePayload(
                Array.isArray(session.chat_documents_state) ? session.chat_documents_state : []
            );
            const hasLawContext = (
                this.inferIsLawFromPath(session.path) ||
                rows.some((row) => !row.is_user_document && this.inferIsLawFromPath(row.path)) ||
                normalizedState.some((entry) => !entry.is_user_document && this.inferIsLawFromPath(entry.path))
            );
            session.is_law = hasLawContext;
            if (!rows.length) return;
            if (rows.length === 1 && !Array.isArray(session.path)) {
                session.is_user_document = !!rows[0].is_user_document;
                return;
            }
            session.is_user_document = rows.map((row) => !!row.is_user_document);
        },
        normalizePendingDocumentInjections(session = this.currentSession) {
            if (!session || typeof session !== 'object') return [];
            if (!Array.isArray(session.pending_document_injections)) {
                session.pending_document_injections = [];
                return session.pending_document_injections;
            }

            const lazyDocumentKeys = new Set(
                this.normalizeChatDocumentsStatePayload(session.chat_documents_state)
                    .filter(isLazyCatalogDocument)
                    .map(entry => entry.key)
            );
            const deduped = [];
            const seen = new Set();
            for (const item of session.pending_document_injections) {
                if (!item || typeof item !== 'object') continue;
                const path = (item.path == null ? '' : String(item.path)).trim();
                if (!path) continue;
                const coercedIsUser = this.coerceBoolean(item.is_user_document);
                const isUser = path.includes('/get_user_upload')
                    ? true
                    : (coercedIsUser === undefined || coercedIsUser === null ? false : !!coercedIsUser);
                const key = this.buildDocumentKey(path, isUser);
                const catalogBindingOnly = item.catalog_binding_only === true;
                if (lazyDocumentKeys.has(key) && !catalogBindingOnly) continue;
                if (seen.has(key)) {
                    // Upgrade duplicate one-shot flags without duplicating a source.
                    const existing = deduped.find((entry) => entry.key === key);
                    if (existing && item.force_reinject) existing.force_reinject = true;
                    if (existing && catalogBindingOnly) existing.catalog_binding_only = true;
                    continue;
                }
                seen.add(key);
                deduped.push({
                    path,
                    page_title: item.page_title == null ? '' : String(item.page_title),
                    is_user_document: isUser,
                    source_kind: String(item.source_kind || '').trim().slice(0, 64),
                    force_reinject: !!item.force_reinject,
                    catalog_binding_only: catalogBindingOnly,
                    key,
                });
            }
            session.pending_document_injections = deduped;
            return session.pending_document_injections;
        },
        getActiveContextDocumentsForMessage(
            session = this.currentSession,
            { includePendingNextMessageDocuments = false } = {}
        ) {
            if (!session || typeof session !== 'object') return [];

            const rows = this.getSessionDocumentRows(session);
            const normalizedState = this.normalizeChatDocumentsStatePayload(
                Array.isArray(session.chat_documents_state) ? session.chat_documents_state : []
            );
            const stateByKey = new Map(normalizedState.map((entry) => [entry.key, entry]));
            const pendingByKey = new Map();
            if (includePendingNextMessageDocuments) {
                const pendingLocal = this.normalizePendingDocumentInjections(session);
                for (const item of pendingLocal) {
                    if (!item || typeof item !== 'object' || !item.key) continue;
                    pendingByKey.set(item.key, item);
                }
            }
            const docs = [];
            const seen = new Set();

            const appendDoc = (doc, stateEntry = null) => {
                if (!doc || typeof doc !== 'object') return;
                const path = (doc.path == null ? '' : String(doc.path)).trim();
                if (!path) return;
                const coercedIsUser = this.coerceBoolean(doc.is_user_document);
                const isUser = path.includes('/get_user_upload')
                    ? true
                    : (coercedIsUser === undefined || coercedIsUser === null ? false : !!coercedIsUser);
                const key = doc.key || this.buildDocumentKey(path, isUser);
                if (seen.has(key)) return;
                const hasPendingInjection = includePendingNextMessageDocuments && pendingByKey.has(key);
                if (stateEntry && stateEntry.added_during === 'added_later') {
                    if (!!stateEntry.is_out_of_context && !hasPendingInjection) {
                        return;
                    }
                    if (!stateEntry.injection_message_id && !hasPendingInjection) {
                        return;
                    }
                }
                seen.add(key);
                docs.push({
                    key,
                    path,
                    page_title: doc.page_title == null ? '' : String(doc.page_title),
                    is_user_document: isUser,
                });
            };

            for (const row of rows) {
                appendDoc(row, stateByKey.get(row.key) || null);
            }

            for (const stateEntry of normalizedState) {
                appendDoc(stateEntry, stateEntry);
            }

            return docs;
        },
        reconcilePendingInjectionsWithServerState(localPendingRaw, session = this.currentSession, { trustServerRemovals = false } = {}) {
            if (!session || typeof session !== 'object') return [];

            const normalizedState = this.normalizeChatDocumentsStatePayload(
                Array.isArray(session.chat_documents_state) ? session.chat_documents_state : []
            );
            const stateByKey = new Map();
            for (const item of normalizedState) {
                if (!item || !item.key) continue;
                stateByKey.set(item.key, item);
            }

            const localSession = {
                pending_document_injections: Array.isArray(localPendingRaw) ? localPendingRaw : [],
            };
            const normalizedLocal = this.normalizePendingDocumentInjections(localSession).map((entry) => ({ ...entry }));

            const merged = new Map();

            for (const entry of normalizedLocal) {
                if (!entry || !entry.key) continue;

                const stateEntry = stateByKey.get(entry.key);
                if (!stateEntry) {
                    if (trustServerRemovals) continue;
                } else {
                    if (stateEntry.added_during !== 'added_later') continue;
                    if (entry.force_reinject) {
                        if (!stateEntry.is_out_of_context && stateEntry.injection_message_id && !stateEntry.pending_reinjection) {
                            continue;
                        }
                    } else {
                        if (stateEntry.is_out_of_context) continue;
                        if (stateEntry.injection_message_id) continue;
                    }
                }

                merged.set(entry.key, entry);
            }

            return Array.from(merged.values());
        },
        mergeChatDocumentsStateFromServer(serverState, session = this.currentSession) {
            if (!session || typeof session !== 'object') return;
            const local = this.normalizeChatDocumentsStatePayload(
                Array.isArray(session.chat_documents_state) ? session.chat_documents_state : []
            );
            const server = this.normalizeChatDocumentsStatePayload(serverState);
            if (!server.length) return;

            const localByKey = new Map();
            const localByDocId = new Map();
            const localByReconcileId = new Map();
            for (const entry of local) {
                if (!entry || typeof entry !== 'object') continue;
                if (entry.key) localByKey.set(entry.key, entry);
                if (entry.doc_id) localByDocId.set(String(entry.doc_id), entry);
                const rid = this.buildDocumentReconcileIdentity(
                    entry.path,
                    !!entry.is_user_document,
                    entry.source_scope_kind || entry.scope || entry.user_document_scope,
                    entry,
                );
                if (rid) localByReconcileId.set(rid, entry);
            }

            for (const serverEntry of server) {
                if (!serverEntry || typeof serverEntry !== 'object') continue;
                const serverDocId = serverEntry.doc_id ? String(serverEntry.doc_id) : '';
                const serverRid = this.buildDocumentReconcileIdentity(
                    serverEntry.path,
                    !!serverEntry.is_user_document,
                    serverEntry.source_scope_kind || serverEntry.scope || serverEntry.user_document_scope,
                    serverEntry,
                );
                const existing =
                    (serverDocId && localByDocId.get(serverDocId)) ||
                    localByKey.get(serverEntry.key) ||
                    localByReconcileId.get(serverRid);
                if (!existing) {
                    local.push(serverEntry);
                    if (serverEntry.key) localByKey.set(serverEntry.key, serverEntry);
                    if (serverDocId) localByDocId.set(serverDocId, serverEntry);
                    if (serverRid) localByReconcileId.set(serverRid, serverEntry);
                    continue;
                }
                if (serverDocId && existing.doc_id !== serverDocId) {
                    existing.doc_id = serverDocId;
                    localByDocId.set(serverDocId, existing);
                }
                if (!existing.page_title && serverEntry.page_title) {
                    existing.page_title = serverEntry.page_title;
                }
                Object.assign(existing, mergeMonotonicDocumentProvenance(existing, serverEntry));
                Object.assign(existing, preserveSourceBinding(serverEntry));
            }

            // Collapse residual duplicates by doc_id/key/reconciliation identity.
            const deduped = [];
            const indexByIdentity = new Map();
            for (const item of local) {
                if (!item || typeof item !== 'object') continue;
                const identities = [];
                if (item.doc_id) identities.push(`d:${String(item.doc_id)}`);
                if (item.key) identities.push(`k:${item.key}`);
                identities.push(
                    `r:${this.buildDocumentReconcileIdentity(
                        item.path,
                        !!item.is_user_document,
                        item.source_scope_kind || item.scope || item.user_document_scope,
                        item,
                    )}`
                );

                let existingIndex = -1;
                for (const identity of identities) {
                    if (indexByIdentity.has(identity)) {
                        existingIndex = indexByIdentity.get(identity);
                        break;
                    }
                }

                if (existingIndex === -1) {
                    deduped.push(item);
                    const idx = deduped.length - 1;
                    for (const identity of identities) {
                        indexByIdentity.set(identity, idx);
                    }
                    continue;
                }

                const target = deduped[existingIndex];
                if (!target.page_title && item.page_title) target.page_title = item.page_title;
                if (target.added_during !== 'added_later' && item.added_during === 'added_later') {
                    target.added_during = 'added_later';
                }
                if (!target.is_out_of_context && item.is_out_of_context) {
                    target.is_out_of_context = true;
                    if (!target.out_of_context_at) target.out_of_context_at = item.out_of_context_at || null;
                }
                if (!target.pending_reinjection && item.pending_reinjection) {
                    target.pending_reinjection = true;
                }
                if (!target.injection_message_id && item.injection_message_id) {
                    target.injection_message_id = item.injection_message_id;
                }
                if (!target.last_injected_at && item.last_injected_at) {
                    target.last_injected_at = item.last_injected_at;
                }
                for (const [field, value] of Object.entries(preserveSourceBinding(item))) {
                    if (!target[field]) target[field] = value;
                }
            }

            session.chat_documents_state = deduped;
            this.ensureSessionDocumentState(session);
            this.synchronizeSessionDocumentMetadataFromState(session);
        },
        getPendingContextDocumentsPayload(
            session = this.currentSession,
            { includeCatalogBindings = true } = {}
        ) {
            const pendingLocal = this.normalizePendingDocumentInjections(session);
            const merged = new Map();

            for (const item of pendingLocal) {
                if (!item || typeof item !== 'object') continue;
                if (!includeCatalogBindings && item.catalog_binding_only === true) continue;
                const path = (item.path == null ? '' : String(item.path)).trim();
                if (!path) continue;
                const coercedIsUser = this.coerceBoolean(item.is_user_document);
                const isUser = path.includes('/get_user_upload')
                    ? true
                    : (coercedIsUser === undefined || coercedIsUser === null ? false : !!coercedIsUser);
                const key = this.buildDocumentKey(path, isUser);
                const existing = merged.get(key);
                merged.set(key, {
                    path,
                    page_title: item.page_title == null ? '' : String(item.page_title),
                    is_user_document: isUser,
                    source_kind: String(item.source_kind || '').trim().slice(0, 64),
                    force_reinject: !!(item.force_reinject || (existing && existing.force_reinject)),
                    key,
                });
            }

            if (!merged.size) return [];
            return Array.from(merged.values()).map((item) => ({
                path: item.path,
                page_title: item.page_title || '',
                is_user_document: !!item.is_user_document,
                source_kind: item.source_kind || '',
                force_reinject: !!item.force_reinject,
            }));
        },
        getOutgoingMessageContextDocuments(session = this.currentSession) {
            return this.normalizeMessageContextDocumentsPayload(
                this.getPendingContextDocumentsPayload(session, {
                    includeCatalogBindings: false,
                })
            );
        },
        clearPendingContextDocuments(documentsToClear = null, session = this.currentSession) {
            if (!session || typeof session !== 'object') return;
            if (!Array.isArray(documentsToClear)) {
                session.pending_document_injections = [];
                return;
            }

            const clearedKeys = new Set(documentsToClear.map((item) => {
                if (!item || typeof item !== 'object') return '';
                const path = String(item.path || '').trim();
                if (!path) return '';
                const coercedIsUser = this.coerceBoolean(item.is_user_document);
                const isUser = path.includes('/get_user_upload')
                    ? true
                    : (coercedIsUser === undefined || coercedIsUser === null ? false : !!coercedIsUser);
                return this.buildDocumentKey(path, isUser);
            }).filter(Boolean));

            if (!clearedKeys.size) return;
            const pending = Array.isArray(session.pending_document_injections)
                ? session.pending_document_injections
                : [];
            session.pending_document_injections = pending.filter((item) => {
                if (!item || typeof item !== 'object') return false;
                const path = String(item.path || '').trim();
                if (!path) return false;
                const coercedIsUser = this.coerceBoolean(item.is_user_document);
                const isUser = path.includes('/get_user_upload')
                    ? true
                    : (coercedIsUser === undefined || coercedIsUser === null ? false : !!coercedIsUser);
                return !clearedKeys.has(this.buildDocumentKey(path, isUser));
            });
        },
        applySessionDocumentRows(rows, session = this.currentSession) {
            if (!session || typeof session !== 'object') return;

            const docs = Array.isArray(rows)
                ? rows.filter((row) => row && typeof row === 'object' && String(row.path || '').trim())
                : [];

            if (!docs.length) {
                session.path = null;
                session.page_title = null;
                session.is_user_document = null;
                session.is_multi_document = false;
                session.page_title_html = null;
                session.is_law = false;
                return;
            }

            if (docs.length === 1) {
                session.path = docs[0].path;
                session.page_title = docs[0].page_title || '';
                session.is_user_document = !!docs[0].is_user_document;
                session.is_multi_document = false;
                session.page_title_html = null;
                session.is_law = this.inferIsLawFromPath(session.path);
                return;
            }

            session.path = docs.map((doc) => doc.path);
            session.page_title = docs.map((doc) => doc.page_title || '');
            session.is_user_document = docs.map((doc) => !!doc.is_user_document);
            session.is_multi_document = true;
            session.page_title_html = "<ol style='margin-left:2%;'>" + docs.map((doc) => (
                `<li><a href='${doc.path}' class='text-secondary font-bold text-center'>${doc.page_title || ''}</a></li>`
            )).join('') + "</ol>";
            session.is_law = this.inferIsLawFromPath(session.path);
        },
        serializeChatDocumentsState(rawState) {
            const normalized = this.normalizeChatDocumentsStatePayload(rawState);
            return normalized.map((entry) => ({
                doc_id: entry.doc_id || entry.key || '',
                path: entry.path == null ? '' : String(entry.path),
                page_title: entry.page_title == null ? '' : String(entry.page_title),
                is_user_document: !!entry.is_user_document,
                ...preserveSourceBinding(entry),
                ...(isLazyCatalogDocument(entry)
                    ? { context_representation: LAZY_DOCUMENT_CONTEXT_REPRESENTATION }
                    : {}),
                added_during: entry.added_during === 'added_later' ? 'added_later' : 'initial',
                is_out_of_context: !!entry.is_out_of_context,
                pending_reinjection: !!entry.pending_reinjection,
                injection_message_id: entry.injection_message_id || null,
                out_of_context_at: entry.out_of_context_at || null,
                last_injected_at: entry.last_injected_at || null,
            }));
        },
        emitCurrentSessionDocumentPatch(session = this.currentSession) {
            if (!session || !session.uuid) return;
            this.$emit('sessionPatched', session.uuid, {
                path: session.path,
                page_title: session.page_title,
                is_user_document: session.is_user_document,
                is_law: session.is_law,
                is_multi_document: !!session.is_multi_document,
                page_title_html: session.page_title_html || null,
                chat_documents_state: Array.isArray(session.chat_documents_state) ? session.chat_documents_state : [],
                pending_document_injections: Array.isArray(session.pending_document_injections) ? session.pending_document_injections : [],
                chat_documents_state_status: session.chat_documents_state_status || '',
                chat_documents_state_persistable: session.chat_documents_state_persistable,
            });
        },
        async persistCurrentSessionDocumentMetadata({ replaceChatDocumentsState = false } = {}) {
            if (window.sync_enabled !== true) return { ok: true, typedErrorCode: '' };
            if (!this.currentSession || !this.currentSession.uuid) {
                return { ok: true, typedErrorCode: '' };
            }
            const targetSession = this.currentSession;
            const targetUuid = String(targetSession.uuid || '').trim();
            const persistenceQueues = this._documentMetadataPersistenceQueues || new Map();
            this._documentMetadataPersistenceQueues = persistenceQueues;
            const previousEntry = persistenceQueues.get(targetUuid);
            const requestVersion = Number((previousEntry && previousEntry.version) || 0) + 1;
            const isCurrentPersistence = () => {
                const currentEntry = persistenceQueues.get(targetUuid);
                return !!(currentEntry && currentEntry.version === requestVersion);
            };

            const persist = async () => {
                if (
                    !isCurrentPersistence()
                    || targetSession !== this.currentSession
                    || String(this.currentSession && this.currentSession.uuid || '').trim()
                        !== targetUuid
                ) {
                    return {
                        ok: true,
                        typedErrorCode: '',
                        authoritativeMetadataApplied: false,
                        stale: true,
                    };
                }
                try {
                    const headers = { 'Content-Type': 'application/json' };
                    try {
                        headers['device-uuid'] = new DeviceUUID().get();
                    } catch (e) { }

                    const response = await fetch('/sync/migrate/chats', {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({
                            chat_uuids: [targetUuid],
                            items: [{
                                uuid: targetUuid,
                                path: targetSession.path,
                                page_title: targetSession.page_title,
                                is_user_document: targetSession.is_user_document,
                                chat_documents_state: this.serializeChatDocumentsState(targetSession.chat_documents_state),
                                replace_chat_documents_state: !!replaceChatDocumentsState,
                            }],
                            activate: false,
                        })
                    });
                    const payload = await response.json().catch(() => ({}));
                    if (
                        !isCurrentPersistence()
                        || targetSession !== this.currentSession
                        || String(this.currentSession && this.currentSession.uuid || '').trim()
                            !== targetUuid
                    ) {
                        return {
                            ok: response.ok,
                            typedErrorCode: '',
                            payload,
                            authoritativeMetadataApplied: false,
                            stale: true,
                        };
                    }
                    const metadataResults = payload
                        && payload.document_metadata_results
                        && typeof payload.document_metadata_results === 'object'
                        && !Array.isArray(payload.document_metadata_results)
                        ? payload.document_metadata_results
                        : null;
                    const authoritativeMetadata = (
                        payload
                        && payload.document_metadata_result
                        && typeof payload.document_metadata_result === 'object'
                        && !Array.isArray(payload.document_metadata_result)
                    )
                        ? payload.document_metadata_result
                        : (metadataResults
                            ? metadataResults[targetUuid]
                            : null);
                    let authoritativeMetadataApplied = false;
                    if (authoritativeMetadata && typeof authoritativeMetadata === 'object') {
                        if (authoritativeMetadata.document_availability) {
                            this.applyDocumentAvailabilityFeedback(
                                {
                                    ...authoritativeMetadata.document_availability,
                                    pruned_count: authoritativeMetadata.pruned_count,
                                },
                                targetSession,
                            );
                        }
                        authoritativeMetadataApplied = this.applyAuthoritativeDocumentMetadata(
                            authoritativeMetadata,
                            targetSession,
                        );
                    }
                    const typedErrorCode = [
                        payload && payload.code,
                        payload && payload.error_code,
                        payload && payload.error,
                    ].map(value => String(value || '').trim()).find(value => [
                        'document_catalog_capacity_exceeded',
                        'document_reader_unavailable',
                        'chat_document_metadata_conflict',
                    ].includes(value)) || '';
                    return {
                        ok: response.ok,
                        typedErrorCode: response.ok ? '' : typedErrorCode,
                        payload,
                        authoritativeMetadataApplied,
                    };
                } catch (e) {
                    return { ok: false, typedErrorCode: '' };
                }
            };
            const previous = previousEntry ? previousEntry.promise : Promise.resolve();
            const operation = previous.then(persist, persist);
            persistenceQueues.set(targetUuid, {
                version: requestVersion,
                promise: operation,
            });
            try {
                return await operation;
            } finally {
                const currentEntry = persistenceQueues.get(targetUuid);
                if (currentEntry && currentEntry.promise === operation) {
                    persistenceQueues.delete(targetUuid);
                }
            }
        },
        async queueOutOfContextDocumentsForNextMessage() {
            if (!this.currentSession || typeof this.currentSession !== 'object') return;
            if (this.currentSession.is_call_log || this.replying) return;
            if (this.currentSession.chat_documents_state_persistable === false) return;

            const normalizedState = this.normalizeChatDocumentsStatePayload(
                Array.isArray(this.currentSession.chat_documents_state)
                    ? this.currentSession.chat_documents_state
                    : []
            );
            const normalizedPending = this.normalizePendingDocumentInjections(this.currentSession)
                .map((entry) => ({ ...entry }));
            const previousState = normalizedState.map(entry => ({ ...entry }));
            const previousPending = normalizedPending.map(entry => ({ ...entry }));
            const queued = queueRecoverableDocuments(normalizedState, normalizedPending);
            if (!queued.queuedCount) return;

            try {
                this.$set(this.currentSession, 'chat_documents_state', queued.chatDocumentsState);
                this.$set(this.currentSession, 'pending_document_injections', queued.pendingDocumentInjections);
            } catch (e) {
                this.currentSession.chat_documents_state = queued.chatDocumentsState;
                this.currentSession.pending_document_injections = queued.pendingDocumentInjections;
            }
            this.ensureSessionDocumentState(this.currentSession);
            this.synchronizeSessionDocumentMetadataFromState(this.currentSession);
            this.store();
            this.emitCurrentSessionDocumentPatch(this.currentSession);
            const persisted = await this.persistCurrentSessionDocumentMetadata({
                replaceChatDocumentsState: true,
            });
            if (
                persisted
                && persisted.typedErrorCode === 'chat_document_metadata_conflict'
                && persisted.authoritativeMetadataApplied !== true
            ) {
                try {
                    this.$set(this.currentSession, 'chat_documents_state', previousState);
                    this.$set(this.currentSession, 'pending_document_injections', previousPending);
                    this.$set(this.currentSession, 'chat_documents_state_persistable', false);
                } catch (e) {
                    this.currentSession.chat_documents_state = previousState;
                    this.currentSession.pending_document_injections = previousPending;
                    this.currentSession.chat_documents_state_persistable = false;
                }
                this.store();
                this.emitCurrentSessionDocumentPatch(this.currentSession);
                this.presentDocumentAvailabilityNotice({
                    tone: 'error',
                    title: 'No se reincluyeron los documentos',
                    detail: 'La información de documentos de este chat no se puede actualizar. Recarga la conversación antes de intentarlo de nuevo.',
                }, this.currentSession);
            }
        },
        async removePendingDocumentFromContext(docKey) {
            if (!this.currentSession || typeof this.currentSession !== 'object') return;
            if (!docKey) return;
            if (this.replying) return;

            const normalizedState = this.normalizeChatDocumentsStatePayload(
                Array.isArray(this.currentSession.chat_documents_state)
                    ? this.currentSession.chat_documents_state
                    : []
            );
            const target = normalizedState.find((entry) => entry && entry.key === docKey);
            if (!target) return;
            if (target.added_during !== 'added_later') return;
            const normalizedPending = this.normalizePendingDocumentInjections(this.currentSession);
            const hasQueuedReinjection = normalizedPending.some((entry) => (
                entry && entry.key === docKey && entry.force_reinject
            ));

            if (target.pending_reinjection || (target.is_out_of_context && hasQueuedReinjection)) {
                this.currentSession.chat_documents_state = normalizedState.map((entry) => (
                    entry && entry.key === docKey
                        ? { ...entry, pending_reinjection: false }
                        : entry
                ));
                this.currentSession.pending_document_injections = normalizedPending
                    .filter((entry) => entry.key !== docKey);
                this.ensureSessionDocumentState(this.currentSession);
                this.synchronizeSessionDocumentMetadataFromState(this.currentSession);
                this.store();
                this.emitCurrentSessionDocumentPatch(this.currentSession);
                await this.persistCurrentSessionDocumentMetadata({ replaceChatDocumentsState: true });
                return;
            }

            if (target.is_out_of_context || target.injection_message_id) return;

            const remainingRows = this.getSessionDocumentRows(this.currentSession)
                .filter((row) => row && row.key !== docKey);
            this.applySessionDocumentRows(remainingRows, this.currentSession);

            this.currentSession.chat_documents_state = normalizedState.filter((entry) => entry.key !== docKey);
            this.currentSession.pending_document_injections = this.normalizePendingDocumentInjections(this.currentSession)
                .filter((entry) => entry.key !== docKey);

            this.ensureSessionDocumentState(this.currentSession);
            this.synchronizeSessionDocumentMetadataFromState(this.currentSession);
            this.store();
            this.emitCurrentSessionDocumentPatch(this.currentSession);
            await this.persistCurrentSessionDocumentMetadata({ replaceChatDocumentsState: true });
        },
        inferIsLawFromPath(pathValue) {
            const items = Array.isArray(pathValue) ? pathValue : (pathValue ? [pathValue] : []);
            if (!items.length) return false;
            return items.some((entry) => {
                if (typeof entry !== 'string') return false;
                let normalized = String(entry).trim();
                if (!normalized) return false;

                try {
                    normalized = decodeURIComponent(normalized);
                } catch (e) { }

                try {
                    const parsed = new URL(normalized, window.location.origin);
                    const pathParam = parsed.searchParams.get('path');
                    if (pathParam) {
                        try {
                            normalized = decodeURIComponent(pathParam);
                        } catch (e) {
                            normalized = pathParam;
                        }
                    } else if (parsed.pathname && parsed.pathname !== '/') {
                        normalized = parsed.pathname;
                        try {
                            normalized = decodeURIComponent(normalized);
                        } catch (e) { }
                    }
                } catch (e) {
                    if (normalized.includes('path=')) {
                        try {
                            normalized = normalized.split('path=', 2)[1].split('&', 1)[0];
                            normalized = decodeURIComponent(normalized);
                        } catch (err) { }
                    }
                }

                try {
                    normalized = decodeURIComponent(normalized);
                } catch (e) { }

                normalized = normalized
                    .replace(/\\/g, '/')
                    .replace(/Legislación_dev/g, 'Legislación')
                    .trim()
                    .toLowerCase();

                return normalized === 'legislación' ||
                    normalized === 'legislacion' ||
                    normalized.startsWith('legislación/') ||
                    normalized.startsWith('legislacion/') ||
                    normalized.includes('/legislación/') ||
                    normalized.includes('/legislacion/');
            });
        },
        applyChatDocumentProjectionStatus(payload, session = this.currentSession) {
            if (!session || !payload || typeof payload !== 'object') return;
            if (!Object.prototype.hasOwnProperty.call(
                payload,
                'chat_documents_state_persistable',
            )) return;
            const persistable = this.coerceBoolean(
                payload.chat_documents_state_persistable,
            ) !== false;
            session.chat_documents_state_persistable = persistable;
            session.chat_documents_state_status = String(
                payload.chat_documents_state_status || ''
            );
            if (persistable) return;

            session.chat_documents_state = this.normalizeChatDocumentsStatePayload(
                session.chat_documents_state,
            ).map(entry => ({ ...entry, pending_reinjection: false }));
            session.pending_document_injections = this.normalizePendingDocumentInjections(
                session,
            ).filter(entry => entry && entry.force_reinject !== true);
        },
        applySessionMetadata(session, { serverData = {}, fallbackPath, fallbackPageTitle, fallbackIsUserDocument, fallbackIsLaw } = {}) {
            if (!session || typeof session !== 'object') return;
            const payload = serverData || {};

            const pathRaw = this.firstDefined(
                Object.prototype.hasOwnProperty.call(payload, 'path') ? payload.path : undefined,
                fallbackPath,
                session.path,
            );
            const normalizedPath = this.normalizePathPayload(pathRaw);
            if (normalizedPath !== undefined) {
                session.path = normalizedPath;
            }

            if (Object.prototype.hasOwnProperty.call(payload, 'is_multi_document')) {
                session.is_multi_document = this.coerceBoolean(payload.is_multi_document);
            } else {
                session.is_multi_document = Array.isArray(session.path);
            }

            if (session.is_multi_document && !Array.isArray(session.path)) {
                const parsedPath = this.normalizePathPayload(session.path);
                if (Array.isArray(parsedPath)) {
                    session.path = parsedPath;
                }
            }

            const pageTitleRaw = this.firstDefined(
                Object.prototype.hasOwnProperty.call(payload, 'page_title') ? payload.page_title : undefined,
                fallbackPageTitle,
                session.page_title,
            );
            const normalizedPageTitle = this.normalizePageTitlePayload(pageTitleRaw, session.is_multi_document);
            if (normalizedPageTitle !== undefined) {
                session.page_title = normalizedPageTitle;
            }

            const isUserDocRaw = this.firstDefined(
                Object.prototype.hasOwnProperty.call(payload, 'is_user_document') ? payload.is_user_document : undefined,
                fallbackIsUserDocument,
                session.is_user_document,
            );
            const normalizedIsUserDoc = this.normalizeIsUserDocumentPayload(isUserDocRaw, session.is_multi_document);
            if (normalizedIsUserDoc !== undefined) {
                session.is_user_document = normalizedIsUserDoc;
            }

            const explicitIsLawRaw = this.firstDefined(
                Object.prototype.hasOwnProperty.call(payload, 'is_law') ? payload.is_law : undefined,
                fallbackIsLaw,
            );
            if (explicitIsLawRaw !== undefined) {
                session.is_law = this.coerceBoolean(explicitIsLawRaw);
            } else {
                const inferredIsLaw = this.inferIsLawFromPath(session.path);
                const existingIsLaw = this.coerceBoolean(session.is_law);
                session.is_law = inferredIsLaw || existingIsLaw === true;
            }

            this.normalizeMultiDocumentSession(session);

            const docsStateRaw = this.firstDefined(
                Object.prototype.hasOwnProperty.call(payload, 'chat_documents_state') ? payload.chat_documents_state : undefined,
                session.chat_documents_state,
            );
            if (docsStateRaw !== undefined) {
                const pendingKeysToPreserve = new Set(
                    this.normalizePendingDocumentInjections(session)
                        .map((entry) => entry && entry.key)
                        .filter(Boolean)
                );
                const currentState = this.normalizeChatDocumentsStatePayload(session.chat_documents_state);
                const incomingState = this.normalizeChatDocumentsStatePayload(docsStateRaw);
                const mergedState = mergeMonotonicDocumentState(currentState, incomingState);
                if (pendingKeysToPreserve.size) {
                    const mergedKeys = new Set(mergedState.map((entry) => entry && entry.key).filter(Boolean));
                    currentState.forEach((entry) => {
                        if (
                            entry
                            && pendingKeysToPreserve.has(entry.key)
                            && !mergedKeys.has(entry.key)
                        ) {
                            mergedState.push(entry);
                            mergedKeys.add(entry.key);
                        }
                    });
                }
                session.chat_documents_state = mergedState;
            } else if (!Array.isArray(session.chat_documents_state)) {
                session.chat_documents_state = [];
            }
            this.ensureSessionDocumentState(session);
            this.synchronizeSessionDocumentMetadataFromState(session);

            const hasExplicitPendingQueue = Object.prototype.hasOwnProperty.call(payload, 'pending_document_injections');
            const hasServerPayload = !!(serverData && typeof serverData === 'object' && Object.keys(serverData).length);
            if (window.sync_enabled === true && hasServerPayload && !hasExplicitPendingQueue) {
                session.pending_document_injections = this.reconcilePendingInjectionsWithServerState(
                    session.pending_document_injections,
                    session,
                    { trustServerRemovals: serverData && serverData.__trust_server_pending_state === true },
                );
            } else {
                const pendingRaw = this.firstDefined(
                    hasExplicitPendingQueue ? payload.pending_document_injections : undefined,
                    session.pending_document_injections,
                );
                if (pendingRaw !== undefined) {
                    session.pending_document_injections = Array.isArray(pendingRaw) ? pendingRaw : [];
                } else if (!Array.isArray(session.pending_document_injections)) {
                    session.pending_document_injections = [];
                }
            }
            this.normalizePendingDocumentInjections(session);
            this.applyChatDocumentProjectionStatus(payload, session);

            if (Object.prototype.hasOwnProperty.call(payload, 'is_pinned')) {
                session.is_pinned = this.coerceBoolean(payload.is_pinned) ? true : false;
            }

            const docFlagCandidate = this.firstDefined(
                payload.is_document_chat,
                session.is_document_chat,
                session.path,
                session.is_user_document,
            );
            const docFlag = this.coerceBoolean(docFlagCandidate);
            session.is_document_chat = docFlag === undefined || docFlag === null ? false : docFlag;
        },
        // Explicitly release the per-chat lock for the current device
        async releaseChatLock(chatUuid = null) {
            try {
                const uuid = chatUuid || (this.currentSession && this.currentSession.uuid);
                if (!uuid) return;
                const deviceId = new DeviceUUID().get();
                await fetch('/chat/unlock', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ uuid: uuid, device_id: deviceId })
                });
            } catch (e) { /* best-effort */ }
        },
        // Check if Ably is available and ready for real-time updates
        isReceivingViaAbly() {
            // In Ably-only mode, we always expect Ably to be available
            return window.ably_realtime_client !== undefined;
        },
        confirmExit(event) {
            if (this.replying) {
                const message = 'El Abogado Virtual está procesando un mensaje. ¿Está seguro de que desea salir? Podría perder la conversación.';
                event.returnValue = message;  // Standard for most browsers
                return message;  // For some older browsers
            }
        },

        async switchSession(sessionKey = null, path = null, page_title = null, is_user_document = null, is_law = null, options = {}) {
            this.cancelDictation?.();
            if (this.replying) {
                alert("Por favor, espere a que el Abogado Virtual termine de responder antes de cambiar de chat.");
                return options.requireServerSuccess === true ? { ok: false, status: 409 } : undefined;
            }
            const switchGeneration = Number(this.sessionSwitchGeneration || 0) + 1;
            this.sessionSwitchGeneration = switchGeneration;
            this.ensureDocumentPreviewController().activate(sessionKey);
            this.documentAvailabilityNotice = null;
            this.documentAdditionCheckpoint = null;
            this.invalidateContinuationProjectionRecovery();
            this.clearOpenChatUuid();
            this.unseenIncomingCount = 0;
            this.collaborationDialogVisible = false;
            this.collaborationInviteStatus = '';
            this.collaborationParticipants = [];
            this.clearReplyTarget();
            this.clearCollaborationAiStatusDismissTimer();
            this.collaborationAiStatusDismissedKey = '';
            this.collaborationAiStatusVisibleKey = '';
            if (sessionKey) {
                const previousSessionUuid = String(this.currentSession && this.currentSession.uuid || '');
                const key = 'lexius_virtual_lawyer_chats';
                let chats = {};
                let canCacheChats = true;
                const fallbackSession = options.fallbackSession;
                if (fallbackSession?.uuid === sessionKey && fallbackSession.is_call_log) {
                    chats[sessionKey] = fallbackSession;
                    // This map contains only the incoming transcript, not the
                    // existing history. Never write it over the legacy cache.
                    canCacheChats = false;
                } else {
                    try {
                        const cached = JSON.parse(localStorage.getItem(key) ?? '{}');
                        if (!cached || typeof cached !== 'object' || Array.isArray(cached)) {
                            canCacheChats = false;
                        } else {
                            chats = cached;
                        }
                    } catch (error) {
                        // Cache access must not prevent loading server-backed chats.
                        canCacheChats = false;
                    }
                }
                const persistSessionCache = () => {
                    if (!canCacheChats || (window.sync_enabled === true && chats[sessionKey]?.is_call_log)) return;
                    try { localStorage.setItem(key, JSON.stringify(chats)); } catch (e) { }
                };
                let serverPayload = null;
                const staleCachedErrorNeedsCanonicalRepair = !!(
                    chats[sessionKey]
                    && chats[sessionKey].lastError === true
                    && (
                        typeof navigator === 'undefined'
                        || navigator.onLine === true
                    )
                );
                const fetchRequiredWithoutStaleRepair = !!(
                    window.sync_enabled === true
                    || !chats[sessionKey]
                    || options.forceServerFetch === true
                );
                const staleErrorRepairOnlyFetch = !!(
                    staleCachedErrorNeedsCanonicalRepair
                    && !fetchRequiredWithoutStaleRepair
                );

                // Prefer server conversation when sync is enabled, and fetch unknown
                // shared chats even for sync-disabled users after accepting invitations. A
                // cached terminal error also needs one authoritative online read so a
                // response completed on the server cannot leave the composer blocked.
                if (
                    fetchRequiredWithoutStaleRepair
                    || staleCachedErrorNeedsCanonicalRepair
                ) {
                    try {
                        const repairAbortController = (
                            staleErrorRepairOnlyFetch
                            && typeof AbortController !== 'undefined'
                        ) ? new AbortController() : null;
                        const repairAbortTimer = repairAbortController
                            ? setTimeout(
                                () => repairAbortController.abort(),
                                CANONICAL_CHAT_REFRESH_TIMEOUT_MS,
                            )
                            : null;
                        let r;
                        try {
                            r = await fetch(this.buildChatGetUrl(sessionKey), {
                                ...(repairAbortController ? { signal: repairAbortController.signal } : {}),
                            });
                        } finally {
                            if (repairAbortTimer) clearTimeout(repairAbortTimer);
                        }
                        if (switchGeneration !== this.sessionSwitchGeneration) {
                            return { ok: false, status: 409, stale: true };
                        }
                        if (!r.ok && options.requireServerSuccess === true) {
                            return { ok: false, status: r.status };
                        }
                        if (r.ok) {
                            const data = await r.json();
                            if (switchGeneration !== this.sessionSwitchGeneration) {
                                return { ok: false, status: 409, stale: true };
                            }
                            serverPayload = data;
                            this.toolReceiptServerConfirmedChats[String(sessionKey)] = true;
                            const normalized = this.normalizeConversationPayload(data) || [];
                            const inProgress = !!data.in_progress;
                            const wasCancelled = !!data.was_cancelled;
                            // Create minimal session object if absent
                            if (!chats[sessionKey]) {
                                // Prefer server-provided first_message or page_title for title; fallback to first user text
                                const firstUserText = (normalized.find(m => m.sender === 'user')?.text || '').trim();
                                const serverTitle = (typeof data.first_message === 'string' && data.first_message.trim()) ? data.first_message.trim()
                                    : (typeof data.page_title === 'string' && data.page_title.trim()) ? data.page_title.trim()
                                        : firstUserText || 'Chat';
                                chats[sessionKey] = { uuid: sessionKey, title: serverTitle, data: [], chat_pagination: null };
                            } else {
                                // If current title is missing or default, refresh it from server metadata
                                try {
                                    const currentTitle = (chats[sessionKey].title || '').trim();
                                    if (!currentTitle || currentTitle.toLowerCase() === 'chat') {
                                        const preferred = (typeof data.first_message === 'string' && data.first_message.trim()) ? data.first_message.trim()
                                            : (typeof data.page_title === 'string' && data.page_title.trim()) ? data.page_title.trim()
                                                : (normalized.find(m => m.sender === 'user')?.text || '').trim();
                                        if (preferred) {
                                            chats[sessionKey].title = preferred;
                                        }
                                    }
                                } catch (e) { }
                            }
                            // A successful chat fetch proves this UUID is a
                            // server chat. Retain that fact for reloads while
                            // leaving brand-new local UUIDs provisional.
                            chats[sessionKey].tool_receipts_server_confirmed = true;
                            const staleErrorRepaired = this.repairStaleChatErrorFromCanonical(
                                chats[sessionKey],
                                normalized,
                                data,
                            );
                            const useCanonicalConversation = (
                                !staleErrorRepairOnlyFetch || staleErrorRepaired
                            );
                            if (useCanonicalConversation) {
                                chats[sessionKey].data = normalized;
                            }
                            const serverToolReceipts = Array.isArray(data.tool_receipts)
                                ? data.tool_receipts.map(item => this.normalizeToolReceipt(item)).filter(Boolean)
                                : [];
                            this.rememberObservedToolReceiptVersions(
                                String(sessionKey),
                                data.tool_receipts,
                            );
                            chats[sessionKey].tool_receipts = mergeVersionedToolReceipts(
                                chats[sessionKey].tool_receipts || [],
                                serverToolReceipts,
                            );
                            // /chats/get does not expose the durable receipt
                            // cursor. Never pair its bounded preview with a
                            // cursor cached from an older, longer hydration.
                            chats[sessionKey].tool_receipts_cursor = '';
                            if (useCanonicalConversation) {
                                this.applyChatPaginationFromPayload(chats[sessionKey], data);
                            }
                            try {
                                chats[sessionKey].is_collaborative = !!data.is_collaborative;
                                chats[sessionKey].collaboration_role = data.collaboration_role || null;
                                chats[sessionKey].owner_username = data.owner_username || null;
                                this.collaborationParticipants = Array.isArray(data.participants) ? data.participants : [];
                            } catch (e) { }
                            // Preserve or infer call-log flag based on server model if available
                            try {
                                if (typeof data.model === 'string' && data.model.toLowerCase() === 'voice-call') {
                                    chats[sessionKey].is_call_log = true;
                                    chats[sessionKey].call_transcript_pending_save = false;
                                }
                            } catch (e) { }
                            // Persist transient state for placeholder logic right after switching
                            chats[sessionKey].inProgressStatus = {
                                inProgress,
                                wasCancelled,
                                runStatus: data.run_status || null,
                                reasoningActivities: Array.isArray(data.reasoning_activities)
                                    ? data.reasoning_activities
                                    : [],
                            };
                            // Use server-provided suggested questions if present
                            try {
                                if (
                                    useCanonicalConversation
                                    && Array.isArray(data.suggested_questions)
                                ) {
                                    chats[sessionKey].suggestedQuestions = data.suggested_questions;
                                }
                            } catch (e) { }
                            persistSessionCache();
                        }
                    } catch (e) {
                        if (switchGeneration !== this.sessionSwitchGeneration) {
                            return { ok: false, status: 409, stale: true };
                        }
                        if (options.requireServerSuccess === true) {
                            return { ok: false, status: 0 };
                        }
                    }
                }

                // If we are online now and a stale offline error message exists, clear it
                try {
                    if (typeof navigator !== 'undefined' && navigator.onLine === true && chats[sessionKey] && chats[sessionKey].lastError) {
                        const msg = String(chats[sessionKey].errorMessage || '');
                        if (msg.includes('Sin conexión a Internet')) {
                            chats[sessionKey].lastError = false;
                            chats[sessionKey].errorMessage = null;
                            persistSessionCache();
                        }
                    }
                } catch (e) { }

                // Ensure we always have a minimal local stub so UI can render even when offline
                if (!chats[sessionKey]) {
                    chats[sessionKey] = {
                        uuid: sessionKey,
                        title: 'Chat',
                        data: [],
                        created_at: null,
                        updated_at: null,
                        path: null,
                        page_title: null,
                        is_user_document: null,
                        is_law: false,
                        is_multi_document: false,
                        page_title_html: null,
                        suggestedQuestions: [],
                        is_call_log: false,
                        is_pinned: false,
                        chat_documents_state: [],
                        pending_document_injections: [],
                        chat_pagination: null,
                    };
                    // If we appear to be offline, surface a friendly message and disable sending
                    try {
                        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                            chats[sessionKey].lastError = true;
                            chats[sessionKey].errorMessage = 'Sin conexión a Internet. No se pudo cargar esta conversación. Conéctate e inténtalo de nuevo.';
                        }
                    } catch (e) { }
                    persistSessionCache();
                }

                const hasExplicitMetadata = (
                    path !== undefined && path !== null && path !== ''
                ) || (
                        page_title !== undefined && page_title !== null && page_title !== ''
                    ) || (
                        is_user_document !== undefined && is_user_document !== null
                    ) || (
                        is_law !== undefined && is_law !== null
                    );

                const shouldMergeMetadata = (window.sync_enabled === true && serverPayload) || hasExplicitMetadata;

                if (shouldMergeMetadata) {
                    this.applySessionMetadata(chats[sessionKey], {
                        serverData: serverPayload,
                        fallbackPath: path,
                        fallbackPageTitle: page_title,
                        fallbackIsUserDocument: is_user_document,
                        fallbackIsLaw: is_law,
                    });
                    persistSessionCache();
                }

                if (switchGeneration !== this.sessionSwitchGeneration) {
                    return { ok: false, status: 409, stale: true };
                }
                this.currentSession = chats[sessionKey];
                this.discoverDocumentPreviews(serverPayload, sessionKey);
                if (serverPayload) {
                    this.applyChatDocumentProjectionStatus(
                        serverPayload,
                        this.currentSession,
                    );
                }
                if (!Array.isArray(this.currentSession.tool_receipts)) this.currentSession.tool_receipts = [];
                if (typeof this.currentSession.tool_receipts_cursor !== 'string') this.currentSession.tool_receipts_cursor = '';
                this.ensureSessionDocumentState(this.currentSession);
                this.synchronizeSessionDocumentMetadataFromState(this.currentSession);
                this.normalizePendingDocumentInjections(this.currentSession);
                window.chat_uuid = this.currentSession.uuid;
                // A changed UUID is hydrated by the watcher below, but a
                // successful refresh of the already active chat still needs
                // to fetch any older paginated receipts.
                if (
                    serverPayload
                    && previousSessionUuid === String(this.currentSession.uuid || '')
                ) {
                    this.hydrateToolReceipts({ reset: true });
                }

                // Ensure suggestedQuestions is an array
                this.currentSession.suggestedQuestions = this.currentSession.suggestedQuestions || [];

                // Read the error status for the current chat
                this.lastError = this.currentSession.lastError ?? false;
                this.errorMessage = this.currentSession.errorMessage ?? null;

                const serverRunStatus = this.currentSession.inProgressStatus || {};
                this.syncAiRunStatusFromServer({
                    inProgress: !!serverRunStatus.inProgress,
                    wasCancelled: !!serverRunStatus.wasCancelled,
                    revealCompletedStatus: false,
                    runStatus: serverRunStatus.runStatus || null,
                    reasoningActivities: Array.isArray(serverRunStatus.reasoningActivities)
                        ? serverRunStatus.reasoningActivities
                        : [],
                });
            } else {
                let is_multi_document = Array.isArray(path);
                var page_title_html = null;

                // Check if the path is an array
                if (is_multi_document) {
                    // Make sure the page_title is also an array
                    if (!Array.isArray(page_title)) {
                        let error_message = "Ocurrió un error al cambiar de chat: el título de los documentos no es válido.";
                        alert(error_message);
                        throw new Error(error_message);
                    }

                    // Make sure is_user_document is an array if path is an array
                    if (is_user_document !== null && !Array.isArray(is_user_document)) {
                        // For backward compatibility, convert boolean to array
                        is_user_document = Array(path.length).fill(is_user_document);
                    }

                    // The page title should be a HTML string that contains all the page titles in an ordered list
                    page_title_html = "<ol style='margin-left:2%;'>";
                    for (let i = 0; i < path.length; i++) {
                        page_title_html += `<li><a href='${path[i]}' class='text-secondary font-bold text-center'>${page_title[i]}</a></li>`;
                    }
                    page_title_html += "</ol>";
                }

                this.currentSession = options.draftSession || {
                    uuid: null,
                    title: null,
                    data: [],
                    created_at: null,
                    updated_at: null,
                    path: path,
                    page_title: page_title,
                    is_user_document: is_user_document,
                    is_law: is_law,
                    is_multi_document: is_multi_document,
                    page_title_html: page_title_html,
                    suggestedQuestions: [], // Reset suggestedQuestions when switching sessions
                    is_call_log: false,
                    is_pinned: false,
                    is_collaborative: false,
                    collaboration_role: null,
                    owner_username: null,
                    aiRunStatus: null,
                    chat_documents_state: [],
                    pending_document_injections: [],
                    chat_pagination: null,
                };
                this.ensureSessionDocumentState(this.currentSession);
                this.synchronizeSessionDocumentMetadataFromState(this.currentSession);
                this.normalizePendingDocumentInjections(this.currentSession);
                this.lastError = false;
                this.errorMessage = null;
            }

            // Suggested questions are already set from the first server response when sync is enabled.

            this.computeSuggestedQuestionsForPath();
            this.initializeDocsHelpPopover();

            // Attach realtime listener for this chat
            try { this.attachChatRealtime(); } catch (e) { }
            try { this.loadCollaborationState(); } catch (e) { }
            this.updateOpenChatUuid();
            this.$nextTick(() => {
                this.attachChatboxScrollListener();
                this.scrollToLongChatAnchor();
                this.markCurrentChatReadDebounced();
                this.updateScrollToBottomVisibility();
            });
            return { ok: true, status: 200 };
        },
        // Realtime helpers
        queueChatRealtimeLifecycle(operation) {
            const previous = this.chatRealtimeLifecyclePromise
                && typeof this.chatRealtimeLifecyclePromise.then === 'function'
                ? this.chatRealtimeLifecyclePromise
                : Promise.resolve();
            const run = previous.catch(() => { }).then(operation);
            const settled = run.catch(() => { });
            this.chatRealtimeLifecyclePromise = settled;
            return settled;
        },
        unsubscribeChatRealtimeSnapshot(snapshot) {
            if (!snapshot || snapshot.unsubscribed) return;
            snapshot.unsubscribed = true;
            try {
                if (snapshot.chatChannel && typeof snapshot.chatChannel.unsubscribe === 'function') {
                    if (snapshot.chatSubscriptions.length) {
                        snapshot.chatSubscriptions.forEach(({ eventName, handler }) => {
                            snapshot.chatChannel.unsubscribe(eventName, handler);
                        });
                    }
                }
            } catch (e) { }
            try {
                if (
                    snapshot.presenceClient
                    && snapshot.presenceClient.connection
                    && typeof snapshot.presenceClient.connection.off === 'function'
                    && snapshot.presenceConnectionHandler
                ) {
                    snapshot.presenceClient.connection.off('connected', snapshot.presenceConnectionHandler);
                }
            } catch (e) { }
            try {
                const presence = snapshot.presenceChannel && snapshot.presenceChannel.presence;
                if (presence && typeof presence.unsubscribe === 'function') {
                    if (snapshot.presenceSubscriptions.length) {
                        snapshot.presenceSubscriptions.forEach(({ eventName, handler }) => {
                            presence.unsubscribe(eventName, handler);
                        });
                    }
                }
            } catch (e) { }
        },
        takeChatRealtimeSnapshot() {
            clearTimeout(this._typingStopTimer);
            this._typingStopTimer = null;
            const snapshot = {
                chatChannel: this.remoteChatChannel,
                chatSubscriptions: Array.isArray(this.remoteChatSubscriptions)
                    ? this.remoteChatSubscriptions.slice()
                    : [],
                presenceChannel: this.remotePresenceChannel,
                presenceSubscriptions: Array.isArray(this.remotePresenceSubscriptions)
                    ? this.remotePresenceSubscriptions.slice()
                    : [],
                presenceClient: this.remotePresenceClient,
                presenceConnectionHandler: this.remotePresenceConnectionHandler,
                unsubscribed: false,
            };
            this.unsubscribeChatRealtimeSnapshot(snapshot);
            this.remoteChatChannel = null;
            this.remoteChatSubscriptions = [];
            this.remotePresenceChannel = null;
            this.remotePresenceSubscriptions = [];
            this.remotePresenceClient = null;
            this.remotePresenceEntered = false;
            this.remotePresenceConnectionHandler = null;
            this.pendingTypingState = false;
            this.presentMembers = [];
            this.typingMembers = [];
            return snapshot;
        },
        async cleanupChatRealtimeSnapshot(snapshot) {
            if (!snapshot) return;
            this.unsubscribeChatRealtimeSnapshot(snapshot);
            await this.detachRealtimeChannel(snapshot.chatChannel);
            // Detaching an entered presence channel also removes the local
            // member. Do not race presence.leave() against channel.detach().
            await this.detachRealtimeChannel(snapshot.presenceChannel);
        },
        attachChatRealtime() {
            if (this.isDemoRecordingMode()) return Promise.resolve();
            const generation = Number(this.chatRealtimeGeneration || 0) + 1;
            this.chatRealtimeGeneration = generation;
            const previous = this.takeChatRealtimeSnapshot();
            const uuid = String(this.currentSession && this.currentSession.uuid || '');
            return this.queueChatRealtimeLifecycle(async () => {
                await this.cleanupChatRealtimeSnapshot(previous);
                if (
                    !uuid
                    || generation !== Number(this.chatRealtimeGeneration || 0)
                    || !this.currentSession
                    || String(this.currentSession.uuid || '') !== uuid
                ) return;
                this.updateOpenChatUuid();
                if (!window.ably_realtime_client && typeof initializeAbly === 'function') {
                    await initializeAbly();
                }
                if (generation !== Number(this.chatRealtimeGeneration || 0)) return;
                const chatClient = typeof window.getChatAblyClient === 'function'
                    ? await window.getChatAblyClient(uuid)
                    : window.ably_realtime_client;
                if (
                    !chatClient
                    || generation !== Number(this.chatRealtimeGeneration || 0)
                    || !this.currentSession
                    || String(this.currentSession.uuid || '') !== uuid
                ) return;

                const chatChannel = chatClient.channels.get(`chat-updates-${uuid}`);
                const userMessageChunkState = { groups: {}, completedIds: [] };
                const previewEpoch = this.ensureDocumentPreviewController().epoch;
                const chatSubscriptions = [
                    { eventName: 'document_preview', handler: message => {
                        const controller = this.ensureDocumentPreviewController();
                        if (generation !== Number(this.chatRealtimeGeneration || 0) || controller.epoch !== previewEpoch) return;
                        controller.notify({ ...(message && message.data || {}), chat_uuid: uuid });
                    } },
                    { eventName: 'chat_update', handler: message => {
                        try { this.onRemoteChatUpdate(message && message.data ? message.data : {}); } catch (e) { }
                    } },
                    { eventName: 'user_message', handler: message => {
                        try { this.onRemoteUserMessage(message && message.data ? message.data : {}); } catch (e) { }
                    } },
                    { eventName: 'user_message_chunk', handler: message => {
                        try {
                            const data = message && message.data ? message.data : {};
                            const expectedSequence = data.sequence !== undefined && data.sequence !== null
                                ? data.sequence
                                : null;
                            const chunkResult = consumeChunkedRealtimeUpdate(
                                userMessageChunkState,
                                data,
                                expectedSequence,
                                'text',
                                { preserveSequenceSiblings: true }
                            );
                            if (chunkResult.status === 'complete' && chunkResult.data) {
                                this.onRemoteUserMessage(chunkResult.data);
                            }
                        } catch (e) { }
                    } },
                    { eventName: 'group_message', handler: message => {
                        try { this.addGroupMessageToSession(message && message.data ? message.data : {}); } catch (e) { }
                    } },
                    { eventName: 'message_reaction', handler: message => {
                        try { this.applyMessageReaction(message && message.data ? message.data : {}); } catch (e) { }
                    } },
                    { eventName: 'chat_tool_update', handler: message => {
                        try { this.onRemoteToolUpdate(message && message.data ? message.data : {}); } catch (e) { }
                    } },
                    { eventName: 'artifact_version', handler: message => {
                        this.onRemoteArtifactVersion(message && message.data ? message.data : {}).catch(() => { });
                    } },
                    { eventName: 'invitation_accepted', handler: () => {
                        try { this.loadCollaborationState(); } catch (e) { }
                    } },
                    { eventName: 'invitation_rejected', handler: () => {
                        try { this.loadCollaborationState(); } catch (e) { }
                    } },
                    { eventName: 'participant_removed', handler: message => {
                        try { this.handleParticipantRemoved(message && message.data ? message.data : {}); } catch (e) { }
                    } },
                    { eventName: 'participant_left', handler: message => {
                        try { this.handleParticipantLeft(message && message.data ? message.data : {}); } catch (e) { }
                    } },
                ];
                const localSnapshot = {
                    chatChannel,
                    chatSubscriptions,
                    presenceChannel: null,
                    presenceSubscriptions: [],
                    presenceClient: null,
                    presenceConnectionHandler: null,
                    unsubscribed: false,
                };
                try {
                    for (const subscription of chatSubscriptions) {
                        const result = chatChannel.subscribe(subscription.eventName, subscription.handler);
                        if (result && typeof result.then === 'function') await result;
                        if (generation !== Number(this.chatRealtimeGeneration || 0)) break;
                    }
                    if (generation !== Number(this.chatRealtimeGeneration || 0)) {
                        await this.cleanupChatRealtimeSnapshot(localSnapshot);
                        return;
                    }
                    const presenceState = await this.attachPresenceRealtime(uuid, generation, chatClient);
                    if (presenceState) {
                        localSnapshot.presenceChannel = presenceState.channel;
                        localSnapshot.presenceSubscriptions = presenceState.subscriptions;
                        localSnapshot.presenceClient = presenceState.client;
                        localSnapshot.presenceConnectionHandler = presenceState.connectionHandler;
                    }
                    if (
                        generation !== Number(this.chatRealtimeGeneration || 0)
                        || !this.currentSession
                        || String(this.currentSession.uuid || '') !== uuid
                    ) {
                        await this.cleanupChatRealtimeSnapshot(localSnapshot);
                        return;
                    }
                    this.remoteChatChannel = chatChannel;
                    this.remoteChatSubscriptions = chatSubscriptions;
                    if (presenceState) {
                        this.remotePresenceChannel = presenceState.channel;
                        this.remotePresenceSubscriptions = presenceState.subscriptions;
                        this.remotePresenceClient = presenceState.client;
                        this.remotePresenceConnectionHandler = presenceState.connectionHandler;
                        this.remotePresenceEntered = presenceState.entered;
                    }
                    this.catchUpContinuationState({ resetReceipts: false });
                } catch (error) {
                    await this.cleanupChatRealtimeSnapshot(localSnapshot);
                }
            });
        },
        async attachPresenceRealtime(uuid, generation, providedClient = null) {
            if (!uuid) return null;
            const chatClient = providedClient || (
                typeof window.getChatAblyClient === 'function'
                    ? await window.getChatAblyClient(uuid)
                    : window.ably_realtime_client
            );
            if (!chatClient || generation !== Number(this.chatRealtimeGeneration || 0)) return null;
            const channel = chatClient.channels.get(`chat-presence-${uuid}`);
            const refreshPresence = async () => {
                try {
                    if (
                        !channel.presence
                        || generation !== Number(this.chatRealtimeGeneration || 0)
                        || !this.currentSession
                        || String(this.currentSession.uuid || '') !== String(uuid)
                    ) return;
                    const result = channel.presence.get();
                    const members = result && typeof result.then === 'function' ? await result : result;
                    if (generation !== Number(this.chatRealtimeGeneration || 0)) return;
                    const present = this.summarizePresenceMembers(members);
                    this.presentMembers = present.slice(0, 5);
                    this.typingMembers = present
                        .filter(data => data && data.typing === true)
                        .map(data => data.display_name || data.email || 'Participante')
                        .slice(0, 3);
                } catch (e) { }
            };
            const subscriptions = ['enter', 'update', 'leave'].map(eventName => ({
                eventName,
                handler: refreshPresence,
            }));
            let entered = false;
            let connectionHandler = null;
            const snapshot = {
                chatChannel: null,
                chatSubscriptions: [],
                presenceChannel: channel,
                presenceSubscriptions: subscriptions,
                presenceClient: chatClient,
                presenceConnectionHandler: null,
                unsubscribed: false,
            };
            try {
                if (channel.presence && typeof channel.presence.subscribe === 'function') {
                    for (const subscription of subscriptions) {
                        const result = channel.presence.subscribe(subscription.eventName, subscription.handler);
                        if (result && typeof result.then === 'function') await result;
                        if (generation !== Number(this.chatRealtimeGeneration || 0)) {
                            await this.cleanupChatRealtimeSnapshot(snapshot);
                            return null;
                        }
                    }
                }
                if (generation !== Number(this.chatRealtimeGeneration || 0)) {
                    await this.cleanupChatRealtimeSnapshot(snapshot);
                    return null;
                }
                if (channel.presence && typeof channel.presence.enter === 'function') {
                    await channel.presence.enter(this.buildPresencePayload(this.pendingTypingState));
                    entered = true;
                    await refreshPresence();
                }
                if (generation !== Number(this.chatRealtimeGeneration || 0)) {
                    await this.cleanupChatRealtimeSnapshot(snapshot);
                    return null;
                }
                if (chatClient.connection && typeof chatClient.connection.on === 'function') {
                    connectionHandler = async () => {
                        try {
                            if (
                                generation !== Number(this.chatRealtimeGeneration || 0)
                                || this.remotePresenceChannel !== channel
                                || !channel.presence
                                || typeof channel.presence.enter !== 'function'
                            ) return;
                            await channel.presence.enter(this.buildPresencePayload(this.pendingTypingState));
                            this.remotePresenceEntered = true;
                            await refreshPresence();
                            this._documentPreviewController?.reconnect();
                            this.catchUpContinuationState({ resetReceipts: true });
                        } catch (e) { }
                    };
                    snapshot.presenceConnectionHandler = connectionHandler;
                    chatClient.connection.on('connected', connectionHandler);
                }
                return { channel, subscriptions, client: chatClient, connectionHandler, entered };
            } catch (error) {
                await this.cleanupChatRealtimeSnapshot(snapshot);
                return null;
            }
        },
        async detachRealtimeChannel(channel) {
            if (!channel || typeof channel.detach !== 'function') return;
            try {
                const result = channel.detach();
                if (result && typeof result.then === 'function') await result;
            } catch (e) { }
        },
        detachChatRealtime() {
            this.chatRealtimeGeneration = Number(this.chatRealtimeGeneration || 0) + 1;
            const snapshot = this.takeChatRealtimeSnapshot();
            return this.queueChatRealtimeLifecycle(
                () => this.cleanupChatRealtimeSnapshot(snapshot)
            );
        },
        onServerChatUpdated(evt) {
            try {
                const uuid = evt && evt.detail && evt.detail.uuid;
                if (!uuid) return;
                if (!this.currentSession || this.currentSession.uuid !== uuid) return;
                if (this.hasActiveAiRun()) {
                    this.scheduleDeferredServerRefresh(uuid);
                    return;
                }
                // Refresh from server to reflect latest state
                const isCollaborative = this.coerceBoolean(this.currentSession.is_collaborative) === true;
                this.refreshConversationFromServer(uuid, { force: isCollaborative });
            } catch (e) { }
        },
        scheduleDeferredServerRefresh(uuid) {
            if (!uuid) return;
            if (this.deferredServerRefreshTimer) {
                clearTimeout(this.deferredServerRefreshTimer);
                this.deferredServerRefreshTimer = null;
            }
            this.deferredServerRefreshTimer = setTimeout(() => {
                this.deferredServerRefreshTimer = null;
                try {
                    if (!this.currentSession || String(this.currentSession.uuid || '') !== String(uuid)) return;
                    // A durable update is also the recovery signal for a
                    // missed terminal stream event. Read canonical state once
                    // while the placeholder is active; refresh preserves it
                    // when the server still reports `in_progress`.
                    this.refreshConversationFromServer(uuid, {
                        force: true,
                        preserveSuggestedQuestions: true,
                    });
                } catch (e) { }
            }, 3000);
        },
        onServerChatDeleted(evt) {
            try {
                const uuid = evt && evt.detail && evt.detail.uuid;
                if (!uuid) return;
                if (!this.currentSession || this.currentSession.uuid !== uuid) return;
                // If the deletion originated from this device, do not show toast or double-reset
                try {
                    const originDeviceId = evt && evt.detail && evt.detail.origin_device_id;
                    const localDeviceId = new DeviceUUID().get();
                    if (originDeviceId && localDeviceId && String(originDeviceId) === String(localDeviceId)) {
                        return; // Already handled locally
                    }
                } catch (e) { }
                // If the opened chat was deleted elsewhere, reset current session locally
                this.currentSession = {
                    uuid: null,
                    title: null,
                    data: [],
                    created_at: null,
                    updated_at: null,
                    path: null,
                    page_title: null,
                    is_user_document: null,
                    is_law: null,
                    is_multi_document: false,
                    page_title_html: null,
                    suggestedQuestions: [],
                    showPopup: false,
                    popupContent: '',
                    is_call_log: false,
                    is_pinned: false,
                    is_collaborative: false,
                    collaboration_role: null,
                    owner_username: null,
                    aiRunStatus: null,
                    chat_documents_state: [],
                    pending_document_injections: [],
                    chat_pagination: null,
                };
                this.lastError = false;
                this.errorMessage = null;
                // Persist cleared state
                this.store();

                // Show a toast to inform the user
                try {
                    const toast_element = $('#chatToast');
                    toast_element.find('.chatToastText').html(`
                        <div class="chatToastIcon">
                            <i class="anticon anticon-delete"></i>
                        </div>
                        <p>Este chat fue eliminado en otro dispositivo.</p>
                    `);
                    toast_element.show();
                    const toast = new bootstrap.Toast(toast_element);
                    toast.show();
                    toast_element.on('hidden.bs.toast', function () {
                        $(this).css('display', 'none');
                    });
                } catch (e) { }

                // Inform parent to clear the active session indicator
                try { this.$emit('sessionActivated', null); } catch (e) { }
            } catch (e) { }
        },
        canonicalAnswerMatchesPendingPlaceholder(canonicalMessages, placeholder) {
            if (!placeholder || !Array.isArray(canonicalMessages)) return false;
            const responseId = durableChatResponseId(
                placeholder.response_message_id || placeholder.message_id
            );
            const assistants = canonicalMessages.filter(message => (
                message
                && message.sender === 'bot'
                && String(message.text || '').trim()
                && (!responseId
                    || !durableChatResponseId(message.response_message_id || message.message_id)
                    || durableChatResponseId(message.response_message_id || message.message_id) === responseId)
            ));
            if (
                responseId
                && !this.isProvisionalLocalAssistantMessageId(responseId)
                && assistants.some(message => this.canonicalMessageIdentityValues(message).includes(responseId))
            ) return true;

            const turnId = String(placeholder.turn_id || '').trim();
            if (turnId && assistants.some(message => String(message.turn_id || '').trim() === turnId)) {
                return true;
            }

            const users = canonicalMessages.filter(message => message && message.sender === 'user');
            const requestId = String(placeholder.client_request_id || '').trim();
            let trigger = requestId
                ? users.find(message => String(message.client_request_id || '').trim() === requestId) || null
                : null;
            const anchorId = String(placeholder.ai_response_anchor_message_id || '').trim();
            if (!trigger && anchorId) {
                trigger = users.find(message => (
                    this.canonicalMessageIdentityValues(message).includes(anchorId)
                )) || null;
            }
            // New turns have a durable client request id.  If that exact id is
            // absent, an older same-text user turn must not donate its answer
            // to the provisional placeholder.
            if (requestId && !trigger) return false;
            if (!trigger && this.currentSession && Array.isArray(this.currentSession.data)) {
                let placeholderIndex = this.currentSession.data.indexOf(placeholder);
                if (placeholderIndex < 0) {
                    const placeholderIds = new Set([
                        placeholder.client_message_id,
                        placeholder.message_id,
                        placeholder.response_message_id,
                        placeholder.client_request_id,
                        placeholder.ai_response_anchor_message_id,
                    ].map(value => String(value || '').trim()).filter(Boolean));
                    const candidates = this.currentSession.data
                        .map((message, index) => ({ message, index }))
                        .filter(({ message }) => (
                            message
                            && message.sender === 'bot'
                            && [
                                message.client_message_id,
                                message.message_id,
                                message.response_message_id,
                                message.client_request_id,
                                message.ai_response_anchor_message_id,
                            ].map(value => String(value || '').trim()).some(id => id && placeholderIds.has(id))
                        ));
                    if (candidates.length === 1) placeholderIndex = candidates[0].index;
                }
                let localTrigger = null;
                for (let index = placeholderIndex - 1; index >= 0; index -= 1) {
                    const candidate = this.currentSession.data[index];
                    if (candidate && candidate.sender === 'user') {
                        localTrigger = candidate;
                        break;
                    }
                }
                if (localTrigger) {
                    const localIds = new Set(this.canonicalMessageIdentityValues(localTrigger));
                    const localTimestamp = String(localTrigger.server_timestamp || '').trim();
                    const localText = this.canonicalMessageText(localTrigger);
                    const candidates = users.filter(message => (
                        this.canonicalMessageIdentityValues(message).some(id => localIds.has(id))
                        || (
                            localTimestamp
                            && String(message.server_timestamp || '').trim() === localTimestamp
                        )
                        || (
                            localText
                            && this.canonicalMessageText(message) === localText
                        )
                    ));
                    if (candidates.length === 1) trigger = candidates[0];
                }
            }
            if (!trigger) return false;
            const triggerIndex = canonicalMessages.indexOf(trigger);
            for (let index = triggerIndex + 1; index < canonicalMessages.length; index += 1) {
                const message = canonicalMessages[index];
                if (!message) continue;
                if (message.sender === 'user') return false;
                if (assistants.includes(message)) return true;
            }
            return false;
        },
        canonicalTriggerMatchesPendingPlaceholder(canonicalMessages, placeholder) {
            if (!placeholder || !Array.isArray(canonicalMessages)) return false;
            const users = canonicalMessages.filter(message => message && message.sender === 'user');
            const requestId = String(placeholder.client_request_id || '').trim();
            if (requestId) {
                return users.some(message => (
                    String(message.client_request_id || '').trim() === requestId
                ));
            }
            const anchorId = String(placeholder.ai_response_anchor_message_id || '').trim();
            if (!anchorId || this.isProvisionalLocalAssistantMessageId(anchorId)) return false;
            return users.some(message => this.canonicalMessageIdentityValues(message).includes(anchorId));
        },
        runSnapshotMatchesPendingPlaceholder(runStatus = {}, placeholder = null) {
            if (!placeholder || !runStatus || typeof runStatus !== 'object') return false;
            const responseId = durableChatResponseId(placeholder.response_message_id || placeholder.message_id);
            const statusResponseId = durableChatResponseId(runStatus.response_message_id);
            if (responseId && statusResponseId && responseId !== statusResponseId) return false;
            const pairs = [
                ['client_request_id', 'client_request_id'],
                ['turn_id', 'turn_id'],
                ['response_message_id', 'response_message_id'],
                ['response_message_id', 'message_id'],
                ['trigger_message_id', 'ai_response_anchor_message_id'],
            ];
            return pairs.some(([statusField, messageField]) => {
                const statusValue = String(runStatus[statusField] || '').trim();
                const messageValue = String(placeholder[messageField] || '').trim();
                return !!statusValue && !!messageValue && statusValue === messageValue;
            });
        },
        buildPendingRecoverySnapshot(canonicalMessages, placeholder, serverData = {}, runStatus = {}) {
            if (!placeholder) {
                const durableWaitingReceipt = (
                    Array.isArray(serverData.tool_receipts)
                        ? serverData.tool_receipts
                        : []
                ).some(receipt => this.isDurableWaitingUserReceipt(receipt));
                if (!durableWaitingReceipt) return null;
                return {
                    uuid: String(this.currentSession && this.currentSession.uuid || ''),
                    placeholder: null,
                    requestId: '',
                    verdict: 'paused',
                    runStateIndeterminate: serverData.run_state_indeterminate === true,
                    receiptsComplete: serverData.tool_receipts_has_more !== true,
                };
            }
            const requestId = String(placeholder.client_request_id || '').trim();
            const triggerPersisted = this.canonicalTriggerMatchesPendingPlaceholder(
                canonicalMessages,
                placeholder,
            );
            const answerPersisted = this.canonicalAnswerMatchesPendingPlaceholder(
                canonicalMessages,
                placeholder,
            );
            const runCorrelated = this.runSnapshotMatchesPendingPlaceholder(runStatus, placeholder);
            const receipts = this.currentSession && Array.isArray(this.currentSession.tool_receipts)
                ? this.currentSession.tool_receipts
                : [];
            const placeholderIds = new Set([
                placeholder.response_message_id,
                placeholder.message_id,
                placeholder.turn_id,
                placeholder.ai_response_anchor_message_id,
                requestId,
            ].map(value => String(value || '').trim()).filter(Boolean));
            const receiptMatchesPlaceholder = (receipt) => [
                receipt && receipt.response_message_id,
                receipt && receipt.trigger_message_id,
                receipt && receipt.turn_id,
                receipt && receipt.client_request_id,
            ].map(value => String(value || '').trim()).some(value => (
                value && placeholderIds.has(value)
            ));
            const correlatedReceiptExists = receipts.some(receiptMatchesPlaceholder);
            const activePause = receipts.some(receipt => (
                this.isActivePauseReceipt(receipt)
                && (
                    receiptMatchesPlaceholder(receipt)
                    || triggerPersisted
                    || runCorrelated
                )
            ));
            const terminalRunEvidence = runCorrelated
                && this.runSnapshotHasTerminalEvidence(runStatus, serverData);
            const inProgress = serverData.in_progress === true;
            let verdict = 'outcome_unknown';
            if (answerPersisted || terminalRunEvidence) verdict = 'resolved';
            else if (activePause) verdict = 'paused';
            else if (inProgress && (runCorrelated || triggerPersisted)) verdict = 'active';
            else if (
                requestId
                && !triggerPersisted
                && !correlatedReceiptExists
                && serverData.run_state_indeterminate !== true
                && serverData.tool_receipts_has_more !== true
                && !inProgress
            ) verdict = 'proven_not_accepted';
            return {
                uuid: String(this.currentSession && this.currentSession.uuid || ''),
                placeholder,
                requestId,
                verdict,
                runStateIndeterminate: serverData.run_state_indeterminate === true,
                receiptsComplete: serverData.tool_receipts_has_more !== true,
            };
        },
        isActivePauseReceipt(receipt) {
            if (!receipt || !receipt.result_ref) return false;
            const resultType = String(receipt.result_ref.type || '');
            const status = String(receipt.status || '');
            if (resultType === 'context_request') return status === 'waiting_user';
            return resultType === 'continuation'
                && ['queued', 'leased', 'running', 'failed_retryable'].includes(status);
        },
        isDurableWaitingUserReceipt(receipt) {
            return !!(
                receipt
                && receipt.result_ref
                && String(receipt.result_ref.type || '') === 'context_request'
                && String(receipt.status || '') === 'waiting_user'
            );
        },
        runSnapshotHasTerminalEvidence(runStatus = {}, serverData = {}) {
            const status = String(runStatus && runStatus.status || '').toLowerCase();
            return !!(
                serverData.was_cancelled === true
                || runStatus.cancelled === true
                || runStatus.done === true
                || runStatus.is_complete === true
                || ['done', 'completed', 'error', 'failed', 'cancelled'].includes(status)
            );
        },
        async refreshConversationFromServer(uuid, {
            force = false,
            preserveSuggestedQuestions = false,
            preserveAiAnswer = null,
            markIncoming = false,
            timeoutMs = 0,
        } = {}) {
            if (this.isDemoRecordingMode()) return false;
            const targetSession = this.currentSession;
            const targetSessionUuid = String(targetSession && targetSession.uuid || '');
            const activeChatIsCollaborative = !!(
                uuid
                && targetSession
                && targetSessionUuid === String(uuid)
                && this.coerceBoolean(targetSession.is_collaborative) === true
            );
            if ((!force && window.sync_enabled !== true && !activeChatIsCollaborative) || !uuid) return false;
            const hadActiveAiRunAtStart = this.hasActiveAiRun();
            const preservedAiAnswer = preserveAiAnswer
                ? this.cloneChatMessageForUiWithPosition(preserveAiAnswer)
                : this.captureRecentCompletedAiAnswer();
            const preservedInFlightAi = this.captureInFlightAiPlaceholder();
            const preservedQueuedRequests = this.captureQueuedAiRequests();
            const preservedHumanOnlyMessages = this.captureServerAckedHumanOnlyMessages();
            let reconciliationViewportSnapshot = null;
            let shouldRestoreViewport = false;
            try {
                const activePagination = this.currentSession && this.currentSession.chat_pagination;
                const activeLoadedSpan = activePagination && activePagination.enabled
                    ? Math.max(0, (activePagination.end || 0) - (activePagination.start || 0))
                    : 0;
                const refreshLimit = Math.max(
                    this.longChatPageSize || 50,
                    Math.min(
                        100,
                        activeLoadedSpan || this.longChatPageSize || 50
                    )
                );
                const refreshParams = activePagination && activePagination.enabled
                    ? (!activePagination.has_next
                        ? { limit: refreshLimit }
                        : { start: activePagination.start, limit: refreshLimit })
                    : {};
                let refreshAbortController = null;
                let refreshAbortTimer = null;
                if (
                    Number(timeoutMs) > 0
                    && typeof AbortController !== 'undefined'
                ) {
                    refreshAbortController = new AbortController();
                    refreshAbortTimer = setTimeout(
                        () => refreshAbortController.abort(),
                        Number(timeoutMs),
                    );
                }
                let r;
                try {
                    r = await fetch(this.buildChatGetUrl(uuid, refreshParams), {
                        ...(refreshAbortController ? { signal: refreshAbortController.signal } : {}),
                    });
                } finally {
                    if (refreshAbortTimer) clearTimeout(refreshAbortTimer);
                }
                if (!r.ok) return false;
                const data = await r.json();
                if (
                    this.currentSession !== targetSession
                    || targetSessionUuid !== String(uuid)
                    || String(this.currentSession && this.currentSession.uuid || '') !== String(uuid)
                ) return false;
                if (!hadActiveAiRunAtStart && this.hasActiveAiRun()) {
                    this.scheduleDeferredServerRefresh(uuid);
                    return false;
                }
                this.discoverDocumentPreviews(data, uuid);
                // Capture immediately before the first canonical mutation. A
                // terminal event may have updated Vue state while this request
                // was in flight, so this is the DOM generation we must restore
                // after reconciliation completes.
                reconciliationViewportSnapshot = reconciliationViewportSnapshot
                    || this.captureChatViewportState();
                shouldRestoreViewport = !!reconciliationViewportSnapshot;
                try {
                    const serverCollaborative = this.coerceBoolean(data.is_collaborative) === true;
                    const currentCollaborative = this.coerceBoolean(this.currentSession.is_collaborative) === true;
                    const mergedCollaborative = serverCollaborative || currentCollaborative;
                    this.currentSession.is_collaborative = mergedCollaborative;
                    this.currentSession.collaboration_role = mergedCollaborative
                        ? (data.collaboration_role || this.currentSession.collaboration_role || null)
                        : null;
                    this.currentSession.owner_username = mergedCollaborative
                        ? (data.owner_username || this.currentSession.owner_username || null)
                        : null;
                    this.collaborationParticipants = mergedCollaborative && Array.isArray(data.participants)
                        ? data.participants
                        : (mergedCollaborative ? this.collaborationParticipants : []);
                } catch (e) { }
                const refreshedDocumentAvailability = data && data.document_availability;
                if (refreshedDocumentAvailability) {
                    this.applyDocumentAvailabilityFeedback({
                        ...refreshedDocumentAvailability,
                    });
                }
                const isSyncEnabled = window.sync_enabled === true;
                const shouldReconcileCanonicalConversation = isSyncEnabled
                    || this.coerceBoolean(this.currentSession.is_collaborative) === true
                    || force === true;
                if (isSyncEnabled) {
                    this.applySessionMetadata(this.currentSession, {
                        serverData: { ...data, __trust_server_pending_state: true },
                    });
                } else {
                    // In local-only mode, avoid clobbering link-friendly local document metadata
                    // with server-normalized storage paths. We only merge document context state.
                    if (Object.prototype.hasOwnProperty.call(data, 'chat_documents_state')) {
                        this.mergeChatDocumentsStateFromServer(data.chat_documents_state, this.currentSession);
                    }
                }
                this.applyChatDocumentProjectionStatus(data, this.currentSession);
                this.applyChatPaginationFromPayload(this.currentSession, data, { preserveAnchorState: true });
                this.reconcileToolReceipts(data.tool_receipts, null, {
                    viewportSnapshot: reconciliationViewportSnapshot,
                    restoreViewport: false,
                });
                const normalized = this.normalizeConversationPayload(data);
                if (Array.isArray(normalized) && shouldReconcileCanonicalConversation) {
                    const runStatus = data.run_status && typeof data.run_status === 'object'
                        ? data.run_status
                        : {};
                    this.repairStaleChatErrorFromCanonical(
                        this.currentSession,
                        normalized,
                        data,
                    );
                    const pendingRecoverySnapshot = this.buildPendingRecoverySnapshot(
                        normalized,
                        preservedInFlightAi,
                        data,
                        runStatus,
                    );
                    this.persistedWaitingRecoverySnapshot = pendingRecoverySnapshot;
                    const hasCanonicalAnswerForPending = !!(
                        pendingRecoverySnapshot
                        && pendingRecoverySnapshot.verdict === 'resolved'
                        && this.canonicalAnswerMatchesPendingPlaceholder(normalized, preservedInFlightAi)
                    );
                    const hasTerminalRunEvidence = !!(
                        pendingRecoverySnapshot
                        && pendingRecoverySnapshot.verdict === 'resolved'
                        && this.runSnapshotMatchesPendingPlaceholder(runStatus, preservedInFlightAi)
                        && this.runSnapshotHasTerminalEvidence(runStatus, data)
                    );
                    const preserveUnresolvedInFlight = !!(
                        preservedInFlightAi
                        && data.in_progress !== true
                        && !hasCanonicalAnswerForPending
                        && !hasTerminalRunEvidence
                    );
                    const activePauseReceipts = (
                        this.currentSession && Array.isArray(this.currentSession.tool_receipts)
                            ? this.currentSession.tool_receipts
                            : []
                    ).filter(receipt => this.isActivePauseReceipt(receipt));
                    const preserveWaitingPlaceholders = activePauseReceipts.length > 0;
                    const pendingRequestId = (
                        preservedInFlightAi
                        && (data.in_progress === true || preserveUnresolvedInFlight)
                    ) ? String(preservedInFlightAi.client_request_id || '').trim() : '';
                    const mergedQueued = this.mergeQueuedAiRequests(normalized, preservedQueuedRequests);
                    const refreshedMessages = this.isLongChatWindowed(this.currentSession)
                        ? this.mergeWindowedChatMessages(this.currentSession.data, mergedQueued)
                        : mergedQueued;
                    const canonicalMessages = this.mergeMissingHumanOnlyMessages(
                        refreshedMessages,
                        preservedHumanOnlyMessages
                    );
                    this.reconcileConversationMessagesInPlace(canonicalMessages, {
                        preserveLoadingPlaceholders: data.in_progress === true || preserveUnresolvedInFlight,
                        preserveWaitingPlaceholders,
                        preservePendingTurnRequestId: pendingRequestId,
                    });
                    this.reconcileChatPaginationWithServerPayload(this.currentSession, data);
                    const restoredAiAnswer = this.preserveCompletedAiAnswerAfterRefresh(preservedAiAnswer);
                    const hasPausedPlaceholder = !!(
                        this.currentSession
                        && Array.isArray(this.currentSession.data)
                        && this.currentSession.data.some(message => (
                            message
                            && message.sender === 'bot'
                            && (message.waitingForUser === true || message.resumingContext === true)
                        ))
                    );
                    const restoredInFlightAi = (
                        (data.in_progress === true || preserveUnresolvedInFlight)
                        && !hasPausedPlaceholder
                    )
                        ? this.preserveInFlightAiPlaceholderAfterRefresh(
                            preservedInFlightAi,
                            runStatus,
                            Array.isArray(data.reasoning_activities) ? data.reasoning_activities : []
                        )
                        : null;
                    const acceptanceRequestId = String(
                        preservedInFlightAi && preservedInFlightAi.client_request_id || ''
                    ).trim();
                    const acceptanceWasConfirmed = !!(
                        acceptanceRequestId
                        && (
                            normalized.some(message => (
                                message
                                && message.sender === 'user'
                                && String(message.client_request_id || '').trim() === acceptanceRequestId
                            ))
                            || String(runStatus.client_request_id || '').trim() === acceptanceRequestId
                        )
                    );
                    if (acceptanceWasConfirmed && this.currentSession && Array.isArray(this.currentSession.data)) {
                        this.currentSession.data.forEach((message) => {
                            if (
                                message
                                && message.sender === 'bot'
                                && String(message.client_request_id || '').trim() === acceptanceRequestId
                            ) message.awaitingInitialRequestAcceptance = false;
                        });
                    }
                    if (!restoredAiAnswer && !restoredInFlightAi) {
                        this.syncAiRunStatusFromServer({
                            inProgress: !!data.in_progress,
                            wasCancelled: !!data.was_cancelled,
                            runStatus: data.run_status || null,
                            reasoningActivities: Array.isArray(data.reasoning_activities)
                                ? data.reasoning_activities
                                : [],
                        });
                    }
                    if (!data.in_progress && !preserveUnresolvedInFlight && !preserveWaitingPlaceholders) {
                        this.reconcileCanonicalTerminalUi(uuid, normalized, data);
                    }
                    // Persist locally
                    this.store();
                }
                else if (this.currentSession && this.currentSession.uuid) {
                    try { this.store(); } catch (e) { }
                }
                if (targetSession.canonicalRecoveryPending === true) {
                    try {
                        this.$set(targetSession, 'canonicalRecoveryPending', false);
                    } catch (e) {
                        targetSession.canonicalRecoveryPending = false;
                    }
                    try { this.store(); } catch (e) { }
                }
                if (Array.isArray(data.suggested_questions)) {
                    const hasIncomingSuggestions = data.suggested_questions.length > 0;
                    const hasCurrentSuggestions = (
                        Array.isArray(this.currentSession.suggestedQuestions)
                        && this.currentSession.suggestedQuestions.length > 0
                    );
                    if (preserveSuggestedQuestions && hasCurrentSuggestions && !hasIncomingSuggestions) {
                        return true;
                    }
                    this.currentSession.suggestedQuestions = data.suggested_questions;
                }
                return true;
            } catch (e) {
                return false;
            } finally {
                if (shouldRestoreViewport) {
                    await this.restoreChatViewportState(reconciliationViewportSnapshot, { markIncoming });
                }
            }
        },
        normalizeConversationPayload(data) {
            try {
                const conv = Array.isArray(data.conversation) ? data.conversation : [];
                const chains = Array.isArray(data.reasoning_chains) ? data.reasoning_chains : [];
                const normalized = [];
                let assistantIndex = 0;
                let firstUserAdded = false;
                const isDocumentChat = this.coerceBoolean(data.is_document_chat) === true;
                const pagination = this.normalizeChatPaginationPayload(data);
                const windowStart = pagination ? pagination.start : null;
                const attachAbsoluteIndex = (message, rawIndex) => {
                    if (!message) return message;
                    // Keep a deterministic canonical position even for the
                    // non-windowed legacy payload. It is the identity fallback
                    // for rows that have neither a durable id nor a timestamp.
                    message._chat_absolute_index = (windowStart === null ? 0 : windowStart) + rawIndex;
                    return message;
                };
                const resolveTimestamp = (rawTimestamp) => {
                    try {
                        if (!rawTimestamp) return Date.now();
                        if (typeof rawTimestamp === 'number') {
                            return Number.isFinite(rawTimestamp) ? rawTimestamp : Date.now();
                        }
                        const parsed = Date.parse(rawTimestamp);
                        return Number.isNaN(parsed) ? Date.now() : parsed;
                    } catch (e) {
                        return Date.now();
                    }
                };
                for (let rawIndex = 0; rawIndex < conv.length; rawIndex++) {
                    const m = conv[rawIndex];
                    if (!m || !m.role) continue;
                    if (m.role === 'tool') continue;
                    if (m.role === 'assistant') {
                        const chain = Array.isArray(chains) && chains.length > assistantIndex ? chains[assistantIndex] : [];
                        assistantIndex++;
                        if (!m.content || (m.tool_calls && m.tool_calls.length)) continue;
                        const msg = {
                            sender: 'bot',
                            text: m.content,
                            timestamp: resolveTimestamp(m.timestamp),
                            server_timestamp: m.timestamp || '',
                            message_id: m.message_id || '',
                            response_message_id: m.response_message_id || m.message_id || '',
                            response_audio_source: m.response_audio_source || null,
                            client_request_id: m.client_request_id || '',
                            turn_id: m.turn_id || '',
                            run_id: m.run_id || '',
                            trigger_message_id: m.trigger_message_id || '',
                            ai_response_anchor_message_id: m.ai_response_anchor_message_id || '',
                            parent_message_id: m.parent_message_id || '',
                            user_message_id: m.user_message_id || '',
                            generation_failed: m.generation_failed === true,
                            failed: m.generation_failed === true,
                            retryable: m.generation_failed === true && m.recoverable !== false,
                        };
                        // An empty chain is also canonical: preserving a
                        // previous array here would leave stale reasoning
                        // steps visible after a terminal reconciliation.
                        msg.researchActivityHistory = Array.isArray(chain) ? chain : [];
                        attachAbsoluteIndex(msg, rawIndex);
                        this.messageReferenceId(msg);
                        normalized.push(msg);
                        continue;
                    }
                    if (m.role === 'system') {
                        if (!m.content) continue;
                        const msg = {
                            sender: 'system',
                            content: m.content,
                            timestamp: resolveTimestamp(m.timestamp),
                            server_timestamp: m.timestamp || '',
                            message_id: m.message_id || '',
                            turn_id: m.turn_id || '',
                        };
                        attachAbsoluteIndex(msg, rawIndex);
                        this.messageReferenceId(msg);
                        normalized.push(msg);
                        continue;
                    }
                    if (m.role === 'user') {
                        if (!m.content) continue;
                        if (m.chat_document_injection === true) {
                            continue;
                        }
                        if (isDocumentChat && !firstUserAdded && this.isDocumentIntroMessage(m.content)) {
                            continue;
                        }
                        const msg = {
                            sender: 'user',
                            text: m.content,
                            timestamp: resolveTimestamp(m.timestamp),
                            server_timestamp: m.timestamp || '',
                            sender_username: m.sender_username || '',
                            sender_email: m.sender_email || '',
                            sender_display_name: m.sender_display_name || m.sender_email || m.sender_username || '',
                            human_only: m.human_only === true || m.ai_queued === true,
                            chat_context_resume_message: m.chat_context_resume_message === true,
                            message_id: m.message_id || '',
                            client_request_id: m.client_request_id || '',
                            turn_id: m.turn_id || '',
                        };
                        if (m.ai_queued === true) {
                            msg.ai_queued = true;
                            msg.ai_queue_status = m.ai_queue_status || 'queued';
                            msg.ai_queue_reason = m.ai_queue_reason || 'Ya hay una respuesta del Abogado Virtual en curso.';
                            if (m.ai_queue_position !== undefined && m.ai_queue_position !== null) {
                                msg.ai_queue_position = m.ai_queue_position;
                            }
                        }
                        const contextDocuments = this.normalizeMessageContextDocumentsPayload(
                            m.context_documents || m.contextDocuments || m.chat_context_documents
                        );
                        if (contextDocuments.length) {
                            msg.contextDocuments = contextDocuments;
                        }
                        if (Array.isArray(m.reactions)) {
                            msg.reactions = m.reactions;
                        }
                        const replyTo = this.normalizeReplyToPayload(m.reply_to || m.replyTo);
                        if (replyTo) {
                            msg.reply_to = replyTo;
                        }
                        attachAbsoluteIndex(msg, rawIndex);
                        this.messageReferenceId(msg);
                        normalized.push(msg);
                        firstUserAdded = true;
                        continue;
                    }
                }
                return normalized;
            } catch (e) { return null; }
        },
        onRemoteChatUpdate(data) {
            try {
                if (!this.currentSession || !this.currentSession.uuid) return;
                if (data && data.uuid && String(data.uuid) !== String(this.currentSession.uuid)) return;
                if (data && data.chat_uuid && String(data.chat_uuid) !== String(this.currentSession.uuid)) return;
                if (
                    data
                    && data.text === ''
                    && data.done !== true
                    && data.is_complete !== true
                    && data.error !== true
                    && !['error', 'failed', 'cancelled', 'waiting_user'].includes(String(data.status || ''))
                    && data.cancelled !== true
                    && data.waiting_for_user !== true
                    && data.waitingForUser !== true
                    && data.phase !== 'waiting_user'
                    && Array.isArray(data.suggested_questions)
                    && data.response_message_id
                ) {
                    // Follow-ups can arrive after BotMessage unsubscribes on
                    // completion. Match only the latest settled response so a
                    // delayed batch cannot revive suggestions for an older turn.
                    // Its response id survives durable replays, whose attempt
                    // UUID replaces the live numeric run id.
                    const messages = this.currentSession.data || [];
                    const latest = messages.length ? messages[messages.length - 1] : null;
                    const responseId = String(data.response_message_id);
                    if (
                        latest
                        && latest.sender === 'bot'
                        && latest.isLoading !== true
                        && latest.waitingForUser !== true
                        && latest.resumingContext !== true
                        && latest.cancelled !== true
                        && [latest.message_id, latest.response_message_id].some(
                            id => id && String(id) === responseId
                        )
                    ) {
                        this.currentSession.suggestedQuestions = data.suggested_questions;
                        this.store();
                    }
                    return;
                }
                const isTerminalError = !!(
                    data
                    && (data.error === true || String(data.status || '') === 'error')
                );
                if (isTerminalError) {
                    const failedMessage = this.ensureTerminalErrorPlaceholder(data);
                    // A runless/unbound error cannot safely seize another
                    // device's active reply. The owning BotMessage still
                    // handles it locally; a later reload rehydrates a fenced
                    // failure when correlation is available.
                    if (!failedMessage) return;
                    this.handleMessageError(failedMessage);
                    this.handleIncomingAiResponseVisibility();
                    return;
                }
                const arr = this.currentSession.data || [];
                const last = arr.length ? arr[arr.length - 1] : null;
                const incomingRunId = data && data.run_id !== undefined && data.run_id !== null ? String(data.run_id) : null;
                const incomingResponseMessageId = data && data.response_message_id !== undefined && data.response_message_id !== null
                    ? String(data.response_message_id).trim()
                    : '';
                const incomingContinuationJobId = data && data.continuation_job_id !== undefined && data.continuation_job_id !== null
                    ? String(data.continuation_job_id).trim()
                    : '';
                const incomingText = data && typeof data.text === 'string' ? data.text : '';
                const lastRunId = last && last.run_id !== undefined && last.run_id !== null ? String(last.run_id) : null;
                const lastText = last && typeof last.text === 'string' ? last.text : '';
                const isWaitingForUser = !!(data && (
                    data.waiting_for_user === true
                    || data.waitingForUser === true
                    || data.phase === 'waiting_user'
                ));
                const isTerminal = !!(data && (data.done || data.is_complete || data.cancelled || isWaitingForUser));
                if (
                    !isWaitingForUser
                    && shouldDeferEmptySequencedTerminal(data)
                ) {
                    // The chunking protocol emits an empty numbered `done`
                    // marker after the authoritative text chunks. ChatLayout
                    // does not track their sequence, so it must leave this
                    // terminal to authoritative recovery. BotMessage verifies
                    // continuity for its active run; the layout also fetches a
                    // durable snapshot for older collaborative placeholders.
                    const deferredIndex = this.findLoadingBotIndexForRun(incomingRunId);
                    let deferredPlaceholder = deferredIndex >= 0
                        ? this.currentSession.data[deferredIndex]
                        : null;
                    if (deferredIndex < 0) {
                        if (this.isStaleRemoteChatUpdate(data)) return;
                        deferredPlaceholder = {
                            sender: 'bot',
                            text: '',
                            timestamp: Date.now(),
                            _is_optimistic: true,
                            isLoading: true,
                            researchActivityHistory: [],
                        };
                        if (incomingRunId) deferredPlaceholder.run_id = incomingRunId;
                        this.setBotResponseIdentity(
                            deferredPlaceholder,
                            incomingResponseMessageId,
                            incomingContinuationJobId,
                        );
                        this.currentSession.data.push(deferredPlaceholder);
                        this.reserveWindowedChatIndexes(deferredPlaceholder);
                        this.startAiRunStatus(deferredPlaceholder, { runId: incomingRunId });
                        this.schedulePendingAiReplyReveal(deferredPlaceholder, {
                            onlyIfFollowing: true,
                        });
                        try { this.store(); } catch (e) { }
                    }
                    this.reconcileEmptySequencedTerminal(data, deferredPlaceholder);
                    return;
                }
                // The durable continuation receives a UUID run id, whereas its
                // paused parent used the live numeric run id.  Complete the
                // exact paused bubble by reserved assistant id instead of
                // appending a second answer in local-only sessions.
                const waitingBotIndex = !isWaitingForUser && isTerminal
                    ? this.findWaitingBotIndexForResponseMessage(incomingResponseMessageId)
                    : -1;
                if (waitingBotIndex >= 0) {
                    const waitingBot = arr[waitingBotIndex];
                    this.setBotResponseIdentity(
                        waitingBot,
                        incomingResponseMessageId,
                        incomingContinuationJobId,
                    );
                    if (incomingRunId) waitingBot.run_id = incomingRunId;
                    if (incomingText) waitingBot.text = incomingText;
                    waitingBot.waitingForUser = false;
                    waitingBot.resumingContext = false;
                    waitingBot.isLoading = false;
                    if (data.cancelled) waitingBot.cancelled = true;
                    if (Array.isArray(data.suggested_questions)) {
                        this.currentSession.suggestedQuestions = data.suggested_questions;
                    }
                    this.handleMessageCompleted(waitingBot);
                    return;
                }
                // The immediate best-effort terminal publish and its durable
                // outbox replay intentionally carry the same reserved
                // assistant id. If another turn started between those
                // deliveries, update that exact completed response instead
                // of appending a duplicate at the end of the conversation.
                const completedResponse = incomingResponseMessageId
                    ? arr.find(item => (
                        item
                        && item.sender === 'bot'
                        && item.isLoading !== true
                        && item.waitingForUser !== true
                        && (
                            String(item.message_id || '').trim() === incomingResponseMessageId
                            || String(item.response_message_id || '').trim() === incomingResponseMessageId
                        )
                    ))
                    : null;
                if (completedResponse) {
                    if (isTerminal && incomingContinuationJobId) {
                        // Terminal continuation delivery is idempotent. Once
                        // this reserved response is settled, a duplicate
                        // realtime wake-up cannot rewrite it after a newer run
                        // has started; canonical projection is the only repair
                        // path for any disagreement.
                        this.completeChatRequestContextForMessage(completedResponse);
                        this.removeDuplicateLoadingBotsForResponse(
                            incomingResponseMessageId,
                            completedResponse,
                        );
                        if (this.repairStaleChatErrorFromRealtimeCompletion(completedResponse)) {
                            this.store();
                        }
                        this.finalizeChatUiAfterTerminal();
                        return;
                    }
                    this.setBotResponseIdentity(
                        completedResponse,
                        incomingResponseMessageId,
                        incomingContinuationJobId,
                    );
                    if (incomingRunId) completedResponse.run_id = incomingRunId;
                    if (incomingText) {
                        if (data.replace === true || isTerminal) {
                            completedResponse.text = incomingText;
                        } else {
                            completedResponse.text = String(completedResponse.text || '') + incomingText;
                        }
                    }
                    if (isTerminal) {
                        completedResponse.isLoading = false;
                        completedResponse.resumingContext = false;
                        if (data.cancelled) completedResponse.cancelled = true;
                    }
                    if (Array.isArray(data.suggested_questions)) {
                        this.currentSession.suggestedQuestions = data.suggested_questions;
                    }
                    if (isTerminal) {
                        this.completeChatRequestContextForMessage(completedResponse);
                        this.removeDuplicateLoadingBotsForResponse(
                            incomingResponseMessageId,
                            completedResponse,
                        );
                        if (!isWaitingForUser) {
                            this.repairStaleChatErrorFromRealtimeCompletion(completedResponse);
                        }
                    }
                    try {
                        this.store();
                        this.handleIncomingAiResponseVisibility();
                    } finally {
                        // A terminal replay can arrive after a durable receipt
                        // already rendered the answer and stopped BotMessage's
                        // subscription. Retire only its correlated request,
                        // then preserve any genuinely newer outstanding run.
                        if (isTerminal) this.finalizeChatUiAfterTerminal();
                    }
                    return;
                }
                // A tagged continuation terminal event without its paused
                // placeholder must not seize a newer live reply.  Receipt
                // hydration/reload remains the recovery path in that rare
                // missed-event case.
                if (incomingContinuationJobId && isTerminal) return;
                if (this.isStaleRemoteChatUpdate(data)) {
                    const exactLoadingIndex = (this.currentSession.data || []).findIndex(item => (
                        item
                        && item.sender === 'bot'
                        && item.isLoading === true
                        && incomingRunId
                        && item.run_id !== undefined
                        && item.run_id !== null
                        && String(item.run_id) === incomingRunId
                    ));
                    if (exactLoadingIndex < 0) return;
                }
                const loadingBotIndex = this.findLoadingBotIndexForRun(incomingRunId);
                const loadingBot = loadingBotIndex >= 0 ? arr[loadingBotIndex] : null;
                const hasLoadingBot = !!loadingBot;
                const matchesLastBotRun = incomingRunId && lastRunId && incomingRunId === lastRunId;
                const matchesLastBotText = incomingText && lastText && (
                    incomingText === lastText ||
                    incomingText.startsWith(lastText)
                );
                if (!hasLoadingBot && last && last.sender === 'bot' && isTerminal && (matchesLastBotRun || matchesLastBotText)) {
                    this.setBotResponseIdentity(
                        last,
                        incomingResponseMessageId,
                        incomingContinuationJobId,
                    );
                    if (incomingText) {
                        last.text = incomingText;
                    }
                    last.isLoading = false;
                    if (data.cancelled) {
                        last.cancelled = true;
                    }
                    if (isWaitingForUser) {
                        last.waitingForUser = true;
                    }
                    if (Array.isArray(data.suggested_questions)) {
                        this.currentSession.suggestedQuestions = data.suggested_questions;
                    }
                    if (isWaitingForUser) {
                        this.handleMessageWaitingForContext(last);
                        this.handleIncomingAiResponseVisibility();
                    } else {
                        this.handleMessageCompleted(last);
                    }
                    return;
                }

                if (!hasLoadingBot) {
                    if (this.isStaleRemoteChatUpdate(data)) return;
                    const existingLoadingIndex = this.findLoadingBotIndex();
                    const existingLoadingBot = existingLoadingIndex >= 0
                        ? arr[existingLoadingIndex]
                        : null;
                    if (
                        existingLoadingBot
                        && existingLoadingBot.awaitingInitialRequestAcceptance === true
                    ) {
                        return;
                    }
                    // Insert a streaming placeholder so BotMessage can subscribe and render updates
                    const placeholder = { sender: 'bot', text: '', timestamp: Date.now(), _is_optimistic: true, isLoading: true, researchActivityHistory: [] };
                    if (incomingRunId) placeholder.run_id = incomingRunId;
                    this.setBotResponseIdentity(
                        placeholder,
                        incomingResponseMessageId,
                        incomingContinuationJobId,
                    );
                    this.currentSession.data.push(placeholder);
                    this.reserveWindowedChatIndexes(placeholder);
                    this.startAiRunStatus(placeholder, { runId: incomingRunId });
                    this.schedulePendingAiReplyReveal(placeholder, {
                        onlyIfFollowing: true,
                    });

                    // If the first event is already terminal, apply it to the placeholder immediately
                    if (data) {
                        if (incomingText && data.done) {
                            placeholder.text = incomingText;
                        } else if (incomingText && incomingText !== '') {
                            // Append first delta
                            placeholder.text = (placeholder.text || '') + incomingText;
                        }
                        if (data.cancelled) {
                            placeholder.isLoading = false;
                            placeholder.cancelled = true;
                        } else if (isWaitingForUser) {
                            placeholder.isLoading = false;
                            placeholder.waitingForUser = true;
                        } else if (data.done || data.is_complete) {
                            placeholder.isLoading = false;
                        }
                    }

                    if (placeholder.isLoading === false && isTerminal) {
                        if (isWaitingForUser) {
                            this.handleMessageWaitingForContext(placeholder);
                            this.handleIncomingAiResponseVisibility();
                        } else {
                            this.handleMessageCompleted(placeholder);
                        }
                    } else {
                        this.store();
                    }
                    return;
                }

                if (isTerminal && loadingBot) {
                    if (loadingBot.awaitingInitialRequestAcceptance === true) return;
                    if (this.isStaleRemoteChatUpdate(data, loadingBot)) return;
                    if (incomingRunId) loadingBot.run_id = incomingRunId;
                    this.setBotResponseIdentity(
                        loadingBot,
                        incomingResponseMessageId,
                        incomingContinuationJobId,
                    );
                    if (incomingText) {
                        loadingBot.text = incomingText;
                    }
                    loadingBot.isLoading = false;
                    if (data.cancelled) {
                        loadingBot.cancelled = true;
                    }
                    if (isWaitingForUser) {
                        loadingBot.waitingForUser = true;
                    }
                    if (Array.isArray(data.suggested_questions)) {
                        this.currentSession.suggestedQuestions = data.suggested_questions;
                    }
                    if (isWaitingForUser) {
                        this.handleMessageWaitingForContext(loadingBot);
                        this.handleIncomingAiResponseVisibility();
                    } else {
                        this.handleMessageCompleted(loadingBot);
                    }
                }
            } catch (e) { }
        },
        onRemoteUserMessage(data) {
            try {
                if (!this.currentSession || !this.currentSession.uuid) return;
                if (data && data.uuid && String(data.uuid) !== String(this.currentSession.uuid)) return;
                if (data && data.chat_uuid && String(data.chat_uuid) !== String(this.currentSession.uuid)) return;
                // Ignore events from same device
                try {
                    const myDevice = new DeviceUUID().get();
                    if (data && data.device_id && String(data.device_id) === String(myDevice)) return;
                } catch (e) { }

                const text = (data && typeof data.text === 'string') ? data.text : '';
                if (!text) return;
                const contextDocuments = this.normalizeMessageContextDocumentsPayload(
                    data && (data.context_documents || data.contextDocuments || data.chat_context_documents)
                );

                const isEdit = !!(data && (data.edit_last_user === true || data.edit === true));
                const aiQueued = !!(data && data.ai_queued === true);
                const humanOnly = !!(data && (data.human_only === true || aiQueued));
                const serverTimestamp = data && data.timestamp ? String(data.timestamp) : '';
                const resolvedTimestamp = serverTimestamp ? Date.parse(serverTimestamp) || Date.now() : Date.now();
                const arr = this.currentSession.data || [];
                let pendingAiReply = null;

                if (isEdit) {
                    let userIdx = -1;
                    for (let i = arr.length - 1; i >= 0; i--) {
                        if (arr[i] && arr[i].sender === 'user') { userIdx = i; break; }
                    }
                    const editedMessage = {
                        sender: 'user',
                        text,
                        timestamp: resolvedTimestamp,
                        server_timestamp: serverTimestamp,
                        sender_username: data.sender_username || '',
                        sender_email: data.sender_email || '',
                        sender_display_name: data.sender_display_name || data.sender_email || data.sender_username || '',
                        human_only: humanOnly,
                        message_id: data.publish_id || data.message_id || '',
                        client_request_id: data.client_request_id || '',
                    };
                    this.applyQueuedAiFields(editedMessage, data);
                    if (contextDocuments.length) {
                        editedMessage.contextDocuments = contextDocuments;
                    }
                    const editedReplyTo = this.normalizeReplyToPayload(data.reply_to || data.replyTo);
                    if (editedReplyTo) {
                        editedMessage.reply_to = editedReplyTo;
                    }
                    this.messageReferenceId(editedMessage);
                    const duplicateKey = this.getMessageKeyForDuplicate(editedMessage);
                    const duplicateExists = arr.some(existing => (
                        existing
                        && existing.sender === 'user'
                        && this.getMessageKeyForDuplicate(existing) === duplicateKey
                    ));
                    if (duplicateExists) return;
                    this.currentSession.suggestedQuestions = [];
                    const replacedAbsoluteIndex = userIdx >= 0
                        ? this.parseChatPaginationNumber(arr[userIdx]._chat_absolute_index, null)
                        : null;
                    if (userIdx >= 0) {
                        // Remove everything from the last user message onwards
                        arr.splice(userIdx);
                    }
                    arr.push(editedMessage);
                    // Immediately insert a loading placeholder so research activity can start
                    const liveMessages = [editedMessage];
                    try {
                        if (!humanOnly) {
                            const placeholder = { sender: 'bot', text: '', timestamp: Date.now(), _is_optimistic: true, isLoading: true, researchActivityHistory: [] };
                            this.annotateAiReplyAnchor(placeholder, editedMessage);
                            arr.push(placeholder);
                            liveMessages.push(placeholder);
                            pendingAiReply = placeholder;
                            this.startAiRunStatus(placeholder, { invocationText: text });
                        }
                    } catch (e) { }
                    if (!this.applyWindowedEditIndexes(liveMessages, replacedAbsoluteIndex)) {
                        this.reserveWindowedChatIndexes(liveMessages);
                    }
                    this.currentSession.data = arr;
                    this.store();
                    this.handleIncomingMessageVisibility({
                        remote: true,
                        payload: data || {},
                        pendingAiReply,
                    });
                    return;
                }

                const userMessage = {
                    sender: 'user',
                    text,
                    timestamp: resolvedTimestamp,
                    server_timestamp: serverTimestamp,
                    sender_username: data.sender_username || '',
                    sender_email: data.sender_email || '',
                    sender_display_name: data.sender_display_name || data.sender_email || data.sender_username || '',
                    human_only: humanOnly,
                    message_id: data.publish_id || data.message_id || '',
                    client_request_id: data.client_request_id || '',
                };
                this.applyQueuedAiFields(userMessage, data);
                if (contextDocuments.length) {
                    userMessage.contextDocuments = contextDocuments;
                }
                const replyTo = this.normalizeReplyToPayload(data.reply_to || data.replyTo);
                if (replyTo) {
                    userMessage.reply_to = replyTo;
                }
                this.messageReferenceId(userMessage);
                const duplicateKey = this.getMessageKeyForDuplicate(userMessage);
                const duplicateExists = arr.some((existing) => (
                    existing && this.getMessageKeyForDuplicate(existing) === duplicateKey
                ));
                if (duplicateExists) return;
                // A genuinely new remote user turn invalidates the previous
                // assistant response's follow-up questions. Replayed Ably
                // events must leave a newer completed batch intact.
                this.currentSession.suggestedQuestions = [];
                this.insertMessageByServerTimestamp(userMessage);
                // Insert a loading bot placeholder immediately to show research activity ASAP
                const liveMessages = [userMessage];
                try {
                    const userMessageIndex = this.currentSession.data.indexOf(userMessage);
                    if (userMessageIndex >= 0 && !humanOnly) {
                        const placeholder = { sender: 'bot', text: '', timestamp: Date.now(), _is_optimistic: true, isLoading: true, researchActivityHistory: [] };
                        this.annotateAiReplyAnchor(placeholder, userMessage);
                        this.currentSession.data.splice(userMessageIndex + 1, 0, placeholder);
                        liveMessages.push(placeholder);
                        pendingAiReply = placeholder;
                        this.startAiRunStatus(placeholder, { invocationText: text });
                    }
                } catch (e) { }
                this.reserveWindowedChatIndexes(liveMessages);
                this.store();
                this.handleIncomingMessageVisibility({
                    remote: true,
                    payload: data || {},
                    pendingAiReply,
                });
            } catch (e) { }
        },
        handleCallTranscriptAdded(event) {
            const { sessionId, session } = event.detail || {};
            if (!sessionId) return;
            if (session?.uuid === sessionId && session.is_call_log) {
                this.$emit('callTranscriptReceived', session);
            }
            if (this.replying) return;

            // Check if chat modal is already visible
            const chatModal = document.getElementById('chatModal');
            const isChatModalVisible = chatModal && chatModal.classList.contains('show');

            // Add a delay to ensure proper timing for DataTables initialization
            this.$nextTick(() => {
                // If modal is already visible, use a shorter delay
                // If not visible, wait for it to be shown first
                const delay = isChatModalVisible ? 300 : 500;

                setTimeout(async () => {
                    if (this.replying) return;
                    // Switch to the new call log session.
                    const result = await this.switchSession(sessionId, null, null, null, null, { fallbackSession: session });
                    if (result?.ok !== true) return;
                    this.$emit('sessionActivated', sessionId);
                    // The parent can now keep the active transcript in the sidebar
                    // even when neither the server list nor localStorage is available.
                    this.$emit('sessionAdded');

                    // Force a re-check of DataTable initialization after another short delay
                    setTimeout(() => {
                        console.log("[ChatLayout] Triggering DataTable re-check for system messages");
                        // Trigger a custom event that SystemMessage components can listen to
                        window.dispatchEvent(new Event('recheckDataTables'));
                    }, 200);
                }, delay);
            });
        },
        computeSuggestedQuestionsForPath() {
            let messages_count = this.currentSession.data.length;

            if (this.currentSession.path && !this.replying && messages_count == 0 && this.currentSession.suggestedQuestions.length == 0) {
                this.normalizeMultiDocumentSession(this.currentSession);
                this.initializeDocsHelpPopover();
                const normalizedDocumentState = this.normalizeChatDocumentsStatePayload(
                    this.currentSession.chat_documents_state
                );
                const documentCount = Math.max(
                    normalizedDocumentState.length,
                    this.getSessionDocumentRows(this.currentSession).length,
                );
                if (documentCount > CHAT_DOCUMENT_LAZY_THRESHOLD) {
                    this.loadingSuggestedQuestions = false;
                    this.currentSession.suggestedQuestions = [];
                    return;
                }
                this.loadingSuggestedQuestions = true; // Set loading state to true
                // Show the suggested questions
                this.showSuggestedQuestions = true;
                var session_path_to_update = null;
                var page_title_to_update = null;
                var is_user_document_to_update = null;
                var is_multi_document_to_update = this.currentSession.is_multi_document;
                var self = this;

                if (is_multi_document_to_update) {
                    session_path_to_update = JSON.stringify(this.currentSession.path);
                    page_title_to_update = JSON.stringify(this.currentSession.page_title);
                    is_user_document_to_update = JSON.stringify(this.currentSession.is_user_document);
                }
                else {
                    session_path_to_update = this.currentSession.path;
                    page_title_to_update = this.currentSession.page_title;
                    is_user_document_to_update = this.currentSession.is_user_document;
                }

                $.ajax({
                    url: `/get_suggested_questions?path=${encodeURIComponent(session_path_to_update)}&page_title=${encodeURIComponent(page_title_to_update)}&is_user_document=${encodeURIComponent(is_user_document_to_update)}`,
                    method: 'GET',
                    success: (data) => {
                        self.loadingSuggestedQuestions = false; // Set loading state to false
                        console.log("Suggested questions:", data);
                        if (data && data.length > 0) {
                            let path_to_check = is_multi_document_to_update ? JSON.stringify(self.currentSession.path) : self.currentSession.path;
                            if (path_to_check == session_path_to_update) {
                                self.currentSession.suggestedQuestions = data;

                                // If not replying and there is more than one message, save the suggested questions
                                if (!self.replying && self.currentSession.data.length > 0) {
                                    self.store();
                                }
                            } else {
                                console.log("Suggested questions are not for the current session, ignoring...");
                            }
                        } else {
                            let path_to_check = is_multi_document_to_update ? JSON.stringify(self.currentSession.path) : self.currentSession.path;
                            if (path_to_check == session_path_to_update) {
                                self.currentSession.suggestedQuestions = [];
                            } else {
                                console.log("Suggested questions are not for the current session, ignoring...");
                            }
                        }
                    },
                    error: (error) => {
                        self.loadingSuggestedQuestions = false; // Set loading state to false
                        console.error("Error fetching suggested questions:", error);
                        if (self.currentSession.path == session_path_to_update) {
                            self.currentSession.suggestedQuestions = [];
                        } else {
                            console.log("Suggested questions are not for the current session, ignoring...");
                        }
                    }
                });
            }
        },
        showUploadDocumentHelpToast() {
            // Show notification toast to the user
            let toast_element = $('#innerChatToast');

            // Change the message of the toast
            let document_usage_guide_url = `https://lexius.io/${window.country_code}-guia-de-uso/#cargar-documentos`
            let document_usage_guide_anchor = `<a href='${document_usage_guide_url}' target='_blank' style='text-decoration: underline; color: blue;'>${document_usage_guide_url}</a>`;

            let toast_message = `Tenga en consideración que actualmente el Abogado Virtual solo puede trabajar con textos de hasta ${this.maximum_characters} caracteres. Para textos más largos, por favor revise como cargar un documento en nuestra guía de uso: ${document_usage_guide_anchor}`
            toast_element.find('.innerChatToastText p').html(toast_message);

            toast_element.show();

            let toast = new bootstrap.Toast(toast_element, { delay: 15000 });
            toast.show();

            toast_element.on('hidden.bs.toast', function () {
                $(this).css('display', 'none');
            });
        },
        showVirtualAssistantRecommendationToast() {
            // Show notification toast to the user
            let toast_element = $('#innerChatToast');

            // Change the message of the toast
            let virtual_assistant_guide_url = `https://lexius.io/${window.country_code}-guia-de-uso/#asistente`
            let virtual_assistant_guide_anchor = `<a href='${virtual_assistant_guide_url}' target='_blank' style='text-decoration: underline; color: blue;'>${virtual_assistant_guide_url}</a>`;

            if (this.currentSession.is_user_document) {
                var document_section = 'del documento'
            }
            else if (this.currentSession.is_law) {
                var document_section = 'del texto legal'
            }
            else {
                var document_section = 'de la sentencia'
            }
            let toast_message = `Si desea una síntesis de la totalidad ${document_section}, le recomendamos que utilice la función de <b>Asistente Virtual</b>. Puede encontrar más información en nuestra guía de uso: ${virtual_assistant_guide_anchor}`
            toast_element.find('.innerChatToastText p').html(toast_message);

            toast_element.show();

            let toast = new bootstrap.Toast(toast_element, { delay: 20000 });
            toast.show();

            toast_element.on('hidden.bs.toast', function () {
                $(this).css('display', 'none');
            });

        },
        async sendMessage(options = {}) {
            if (this.dictationStarting || this.dictationEditorBusy || this.composerSendInFlight) return;
            if (this.dictationBusy && !this.canSendCurrentMessage) return;
            this.composerSendInFlight = true;
            try {
                if (this.dictationBusy && !await this.finishDictationForSend()) return;
                if (this.contextResumeBusy) return;
                if (this.reviewIntentChecking) return;
                if (this.lastError) {
                    alert("Por favor, reintenta tu último mensaje fallido antes de añadir uno nuevo.");
                    return;
                }

                const outgoingOriginalMessage = String(this.message || '');
                const outgoingText = outgoingOriginalMessage.trim();
                const explicitReviewAction = String(options.reviewFollowupAction || '');
                const isCollaborativeAiInvocation = !!(
                    this.currentSession
                    && this.currentSession.uuid
                    && this.isCollaborativeChat
                    && outgoingText
                    && (explicitReviewAction || this.isAiInvocationText(outgoingText))
                );
                const isHumanOnlyCollaborationMessage = !!(
                    this.currentSession
                    && this.currentSession.uuid
                    && this.isCollaborativeChat
                    && outgoingText
                    && !explicitReviewAction
                    && !this.isAiInvocationText(outgoingText)
                );
                const requiresActiveReviewHydration = !!(
                    !options.reviewIntentResolved
                    && !explicitReviewAction
                    && !isHumanOnlyCollaborationMessage
                );

                if (this.activeReviewSubmitChecking && requiresActiveReviewHydration) return;

                if (this.replying && !isHumanOnlyCollaborationMessage && !isCollaborativeAiInvocation) {
                    alert("Por favor, espere a que el Abogado Virtual termine de responder antes de enviar un nuevo mensaje.");
                    return;
                }

                if (!outgoingText) {
                    this.replying = false;
                    return;
                }

                const hydrationChatUuid = String(this.currentSession && this.currentSession.uuid || '');
                if (requiresActiveReviewHydration && hydrationChatUuid) {
                    this.activeReviewSubmitChecking = true;
                    let hydration = null;
                    try {
                        hydration = await this.ensureActiveReviewHydratedForMessage(hydrationChatUuid);
                    } catch (error) {
                        hydration = { ready: false, stale: false };
                    } finally {
                        if (String(this.currentSession && this.currentSession.uuid || '') === hydrationChatUuid) {
                            this.activeReviewSubmitChecking = false;
                        }
                    }
                    const sameHydrationChat = (
                        String(this.currentSession && this.currentSession.uuid || '') === hydrationChatUuid
                    );
                    const sameHydrationDraft = String(this.message || '') === outgoingOriginalMessage;
                    const hydrationStale = !!(hydration && hydration.stale);
                    if (!sameHydrationChat || !sameHydrationDraft || hydrationStale) return;
                    if (!hydration || hydration.ready !== true) {
                        // A visibility/read failure must not trap an unrelated
                        // message in the composer. Continue as ordinary chat, but
                        // mark review intent resolved locally so a stale review
                        // cannot trigger classification or apply metadata.
                        this.activeReviewHydrationError = '';
                        options = {
                            ...options,
                            reviewIntentResolved: true,
                        };
                    }
                }

                let hasPendingShortcutUploads = false;
                try {
                    hasPendingShortcutUploads = typeof window.hasPendingChatShortcutUploads === 'function'
                        && window.hasPendingChatShortcutUploads() === true;
                } catch (error) {
                    console.warn('No se pudo comprobar el estado de las cargas iniciadas desde el chat.', error);
                }
                if (hasPendingShortcutUploads) {
                    if (typeof window.confirmChatMessageWithPendingUpload !== 'function') {
                        console.warn('No está disponible la confirmación para cargas pendientes del chat.');
                        return;
                    }
                    try {
                        const continueSending = await window.confirmChatMessageWithPendingUpload();
                        if (!continueSending) return;
                    } catch (error) {
                        console.error('No se pudo confirmar el envío con una carga pendiente.', error);
                        return;
                    }
                }

                if (this.dictationBusy) return;
                this.hideMentionSuggestions();

                // Hide the suggested questions if visible
                if (this.showSuggestedQuestions) {
                    this.toggleSuggestedQuestions();
                }

                let message_length = this.message.length;
                if (message_length > this.maximum_characters) {
                    alert(`El mensaje es demasiado largo (${message_length} caracteres). Por favor, reduce su longitud a ${this.maximum_characters} caracteres o menos.`);
                    this.replying = false;
                    this.showUploadDocumentHelpToast();
                    return;
                }

                // Long-time users can keep the familiar attach-and-send flow
                // while a document request is paused. Only bridge when there are
                // actual pending attachments; a typed-only message remains a new
                // turn, exactly as before.
                if (!isHumanOnlyCollaborationMessage
                    && await this.resumeWaitingContextWithPendingDocuments(outgoingText)) {
                    return;
                }

                if (isHumanOnlyCollaborationMessage) {
                    await this.sendCollaborationHumanMessage(outgoingText);
                    setTimeout(() => {
                        try { this.$refs.chatbox.scroll({ top: this.$refs.chatbox.scrollHeight, behavior: 'smooth' }); } catch (e) { }
                    }, 100);
                    return;
                }

                const reviewPresentation = getReviewFollowupPresentation(this.activeReview);
                if (
                    !options.reviewIntentResolved
                    && !explicitReviewAction
                    && reviewPresentation.requiresIntentResolution
                ) {
                    const routing = await this.resolveReviewIntent(outgoingOriginalMessage);
                    // The intent request never owns the composer. If the user
                    // edited the draft while it was being classified, keep the
                    // newer text and wait for an explicit send.
                    if (String(this.message || '') !== outgoingOriginalMessage) {
                        if (
                            this.reviewCheckpoint
                            && String(this.reviewCheckpoint.originalMessage || '') === outgoingOriginalMessage
                        ) {
                            this.reviewCheckpoint = null;
                        }
                        return;
                    }
                    if (!routing.proceed) return;
                    const routingApplySnapshot = routing.action === 'apply_review_changes'
                        ? validReviewApplySnapshot(routing.applySnapshot)
                        : null;
                    if (routing.action === 'apply_review_changes' && !routingApplySnapshot) {
                        this.reviewCheckpoint = this.reviewClarificationCheckpoint(
                            'ambiguous_review',
                            outgoingOriginalMessage,
                            'La revisión se actualizó. ¿Quieres aplicar sus recomendaciones al documento?',
                            this.activeReview,
                        );
                        return;
                    }
                    options = {
                        ...options,
                        reviewIntentResolved: true,
                        reviewFollowupAction: routing.action,
                        reviewId: String(routing.reviewId || ''),
                        ...(routingApplySnapshot ? {
                            reviewApplySnapshot: routingApplySnapshot,
                            reviewMaterialState: routingApplySnapshot.materialState,
                            reviewFindingIds: [...routingApplySnapshot.approvedFindingIds],
                            reviewApprovedCount: routingApplySnapshot.approvedCount,
                        } : {}),
                    };
                }

                if (this.dictationBusy) return;
                // Instead of clearing suggested questions, set loading state
                this.loadingSuggestedQuestions = true;

                // Check if this is a new chat before adding the message
                const isNewChat = !this.currentSession.uuid;

                const optimisticUserMessage = this.sessionAddMessage();
                if (optimisticUserMessage) {
                    options = { ...options, optimisticUserMessage };
                }
                this.store();

                // Let collaborative AI invocations reach /chat_stream so the server can run or queue them atomically.
                if (!isNewChat && !isCollaborativeAiInvocation) {
                    try {
                        const deviceId = new DeviceUUID().get();
                        const res = await fetch('/chat/lock', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ uuid: (this.currentSession.uuid || window.chat_uuid), device_id: deviceId })
                        });
                        if (res.status === 409) {
                            alert('Este chat está siendo usado en otro dispositivo. Inténtelo más tarde.');
                            this.replying = false;
                            return;
                        }
                    } catch (e) {
                        console.warn('No se pudo adquirir el bloqueo del chat. Continuando de todas formas.', e);
                    }
                }

                this.reply(this.message, options);

                // If this was a new chat, emit the events
                if (isNewChat) {
                    this.$emit('sessionAdded');
                    this.$emit('sessionActivated', this.currentSession.uuid);
                    try { this.attachChatRealtime(); } catch (e) { }
                }

                this.message = null;
                this.publishTypingState(false);
            } finally {
                this.composerSendInFlight = false;
            }
        },
        reply(message, options = {}) {
            this.replying = true;
            this.sessionAddReply(message, false, false, options);
        },
        async handleEditUserMessage({ original, updated }) {
            if (this.dictationBusy || this.dictationEditorBusy || this.composerSendInFlight) return;
            this.composerSendInFlight = true;
            try {
                // Capture whether the edit began while a response was running, but
                // do not cancel or mutate that turn until review routing succeeds.
                const wasReplying = this.replying;
                const replyRequestContext = this.activeChatRequestContext || null;
                // Only allow editing the latest user message
                let messages = this.currentSession.data;
                let userMessageIndex = -1;
                for (let i = messages.length - 1; i >= 0; i--) {
                    if (messages[i].sender === 'user') {
                        userMessageIndex = i;
                        break;
                    }
                }

                if (userMessageIndex === -1) return; // No user message found

                let userMessage = messages[userMessageIndex];
                const userMessageId = String(
                    userMessage.message_id || userMessage.client_message_id || ''
                );
                if (this.isCollaborativeChat) {
                    const senderEmail = String(userMessage.sender_email || '').trim().toLowerCase();
                    const currentEmail = this.getCurrentUserEmail();
                    if (!senderEmail || !currentEmail || senderEmail !== currentEmail) {
                        alert('Solo puedes editar tus propios mensajes.');
                        return;
                    }
                }

                const routing = await this.resolveExistingMessageReviewRouting(updated);
                const currentMessages = this.currentSession && Array.isArray(this.currentSession.data)
                    ? this.currentSession.data
                    : [];
                let currentUserMessageIndex = -1;
                for (let i = currentMessages.length - 1; i >= 0; i--) {
                    if (currentMessages[i] && currentMessages[i].sender === 'user') {
                        currentUserMessageIndex = i;
                        break;
                    }
                }
                const currentUserMessage = currentMessages[currentUserMessageIndex];
                const currentUserMessageId = String(
                    currentUserMessage
                    && (currentUserMessage.message_id || currentUserMessage.client_message_id)
                    || ''
                );
                const stillOwnsLatestUserMessage = !!(
                    currentUserMessage
                    && String(currentUserMessage.text || '') === String(original || '')
                    && (
                        userMessageId
                            ? currentUserMessageId === userMessageId
                            : currentUserMessage === userMessage
                    )
                );
                if (!stillOwnsLatestUserMessage) {
                    if (
                        this.reviewCheckpoint
                        && String(this.reviewCheckpoint.originalMessage || '') === String(updated || '')
                    ) {
                        this.reviewCheckpoint = null;
                    }
                    return;
                }
                if (!routing.proceed) return;

                messages = currentMessages;
                userMessageIndex = currentUserMessageIndex;
                userMessage = currentUserMessage;
                const replyStillActive = !!(
                    wasReplying
                    && (
                        replyRequestContext
                            ? this.isChatRequestContextActive(replyRequestContext)
                            : this.replying
                    )
                );

                if (replyStillActive) {
                    this.cancelPendingChatRequest();
                    try {
                        await fetch('/cancel_chat', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ uuid: window.chat_uuid })
                        });
                    } catch (e) {
                        console.error('Error cancelling ongoing chat before edit:', e);
                        // Continue regardless; UI/state will be reconciled below
                    }
                }
                this.clearLastChatError();

                // Store the complete conversation turn before making changes
                if (original !== updated) {
                    // Initialize conversation history if it doesn't exist
                    if (!userMessage.conversationHistory) {
                        userMessage.conversationHistory = [];
                    }

                    // Find the corresponding bot response (if any)
                    let botResponse = null;
                    if (userMessageIndex + 1 < messages.length && messages[userMessageIndex + 1].sender === 'bot') {
                        botResponse = messages[userMessageIndex + 1];
                    }

                    // Store the previous conversation turn
                    const conversationTurn = {
                        userText: original,
                        contextDocuments: Array.isArray(userMessage.contextDocuments)
                            ? this.normalizeMessageContextDocumentsPayload(userMessage.contextDocuments)
                            : [],
                        botResponse: botResponse ? {
                            text: botResponse.text,
                            timestamp: botResponse.timestamp,
                            researchActivityHistory: botResponse.researchActivityHistory || [],
                            cancelled: botResponse.cancelled || false
                        } : null,
                        editedAt: Date.now(),
                        wasCancelled: replyStillActive || (botResponse && botResponse.cancelled)
                    };

                    userMessage.conversationHistory.push(conversationTurn);
                }

                // Set the new text for the user message
                userMessage.text = updated;
                userMessage.contextDocuments = this.getOutgoingMessageContextDocuments(this.currentSession);

                // If the edited user message is the very first one in the chat,
                // update the chat title to keep it in sync
                try {
                    const userMessagesCount = messages.filter(m => m.sender === 'user').length;
                    if (userMessagesCount === 1) {
                        this.currentSession.title = updated;
                    }
                } catch (e) {
                    // No-op: defensive guard to avoid breaking edit flow
                }

                // Remove the trailing bot message if exists (previous response)
                let wasCancelled = replyStillActive; // if we just cancelled, ensure the flag is set
                if (this.currentSession.data.length > 0) {
                    const lastMsg = this.currentSession.data[this.currentSession.data.length - 1];
                    if (lastMsg.sender === 'bot') {
                        // Capture whether the last bot message was a cancelled one
                        wasCancelled = wasCancelled || !!lastMsg.cancelled;
                        this.currentSession.data.pop();
                    }
                }

                // Persist
                this.store();

                // Re-run the chat from this point with the edited text (single request)
                this.replying = true;
                this.sessionAddReply(updated, true, wasCancelled, {
                    ...(routing.options || {}),
                    optimisticUserMessage: userMessage,
                });
                // Editing a prior message does not consume the separate unsent draft.
            } finally {
                this.composerSendInFlight = false;
            }
        },

        prepareSession() {
            if (this.currentSession.uuid) {
                window.chat_uuid = this.currentSession.uuid;
                return;
            }

            this.currentSession.uuid = this.generateUUID();
            this.unconfirmedNewChatUuid = this.currentSession.uuid;
            if (
                this.documentAdditionCheckpoint
                && this.documentAdditionCheckpoint.session === this.currentSession
                && !String(this.documentAdditionCheckpoint.uuid || '')
            ) {
                this.documentAdditionCheckpoint.uuid = this.currentSession.uuid;
            }
            this.currentSession.title = this.message;
            this.currentSession.created_at = Date.now();

            window.chat_uuid = this.currentSession.uuid;
        },

        updateSession() {
            if (!this.currentSession.uuid) {
                return;
            }

            this.currentSession.updated_at = Date.now()
        },

        sessionAddMessage() {
            if (!this.message) {
                return;
            }

            const prepareSession = this.prepareSession();
            this.updateSession();

            const userMessage = {
                text: this.message,
                sender: 'user',
                timestamp: Date.now(),
                _is_optimistic: true,
                sender_email: this.getCurrentUserEmail(),
                sender_display_name: this.getCurrentUserEmail(),
                human_only: this.isCollaborativeChat && !this.isAiInvocationText(this.message),
            };
            const contextDocuments = this.getOutgoingMessageContextDocuments(this.currentSession);
            if (contextDocuments.length) {
                userMessage.contextDocuments = contextDocuments;
            }
            if (this.isCollaborativeChat && this.replyTarget) {
                userMessage.reply_to = this.replyTarget;
            }
            this.messageReferenceId(userMessage);

            this.currentSession.data.push(userMessage);
            this.reserveWindowedChatIndexes(userMessage);

            return userMessage;

        },

        isSameChatRequestContext(left, right) {
            return !!(
                left
                && right
                && left.generation === right.generation
            );
        },
        getTrackedChatRequestContext(context) {
            if (!context || !Array.isArray(this.chatRequestContexts)) {
                return null;
            }
            return this.chatRequestContexts.find(candidate => (
                this.isSameChatRequestContext(candidate, context)
            )) || null;
        },
        hasActiveChatRequestContexts() {
            return Array.isArray(this.chatRequestContexts)
                && this.chatRequestContexts.some(context => context && !context.cancelled && !context.completed);
        },
        findChatRequestContextForMessage(message) {
            return findTrackedContextForMessage(this.chatRequestContexts, message);
        },
        hasOutstandingChatWork() {
            const currentUuid = String(this.currentSession && this.currentSession.uuid || '');
            const hasTrackedContext = (this.chatRequestContexts || []).some(context => (
                context
                && context.cancelled !== true
                && context.completed !== true
                && String(context.uuid || '') === currentUuid
            ));
            const hasVisiblePlaceholder = !!(
                this.currentSession
                && Array.isArray(this.currentSession.data)
                && this.currentSession.data.some(message => (
                    message
                    && message.sender === 'bot'
                    && (
                        message.isLoading === true
                        || message.resumingContext === true
                    )
                ))
            );
            return hasTrackedContext || hasVisiblePlaceholder;
        },
        sessionNeedsCanonicalRecovery(session = this.currentSession) {
            return !!(
                session
                && session.uuid
                && (
                    session.canonicalRecoveryPending === true
                    || (
                        Array.isArray(session.data)
                        && session.data.some(message => (
                            message
                            && message.sender === 'bot'
                            && (
                                message.isLoading === true
                                || message.awaitingInitialRequestAcceptance === true
                                || message.resumingContext === true
                                || message.waitingForUser === true
                            )
                        ))
                    )
                )
            );
        },
        clearPersistedWaitingRecovery({ resetAttempt = true } = {}) {
            if (this.persistedWaitingRecoveryTimer) {
                clearTimeout(this.persistedWaitingRecoveryTimer);
                this.persistedWaitingRecoveryTimer = null;
            }
            this.persistedWaitingRecoveryGeneration = Number(
                this.persistedWaitingRecoveryGeneration || 0
            ) + 1;
            this.persistedWaitingRecoveryFlight = null;
            this.persistedWaitingRecoverySnapshot = null;
            if (resetAttempt) this.persistedWaitingRecoveryAttempt = 0;
        },
        schedulePersistedWaitingRecovery(session, uuid) {
            if (
                !session
                || !uuid
                || this.persistedWaitingRecoveryTimer
                || !this.sessionNeedsCanonicalRecovery(session)
            ) return false;
            const attempt = Math.max(0, Number(this.persistedWaitingRecoveryAttempt || 0));
            const delayMs = Math.min(
                PERSISTED_WAITING_RECOVERY_MAX_DELAY_MS,
                1000 * (2 ** Math.min(attempt, 5)),
            );
            this.persistedWaitingRecoveryTimer = setTimeout(() => {
                this.persistedWaitingRecoveryTimer = null;
                if (
                    this.currentSession !== session
                    || String(this.currentSession && this.currentSession.uuid || '') !== uuid
                ) return;
                this.reconcilePersistedWaitingSession();
            }, delayMs);
            return true;
        },
        finalizePersistedWaitingRecovery(snapshot = this.persistedWaitingRecoverySnapshot) {
            if (!this.currentSession || !Array.isArray(this.currentSession.data)) return false;
            let placeholder = snapshot && snapshot.placeholder;
            if (!placeholder || !this.currentSession.data.includes(placeholder)) {
                placeholder = this.captureInFlightAiPlaceholder();
            }
            if (
                !placeholder
                || placeholder.waitingForUser === true
                || placeholder.resumingContext === true
            ) return false;
            const retryable = !!(snapshot && snapshot.verdict === 'proven_not_accepted');
            placeholder.isLoading = false;
            placeholder.awaitingInitialRequestAcceptance = false;
            placeholder.waitingForUser = false;
            placeholder.resumingContext = false;
            placeholder.failed = true;
            placeholder.retryable = retryable;
            placeholder.text = retryable
                ? 'La solicitud no llegó al servidor. Puedes reintentarla.'
                : 'No pudimos confirmar si la solicitud llegó al servidor. Recarga la conversación antes de volver a enviarla.';
            try {
                this.$set(this.currentSession, 'canonicalRecoveryPending', !retryable);
            } catch (e) {
                this.currentSession.canonicalRecoveryPending = !retryable;
            }
            this.completeChatRequestContextForMessage(placeholder);
            this.markAiRunError(placeholder.text);
            this.finalizeChatUiAfterTerminal();
            try { this.store(); } catch (e) { }
            this.clearPersistedWaitingRecovery();
            return true;
        },
        reconcilePersistedWaitingSession() {
            const session = this.currentSession;
            // A new local request already has acceptance/recovery timers. The
            // UUID watcher runs before /chat_stream creates its server row;
            // fetching history here produces a premature 404. Reloaded pending
            // sessions have no local request context and still recover below.
            if ((this.chatRequestContexts || []).some(context => (
                context && context.uuid === String(session?.uuid || '')
                && !context.cancelled && !context.completed && context.accepted !== true
            ))) {
                this.clearPersistedWaitingRecovery();
                return false;
            }
            if (!this.sessionNeedsCanonicalRecovery(session)) {
                this.clearPersistedWaitingRecovery();
                return false;
            }
            const uuid = String(session.uuid || '');
            const existingFlight = this.persistedWaitingRecoveryFlight;
            if (existingFlight && existingFlight.session === session && existingFlight.uuid === uuid) {
                return true;
            }
            this.clearPersistedWaitingRecovery({ resetAttempt: false });
            const recoveryGeneration = Number(this.persistedWaitingRecoveryGeneration || 0);
            this.persistedWaitingRecoveryAttempt = Math.max(
                0,
                Number(this.persistedWaitingRecoveryAttempt || 0),
            ) + 1;
            const flight = Promise.resolve(this.refreshConversationFromServer(uuid, {
                force: true,
                preserveSuggestedQuestions: true,
                timeoutMs: CANONICAL_CHAT_REFRESH_TIMEOUT_MS,
            })).catch(() => false).then((refreshed) => {
                if (
                    this.currentSession !== session
                    || String(this.currentSession && this.currentSession.uuid || '') !== uuid
                ) return false;
                if (!this.sessionNeedsCanonicalRecovery(session)) {
                    this.clearPersistedWaitingRecovery();
                    return refreshed;
                }
                const snapshot = this.persistedWaitingRecoverySnapshot;
                if (snapshot && ['active', 'paused', 'resolved'].includes(snapshot.verdict)) {
                    this.clearPersistedWaitingRecovery();
                    return refreshed;
                }
                if (
                    Number(this.persistedWaitingRecoveryAttempt || 0)
                    >= PERSISTED_WAITING_RECOVERY_MAX_ATTEMPTS
                ) {
                    this.finalizePersistedWaitingRecovery(snapshot);
                    return refreshed;
                }
                this.schedulePersistedWaitingRecovery(session, uuid);
                return refreshed;
            }).finally(() => {
                if (Number(this.persistedWaitingRecoveryGeneration || 0) === recoveryGeneration) {
                    this.persistedWaitingRecoveryFlight = null;
                }
            });
            const flightState = { session, uuid, promise: flight };
            this.persistedWaitingRecoveryFlight = flightState;
            return true;
        },
        setChatSpinnerVisibility(visibility) {
            const spinner = document.getElementById('mini-spinner-lawyer');
            if (spinner) spinner.style.visibility = visibility;
        },
        completeChatRequestContextForMessage(message) {
            const context = this.findChatRequestContextForMessage(message);
            if (context) this.completeChatRequestContext(context);
            return context;
        },
        finalizeChatUiAfterTerminal() {
            const hasOutstandingWork = this.hasOutstandingChatWork();
            this.replying = hasOutstandingWork;
            this.loadingSuggestedQuestions = hasOutstandingWork;
            if (!hasOutstandingWork) {
                this.reviewApplyInFlight = null;
                window.dispatchEvent(new Event('stop-polling-research'));
                this.releaseChatLock();
            }
            // Cosmetic DOM state is deliberately last and null-safe so it can
            // never prevent timer/lock cleanup during route or modal remounts.
            this.setChatSpinnerVisibility(hasOutstandingWork ? 'visible' : 'hidden');
            return hasOutstandingWork;
        },
        reconcileCanonicalTerminalUi(chatUuid, canonicalMessages, serverData = {}) {
            const uuid = String(chatUuid || '').trim();
            if (
                !uuid
                || serverData.in_progress === true
                || !this.currentSession
                || String(this.currentSession.uuid || '') !== uuid
            ) return false;

            const canonical = Array.isArray(canonicalMessages) ? canonicalMessages : [];
            const canonicalAssistantIds = new Set(canonical
                .filter(message => message && message.sender === 'bot')
                .flatMap(message => this.canonicalMessageIdentityValues(message))
                .map(value => String(value || '').trim())
                .filter(Boolean));
            const activeContexts = (this.chatRequestContexts || []).filter(context => (
                context
                && context.cancelled !== true
                && context.completed !== true
                && String(context.uuid || '') === uuid
            ));
            const messageIdentityMatches = (message, expectedId) => {
                const identity = String(expectedId || '').trim();
                if (!message || !identity) return false;
                return [message.message_id, message.response_message_id]
                    .map(value => String(value || '').trim())
                    .filter(Boolean)
                    .includes(identity);
            };
            const hasTerminalEvidence = activeContexts.every((context) => {
                const responseMessageId = String(context.responseMessageId || '').trim();
                if (responseMessageId) {
                    return canonicalAssistantIds.has(responseMessageId);
                }

                // An acceptance-gap placeholder may not yet have either
                // reserved server id. If the narrowly anchor-matched object
                // now carries a canonical assistant id, it is equally strong
                // terminal evidence for that exact active request.
                const loadingMessage = context.loadingMessage;
                const loadingMessageId = String(
                    loadingMessage && loadingMessage.message_id || ''
                ).trim();
                if (
                    loadingMessage
                    && Array.isArray(this.currentSession.data)
                    && this.currentSession.data.includes(loadingMessage)
                    && loadingMessageId
                    && canonicalAssistantIds.has(loadingMessageId)
                ) {
                    return true;
                }

                // Legacy/cross-device projections may not retain the reserved
                // assistant ID. In that case, prove ancestry instead: the
                // active trigger user must be present and an assistant must
                // follow it in canonical order. A prior-turn assistant alone
                // is never terminal evidence for the active request.
                const triggerMessageId = String(context.triggerMessageId || '').trim();
                if (!triggerMessageId) return false;
                const triggerIndex = canonical.findIndex(message => (
                    message
                    && message.sender === 'user'
                    && messageIdentityMatches(message, triggerMessageId)
                ));
                if (triggerIndex < 0) return false;
                return canonical.slice(triggerIndex + 1).some(message => (
                    message && message.sender === 'bot'
                ));
            });
            if (activeContexts.length && !hasTerminalEvidence) return false;

            activeContexts.slice().forEach(context => this.completeChatRequestContext(context));

            if (Array.isArray(this.currentSession.data)) {
                const retainedMessages = this.currentSession.data.filter((message) => {
                    if (!message || message.sender !== 'bot') return true;
                    const ids = this.canonicalMessageIdentityValues(message);
                    if (ids.some(id => canonicalAssistantIds.has(id))) {
                        this.clearCanonicalAssistantTransientState(message);
                        return true;
                    }
                    if (message.isLoading !== true && message.resumingContext !== true) return true;
                    return false;
                });
                this.currentSession.data.splice(
                    0,
                    this.currentSession.data.length,
                    ...retainedMessages,
                );
            }

            this.contextResumeBusy = false;
            this.contextDocumentBindingState = 'idle';
            this.contextDocumentBindingError = '';
            const latestBot = this.findLatestBotMessage();
            if (latestBot && latestBot.isLoading !== true) {
                this.completeAiRunStatus(latestBot, {
                    cancelled: serverData.was_cancelled === true,
                    touchCompletion: false,
                    revealStatus: false,
                });
            }
            this.finalizeChatUiAfterTerminal();
            try { this.store(); } catch (e) { }
            return true;
        },
        isChatRequestContextActive(context) {
            const trackedContext = this.getTrackedChatRequestContext(context);
            if (
                !context
                || context.cancelled
                || !trackedContext
                || trackedContext.cancelled
                || trackedContext.completed
            ) {
                return false;
            }
            return !!(
                this.currentSession
                && String(this.currentSession.uuid || '') === context.uuid
            );
        },
        clearPendingChatRetryTimer(context = null) {
            const trackedContext = context
                ? this.getTrackedChatRequestContext(context)
                : this.activeChatRequestContext;
            if (context && !trackedContext) {
                return;
            }
            const retryTimer = trackedContext && trackedContext.retryTimer;
            if (retryTimer) {
                clearTimeout(retryTimer);
                trackedContext.retryTimer = null;
                if (context && context !== trackedContext) {
                    context.retryTimer = null;
                }
            }
            if (trackedContext) {
                trackedContext.hasPendingRetry = false;
            }
            if (context && context !== trackedContext) {
                context.hasPendingRetry = false;
            }
        },
        cancelPendingChatRequest(context = null, releaseRejectedLock = true) {
            const trackedContext = context
                ? this.getTrackedChatRequestContext(context)
                : null;
            if (context && !trackedContext) {
                return;
            }
            const contextsToCancel = trackedContext
                ? [trackedContext]
                : [...(this.chatRequestContexts || [])];
            contextsToCancel.forEach(activeContext => {
                const hadPendingRetry = !!activeContext.hasPendingRetry;
                this.clearPendingChatRetryTimer(activeContext);
                this.clearInitialRequestAcceptanceReconciliation(activeContext);
                this.clearInitialToolReceiptReconciliation(activeContext);
                const shouldReleaseLock = (
                    releaseRejectedLock
                    && hadPendingRetry
                    && activeContext.accepted !== true
                );
                activeContext.cancelled = true;
                if (shouldReleaseLock && activeContext.uuid) {
                    this.releaseChatLock(activeContext.uuid);
                }
            });
            const cancelledGenerations = new Set(
                contextsToCancel.map(item => item && item.generation)
            );
            this.chatRequestContexts = (this.chatRequestContexts || []).filter(item => (
                item && !cancelledGenerations.has(item.generation)
            ));
            this.activeChatRequestContext = this.chatRequestContexts.length
                ? this.chatRequestContexts[this.chatRequestContexts.length - 1]
                : null;
        },
        completeChatRequestContext(context) {
            const trackedContext = this.getTrackedChatRequestContext(context);
            if (!trackedContext) {
                return;
            }
            this.clearPendingChatRetryTimer(trackedContext);
            this.clearInitialRequestAcceptanceReconciliation(trackedContext);
            this.clearInitialToolReceiptReconciliation(trackedContext);
            trackedContext.completed = true;
            if (context !== trackedContext) {
                context.completed = true;
            }
            this.chatRequestContexts = this.chatRequestContexts.filter(item => (
                !this.isSameChatRequestContext(item, trackedContext)
            ));
            this.activeChatRequestContext = this.chatRequestContexts.length
                ? this.chatRequestContexts[this.chatRequestContexts.length - 1]
                : null;
        },
        scheduleChatRequestRetry(context, callback, delayMs) {
            if (!this.isChatRequestContextActive(context)) {
                return false;
            }
            this.clearPendingChatRetryTimer(context);
            const trackedContext = this.getTrackedChatRequestContext(context);
            if (!trackedContext) {
                return false;
            }
            context.hasPendingRetry = true;
            trackedContext.hasPendingRetry = true;
            const retryTimer = setTimeout(() => {
                trackedContext.retryTimer = null;
                context.hasPendingRetry = false;
                trackedContext.hasPendingRetry = false;
                if (!this.isChatRequestContextActive(context)) {
                    return;
                }
                callback();
            }, delayMs);
            context.retryTimer = retryTimer;
            trackedContext.retryTimer = retryTimer;
            return true;
        },

        sessionAddReply(message, edit_last_user = false, edit_after_cancel = false, options = {}) {
            if (!message) {
                return;
            }
            this.toolActionInlineStatus = '';
            this.toolActionInlineStatusToolRunId = '';

            const currentChatUuid = String(
                this.currentSession && this.currentSession.uuid || ''
            );
            const preserveConcurrentCollaborativeRequests = (
                this.isCollaborativeChat
                && (this.chatRequestContexts || []).some(context => (
                    context
                    && !context.cancelled
                    && !context.completed
                    && context.uuid === currentChatUuid
                ))
            );
            if (!preserveConcurrentCollaborativeRequests) {
                this.cancelPendingChatRequest(null, false);
            }
            this.prepareSession();
            this.updateSession();

            // Set global chat UUID for Ably
            window.chat_uuid = this.currentSession.uuid;

            // Set to replying status
            this.replying = true;
            this.chatResponseReceived = false;

            // Clear suggested questions
            this.currentSession.suggestedQuestions = [];

            // Start suggested questions loading
            this.loadingSuggestedQuestions = true;

            this.normalizeMultiDocumentSession(this.currentSession);
            this.ensureSessionDocumentState(this.currentSession);
            this.synchronizeSessionDocumentMetadataFromState(this.currentSession);

            const contextDocumentsToInject = !edit_last_user
                ? this.getPendingContextDocumentsPayload()
                : [];
            const originatingSession = this.currentSession;
            const originatingChatUuid = String(originatingSession.uuid || '');
            const documentAdditionCheckpointVersion = (
                typeof this.currentDocumentAdditionCheckpointVersion === 'function'
            ) ? this.currentDocumentAdditionCheckpointVersion(originatingSession) : null;
            const clientRequestId = String(options && options.clientRequestId || '').trim()
                || this.generateUUID();
            const exactOptimisticUserMessage = options && options.optimisticUserMessage;
            const matchingUserMessage = (
                exactOptimisticUserMessage
                && Array.isArray(originatingSession.data)
                && originatingSession.data.includes(exactOptimisticUserMessage)
            )
                ? exactOptimisticUserMessage
                : [...(Array.isArray(originatingSession.data) ? originatingSession.data : [])]
                    .reverse()
                    .find(item => item && item.sender === 'user' && String(item.text || '') === String(message));
            if (matchingUserMessage) {
                try {
                    this.$set(matchingUserMessage, 'client_request_id', clientRequestId);
                } catch (e) {
                    matchingUserMessage.client_request_id = clientRequestId;
                }
            }
            this.chatRequestGeneration += 1;
            const requestGeneration = this.chatRequestGeneration;
            const requestContext = {
                generation: requestGeneration,
                uuid: originatingChatUuid,
                accepted: false,
                cancelled: false,
                completed: false,
                hasPendingRetry: false,
                retryTimer: null,
                reconcileInitialToolReceipts: !(
                    options && options.reconcileInitialToolReceipts === false
                ),
                initialToolReceiptReconciliationTimer: null,
                initialAcceptanceReconciliationTimer: null,
                loadingMessage: null,
                responseMessageId: '',
                triggerMessageId: '',
                turnId: '',
                runId: '',
                clientRequestId,
                documentAdditionCheckpointVersion,
            };
            this.chatRequestContexts.push(requestContext);
            this.activeChatRequestContext = requestContext;

            const requestPayload = {
                uuid: originatingChatUuid,
                client_request_id: clientRequestId,
                message: message,
                agentic_tool_ui_version: 1,
                is_user_document: originatingSession.is_user_document,
                path: originatingSession.path,
                page_title: originatingSession.page_title,
                is_law: originatingSession.is_law,
                edit_last_user: !!edit_last_user,
                edit_after_cancel: !!edit_after_cancel,
            };
            if (options && options.reviewFollowupAction) {
                requestPayload.review_followup_action = String(options.reviewFollowupAction);
                requestPayload.review_id = String(options.reviewId || '');
                if (String(options.reviewFollowupAction) === 'apply_review_changes') {
                    requestPayload.review_finding_ids = Array.isArray(options.reviewFindingIds)
                        ? options.reviewFindingIds.map(value => String(value || ''))
                        : [];
                    requestPayload.review_material_state = String(options.reviewMaterialState || '');
                    requestPayload.review_approved_count = Number(options.reviewApprovedCount || 0);
                }
            }
            if (options && options.processQueuedAiRequest === true) {
                requestPayload.process_queued_ai_request = true;
                requestPayload.queued_message_timestamp = String(options.queuedMessageTimestamp || '').trim();
            }
            if (Array.isArray(contextDocumentsToInject) && contextDocumentsToInject.length > 0) {
                requestPayload.context_documents_to_inject = contextDocumentsToInject;
            }
            if (this.isCollaborativeChat && this.replyTarget) {
                requestPayload.reply_to = this.replyTarget;
            }
            const requestBody = JSON.stringify(requestPayload);

            let self = this;  // Capture the outer 'this' for use in inner functions

            // Add initial loading message
            const loadingMessage = {
                text: "",
                sender: "bot",
                timestamp: Date.now(),
                _is_optimistic: true,
                isLoading: true,
                awaitingInitialRequestAcceptance: true,
                client_request_id: clientRequestId,
                researchActivityHistory: []
            };
            requestContext.loadingMessage = loadingMessage;
            this.insertAiReplyAfterAnchor(loadingMessage, options, message);
            this.reserveWindowedChatIndexes(loadingMessage);
            this.startAiRunStatus(loadingMessage, { invocationText: message });
            // Every local entry path (send, edit, retry, or queued request)
            // inserts its optimistic reply here. Reveal it before realtime
            // activity can grow the response, then keep that position pinned.
            this.schedulePendingAiReplyReveal(loadingMessage);

            // Store the session immediately after adding the loading message
            this.store();

            // Hide the old spinner since we now have the loading message UI
            this.setChatSpinnerVisibility('visible');

            const selfRef = this;

            function saveMessage(message, is_status_check) {
                // Human-only group messages can be appended while AI is replying.
                // Keep updating the placeholder created for this AI turn.
                const currentMessage = loadingMessage;

                // Ensure we never lose the research activity history
                if (!currentMessage.researchActivityHistory) {
                    currentMessage.researchActivityHistory = [];
                }

                // Update only the mutable fields
                currentMessage.text = message;
                currentMessage.isLoading = false; // Once we have any content, consider loading finished

                // No need to touch the timestamp – keeping it stable avoids component re-creation.
                // The timestamp can still be useful for ordering and will keep its initial value.
            }

            this.adjustTextareaHeightAndCheckLength();

            // Making an AJAX call to the backend - Ably-only mode (no polling fallback)
            function sendRequest(max_retries = 1, current_try = 1, is_status_check = false, busyRetryCount = 0) {
                if (!self.isChatRequestContextActive(requestContext)) {
                    return;
                }
                var url = '/chat_stream';

                if (is_status_check) {
                    // Status check polling is disabled in Ably-only mode
                    console.log("Ably-only mode: skipping status check polling");
                    return;
                }
                else {
                    console.log("Sending initial message to chat...");
                }

                if (window.test_ai_fallback === true && typeof window.logAiFallbackTestEvent === 'function') {
                    window.logAiFallbackTestEvent('chat', 'initial-request-start', {
                        chat_uuid: originatingChatUuid,
                        message_length: typeof message === 'string' ? message.length : 0,
                        edit_last_user: !!edit_last_user,
                        edit_after_cancel: !!edit_after_cancel,
                        has_context_documents: Array.isArray(contextDocumentsToInject) && contextDocumentsToInject.length > 0,
                        current_try,
                        is_status_check: !!is_status_check,
                    });
                }

                self.scheduleInitialRequestAcceptanceReconciliation(
                    requestContext
                );
                $.ajax({
                    url: url,
                    method: 'POST',
                    data: requestBody,
                    contentType: "application/json",
                    headers: (function () {
                        try {
                            const deviceId = new DeviceUUID().get();
                            return { 'device-uuid': deviceId };
                        } catch (e) { return {}; }
                    })(),
                    success: (data) => {
                        if (!self.isChatRequestContextActive(requestContext)) {
                            return;
                        }
                        self.discoverDocumentPreviews(data, originatingChatUuid);
                        requestContext.accepted = true;
                        if (self.unconfirmedNewChatUuid === originatingChatUuid
                            && data?.trigger_message_id && data?.turn_id && data?.response_message_id) {
                            self.unconfirmedNewChatUuid = '';
                            self.scheduleActiveReviewRefresh(0);
                        }
                        self.clearPendingChatRetryTimer(requestContext);
                        self.clearInitialRequestAcceptanceReconciliation(
                            requestContext
                        );
                        self.reserveAcceptedChatResponseIdentity(requestContext, data);
                        loadingMessage.awaitingInitialRequestAcceptance = false;
                        const acceptedDocumentAvailability = data && (
                            data.document_availability
                            || (data.document_metadata_result && data.document_metadata_result.document_availability)
                        );
                        const currentDocumentCheckpointVersion = (
                            typeof self.currentDocumentAdditionCheckpointVersion === 'function'
                        ) ? self.currentDocumentAdditionCheckpointVersion(originatingSession) : null;
                        const documentCheckpointStillMatches = (
                            documentAdditionCheckpointVersion === null
                                ? currentDocumentCheckpointVersion === null
                                : Number(currentDocumentCheckpointVersion)
                                    === Number(documentAdditionCheckpointVersion)
                        );
                        if (acceptedDocumentAvailability && documentCheckpointStillMatches) {
                            self.applyDocumentAvailabilityFeedback(
                                {
                                    ...acceptedDocumentAvailability,
                                    pruned_count: data.document_metadata_result
                                        && data.document_metadata_result.pruned_count,
                                },
                                originatingSession
                            );
                        }
                        if (
                            data
                            && data.document_metadata_result
                            && typeof data.document_metadata_result === 'object'
                            && documentCheckpointStillMatches
                        ) {
                            self.applyAuthoritativeDocumentMetadata(
                                data.document_metadata_result,
                                originatingSession,
                            );
                        }
                        self.clearDocumentAdditionCheckpoint(
                            originatingChatUuid,
                            documentAdditionCheckpointVersion,
                        );
                        self.scheduleInitialToolReceiptReconciliation(requestContext);
                        if (window.test_ai_fallback === true && typeof window.logAiFallbackTestEvent === 'function') {
                            window.logAiFallbackTestEvent('chat', 'initial-request-success', {
                                chat_uuid: originatingChatUuid,
                                done: !!(data && data.done),
                                message_length: data && typeof data.message === 'string' ? data.message.length : 0,
                                suggested_questions_count: data && Array.isArray(data.suggested_questions) ? data.suggested_questions.length : 0,
                                current_try,
                                is_status_check: !!is_status_check,
                            });
                        }
                        if (!is_status_check) {
                            // First response from chat_stream - Ably will handle the rest
                            self.chatResponseReceived = true;
                            console.log("ChatLayout: Initial request accepted; BotMessage will prefer Ably updates and may fall back to status polling if liveness drops");
                            if (Array.isArray(contextDocumentsToInject) && contextDocumentsToInject.length > 0) {
                                self.clearPendingContextDocuments(
                                    contextDocumentsToInject,
                                    originatingSession
                                );
                                // A synchronous terminal response is persisted below after
                                // its stale error/failure state has been cleared. Avoid an
                                // earlier terminal snapshot that can re-block the composer
                                // when the canonical follow-up request is unavailable.
                                if (!(data && data.done)) self.store();
                            }
                            self.clearReplyTarget();
                        }

                        if (data && data.queued) {
                            self.completeChatRequestContext(requestContext);
                            self.handleQueuedAiRequest(data, loadingMessage, message);
                            if (self.hasActiveChatRequestContexts()) {
                                self.replying = true;
                                self.loadingSuggestedQuestions = true;
                                self.setChatSpinnerVisibility('visible');
                            }
                            return;
                        }

                        if (data.done) {
                            const terminalViewportSnapshot = self.captureChatViewportState();
                            saveMessage(data.message, is_status_check);
                            const completedMessage = self.repositionAiReplyAfterAnchor(loadingMessage);
                            self.completeAiRunStatus(completedMessage, { cancelled: false });
                            self.clearCanonicalAssistantTransientState(
                                completedMessage || loadingMessage
                            );
                            self.repairStaleChatErrorFromRealtimeCompletion(
                                completedMessage || loadingMessage
                            );

                            // Update suggested questions if provided and reset loading state
                            if (data.suggested_questions) {
                                self.currentSession.suggestedQuestions = data.suggested_questions;
                            } else {
                                self.currentSession.suggestedQuestions = [];
                            }
                            self.loadingSuggestedQuestions = false;

                            try {
                                self.store();
                                // Restore the terminal DOM change on the next
                                // layout immediately. The canonical fetch
                                // captures its own fresh snapshot when it
                                // returns, so it cannot replay this old view.
                                const terminalRestore = self.restoreChatViewportState(terminalViewportSnapshot, {
                                    markIncoming: true,
                                });
                                let canonicalRestore = null;
                                try {
                                    const refresh = self.refreshConversationFromServer(
                                        self.currentSession.uuid,
                                        {
                                            force: true,
                                            preserveSuggestedQuestions: true,
                                            preserveAiAnswer: completedMessage || loadingMessage,
                                            markIncoming: true,
                                        }
                                    );
                                    canonicalRestore = Promise.resolve(refresh).then((refreshed) => {
                                        if (!refreshed) {
                                            return self.restoreChatViewportState(terminalViewportSnapshot, {
                                                markIncoming: true,
                                            });
                                        }
                                        return null;
                                    }).catch(() => {
                                        // The immediate restoration above is
                                        // sufficient; its user-scroll revision
                                        // guard prevents this fallback from
                                        // overriding an intentional scroll.
                                        return self.restoreChatViewportState(terminalViewportSnapshot, {
                                            markIncoming: true,
                                        });
                                    });
                                } catch (e) { }
                                self.settlePendingAiReplyAfterTerminal(
                                    completedMessage || loadingMessage,
                                    terminalRestore,
                                    canonicalRestore,
                                ).catch(() => {});
                            } finally {
                                self.completeChatRequestContext(requestContext);
                                self.finalizeChatUiAfterTerminal();
                            }

                            const chatModal = document.getElementById('chatModal');
                            if (!chatModal || !chatModal.classList.contains('show')) {
                                console.log("Chat modal is not visible, showing toast...");
                                // If the chat modal is not visible, show a Bootstrap toast
                                let toast_element = $('#chatToast');

                                // Change the message of the toast chatToastText and add the icon
                                toast_element.find('.chatToastText').html(`
                                    <div class="chatToastIcon">
                                        <i class="anticon anticon-message"></i>
                                    </div>
                                    <p>¡El Abogado Virtual contestó tu mensaje!</p>
                                `);

                                toast_element.show();

                                let toast = new bootstrap.Toast(toast_element);
                                toast.show();

                                toast_element.on('hidden.bs.toast', function () {
                                    $(this).css('display', 'none');
                                });
                            }
                        } else {
                            console.log("ChatLayout: Message not complete, waiting for Ably updates or recovery fallback if needed");
                            self.startAiRunStatus(loadingMessage, { invocationText: message });
                        }
                    },
                    error: (error) => {
                        if (!self.isChatRequestContextActive(requestContext)) {
                            return;
                        }
                        self.clearInitialRequestAcceptanceReconciliation(
                            requestContext
                        );
                        console.error("Error fetching response from backend:", error);
                        if (window.test_ai_fallback === true && typeof window.logAiFallbackTestEvent === 'function') {
                            window.logAiFallbackTestEvent('chat', 'initial-request-error', {
                                chat_uuid: originatingChatUuid,
                                status: error && error.status !== undefined ? error.status : null,
                                status_text: error && error.statusText ? error.statusText : null,
                                current_try,
                                is_status_check: !!is_status_check,
                                response_text: error && typeof error.responseText === 'string'
                                    ? error.responseText.slice(0, 500)
                                    : null,
                            });
                        }

                        const restoreQueuedRequestAfterError = () => {
                            if (!(options && options.processQueuedAiRequest === true && options.queuedMessage)) {
                                return false;
                            }
                            const loadingIndex = self.currentSession.data.indexOf(loadingMessage);
                            if (loadingIndex >= 0) {
                                self.currentSession.data.splice(loadingIndex, 1);
                            }
                            let messageText = '';
                            try {
                                messageText = JSON.parse(error.responseText || '{}').message || '';
                            } catch (e) { }
                            self.restoreQueuedAiRequest(options.queuedMessage, messageText);
                            self.chatResponseReceived = true;
                            self.currentSession.suggestedQuestions = [];
                            self.lastError = false;
                            self.errorMessage = null;
                            self.completeChatRequestContext(requestContext);
                            self.finalizeChatUiAfterTerminal();
                            self.settlePendingAiReplyReveal(loadingMessage, {
                                revealAnswerStart: false,
                            }).catch(() => {});
                            return true;
                        };

                        let responsePayload = error && error.responseJSON;
                        if (!responsePayload && error && typeof error.responseText === 'string') {
                            try {
                                responsePayload = JSON.parse(error.responseText);
                            } catch (e) { }
                        }
                        const typedDocumentErrorCodes = new Set([
                            'document_catalog_capacity_exceeded',
                            'document_reader_unavailable',
                            'chat_document_metadata_conflict',
                        ]);
                        const documentErrorCode = [
                            responsePayload && responsePayload.code,
                            responsePayload && responsePayload.error_code,
                            responsePayload && responsePayload.type,
                            responsePayload && responsePayload.error,
                        ].map(value => String(value || '').trim())
                            .find(value => typedDocumentErrorCodes.has(value)) || '';
                        const responseStatus = Number(error && error.status || 0);
                        const explicitCapacityRejection = !!(
                            responseStatus === 503
                            && responsePayload
                            && responsePayload.retryable === true
                        );
                        const outcomeUnknownTransportFailure = (
                            !is_status_check
                            && !requestContext.acceptanceRecoveryStarted
                            && !documentErrorCode
                            && !explicitCapacityRejection
                            && (
                                [0, 502, 504, 521].includes(responseStatus)
                                || (responseStatus >= 500 && responseStatus < 600)
                            )
                        );
                        if (outcomeUnknownTransportFailure) {
                            requestContext.acceptanceRecoveryStarted = true;
                            Promise.resolve(self.refreshConversationFromServer(
                                originatingChatUuid,
                                {
                                    force: true,
                                    preserveSuggestedQuestions: true,
                                    timeoutMs: CANONICAL_CHAT_REFRESH_TIMEOUT_MS,
                                }
                            )).catch(() => false).then(() => {
                                if (!self.isChatRequestContextActive(requestContext)) return;
                                const messages = Array.isArray(self.currentSession.data)
                                    ? self.currentSession.data
                                    : [];
                                const canonicalTurnExists = messages.some(item => (
                                    item
                                    && item._is_optimistic !== true
                                    && String(item.client_request_id || '').trim() === clientRequestId
                                ));
                                const runStatus = self.currentSession.aiRunStatus || {};
                                const correlatedRunIsActive = (
                                    String(runStatus.client_request_id || '').trim() === clientRequestId
                                    && String(runStatus.state || '').toLowerCase() === 'running'
                                );
                                if (canonicalTurnExists || correlatedRunIsActive) {
                                    requestContext.accepted = true;
                                    loadingMessage.awaitingInitialRequestAcceptance = false;
                                    self.clearPersistedWaitingRecovery();
                                    self.startAiRunStatus(loadingMessage, {
                                        invocationText: message,
                                    });
                                    self.scheduleInitialToolReceiptReconciliation(requestContext);
                                    try { self.store(); } catch (e) { }
                                    return;
                                }
                                self.persistedWaitingRecoveryAttempt = Math.max(
                                    1,
                                    Number(self.persistedWaitingRecoveryAttempt || 0),
                                );
                                self.schedulePersistedWaitingRecovery(
                                    originatingSession,
                                    originatingChatUuid,
                                );
                                try { self.store(); } catch (e) { }
                            });
                            return;
                        }
                        const documentErrorMessages = {
                            document_catalog_capacity_exceeded: 'Este chat admite hasta 100 documentos. No se agregó ningún documento nuevo.',
                            document_reader_unavailable: 'Los documentos no se agregaron porque Lexius no puede consultarlos en este momento. Inténtalo de nuevo más tarde.',
                            chat_document_metadata_conflict: 'No se pudo actualizar la información de documentos de este chat. Inténtalo de nuevo más tarde.',
                        };
                        if (documentErrorCode) {
                            self.rollbackDocumentAdditionCheckpoint(
                                originatingChatUuid,
                                documentAdditionCheckpointVersion,
                            );
                            const documentErrorMessage = documentErrorMessages[documentErrorCode];
                            responsePayload = {
                                ...(responsePayload && typeof responsePayload === 'object' ? responsePayload : {}),
                                message: documentErrorMessage,
                            };
                            self.presentDocumentAvailabilityNotice({
                                tone: 'error',
                                title: 'No se agregaron los documentos',
                                detail: documentErrorMessage,
                            }, originatingSession);
                        }
                        const isRetryableCapacityResponse = (
                            !is_status_check
                            && !documentErrorCode
                            && error
                            && error.status === 503
                            && responsePayload
                            && responsePayload.retryable === true
                        );
                        if (isRetryableCapacityResponse && busyRetryCount < 3) {
                            const nextBusyRetry = busyRetryCount + 1;
                            let retryAfterSeconds = Number.parseFloat(
                                error && typeof error.getResponseHeader === 'function'
                                    ? error.getResponseHeader('Retry-After')
                                    : ''
                            );
                            if (!Number.isFinite(retryAfterSeconds)) {
                                retryAfterSeconds = nextBusyRetry;
                            }
                            const retryDelayMs = Math.max(
                                250,
                                Math.min(retryAfterSeconds * 1000, 30000)
                            );
                            console.log(
                                `Chat service is busy; retrying in ${retryDelayMs}ms `
                                + `(capacity retry ${nextBusyRetry}/3)`
                            );
                            self.scheduleChatRequestRetry(requestContext, () => {
                                sendRequest(
                                    max_retries,
                                    current_try,
                                    false,
                                    nextBusyRetry
                                );
                            }, retryDelayMs);
                            return;
                        }

                        // Reset loading state for suggested questions on error
                        self.loadingSuggestedQuestions = false;
                        self.currentSession.suggestedQuestions = [];

                        // In Ably-only mode, only retry the initial request, not status checks
                        if (!is_status_check && !documentErrorCode && current_try < max_retries) {
                            console.log(`Retrying initial request... (try ${current_try})`);
                            self.scheduleChatRequestRetry(requestContext, () => {
                                sendRequest(
                                    max_retries,
                                    current_try + 1,
                                    false,
                                    busyRetryCount
                                );
                            }, 1000);
                            return;
                        }
                        else if (error.status == 409 && !documentErrorCode) {
                            if (restoreQueuedRequestAfterError()) {
                                return;
                            }
                            // Another run is already in progress for this chat (server-side guard).
                            // Do not treat as an error; keep the loading placeholder and rely on Ably updates.
                            loadingMessage.awaitingInitialRequestAcceptance = false;
                            console.log('ChatLayout: /chat_stream returned 409 - run already in progress; relying on Ably updates.');
                            if (window.test_ai_fallback === true && typeof window.logAiFallbackTestEvent === 'function') {
                                window.logAiFallbackTestEvent('chat', 'initial-request-conflict', {
                                    chat_uuid: self.currentSession.uuid,
                                    current_try,
                                });
                            }
                            // Ensure we remain in replying state so UI expects streaming
                            self.replying = true;
                            // Do not alert; do not modify current placeholder
                            return;
                        } else {
                            if (restoreQueuedRequestAfterError()) {
                                return;
                            }
                            // Remove the loading message on error
                            const loadingIndex = self.currentSession.data.indexOf(loadingMessage);
                            if (loadingIndex >= 0) {
                                self.currentSession.data.splice(loadingIndex, 1);
                            }

                            self.lastError = true;
                            self.markAiRunError();
                            self.completeChatRequestContext(requestContext);

                            // Try to get the error message from the response
                            self.errorMessage = responsePayload && responsePayload.message
                                ? String(responsePayload.message)
                                : null;

                            try {
                                self.store(); // Store the error status immediately
                            } finally {
                                self.finalizeChatUiAfterTerminal();
                            }
                            self.settlePendingAiReplyReveal(loadingMessage, {
                                revealAnswerStart: false,
                            }).catch(() => {});

                            // Handle different types of errors with specific messages
                            if (error.status == 401) {
                                handleSessionExpired("el Abogado Virtual");
                            }
                            else if (error.status == 418) {
                                alert("Debes estar suscrito a un plan para continuar utilizando el Abogado Virtual.");
                            }
                            else if (error.status == 0 || !error.status) {
                                // Network connectivity issues
                                alert("No se pudo conectar al servidor. Por favor, verifica tu conexión a internet y vuelve a intentarlo. Si estás usando una VPN, intenta desconectarla temporalmente.");
                            }
                            else if (error.status == 502 || error.status == 521) {
                                // Bad Gateway or Origin Down - maintenance mode
                                alert("El sistema está en mantenimiento. Por favor, intenta de nuevo en unos minutos.");
                            }
                            else if (error.status == 504) {
                                // Gateway timeout
                                alert("El servidor tardó demasiado tiempo en responder. Por favor, intenta de nuevo en unos minutos.");
                            }
                            else if (error.status >= 500 && error.status < 600) {
                                // Other server errors (5xx)
                                if (self.errorMessage) {
                                    alert(self.errorMessage);
                                } else {
                                    alert("El servidor está experimentando problemas técnicos. Por favor, intenta de nuevo en unos minutos. Si el problema persiste, contacta al soporte técnico.");
                                }
                            }
                            else if (error.status == 429) {
                                // Rate limiting
                                if (self.errorMessage) {
                                    alert(self.errorMessage);
                                } else {
                                    alert("Has enviado demasiadas solicitudes. Por favor, espera unos momentos antes de intentar nuevamente.");
                                }
                            }
                            else if (error.status == 400) {
                                // Bad request - show server message if available
                                if (self.errorMessage) {
                                    alert(self.errorMessage);
                                } else {
                                    alert("Hubo un problema con tu solicitud. Por favor, verifica que tu mensaje sea válido o reinicia la aplicación e intenta nuevamente.");
                                }
                            }
                            else if (error.status == 403) {
                                // Forbidden
                                if (self.errorMessage) {
                                    alert(self.errorMessage);
                                } else {
                                    alert("No tienes permisos para realizar esta acción. Por favor, cierre la sesión, ingrese nuevamente y vuelva a intentarlo.");
                                }
                            }
                            else if (error.status == 408 || error.statusText === 'timeout') {
                                // Request timeout
                                alert("La solicitud tardó demasiado tiempo. Esto puede deberse a una conexión lenta o problemas del servidor. Por favor, verifica tu conexión e intenta nuevamente.");
                            }
                            else {
                                // Generic error with server message if available
                                if (self.errorMessage) {
                                    alert(self.errorMessage);
                                }
                                else {
                                    alert("Ocurrió un error inesperado al procesar tu mensaje. Por favor, recarga la página e intenta de nuevo. Si el problema persiste, contacta al soporte técnico.");
                                }
                            }
                        }
                    }
                });
            }

            // Start the AJAX request
            sendRequest();
        },

        generateUUID() {
            // Prefer crypto.randomUUID; fallback to crypto.getRandomValues-based v4; final fallback: timestamp
            try {
                if (window.crypto && typeof window.crypto.randomUUID === 'function') {
                    return window.crypto.randomUUID();
                }
            } catch (e) { }

            try {
                if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
                    const bytes = new Uint8Array(16);
                    window.crypto.getRandomValues(bytes);
                    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
                    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
                    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
                    return [
                        hex.substring(0, 8),
                        hex.substring(8, 12),
                        hex.substring(12, 16),
                        hex.substring(16, 20),
                        hex.substring(20)
                    ].join('-');
                }
            } catch (e) { }

            return String(Date.now());
        },

        // Keep a local copy; synchronized chats must not depend on cache availability.
        store() {
            const key = 'lexius_virtual_lawyer_chats';

            try {
                this.ensureSessionDocumentState(this.currentSession);
                this.synchronizeSessionDocumentMetadataFromState(this.currentSession);
                this.normalizePendingDocumentInjections(this.currentSession);
            } catch (e) { }

            // Keep error state current even if the local cache cannot be updated.
            this.currentSession.lastError = this.lastError;
            this.currentSession.errorMessage = this.errorMessage;

            if (window.sync_enabled === true && this.currentSession.is_call_log) return;

            try {
                const chats = JSON.parse(localStorage.getItem(key) ?? '{}');
                chats[this.currentSession.uuid] = this.currentSession;
                localStorage.setItem(key, JSON.stringify(chats));
            } catch (error) {
                if (window.sync_enabled === true) {
                    // Do not erase existing local chats: some may still await migration.
                    console.warn('ChatLayout: Unable to update local chat cache', error);
                    return;
                }
                const STORAGE_ERROR_MESSAGE_WITHOUT_SYNC = "Parece que su navegador está bloqueando el almacenamiento de más datos locales. Por favor, elimine los chats antiguos que ya no necesite o active la sincronización en la configuración para tener almacenamiento ilimitado.";

                function try_to_open_theme_settings_and_scroll_to_delete_chats() {
                    try {
                        if (typeof open_theme_settings_and_scroll_to_delete_chats === 'function') {
                            console.log("ChatLayout: Guide user to delete data section in settings");
                            $(document).trigger('close-chat-modal');
                            setTimeout(open_theme_settings_and_scroll_to_delete_chats, 100);
                        } else {
                            var themeBar = document.getElementsByClassName('custom-theme-bar')[0];
                            if (themeBar) { $(themeBar).fadeIn('fast'); }
                            var target = document.getElementById('delete-chats-section');
                            if (target) { setTimeout(function () { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 200); }
                        }
                    } catch (e) {
                        console.error("ChatLayout: Error opening theme settings and scrolling to delete chats: " + e);
                    }
                }

                function handle_storage_error(errorMessage, errorDetail) {
                    console.error("ChatLayout: " + errorMessage);
                    alert(STORAGE_ERROR_MESSAGE_WITHOUT_SYNC);
                    try_to_open_theme_settings_and_scroll_to_delete_chats();
                    throw new Error(errorMessage + ": " + errorDetail);
                }

                handle_storage_error("Error storing chat data", error);
            }
        },
        async retryLastMessage() {
            if (this.lastError) {
                const messages = this.currentSession.data;
                const lastMessageInArray = messages[messages.length - 1];
                const failedBotMessage = lastMessageInArray && lastMessageInArray.sender === 'bot'
                    ? lastMessageInArray
                    : null;
                const lastUserMessage = failedBotMessage
                    ? messages[messages.length - 2]
                    : lastMessageInArray;

                if (lastUserMessage && lastUserMessage.sender === 'user') {
                    const routing = await this.resolveExistingMessageReviewRouting(lastUserMessage.text);
                    const currentMessages = this.currentSession
                        && Array.isArray(this.currentSession.data)
                        ? this.currentSession.data
                        : [];
                    const currentLast = currentMessages[currentMessages.length - 1];
                    const currentLastUser = failedBotMessage
                        ? currentMessages[currentMessages.length - 2]
                        : currentLast;
                    if (
                        currentLastUser !== lastUserMessage
                        || (failedBotMessage && currentLast !== failedBotMessage)
                    ) {
                        if (
                            this.reviewCheckpoint
                            && String(this.reviewCheckpoint.originalMessage || '')
                                === String(lastUserMessage.text || '')
                        ) {
                            this.reviewCheckpoint = null;
                        }
                        return;
                    }
                    if (!routing.proceed) return;
                    // Keep the failed response visible until the replacement
                    // request has explicit, current review routing.
                    if (failedBotMessage) {
                        currentMessages.pop();
                    }
                    this.clearLastChatError();
                    this.reply(lastUserMessage.text, routing.options);
                } else {
                    alert("No se pudo encontrar el último mensaje del usuario para reintentar.");
                    this.clearLastChatError();
                    this.replying = false;
                }
                this.store(); // Store the state change immediately
            }
        },
        clearLastChatError() {
            this.lastError = false;
            this.errorMessage = null;
            if (this.currentSession && typeof this.currentSession === 'object') {
                this.currentSession.lastError = false;
                this.currentSession.errorMessage = null;
            }
        },
        adjustTextareaHeightAndCheckLength(event, { dictation = false } = {}) {
            const sourceTextarea = (event && event.target) || this.$refs.messageInput;
            const currentValue = sourceTextarea && typeof sourceTextarea.value === 'string'
                ? sourceTextarea.value
                : String(this.message || '');
            this.publishTypingState(!!currentValue.trim());
            if (!dictation) this.updateMentionSuggestions(event);
            this.$nextTick(() => {
                const textarea = this.$refs.messageInput;
                if (!textarea) return;
                textarea.style.height = 'auto';
                const nextHeight = Math.min(textarea.scrollHeight, CHAT_COMPOSER_MAX_HEIGHT_PX);
                textarea.style.height = nextHeight + 'px';
                textarea.style.overflowY = textarea.scrollHeight > CHAT_COMPOSER_MAX_HEIGHT_PX
                    ? 'auto'
                    : 'hidden';

                let warning_element = $('#num_characters_warning');
                if (textarea.value.length > this.maximum_characters) {
                    textarea.style.setProperty('border', '1px solid red', 'important');

                    // Show a warning message if the length is more than the allowed
                    warning_element.html(`<p style='color:red;'>El mensaje es demasiado largo (${textarea.value.length} caracteres). Por favor, reduzca su longitud a ${this.maximum_characters} caracteres o menos.</p>`);
                    warning_element.show();

                    this.showUploadDocumentHelpToast();
                } else {
                    // Remove the red border if the length is less than the allowed
                    textarea.style.removeProperty('border');

                    // Hide the warning message if the length is less than the allowed
                    warning_element.hide();
                }
            });
        },
        handleKeydown(event) {
            if (event.isComposing || event.keyCode === 229) return;
            if ((this.dictationStarting || this.dictationEditorBusy || this.composerSendInFlight) && event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                return;
            }
            if (this.handleMentionSuggestionKeydown(event)) {
                return;
            }
            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
            // Check if Enter key is pressed without the Shift key and not currently replying
            if (!isMobile && event.key === "Enter" && !event.shiftKey) {
                if (this.contextResumeBusy) {
                    event.preventDefault();
                    return;
                }
                const text = String(this.message || '').trim();
                const canSendHumanOnly = !!(
                    this.isCollaborativeChat
                    && text
                    && !this.isAiInvocationText(text)
                );
                const canQueueCollaborativeAi = !!(
                    this.isCollaborativeChat
                    && text
                    && this.isAiInvocationText(text)
                );
                if (this.replying && !canSendHumanOnly && !canQueueCollaborativeAi) {
                    alert("Por favor, espere a que el Abogado Virtual termine de responder antes de enviar un nuevo mensaje.");
                    return;
                }
                event.preventDefault(); // Prevent default behavior (line break)
                this.sendMessage(); // Call the method to send the message
            }
        },
        handleQuestionClick(questionText) {
            this.pauseDictationForEdit?.();
            this.message = questionText;
            this.showSuggestedQuestions = false;
            this.$nextTick(() => {
                const input = this.$refs && this.$refs.messageInput;
                if (!input) return;
                input.focus();
                this.adjustTextareaHeightAndCheckLength({ target: input });
            });
        },
        toggleSuggestedQuestions() {
            this.showSuggestedQuestions = !this.showSuggestedQuestions;
            // Save state to cookie - expires in 30 days
            setaCookie("showSuggestedQuestions", this.showSuggestedQuestions, 30);
        },
        findDocumentIndexByKey(docKey) {
            const rows = this.getSessionDocumentRows(this.currentSession);
            for (let i = 0; i < rows.length; i++) {
                if (rows[i].key === docKey) return i;
            }
            return -1;
        },
        deleteDocument(index) {
            // Only allow deletion if there are no messages and it's a multi-document chat
            if (this.currentSession.is_multi_document && this.currentSession.data.length === 0) {
                const rows = this.getSessionDocumentRows(this.currentSession);
                const removedRow = rows[index];
                if (!removedRow) return;
                const removedKey = removedRow.key;

                if (Array.isArray(this.currentSession.chat_documents_state)) {
                    this.currentSession.chat_documents_state = this.currentSession.chat_documents_state.filter((entry) => {
                        if (!entry || typeof entry !== 'object') return false;
                        const entryIsUser = this.coerceBoolean(entry.is_user_document);
                        const key = this.buildDocumentKey(
                            entry.path,
                            (entryIsUser === undefined || entryIsUser === null)
                                ? String(entry.path || '').includes('/get_user_upload')
                                : (!!entryIsUser),
                            entry.source_scope_kind || entry.scope || entry.user_document_scope,
                            entry,
                        );
                        return key !== removedKey;
                    });
                }

                if (Array.isArray(this.currentSession.pending_document_injections)) {
                    this.currentSession.pending_document_injections = this.currentSession.pending_document_injections.filter((entry) => {
                        if (!entry || typeof entry !== 'object') return false;
                        const entryIsUser = this.coerceBoolean(entry.is_user_document);
                        const key = this.buildDocumentKey(
                            entry.path,
                            (entryIsUser === undefined || entryIsUser === null)
                                ? String(entry.path || '').includes('/get_user_upload')
                                : (!!entryIsUser)
                        );
                        return key !== removedKey;
                    });
                }

                const remainingRows = rows.filter((row) => row && row.key !== removedKey);

                // If no documents remain, emit event to close chat
                if (!remainingRows.length) {
                    this.$emit('closeChat');
                    return;
                }

                this.applySessionDocumentRows(remainingRows, this.currentSession);
                // Recompute suggested questions for the updated document set
                this.currentSession.suggestedQuestions = [];
                this.ensureSessionDocumentState(this.currentSession);
                this.computeSuggestedQuestionsForPath();
            }
        },
        updatePopupPosition(position) {
            this.popupPosition = position;
        },
        handleMessageCompleted(message, { refreshConversation = true } = {}) {
            console.log("ChatLayout: Message completed via Ably");
            const completionViewportSnapshot = this.captureChatViewportState();
            this.completeChatRequestContextForMessage(message);
            let completedMessage = message;
            try {
                completedMessage = this.repositionAiReplyAfterAnchor(message);
                this.completeAiRunStatus(completedMessage, { cancelled: !!(completedMessage && completedMessage.cancelled) });
                this.repairStaleChatErrorFromRealtimeCompletion(completedMessage);
                this.store();
                this.hydrateToolReceipts({ reset: false });
                // Apply the immediate terminal layout correction now. The
                // server refresh below must capture a new snapshot after its
                // request completes, allowing any manual scroll in flight to
                // win over this older terminal snapshot.
                const terminalRestore = this.restoreChatViewportState(completionViewportSnapshot, {
                    markIncoming: true,
                });
                let canonicalRestore = null;
                if (refreshConversation) {
                    try {
                        const refresh = this.refreshConversationFromServer(this.currentSession.uuid, {
                            force: true,
                            preserveAiAnswer: completedMessage,
                            markIncoming: true,
                        });
                        canonicalRestore = Promise.resolve(refresh).then((refreshed) => {
                            if (!refreshed) {
                                return this.restoreChatViewportState(completionViewportSnapshot, {
                                    markIncoming: true,
                                });
                            }
                            return null;
                        }).catch(() => {
                            return this.restoreChatViewportState(completionViewportSnapshot, {
                                markIncoming: true,
                            });
                        });
                    } catch (e) { }
                }
                this.settlePendingAiReplyAfterTerminal(
                    completedMessage,
                    terminalRestore,
                    canonicalRestore,
                ).catch(() => {});
            } finally {
                // A terminal for one collaborative run must not cancel timers,
                // unlock the chat, or enable the composer for another run.
                this.finalizeChatUiAfterTerminal();
            }

            // If it was a manual cancel, do not show the "responded" toast
            if (!(completedMessage && completedMessage.cancelled)) {
                const chatModal = document.getElementById('chatModal');
                if (!chatModal || !chatModal.classList.contains('show')) {
                    console.log("Chat modal is not visible, showing toast...");
                    let toast_element = $('#chatToast');
                    toast_element.find('.chatToastText').html(`
                        <div class="chatToastIcon">
                            <i class="anticon anticon-message"></i>
                        </div>
                        <p>¡El Abogado Virtual contestó tu mensaje!</p>
                    `);
                    toast_element.show();
                    let toast = new bootstrap.Toast(toast_element);
                    toast.show();
                    toast_element.on('hidden.bs.toast', function () {
                        $(this).css('display', 'none');
                    });
                }
            }
        },
        handleMessageWaitingForContext(message) {
            if (!message) return;
            // A context request is a terminal state for this generation, but it
            // is not an assistant answer. Keep the placeholder visible as an
            // actionable pause instead of marking it as a completed reply.
            this.completeChatRequestContextForMessage(message);
            try {
                message.isLoading = false;
                message.waitingForUser = true;
                message.resumingContext = false;
                this.lastError = false;
                this.errorMessage = null;
                this.store();
                this.hydrateToolReceipts({ reset: true });
            } finally {
                this.finalizeChatUiAfterTerminal();
            }
            this.settlePendingAiReplyReveal(message, {
                revealAnswerStart: false,
            }).catch(() => {});
        },
        // Triggered by BotMessage after manual cancel
        async handleRetryAfterCancel(terminalMessage = null) {
            const messages = this.currentSession && Array.isArray(this.currentSession.data)
                ? this.currentSession.data
                : [];
            const actionState = resolveTerminalActionState(messages, false);
            if (actionState.mode !== 'inline') return;
            const ownedTerminal = messages[actionState.terminalIndex];
            const triggerMessage = messages[actionState.triggerIndex];
            if (
                !ownedTerminal
                || !triggerMessage
                || (terminalMessage && terminalMessage !== ownedTerminal)
            ) return;

            const wasCancelled = ownedTerminal.cancelled === true;
            const isRunningQueuedAi = !!(
                triggerMessage.ai_queued === true
                && String(triggerMessage.ai_queue_status || '').trim().toLowerCase() === 'running'
            );
            if (isRunningQueuedAi) {
                // Keep the original collaborative message identity and let the
                // existing queue path set process_queued_ai_request plus its
                // durable timestamp. If a new run cannot start, it remains
                // visibly queued instead of becoming an ordinary AI message.
                messages.splice(actionState.terminalIndex, 1);
                this.clearLastChatError();
                this.restoreQueuedAiRequest(triggerMessage, ownedTerminal.text);
                this.runQueuedAiRequest(triggerMessage);
                return;
            }

            const routing = await this.resolveExistingMessageReviewRouting(triggerMessage.text);
            const currentMessages = this.currentSession && Array.isArray(this.currentSession.data)
                ? this.currentSession.data
                : [];
            const currentActionState = resolveTerminalActionState(currentMessages, false);
            if (
                currentActionState.mode !== 'inline'
                || currentMessages[currentActionState.terminalIndex] !== ownedTerminal
                || currentMessages[currentActionState.triggerIndex] !== triggerMessage
            ) {
                if (
                    this.reviewCheckpoint
                    && String(this.reviewCheckpoint.originalMessage || '')
                        === String(triggerMessage.text || '')
                ) {
                    this.reviewCheckpoint = null;
                }
                return;
            }
            if (!routing.proceed) return;
            currentMessages.splice(currentActionState.terminalIndex, 1);
            this.clearLastChatError();

            // A cancelled attempt needs the legacy stale-cancel override; an
            // ordinary terminal failure is a new retry of its correlated turn.
            this.sessionAddReply(triggerMessage.text, false, wasCancelled, {
                ...(routing.options || {}),
                optimisticUserMessage: triggerMessage,
            });
            this.store();
        },
        handleEditLastUser(terminalMessage = null) {
            const messages = this.currentSession && Array.isArray(this.currentSession.data)
                ? this.currentSession.data
                : [];
            const actionState = resolveTerminalActionState(messages, false);
            if (actionState.mode !== 'inline') return;
            const ownedTerminal = messages[actionState.terminalIndex];
            const triggerMessage = messages[actionState.triggerIndex];
            if (
                !ownedTerminal
                || !triggerMessage
                || (terminalMessage && terminalMessage !== ownedTerminal)
                || (
                    triggerMessage.ai_queued === true
                    && String(triggerMessage.ai_queue_status || '').trim().toLowerCase() === 'running'
                )
            ) return;
            // Start inline editing on the last user message via MessageUI
            if (this.$refs.messageUI && typeof this.$refs.messageUI.startEditLastUser === 'function') {
                this.clearLastChatError();
                this.$refs.messageUI.startEditLastUser();
            }
        },
        handleMessageError(message) {
            console.log("ChatLayout: Message error via Ably");
            this.completeChatRequestContextForMessage(message);
            try {
                this.lastError = true;
                this.errorMessage = message.text || "Ocurrió un error al procesar tu mensaje";
                this.markAiRunError(this.errorMessage);
                this.store();
                this.hydrateToolReceipts({ reset: false });
            } finally {
                this.finalizeChatUiAfterTerminal();
            }
            this.settlePendingAiReplyReveal(message).catch(() => {});

            // Retryable terminal failures are already rendered in the thread
            // with retry/edit actions. Keep alerts for errors that cannot be
            // acted on from that message.
            if (!(message && message.failed && message.retryable !== false)) {
                alert(this.errorMessage);
            }
        },
        handleSuggestedQuestionsReceived(questions) {
            console.log("ChatLayout: Suggested questions received via Ably");
            this.loadingSuggestedQuestions = this.hasOutstandingChatWork();

            // Update suggested questions directly in the Vue data
            if (questions && questions.length > 0) {
                this.currentSession.suggestedQuestions = questions;
                // Keep the completed answer as the primary reading surface.
                // Suggestions remain one click away in the compact rail.
                if (this.currentSession.data.length > 0) {
                    this.showSuggestedQuestions = false;
                }
            } else {
                this.currentSession.suggestedQuestions = [];
            }
        },
        handleMessageUpdated(message) {
            // Store the session when a message is updated
            this.store();
        },
        handleResearchActivityAdded(message) {
            // The child emits synchronously after mutating the history but
            // before Vue replaces the panel DOM, so this snapshot preserves
            // either bottom-following or the reader's visible message.
            const viewportSnapshot = this.captureChatViewportState();
            this.store();
            this.restoreChatViewportState(viewportSnapshot).catch(() => {});
        },

    },
    computed: {
        readyDocumentResponse() {
            const notice = this.documentResponseReadyNotice;
            if (!notice || notice.chatUuid !== String(this.currentSession?.uuid || '')) return null;
            return this.currentSession.data.find(message => (
                this.messageReferenceId(message) === notice.messageId
                && !message.isLoading && !message.cancelled && !message.failed && !message.waitingForUser
            )) || null;
        },
        activeReviewPresentation() {
            return getReviewFollowupPresentation(
                this.activeReviewForPresentation || this.activeReview,
            );
        },
        activeReviewForPresentation() {
            const flight = this.reviewApplyInFlight;
            const review = normalizeActiveReview(this.activeReview);
            const currentChatUuid = String(this.currentSession && this.currentSession.uuid || '');
            if (
                !flight
                || !review
                || flight.chatUuid !== currentChatUuid
                || flight.reviewId !== review.reviewId
                || flight.materialState !== review.materialStateKey
            ) return this.activeReview;
            return {
                ...this.activeReview,
                approved_pending_count: 0,
                approved_pending_finding_ids: [],
                approved_changes_applied: true,
            };
        },
        activeReviewApplyInFlightCopy() {
            const count = Number(this.reviewApplyInFlight && this.reviewApplyInFlight.approvedCount || 0);
            if (count === 1) return 'Edición solicitada · Aplicando 1 recomendación aprobada';
            return `Edición solicitada · Aplicando ${count} recomendaciones aprobadas`;
        },
        showActiveReviewBar() {
            return !!(
                !this.reviewCheckpoint
                && this.activeReviewPresentation
                && this.activeReviewPresentation.bar
                && this.activeReviewPresentation.bar.visible
            );
        },
        activeReviewDismissLabel() {
            return REVIEW_FOLLOWUP_DISMISS_LABEL;
        },
        toolReceiptReconnectNotice() {
            const state = String(this.toolReceiptReconnectStatus || '').trim();
            if (!state) return null;
            const notice = TOOL_RECEIPT_RECONNECT_NOTICES[state];
            if (notice) return { state, ...notice };
            return {
                state: 'syncing',
                ...TOOL_RECEIPT_RECONNECT_NOTICES.syncing,
            };
        },
        country() {
            let country = window.country;

            if (country == 'peru') {
                country = 'perú';
            }
            else if (country == 'panama') {
                country = 'panamá';
            }
            else if (country == 'mexico') {
                country = 'méxico';
            }
            else if (country == "costa rica") {
                return 'Costa Rica';
            }
            else if (country == "el salvador") {
                return 'El Salvador';
            }
            else if (country == "republica dominicana") {
                return 'República Dominicana';
            }
            else if (country == "honduras") {
                return 'Honduras';
            }
            else if (country == "puerto rico") {
                return 'Puerto Rico';
            }
            else if (country == "guatemala") {
                return 'Guatemala';
            }
            else if (country == "cuba") {
                return 'Cuba';
            }
            else if (country == "nicaragua") {
                return 'Nicaragua';
            }

            return country.charAt(0).toUpperCase() + country.slice(1).toLowerCase();;
        },
        documentCount() {
            if (!this.currentSession.is_multi_document) return 1;
            try {
                return Array.isArray(this.currentSession.path) ? this.currentSession.path.length : 0;
            } catch (e) {
                return 0;
            }
        },
        isCollaborativeChat() {
            try {
                const value = this.currentSession && this.currentSession.is_collaborative;
                const role = String(this.currentSession && this.currentSession.collaboration_role || '').trim();
                const explicitCollaborative = value === true || value === 1 || value === '1' || value === 'true';
                const explicitNonCollaborative = value === false || value === 0 || value === '0' || value === 'false';
                return !!(
                    this.currentSession
                    && this.currentSession.uuid
                    && !this.currentSession.is_call_log
                    && (
                        explicitCollaborative
                        || (!explicitNonCollaborative && role)
                    )
                );
            } catch (e) {
                return false;
            }
        },
        showCollaborativeNewMessagesCue() {
            return !!this.isCollaborativeChat && (this.unseenIncomingCount || 0) > 0;
        },
        canOpenCollaborationDialog() {
            try {
                return !!(this.currentSession && !this.currentSession.is_call_log);
            } catch (e) {
                return false;
            }
        },
        collaborationHeaderButtonLabel() {
            const fallback = 'este chat';
            let title = fallback;
            try {
                title = String(this.currentSession.title || this.currentSession.page_title || fallback).trim() || fallback;
            } catch (e) {
                title = fallback;
            }
            if (this.currentSession && this.currentSession.collaboration_role === 'member') {
                return `Ver colaboradores de ${title}`;
            }
            return this.isCollaborativeChat
                ? `Administrar colaboradores de ${title}`
                : `Invitar colaboradores a ${title}`;
        },
        collaborationPresenceText() {
            const count = Array.isArray(this.presentMembers) ? this.presentMembers.length : 0;
            if (!this.isCollaborativeChat || count <= 0) return '';
            if (count === 1) {
                const name = this.presentMembers[0].display_name || this.presentMembers[0].email || '1 colaborador';
                return `${name} en línea`;
            }
            return `${count} colaboradores en línea`;
        },
        collaborationAiStatus() {
            if (!this.isCollaborativeChat || !this.currentSession) return null;
            const messages = Array.isArray(this.currentSession.data) ? this.currentSession.data : [];
            const loading = messages.find((item) => item && item.sender === 'bot' && item.isLoading === true);
            const status = this.currentSession.aiRunStatus || {};
            const queuedRequests = this.getQueuedAiRequests();
            const queuedCount = queuedRequests.length;
            const queuedDetail = queuedCount === 1
                ? '1 solicitud de Lexius está en cola y seguirá visible para el grupo.'
                : `${queuedCount} solicitudes de Lexius están en cola y seguirán visibles para el grupo.`;
            if (loading || this.replying || status.state === 'running') {
                const target = loading || this.findLatestBotMessage();
                const runningDetail = 'El grupo puede seguir escribiendo. La respuesta se mantendrá en su lugar original.';
                const runningStatus = {
                    state: 'running',
                    title: 'Abogado Virtual está respondiendo',
                    detail: queuedCount > 0
                        ? `${runningDetail} ${queuedDetail}`
                        : runningDetail,
                    actionLabel: target ? 'Ver avance' : '',
                    messageId: target ? this.messageReferenceId(target) : '',
                    preview: '',
                };
                runningStatus.statusKey = this.collaborationAiStatusKey(runningStatus);
                if (runningStatus.statusKey === this.collaborationAiStatusDismissedKey) return null;
                return runningStatus;
            }
            if (queuedCount > 0) {
                const target = queuedRequests[0];
                const queuedStatus = {
                    state: 'queued',
                    title: queuedCount === 1 ? 'Solicitud a Lexius en cola' : 'Solicitudes a Lexius en cola',
                    detail: queuedDetail,
                    actionLabel: 'Ver cola',
                    messageId: this.messageReferenceId(target),
                    preview: String(target.text || '').replace(/\s+/g, ' ').trim().slice(0, 180),
                };
                queuedStatus.statusKey = this.collaborationAiStatusKey(queuedStatus);
                if (queuedStatus.statusKey === this.collaborationAiStatusDismissedKey) return null;
                return queuedStatus;
            }
            const transientStatusKey = this.isTransientCollaborationAiStatus(status.state)
                ? this.collaborationAiStatusKey(status)
                : '';
            if (transientStatusKey && transientStatusKey === this.collaborationAiStatusDismissedKey) return null;
            const transientCompletedAt = Number(status.completedAt || 0);
            const acknowledgedAt = Number(this.currentSession.aiRunStatusAckAt || 0);
            if (
                this.isTransientCollaborationAiStatus(status.state)
                && transientStatusKey !== this.collaborationAiStatusVisibleKey
                && transientCompletedAt <= acknowledgedAt
            ) {
                return null;
            }
            if (status.state === 'answered' && status.answerMessageId) {
                const shouldShowAnsweredStatus = (
                    transientStatusKey
                    && (
                        transientStatusKey === this.collaborationAiStatusVisibleKey
                        || this.isRecentCollaborationAiAnswer(status)
                    )
                );
                if (!shouldShowAnsweredStatus) {
                    return null;
                }
                return {
                    state: 'answered',
                    statusKey: transientStatusKey,
                    stableKey: this.collaborationAiStatusStableKey(status),
                    completedAt: status.completedAt || 0,
                    title: 'Abogado Virtual respondió',
                    detail: 'La respuesta quedó en su lugar original. Usa este aviso para ir directamente a ella.',
                    actionLabel: 'Ver respuesta',
                    messageId: status.answerMessageId,
                    preview: status.answerPreview || '',
                };
            }
            if (status.state === 'cancelled' && status.answerMessageId) {
                return {
                    state: 'cancelled',
                    statusKey: transientStatusKey,
                    stableKey: this.collaborationAiStatusStableKey(status),
                    completedAt: status.completedAt || 0,
                    title: 'Respuesta cancelada',
                    detail: 'La solicitud al Abogado Virtual fue cancelada.',
                    actionLabel: 'Ver estado',
                    messageId: status.answerMessageId,
                    preview: '',
                };
            }
            if (status.state === 'error') {
                return {
                    state: 'error',
                    statusKey: transientStatusKey,
                    stableKey: this.collaborationAiStatusStableKey(status),
                    completedAt: status.completedAt || 0,
                    title: 'No se completó la respuesta',
                    detail: status.message || 'El Abogado Virtual no pudo completar esta respuesta.',
                    actionLabel: '',
                    messageId: '',
                    preview: '',
                };
            }
            return null;
        },
        canInviteCollaborators() {
            try {
                if (!this.currentSession || this.currentSession.is_call_log) return false;
                return this.currentSession.collaboration_role !== 'member';
            } catch (e) {
                return false;
            }
        },
        canLeaveCollaborationChat() {
            try {
                const collaborativeValue = this.currentSession && this.currentSession.is_collaborative;
                const isCollaborative = collaborativeValue === true || collaborativeValue === 1 || collaborativeValue === '1' || collaborativeValue === 'true';
                return !!(
                    this.currentSession
                    && this.currentSession.uuid
                    && !this.currentSession.is_call_log
                    && isCollaborative
                    && this.currentSession.collaboration_role === 'member'
                );
            } catch (e) {
                return false;
            }
        },
        collaborationPushButtonLabel() {
            if (this.collaborationPushBusy) return 'Activando...';
            if (this.collaborationPushEnabled) return 'Notificaciones activas';
            if (this.collaborationPushSupported === false) return 'No disponible';
            if (this.collaborationPushPermission === 'denied') return 'Bloqueadas';
            return 'Activar notificaciones';
        },
        collaborationPushButtonDisabled() {
            return !!(
                this.collaborationPushBusy
                || this.collaborationPushEnabled
                || this.collaborationPushSupported === false
                || this.collaborationPushPermission === 'denied'
            );
        },
        collaborationPushButtonClass() {
            const disabled = this.collaborationPushButtonDisabled;
            return {
                'is-active': this.collaborationPushEnabled,
                'is-warning': disabled && this.collaborationPushVariant === 'warning',
                'is-error': disabled && this.collaborationPushVariant === 'danger',
                'is-busy': this.collaborationPushBusy,
            };
        },
        collaborationPushStatusClass() {
            return {
                'is-success': this.collaborationPushEnabled || this.collaborationPushVariant === 'success',
                'is-warning': this.collaborationPushVariant === 'warning',
                'is-error': this.collaborationPushVariant === 'danger',
            };
        },
        mentionSuggestions() {
            if (!this.isCollaborativeChat || !this.mentionSuggestionsVisible) return [];
            const query = String(this.mentionQuery || '').trim().toLowerCase();
            const lexiusSuggestion = {
                id: 'lexius',
                type: 'ai',
                label: 'Lexius',
                handle: 'Lexius',
                mention: '@Lexius',
                detail: 'Abogado Virtual',
                status: 'active',
                initial: 'L',
                searchText: 'lexius abogado virtual ia ai',
            };
            const participantSuggestions = (Array.isArray(this.collaborationParticipants) ? this.collaborationParticipants : [])
                .map((participant) => this.participantMentionSuggestion(participant))
                .filter(Boolean);
            const seen = new Set(['lexius']);
            const suggestions = [lexiusSuggestion];
            participantSuggestions.forEach((suggestion) => {
                const key = String(suggestion.id || suggestion.handle || '').toLowerCase();
                if (!key || seen.has(key)) return;
                seen.add(key);
                suggestions.push(suggestion);
            });
            return suggestions
                .filter((suggestion) => {
                    if (!query) return true;
                    return String(suggestion.searchText || '').includes(query);
                })
                .slice(0, 8);
        },
        showMentionSuggestions() {
            return !!(this.mentionSuggestionsVisible && this.mentionSuggestions.length);
        },
        chatInputPlaceholder() {
            return this.isCollaborativeChat
                ? 'Escribe al grupo o menciona @Lexius para consultar a la IA'
                : 'Pregúntame algo';
        },
        canSendCurrentMessage() {
            if (this.dictationStarting || this.dictationEditorBusy || this.composerSendInFlight) return false;
            const text = String(this.message || '').trim();
            if (
                !text
                || this.lastError
                || this.contextResumeBusy
                || this.reviewIntentChecking
                || this.activeReviewSubmitChecking
            ) return false;
            if (!this.replying) return true;
            return !!this.isCollaborativeChat;
        },
        isHumanOnlyCollaborationDraft() {
            const text = String(this.message || '').trim();
            return !!(this.isCollaborativeChat && text && !this.isAiInvocationText(text));
        },
        showSendIcon() {
            return !this.dictationSendPending
                && !this.contextResumeBusy
                && !this.reviewIntentChecking
                && !this.activeReviewSubmitChecking
                && (!this.replying || this.isCollaborativeChat);
        },
        recoverableOutOfContextDocumentCount() {
            try {
                if (this.currentSession.chat_documents_state_persistable === false) {
                    return 0;
                }
                const normalizedState = this.normalizeChatDocumentsStatePayload(
                    Array.isArray(this.currentSession.chat_documents_state)
                        ? this.currentSession.chat_documents_state
                        : []
                );
                return recoverableDocumentCount(normalizedState);
            } catch (e) {
                return 0;
            }
        },
        nextMessagePendingDocuments() {
            try {
                return this.documentGroups.addedLater.filter((doc) => doc && doc.pending_injection);
            } catch (e) {
                return [];
            }
        },
        documentGroups() {
            try {
                const rows = this.getSessionDocumentRows(this.currentSession);
                const normalizedState = this.normalizeChatDocumentsStatePayload(
                    Array.isArray(this.currentSession.chat_documents_state)
                        ? this.currentSession.chat_documents_state
                        : []
                );
                const stateMap = new Map(normalizedState.map((entry) => [entry.key, entry]));
                const docs = [];
                const seen = new Set();

                for (const row of rows) {
                    const stateEntry = stateMap.get(row.key);
                    const merged = {
                        key: row.key,
                        path: row.path,
                        page_title: row.page_title || row.path,
                        is_user_document: !!row.is_user_document,
                        added_during: stateEntry ? stateEntry.added_during : 'initial',
                        is_out_of_context: stateEntry ? !!stateEntry.is_out_of_context : false,
                        is_catalog_readable: stateEntry ? isLazyCatalogDocument(stateEntry) : false,
                        injection_message_id: stateEntry ? stateEntry.injection_message_id : null,
                        pending_injection: false,
                    };
                    docs.push(merged);
                    seen.add(merged.key);
                }

                for (const stateEntry of normalizedState) {
                    if (!stateEntry || seen.has(stateEntry.key)) continue;
                    docs.push({
                        key: stateEntry.key,
                        path: stateEntry.path,
                        page_title: stateEntry.page_title || stateEntry.path,
                        is_user_document: !!stateEntry.is_user_document,
                        added_during: stateEntry.added_during,
                        is_out_of_context: !!stateEntry.is_out_of_context,
                        is_catalog_readable: isLazyCatalogDocument(stateEntry),
                        injection_message_id: stateEntry.injection_message_id || null,
                        pending_injection: false,
                    });
                    seen.add(stateEntry.key);
                }

                const pendingRawLocal = Array.isArray(this.currentSession.pending_document_injections)
                    ? this.currentSession.pending_document_injections
                    : [];
                const pendingSet = new Set();
                for (const entry of pendingRawLocal) {
                    if (!entry || typeof entry !== 'object') continue;
                    const pathValue = (entry.path == null ? '' : String(entry.path)).trim();
                    if (!pathValue) continue;
                    const coercedIsUser = this.coerceBoolean(entry.is_user_document);
                    const isUser = pathValue.includes('/get_user_upload')
                        ? true
                        : (coercedIsUser === undefined || coercedIsUser === null ? false : !!coercedIsUser);
                    pendingSet.add(this.buildDocumentKey(pathValue, isUser));
                }
                for (const item of docs) {
                    item.pending_injection = !item.is_catalog_readable && pendingSet.has(item.key);
                }

                const initial = docs.filter((doc) => doc.added_during !== 'added_later');
                const addedLater = docs.filter((doc) => doc.added_during === 'added_later');
                return { all: docs, initial, addedLater };
            } catch (e) {
                return { all: [], initial: [], addedLater: [] };
            }
        },
        hasRetryableMessage() {
            try {
                const arr = (this.currentSession && Array.isArray(this.currentSession.data)) ? this.currentSession.data : [];
                // Show retry only if there is at least one user message to retry
                return arr.some(m => m && m.sender === 'user');
            } catch (e) {
                return false;
            }
        },
        latestTerminalMessageOwnsInlineActions() {
            try {
                const messages = this.currentSession && Array.isArray(this.currentSession.data)
                    ? this.currentSession.data
                    : [];
                return resolveTerminalActionState(messages, this.lastError).mode === 'inline';
            } catch (e) {
                return false;
            }
        },
        shouldShowLegacyRetryFallback() {
            try {
                const messages = this.currentSession && Array.isArray(this.currentSession.data)
                    ? this.currentSession.data
                    : [];
                return resolveTerminalActionState(messages, this.lastError).mode === 'fallback';
            } catch (e) {
                return false;
            }
        },
        popupStyles() {
            const isMobile = typeof window !== 'undefined' && 'ontouchstart' in window;

            if (isMobile) {
                // For mobile: center the popup within the viewport
                return {
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'var(--primary)',
                    border: '1px solid var(--border-fourth)',
                    'box-shadow': 'var(--doc-popup-shadow)',
                    color: 'var(--text-secondary)',
                    padding: '15px',
                    'max-width': '90vw',
                    'width': '90vw',
                    'min-width': 'auto',
                    'max-height': '90vh',
                    'min-height': '100px',
                    'overflow-y': 'auto',
                    'z-index': '9999',
                    'border-radius': '8px',
                    'transition': 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out'
                };
            } else {
                // For desktop: position the popup relative to the link
                return {
                    position: 'fixed',
                    top: `${this.popupPosition.top}px`,
                    left: `${this.popupPosition.left}px`,
                    transform: 'none', // Remove the transform for precise positioning
                    background: 'var(--primary)',
                    border: '1px solid var(--border-fourth)',
                    'box-shadow': 'var(--doc-popup-shadow)',
                    color: 'var(--text-secondary)',
                    padding: '15px',
                    'max-width': '40vw',
                    'width': 'auto',
                    'min-width': '300px',
                    'max-height': '50vh',
                    'min-height': '100px',
                    'overflow-y': 'auto',
                    'z-index': '9999',
                    'border-radius': '8px',
                    'transition': 'opacity 0.2s ease-in-out'
                };
            }
        }
    },

    watch: {
        collaborationAiStatus(status) {
            this.scheduleCollaborationAiStatusDismiss(status);
        },
        'currentSession.uuid'(newUuid) {
            this.documentResponseReadyNotice = null;
            const normalizedUuid = String(newUuid || '');
            const previewController = this.ensureDocumentPreviewController();
            if (previewController.chatUuid !== normalizedUuid) previewController.activate(normalizedUuid);
            this.cancelActiveReviewRequest();
            this.clearActiveReviewTimers();
            this.activeReview = null;
            this.activeReviewHydratedChatUuid = '';
            this.activeReviewHydrationError = '';
            this.activeReviewSubmitChecking = false;
            this.reviewApplyInFlight = null;
            this.reviewCheckpoint = null;
            this.reviewDeferredMessage = null;
            this.reviewUpdateNotice = '';
            this.cancelToolActionMutationRequests();
            this.clearToolReceiptHydrationRetry();
            this.clearPersistedWaitingRecovery();
            this.invalidateContinuationProjectionRecovery();
            this.contextResumeBusy = false;
            this.contextDocumentBindingState = 'idle';
            this.contextDocumentBindingError = '';
            this.toolActionInlineStatus = '';
            this.toolActionInlineStatusToolRunId = '';
            this.authorizedArtifactWakeups = {};
            const hasRequestForAnotherChat = (this.chatRequestContexts || []).some(context => (
                context
                && !context.cancelled
                && !context.completed
                && context.uuid !== normalizedUuid
            ));
            if (hasRequestForAnotherChat) {
                this.cancelPendingChatRequest();
            }
            this.clearCollaborationAiStatusDismissTimer();
            this.collaborationAiStatusDismissedKey = '';
            this.collaborationAiStatusVisibleKey = '';
            this.unseenIncomingCount = 0;
            this.chatboxFollowingBottom = null;
            this.chatViewportPendingReplyReveals = [];
            this.chatViewportUserScrollRevision = Number(this.chatViewportUserScrollRevision || 0) + 1;
            this.releaseProgrammaticChatScroll();
            this.updateOpenChatUuid();
            if (
                normalizedUuid
                && (
                    this.toolReceiptServerConfirmedChats[normalizedUuid]
                    || (this.currentSession && this.currentSession.tool_receipts_server_confirmed === true)
                )
            ) {
                this.catchUpContinuationState({ resetReceipts: true });
            }
            if (normalizedUuid) this.scheduleActiveReviewRefresh(0);
            this.$nextTick(() => {
                this.attachChatboxScrollListener();
                this.updateScrollToBottomVisibility();
                this.reconcilePersistedWaitingSession();
            });
        },
        'currentSession.data.length'() {
            this.$nextTick(() => {
                this.updateScrollToBottomVisibility();
            });
        },
        message(newValue) {
            if (
                this.reviewCheckpoint
                && String(newValue || '') !== String(this.reviewCheckpoint.originalMessage || '')
            ) {
                this.reviewCheckpoint = null;
                this.reviewDeferredMessage = null;
            }
        },
    },

    /*html*/
    template: `
        <div 
            class="h-full w-full flex flex-col"
        >
            <div
                class="chat-modal-header bg-body-bg sticky top-0"
                data-tour="chat-header"
            >
                <div class="chat-modal-header__left">
                    <button
                        v-if="canOpenCollaborationDialog"
                        type="button"
                        class="chat-collaboration-trigger chat-collaboration-trigger--header"
                        :class="{ 'is-active': isCollaborativeChat }"
                        :aria-label="collaborationHeaderButtonLabel"
                        :title="collaborationHeaderButtonLabel"
                        data-testid="chat-collaboration-trigger"
                        @click="openCollaborationDialog"
                    >
                        <i class="anticon anticon-usergroup-add"></i>
                    </button>
                    <span
                        v-if="collaborationPresenceText"
                        class="collaboration-presence-pill"
                        data-testid="collaboration-presence"
                    >
                        <span class="collaboration-presence-dot"></span>
                        {{ collaborationPresenceText }}
                    </span>
                </div>
                <h5
                    class="chat-modal-header__title font-medium text-secondary"
                >
                    <span class="chat-modal-header__title-main">Abogado Virtual</span>
                    <span class="chat-modal-header__title-connector"> de </span>
                    <span class="chat-modal-header__title-brand">LEXIUS {{ country }}</span>
                </h5>

                <div class="chat-modal-header__actions">
                    <div class="mini-spinner" id="mini-spinner-lawyer" style="visibility: hidden;"></div>
                    <button
                        type="button"
                        class="demo-recording-touch-control border-fourth border rounded-md text-sm w-8 h-8 flex justify-center items-center text-secondary hover:text-primary hover:bg-theme-color transition-all"
                        title="Grabar demo"
                        aria-label="Grabar demo del chat"
                        data-testid="chat-demo-recording-button"
                        style="display:none;"
                    >
                        <i class="anticon anticon-video-camera"></i>
                    </button>
                    <span v-if="hasCustomInstructions" class="custom-instructions-indicator" title="Instrucciones personalizadas activas" style="color: var(--theme-color); cursor: pointer;" role="button" tabindex="0" @click.prevent="handleCustomInstructionsIndicatorClick" @keydown.enter.prevent="handleCustomInstructionsIndicatorClick" @keydown.space.prevent="handleCustomInstructionsIndicatorClick">
                        <i class="anticon anticon-bulb"></i>
                    </span>
                    <a tabindex="0" href="#" class="btn btn-sm help-button-top" type="button"
                        data-toggle="popover" data-trigger="focus"
                        title="Bienvenido al primer <span class='color-bold'>Abogado Virtual</span> capaz de responder consultas legales, redactar borradores de documentos, realizar correcciones gramaticales, profundizar en temas jurídicos, revisar documentos, crear tablas, y mucho más.
                        <br><br>
                        Te recomendamos que <b>siempre verifiques la información proporcionada</b>.">
                        <i class="anticon anticon-question"></i>
                    </a>
                    <button
                        type="button"
                        class="chat-window-toggle"
                        data-chat-window-toggle
                        data-testid="chat-window-toggle"
                        aria-label="Minimizar ventana"
                        title="Minimizar ventana"
                        aria-expanded="true"
                    >
                        <svg class="chat-window-toggle__minimize" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M4 4h16v16H4zM11 14h6v3h-6z" />
                        </svg>
                        <svg class="chat-window-toggle__expand" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
                        </svg>
                    </button>
                    <button
                        class="border-fourth border rounded-md text-sm w-8 h-8 flex justify-center items-center text-secondary hover:text-primary hover:bg-theme-color transition-all"
                        data-bs-dismiss="modal"
                    >
                        <i class="anticon anticon-close" ></i>
                    </button>
                </div>
            </div>
            <div v-if="pendingCollaborationInvitations.length" class="collaboration-inbox">
                <div
                    v-for="invitation in pendingCollaborationInvitations"
                    :key="invitation.invitation_uuid"
                    class="collaboration-inbox__item"
                >
                    <div class="collaboration-inbox__copy">
                        <strong>{{ invitation.inviter_display_name || invitation.inviter_email }}</strong>
                        <span>te invitó a colaborar en {{ invitation.title || 'un chat de LEXIUS' }}.</span>
                    </div>
                    <div class="collaboration-inbox__actions">
                        <button type="button" @click="respondToCollaborationInvitation(invitation, 'accept')">Aceptar</button>
                        <button type="button" class="secondary" @click="respondToCollaborationInvitation(invitation, 'reject')">Rechazar</button>
                    </div>
                </div>
            </div>
            <div
                v-if="collaborationDialogVisible"
                class="collaboration-dialog-backdrop"
                role="presentation"
                @click.self="closeCollaborationDialog"
            >
                <section
                    class="collaboration-dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="collaboration-dialog-title"
                    @click.stop
                >
                    <header class="collaboration-dialog__header">
                        <div class="collaboration-dialog__title-group">
                            <span class="collaboration-dialog__eyebrow">Chat colaborativo</span>
                            <h5 id="collaboration-dialog-title">Colaboradores</h5>
                            <p>{{ currentSession.title || currentSession.page_title || 'Chat' }}</p>
                        </div>
                        <button
                            type="button"
                            class="collaboration-dialog__close"
                            @click="closeCollaborationDialog"
                            aria-label="Cerrar colaboradores"
                            title="Cerrar"
                        >
                            <i class="anticon anticon-close"></i>
                        </button>
                    </header>

                    <div class="collaboration-dialog__body">
                        <div class="collaboration-dialog__section">
                            <div class="collaboration-dialog__section-header">
                                <span>Participantes</span>
                            </div>
                            <div class="collaboration-panel__participants">
                                <div
                                    v-for="participant in collaborationParticipants"
                                    :key="(participant.email || participant.username || participant.display_name) + participant.role"
                                    class="collaboration-chip"
                                    :class="{ 'pending': participant.status === 'pending', 'is-admin': participant.role === 'owner' }"
                                    :title="participant.email"
                                >
                                    <span class="collaboration-chip__avatar" aria-hidden="true">{{ participantInitial(participant) }}</span>
                                    <span class="collaboration-chip__main">{{ participantDisplayName(participant) }}</span>
                                    <span
                                        v-for="badge in participantBadges(participant)"
                                        :key="badge"
                                        class="collaboration-chip__badge"
                                    >
                                        {{ badge }}
                                    </span>
                                    <button
                                        v-if="canRemoveParticipant(participant)"
                                        type="button"
                                        class="collaboration-chip__remove"
                                        :aria-label="'Remover a ' + participantDisplayName(participant)"
                                        :title="'Remover a ' + participantDisplayName(participant)"
                                        @click.stop="removeCollaborator(participant)"
                                    >
                                        <i class="anticon anticon-close"></i>
                                    </button>
                                </div>
                                <span v-if="!collaborationParticipants.length" class="collaboration-empty-state">Solo tú</span>
                            </div>
                        </div>

                        <div
                            v-if="!collaborationPushEnabled"
                            class="collaboration-dialog__section collaboration-notifications-section"
                        >
                            <div class="collaboration-dialog__section-header">
                                <span class="collaboration-section-title-with-icon">
                                    <i class="anticon anticon-bell"></i>
                                    Notificaciones del chat
                                </span>
                            </div>
                            <p class="collaboration-notifications-copy">
                                La campana activa notificaciones push en este navegador para avisarte de actividad relevante en tus chats colaborativos, incluso cuando no tengas esta ventana abierta.
                            </p>
                            <div class="collaboration-notifications-action-row">
                                <button
                                    type="button"
                                    class="collaboration-push-button"
                                    :class="collaborationPushButtonClass"
                                    :disabled="collaborationPushButtonDisabled"
                                    @click="enableCollaborationPushNotifications"
                                    data-testid="collaboration-push-action"
                                    aria-live="polite"
                                >
                                    <i class="anticon" :class="collaborationPushEnabled ? 'anticon-check-circle' : 'anticon-bell'"></i>
                                    <span>{{ collaborationPushButtonLabel }}</span>
                                </button>
                                <span
                                    v-if="collaborationPushEnabled"
                                    class="collaboration-push-enabled-badge"
                                >
                                    Activas en este dispositivo
                                </span>
                            </div>
                            <p
                                v-if="collaborationPushStatus"
                                class="collaboration-push-status"
                                :class="collaborationPushStatusClass"
                            >
                                {{ collaborationPushStatus }}
                            </p>
                        </div>

                        <form v-if="canInviteCollaborators" class="collaboration-invite-form" @submit.prevent="inviteCollaborator">
                            <label for="collaborationInviteEmail">Invitar por correo</label>
                            <div class="collaboration-invite-form__row">
                                <input
                                    id="collaborationInviteEmail"
                                    v-model="collaborationInviteEmail"
                                    type="email"
                                    placeholder="correo@ejemplo.com"
                                    aria-label="Correo del colaborador"
                                >
                                <button
                                    type="submit"
                                    :class="{ 'opacity-70': !collaborationInviteEmail }"
                                    :aria-disabled="!collaborationInviteEmail ? 'true' : 'false'"
                                    :disabled="!collaborationInviteEmail"
                                >
                                    <i class="anticon anticon-mail"></i>
                                    <span>Enviar</span>
                                </button>
                            </div>
                        </form>

                        <p v-if="collaborationInviteStatus" class="collaboration-status">{{ collaborationInviteStatus }}</p>
                        <p v-if="collaborationRemovalStatus" class="collaboration-status">{{ collaborationRemovalStatus }}</p>

                        <div
                            v-if="canLeaveCollaborationChat"
                            class="collaboration-dialog__section collaboration-leave-section"
                        >
                            <div class="collaboration-dialog__section-header">
                                <span class="collaboration-section-title-with-icon">
                                    <i class="anticon anticon-logout"></i>
                                    Abandonar chat
                                </span>
                            </div>
                            <p class="collaboration-leave-copy">
                                Al abandonar este chat dejarás de verlo en tu lista y perderás acceso a sus mensajes y documentos compartidos.
                            </p>
                            <button
                                type="button"
                                class="collaboration-leave-button"
                                data-testid="collaboration-leave-action"
                                @click="leaveCollaborationChat"
                            >
                                <i class="anticon anticon-logout"></i>
                                <span>Abandonar</span>
                            </button>
                        </div>

                        <div class="collaboration-dialog__section collaboration-help" aria-label="Guía rápida de colaboración">
                            <div class="collaboration-dialog__section-header collaboration-help__header">
                                <span class="collaboration-section-title-with-icon">
                                    <i class="anticon anticon-team"></i>
                                    Cómo funciona el chat colaborativo
                                </span>
                            </div>
                            <p class="collaboration-help__intro">
                                Piensa en este chat como una mesa de trabajo: las personas invitadas ven la conversación, los documentos agregados y las respuestas del Abogado Virtual en el mismo hilo.
                            </p>
                            <div class="collaboration-help__flow">
                                <div class="collaboration-help__item">
                                    <span class="collaboration-help__icon"><i class="anticon anticon-usergroup-add"></i></span>
                                    <div>
                                        <strong>Invita por correo</strong>
                                        <span>Los invitados aparecen como pendientes hasta aceptar; quien administra el chat puede remover participantes.</span>
                                    </div>
                                </div>
                                <div class="collaboration-help__item">
                                    <span class="collaboration-help__icon"><i class="anticon anticon-message"></i></span>
                                    <div>
                                        <strong>Escriban en grupo</strong>
                                        <span>Los mensajes quedan visibles para todos los colaboradores en una conversación compartida.</span>
                                    </div>
                                </div>
                                <div class="collaboration-help__item">
                                    <span class="collaboration-help__icon"><i class="anticon anticon-robot"></i></span>
                                    <div>
                                        <strong>Habla con Lexius</strong>
                                        <span>Usa @Lexius para pedir una respuesta del Abogado Virtual dentro del hilo, si no utilizas dicha mención tu mensaje no iniciará una respuesta del Abogado Virtual.</span>
                                    </div>
                                </div>
                                <div class="collaboration-help__item">
                                    <span class="collaboration-help__icon"><i class="anticon anticon-file-text"></i></span>
                                    <div>
                                        <strong>Contexto compartido</strong>
                                        <span>Los documentos del chat sirven como contexto para todos y para las respuestas del Abogado Virtual.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <span v-if="currentSession.path" class="text-sm m-2" data-tour="chat-documents">
                <span class='dynamic-color' style='max-height:100px; overflow-y: scroll; display: block'>
                    <div class="flex items-center gap-2">
                        <strong>Documentos del chat:</strong>
                        
                        <a v-if="(currentSession.is_multi_document || currentSession.path)" 
                           tabindex="0" 
                           href="#" 
                           class="help-button-docs" 
                           type="button"
                           data-toggle="popover" 
                           data-trigger="focus"
                           title="Ayuda"
                           data-content="Para agregar más sentencias, archivos o textos legales a este chat, pulsa nuevamente el botón 'Chatear con el Abogado Virtual' desde el archivo que desees agregar"
                           aria-label="Ayuda sobre cómo agregar más documentos al chat"
                        >
                            <i class="anticon anticon-question-circle"></i>
                        </a>
                    </div>
                    <div v-if="documentGroups.initial.length > 0" class="chat-doc-group">
                        <p v-if="documentGroups.addedLater.length > 0" class="chat-doc-group-title">Documentos iniciales ({{ documentGroups.initial.length }})</p>
                        <ol style='margin-left:4%;'>
                            <li v-for="doc in documentGroups.initial" :key="'initial-' + doc.key" class="document-item">
                                <a :href="documentHref(doc)" class="text-secondary font-bold text-center">
                                    {{ doc.page_title }}
                                </a>
                                <button
                                    v-if="currentSession.data.length === 0 && currentSession.is_multi_document && findDocumentIndexByKey(doc.key) >= 0"
                                    @click="deleteDocument(findDocumentIndexByKey(doc.key))"
                                    class="delete-document-btn"
                                    title="Eliminar documento"
                                >
                                    <i class="anticon anticon-delete"></i>
                                </button>
                            </li>
                        </ol>
                    </div>
                    <div v-if="documentGroups.addedLater.length > 0" class="chat-doc-group">
                        <p class="chat-doc-group-title">Documentos agregados después de iniciar el chat ({{ documentGroups.addedLater.length }})</p>
                        <ol style='margin-left:4%;'>
                            <li v-for="doc in documentGroups.addedLater" :key="'later-' + doc.key" class="document-item">
                                <a :href="documentHref(doc)" class="text-secondary font-bold text-center">
                                    {{ doc.page_title }}
                                </a>
                                <span
                                    v-if="doc.pending_injection && !doc.is_catalog_readable"
                                    class="chat-doc-status chat-doc-status-pending"
                                    title="Este documento se agregará al contexto en su próximo mensaje."
                                >
                                    <i class="anticon anticon-clock-circle"></i>
                                </span>
                                <button
                                    v-if="doc.pending_injection && !doc.is_catalog_readable"
                                    type="button"
                                    class="delete-document-btn pending-document-remove-btn"
                                    title="Quitar este documento del contexto antes de enviar su próximo mensaje."
                                    @click.stop.prevent="removePendingDocumentFromContext(doc.key)"
                                >
                                    <i class="anticon anticon-close"></i>
                                </button>
                                <span
                                    v-if="doc.is_out_of_context && !doc.pending_injection && !doc.is_catalog_readable"
                                    class="chat-doc-status chat-doc-status-warning"
                                    title="Este documento salió del contexto por el límite de longitud. Use «Reincluir en el próximo mensaje» para incorporarlo de nuevo."
                                >
                                    <i class="anticon anticon-warning"></i>
                                </span>
                            </li>
                        </ol>
                    </div>
                </span>
                <a v-if="!currentSession.is_multi_document" :href="documentHref({ path: currentSession.path, page_title: currentSession.page_title, is_user_document: currentSession.is_user_document })" class="ml-2 dynamic-color">
                    <button class="border border-primary rounded-md text-sm px-2 py-1 transition-all dynamic-color open-chat-doc-button">
                        Abrir 
                        {{ currentSession.is_law ? 'texto legal' : (currentSession.is_user_document ? 'documento' : 'sentencia') }}
                    </button>
                </a>
                <hr class='mt-2'>
            </span>
            <section
                v-if="documentAvailabilityNotice"
                class="mx-4 mb-2 rounded-md border px-3 py-2 text-sm"
                :class="{
                    'border-blue-200 bg-blue-50 text-blue-900': documentAvailabilityNotice.tone === 'info',
                    'border-amber-300 bg-amber-50 text-amber-950': documentAvailabilityNotice.tone === 'warning',
                    'border-red-300 bg-red-50 text-red-900': documentAvailabilityNotice.tone === 'error'
                }"
                data-testid="document-availability-notice"
                :role="documentAvailabilityNotice.tone === 'error' ? 'alert' : 'status'"
                aria-live="polite"
            >
                <div class="flex items-start gap-2">
                    <i
                        class="anticon mt-1"
                        :class="documentAvailabilityNotice.tone === 'error' ? 'anticon-close-circle' : (documentAvailabilityNotice.tone === 'warning' ? 'anticon-warning' : 'anticon-info-circle')"
                        aria-hidden="true"
                    ></i>
                    <div class="min-w-0 flex-1">
                        <strong class="block">{{ documentAvailabilityNotice.title }}</strong>
                        <p v-if="documentAvailabilityNotice.detail" class="mb-0">{{ documentAvailabilityNotice.detail }}</p>
                        <ul v-if="documentAvailabilityNotice.failedTitles.length" class="mb-0 mt-1 pl-4">
                            <li v-for="(title, index) in documentAvailabilityNotice.failedTitles" :key="'failed-document-' + index">
                                {{ title }}
                            </li>
                        </ul>
                    </div>
                    <button
                        type="button"
                        class="border-0 bg-transparent p-1"
                        data-testid="document-availability-dismiss"
                        aria-label="Ocultar aviso de documentos"
                        @click="dismissDocumentAvailabilityNotice"
                    >
                        <i class="anticon anticon-close" aria-hidden="true"></i>
                    </button>
                </div>
            </section>
            <section
                v-if="toolReceiptReconnectNotice"
                class="tool-receipt-connectivity"
                :class="'tool-receipt-connectivity--' + toolReceiptReconnectNotice.tone"
                :data-state="toolReceiptReconnectNotice.state"
                data-testid="tool-receipt-reconnect-status"
                :role="toolReceiptReconnectNotice.role"
                :aria-live="toolReceiptReconnectNotice.role === 'alert' ? 'assertive' : 'polite'"
                aria-atomic="true"
            >
                <span class="tool-receipt-connectivity__icon" aria-hidden="true">
                    <i :class="toolReceiptReconnectNotice.iconClass"></i>
                </span>
                <span class="tool-receipt-connectivity__copy">
                    <strong>{{ toolReceiptReconnectNotice.title }}</strong>
                    <span>{{ toolReceiptReconnectNotice.detail }}</span>
                </span>
                <button
                    v-if="toolReceiptReconnectNotice.retryable"
                    type="button"
                    class="tool-receipt-connectivity__retry"
                    data-testid="tool-receipt-reconnect-retry"
                    :disabled="toolReceiptReconnectRetrying"
                    @click="retryToolReceiptReconnect"
                >
                    <i :class="toolReceiptReconnectRetrying ? 'anticon anticon-loading anticon-spin' : 'anticon anticon-reload'" aria-hidden="true"></i>
                    <span>{{ toolReceiptReconnectRetrying ? 'Reintentando…' : 'Reintentar' }}</span>
                </button>
            </section>
            <div
                v-if="toolActionInlineStatus"
                class="mx-4 mb-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                data-testid="tool-action-inline-status"
                role="alert"
            >{{ toolActionInlineStatus }}</div>
            <div class="chat-scroll-area flex-1 !p-4 overflow-y-auto overflow-x-hidden" ref="chatbox" data-testid="chat-scroll-area">
                <div
                    v-if="currentSession.chat_pagination && currentSession.chat_pagination.loading_previous"
                    class="chat-window-loader"
                    role="status"
                    aria-label="Cargando mensajes anteriores"
                >
                    <span class="chat-window-loader__spinner"></span>
                </div>
                <MessageUI
                    :session="currentSession"
                    :document-previews="documentPreviews"
                    :document-preview-ownership="documentPreviewOwnership"
                    :active-review="activeReviewForPresentation"
                    :replying="replying" 
                    :collaborative="isCollaborativeChat"
                    ref="messageUI"
                    v-if="currentSession.data.length"
                    @messageCompleted="handleMessageCompleted"
                    @documentResponseReady="notifyDocumentResponseReady"
                    @messageWaitingForContext="handleMessageWaitingForContext"
                    @messageError="handleMessageError"
                    @suggestedQuestionsReceived="handleSuggestedQuestionsReceived"
                    @messageUpdated="handleMessageUpdated"
                    @researchActivityAdded="handleResearchActivityAdded"
                    @editUserMessage="handleEditUserMessage"
                    @reactToMessage="reactToMessage"
                    @retryAfterCancel="handleRetryAfterCancel"
                    @editLastUser="handleEditLastUser"
                    @replyToMessage="selectReplyTarget"
                    @navigateToMessage="scrollToMessageReference"
                    @runQueuedAiRequest="runQueuedAiRequest"
                    @toolAction="handleToolAction"
                    @reviewAction="handleReviewAction"
                />
                <EmptyUI v-else @startAudioCall="$emit('startAudioCall')"/>
            </div>
            <tool-workspace
                ref="toolWorkspace"
                :receipt="activeToolReceipt"
                :chat-uuid="currentSession && currentSession.uuid ? String(currentSession.uuid) : ''"
                :active-review="activeReviewForPresentation"
                :review-focus-mode="activeToolReceipt && activeToolReceipt.review_focus_mode ? activeToolReceipt.review_focus_mode : 'all'"
                @close="closeToolWorkspace"
                @resolved="handleToolWorkspaceResolved"
                @review-action="handleWorkspaceReviewAction"
                @review-state-changed="handleReviewStateChanged"
            />
            <div
                v-if="showCollaborativeNewMessagesCue"
                class="chat-new-messages-cue"
                role="status"
                aria-live="polite"
            >
                <button
                    type="button"
                    class="chat-new-messages-cue__button"
                    @click="scrollToNewMessages"
                >
                    <span>{{ unseenIncomingCount > 99 ? '99+' : unseenIncomingCount }} {{ unseenIncomingCount === 1 ? 'mensaje nuevo' : 'mensajes nuevos' }}</span>
                    <i class="anticon anticon-arrow-down" aria-hidden="true"></i>
                </button>
            </div>
            <div v-if="readyDocumentResponse" class="chat-response-ready-cue"
                 :class="{ 'chat-response-ready-cue--stacked': showCollaborativeNewMessagesCue }"
                 data-testid="document-response-ready-cue" role="status" aria-live="polite">
                <div class="chat-response-ready-cue__pill">
                    <button type="button" class="chat-response-ready-cue__open" @click="scrollToDocumentResponse">
                        <i class="anticon anticon-check-circle" aria-hidden="true"></i>
                        <span>Respuesta lista <span class="chat-response-ready-cue__action">· Ver</span></span>
                        <i class="anticon anticon-arrow-up" aria-hidden="true"></i>
                    </button>
                    <button type="button" class="chat-response-ready-cue__dismiss" aria-label="Descartar aviso de respuesta lista"
                            @click="documentResponseReadyNotice = null">
                        <i class="anticon anticon-close" aria-hidden="true"></i>
                    </button>
                </div>
            </div>
            <div v-if="showScrollToBottom && !readyDocumentResponse" class="chat-scroll-bottom-cue">
                <button type="button" class="chat-scroll-bottom-cue__button"
                        data-testid="chat-scroll-to-bottom"
                        aria-label="Ir a los mensajes más recientes"
                        @click="scrollToLatestMessages">
                    <i class="anticon anticon-arrow-down" aria-hidden="true"></i>
                </button>
            </div>
            <div
                v-if="collaborationAiStatus"
                class="collaboration-ai-status"
                :class="'collaboration-ai-status--' + collaborationAiStatus.state"
                data-testid="collaboration-ai-status"
                role="status"
                aria-live="polite"
            >
                <div class="collaboration-ai-status__indicator" aria-hidden="true">
                    <span v-if="collaborationAiStatus.state === 'running'" class="collaboration-ai-status__spinner"></span>
                    <i
                        v-else
                        class="anticon"
                        :class="{
                            'anticon-check-circle': collaborationAiStatus.state === 'answered',
                            'anticon-clock-circle': collaborationAiStatus.state === 'queued',
                            'anticon-close-circle': collaborationAiStatus.state === 'cancelled' || collaborationAiStatus.state === 'error'
                        }"
                    ></i>
                </div>
                <div class="collaboration-ai-status__copy">
                    <span class="collaboration-ai-status__title">{{ collaborationAiStatus.title }}</span>
                    <p>{{ collaborationAiStatus.detail }}</p>
                    <p v-if="collaborationAiStatus.preview" class="collaboration-ai-status__preview">
                        {{ collaborationAiStatus.preview }}
                    </p>
                </div>
                <button
                    v-if="collaborationAiStatus.actionLabel"
                    type="button"
                    class="collaboration-ai-status__action"
                    @click="scrollToAiStatusMessage"
                >
                    {{ collaborationAiStatus.actionLabel }}
                </button>
                <button
                    type="button"
                    class="collaboration-ai-status__dismiss"
                    data-testid="collaboration-ai-status-dismiss"
                    aria-label="Ocultar estado de Lexius"
                    title="Ocultar estado"
                    @click.stop.prevent="dismissCollaborationAiStatus"
                >
                    <i class="anticon anticon-close"></i>
                </button>
            </div>
            <div v-if="typingMembers.length" class="collaboration-typing">
                {{ typingMembers.join(', ') }} está escribiendo...
            </div>
            <!-- Keep this rail mounted so starting or finishing an answer does
                 not recenter the surrounding modal and move the reading viewport. -->
            <div class="chat-reading-actions px-4">
            <button
                v-if="recoverableOutOfContextDocumentCount > 0 && !currentSession.is_call_log && !replying"
                type="button"
                class="chat-context-recovery-tab"
                data-testid="chat-reinject-documents"
                :aria-label="'Reincluir ' + recoverableOutOfContextDocumentCount + (recoverableOutOfContextDocumentCount === 1 ? ' fuente' : ' fuentes') + ' en el próximo mensaje'"
                @click="queueOutOfContextDocumentsForNextMessage"
            >
                <i class="anticon anticon-file-text" aria-hidden="true"></i>
                <span class="chat-context-recovery-tab__label">
                    {{ recoverableOutOfContextDocumentCount }}
                    {{ recoverableOutOfContextDocumentCount === 1 ? 'fuente fuera de contexto' : 'fuentes fuera de contexto' }}
                </span>
                <span class="chat-context-recovery-tab__action">Reincluir en el próximo mensaje</span>
                <i class="anticon anticon-arrow-right" aria-hidden="true"></i>
            </button>
            <div
                class="chat-suggestions-rail"
                :data-tour="currentSession.suggestedQuestions && currentSession.suggestedQuestions.length > 0 ? 'chat-response-suggestions' : null"
            >
            <!-- Suggested Questions -->
            <button
                v-if="(currentSession.suggestedQuestions && currentSession.suggestedQuestions.length > 0) || loadingSuggestedQuestions"
                type="button"
                class="chat-suggestions-rail__toggle"
                :aria-expanded="showSuggestedQuestions ? 'true' : 'false'"
                aria-controls="chat-suggested-questions"
                :title="showSuggestedQuestions ? 'Ocultar preguntas sugeridas' : 'Mostrar preguntas sugeridas'"
                @click="toggleSuggestedQuestions"
            >
                <i class="anticon anticon-file-text" aria-hidden="true"></i>
                <span>Preguntas sugeridas</span>
                <span
                    v-if="!loadingSuggestedQuestions && currentSession.suggestedQuestions && currentSession.suggestedQuestions.length"
                    class="chat-suggestions-rail__count"
                >
                    {{ currentSession.suggestedQuestions.length }}
                </span>
                <span v-if="loadingSuggestedQuestions && showSuggestedQuestions" class="spinner-border spinner-border-sm" role="status">
                    <span class="sr-only">Cargando preguntas sugeridas</span>
                </span>
                <i
                    class="anticon chat-suggestions-rail__chevron"
                    :class="showSuggestedQuestions ? 'anticon-arrow-up' : 'anticon-arrow-down'"
                    aria-hidden="true"
                ></i>
            </button>
            <div v-if="!loadingSuggestedQuestions" 
                 id="chat-suggested-questions"
                 :class="['suggested-questions', showSuggestedQuestions ? 'expanded' : 'collapsed']" 
                 class="flex flex-wrap gap-2 overflow-y-auto">
                <button v-for="(question, index) in currentSession.suggestedQuestions" :key="index" 
                        class="suggested-question chat-suggestions-rail__question rounded-lg"
                        :title="question"
                        @click="handleQuestionClick(question)" 
                        style="text-transform: none !important;">
                    {{ question }}
                </button>
            </div>
            <div v-else-if="showSuggestedQuestions" 
                 class="suggested-questions-loading">
            </div>
            <!-- End of Suggested Questions -->
            </div>
            <p v-if="lastError && shouldShowLegacyRetryFallback" style='color:red'>
                {{ errorMessage || 'Ocurrió un error al enviar su último mensaje. Por favor, inténtelo de nuevo pulsando el botón con el texto "Reintentar".' }}
            </p>
            <button 
                v-if="lastError && hasRetryableMessage && shouldShowLegacyRetryFallback"
                data-testid="chat-error-retry-fallback"
                class="!p-3 w-[120px] flex justify-center items-center rounded-lg !bg-theme-color dynamic-color"
                @click="retryLastMessage()"
            >
                Reintentar
            </button>
            </div>
            <div class="row lawyer-free-plan dynamic-color" style="margin:10px;">
                <h2>Característica disponible solo para clientes</h2>
                <p>Al adquirir un plan podrás desbloquear el poder del Abogado Virtual de LEXIUS: una herramienta indispensable potenciada por Inteligencia Artificial que te asiste en consultas legales, redacta borradores básicos de documentos, corrige gramática, analiza temas jurídicos y mucho más. ¡Optimiza tu trabajo y gana tranquilidad con la mejor asistencia legal digital!
                </p>
                <br>
                <p> Suscríbete hoy mismo y únete a esta experiencia que llegó para revolucionar el mundo
                    del derecho.</p>
                <button class="btn adquire-plan" style="max-width:150px">Adquirir
                    plan</button>
            </div>

            <div style="text-align:right; display:none;" class="credentials-buttons-section">
                <button class="btn credentials-buttons login-button" style="margin:2%;">Entrar</button>
                <button class="btn credentials-buttons register-button"
                    style="margin:2%;">Registrarse</button>
            </div>

            <div id="innerChatToast" style="display:none;">
                <div class="innerChatToastContent">
                    <div class="innerChatToastIcon">
                        <i class="anticon anticon-message"></i>
                    </div>
                    <div class="innerChatToastText">
                        <p>¡El Abogado Virtual contestó tu mensaje!</p>
                    </div>
                </div>
            </div>

            <div id="num_characters_warning" style="display:none;"></div>
            <div class="!p-1 sticky bottom-0 bg-primary">
                <div
                    v-if="contextDocumentBindingState !== 'idle'"
                    class="mb-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                    data-testid="context-document-binding-status"
                    :role="contextDocumentBindingState === 'failed' ? 'alert' : 'status'"
                    aria-live="polite"
                >
                    <span v-if="contextDocumentBindingState === 'binding_context_documents'">
                        Vinculando los documentos con la solicitud pausada…
                    </span>
                    <span v-else-if="contextDocumentBindingState === 'resolving_context_documents'">
                        Confirmando los documentos para continuar…
                    </span>
                    <span v-else>{{ contextDocumentBindingError }}</span>
                    <button
                        v-if="contextDocumentBindingState === 'failed'"
                        type="button"
                        class="ml-2 underline font-semibold"
                        data-testid="context-document-binding-retry"
                        :disabled="contextResumeBusy || dictationBusy || dictationEditorBusy || composerSendInFlight"
                        @click="retryContextDocumentBinding"
                    >Reintentar vinculación</button>
                </div>
                <details
                    v-if="nextMessagePendingDocuments.length && !currentSession.is_call_log && !replying"
                    class="next-message-docs-indicator"
                >
                    <summary class="next-message-docs-indicator__header">
                        <i class="anticon anticon-file-text" aria-hidden="true"></i>
                        <span class="next-message-docs-indicator__copy" aria-live="polite">
                            <span class="next-message-docs-indicator__eyebrow">Próximo mensaje</span>
                            <span class="next-message-docs-indicator__summary">
                                {{ nextMessagePendingDocuments.length === 1
                                    ? '1 fuente se incluirá'
                                    : nextMessagePendingDocuments.length + ' fuentes se incluirán' }}
                            </span>
                        </span>
                        <span class="next-message-docs-indicator__count">
                            {{ nextMessagePendingDocuments.length }}
                        </span>
                        <i class="anticon anticon-arrow-down next-message-docs-indicator__chevron" aria-hidden="true"></i>
                    </summary>
                    <div class="next-message-docs-indicator__list">
                        <div
                            v-for="doc in nextMessagePendingDocuments"
                            :key="'next-message-doc-' + doc.key"
                            class="next-message-doc-chip"
                        >
                            <a
                                :href="documentHref(doc)"
                                class="next-message-doc-chip__link"
                                :title="doc.page_title || doc.path"
                            >
                                <i class="anticon anticon-file-text"></i>
                                <span>{{ doc.page_title || doc.path }}</span>
                            </a>
                            <button
                                type="button"
                                class="next-message-doc-chip__remove"
                                :title="'Quitar ' + (doc.page_title || doc.path) + ' del próximo mensaje'"
                                @click.stop.prevent="removePendingDocumentFromContext(doc.key)"
                            >
                                <i class="anticon anticon-close"></i>
                            </button>
                        </div>
                    </div>
                </details>
                <div
                    v-if="reviewUpdateNotice"
                    class="review-followup-notice review-followup-notice--success"
                    data-testid="review-update-success"
                    role="status"
                    aria-live="polite"
                >
                    <i class="anticon anticon-check-circle" aria-hidden="true"></i>
                    <span>{{ reviewUpdateNotice }}</span>
                </div>
                <div
                    v-else-if="reviewApplyInFlight"
                    class="review-followup-notice review-followup-notice--progress"
                    data-testid="review-apply-in-flight"
                    role="status"
                    aria-live="polite"
                >
                    <i class="anticon anticon-loading anticon-spin" aria-hidden="true"></i>
                    <span>{{ activeReviewApplyInFlightCopy }}</span>
                </div>
                <section
                    v-else-if="activeReviewHydrationError"
                    class="review-followup-checkpoint"
                    data-testid="review-hydration-status"
                    role="alert"
                    aria-live="assertive"
                >
                    <div class="review-followup-checkpoint__copy">
                        <i class="anticon anticon-cloud-sync" aria-hidden="true"></i>
                        <span>{{ activeReviewHydrationError }}</span>
                    </div>
                    <div class="review-followup-checkpoint__actions">
                        <button
                            type="button"
                            class="review-followup-action review-followup-action--primary"
                            data-testid="review-hydration-retry"
                            :disabled="activeReviewSubmitChecking"
                            @click="retryActiveReviewHydration"
                        >{{ activeReviewSubmitChecking ? 'Reintentando…' : 'Reintentar' }}</button>
                    </div>
                </section>
                <section
                    v-else-if="reviewCheckpoint"
                    class="review-followup-checkpoint"
                    data-testid="review-followup-checkpoint"
                    aria-labelledby="review-followup-checkpoint-copy"
                >
                    <div class="review-followup-checkpoint__copy">
                        <i class="anticon anticon-check-square" aria-hidden="true"></i>
                        <span id="review-followup-checkpoint-copy">{{ reviewCheckpoint.copy }}</span>
                    </div>
                    <div class="review-followup-checkpoint__actions">
                        <template v-if="reviewCheckpoint.kind === 'approved_pending'">
                            <button type="button" class="review-followup-action review-followup-action--primary" @click="reviewCheckpointApplyApproved">
                                {{ reviewCheckpoint.approvedCount === 1
                                    ? 'Aplicar la aprobada'
                                    : 'Aplicar las ' + reviewCheckpoint.approvedCount + ' aprobadas' }}
                            </button>
                            <button type="button" class="review-followup-action" @click="reviewCheckpointReviewPending">
                                {{ reviewCheckpoint.undecidedCount === 1
                                    ? 'Revisar 1 restante'
                                    : 'Revisar las ' + reviewCheckpoint.undecidedCount + ' restantes' }}
                            </button>
                            <button type="button" class="review-followup-action review-followup-action--quiet" @click="reviewCheckpoint = null">Cancelar</button>
                        </template>
                        <template v-else-if="reviewCheckpoint.kind === 'none_approved'">
                            <button type="button" class="review-followup-action review-followup-action--primary" @click="reviewCheckpointReviewPending">Revisar recomendaciones</button>
                            <button type="button" class="review-followup-action" @click="reviewCheckpointContinue('no_document_edit')">Continuar sin actualizar</button>
                        </template>
                        <template v-else-if="reviewCheckpoint.kind === 'ambiguous_review'">
                            <button type="button" class="review-followup-action review-followup-action--primary" @click="reviewCheckpointApplyReview">Sí, aplicarlas</button>
                            <button type="button" class="review-followup-action" @click="reviewCheckpointContinue('no_document_edit')">No, solo responder</button>
                        </template>
                        <template v-else-if="reviewCheckpoint.kind === 'target_review_unavailable'">
                            <button type="button" class="review-followup-action review-followup-action--primary" @click="reviewCheckpointContinue('no_document_edit')">Solo continuar</button>
                            <button type="button" class="review-followup-action review-followup-action--quiet" @click="reviewCheckpoint = null">Cancelar</button>
                        </template>
                        <template v-else>
                            <button type="button" class="review-followup-action review-followup-action--primary" @click="reviewCheckpointContinue('other_document_edit')">Actualizar documento</button>
                            <button type="button" class="review-followup-action" @click="reviewCheckpointContinue('no_document_edit')">Solo continuar</button>
                        </template>
                    </div>
                </section>
                <div
                    v-else-if="showActiveReviewBar"
                    class="review-followup-bar"
                    data-testid="review-followup-bar"
                    role="status"
                    aria-live="polite"
                >
                    <div class="review-followup-bar__copy">
                        <i class="anticon anticon-check-square" aria-hidden="true"></i>
                        <span>{{ activeReviewPresentation.bar.copy }}</span>
                    </div>
                    <div class="review-followup-bar__actions">
                        <button
                            v-for="action in activeReviewPresentation.bar.actions"
                            :key="action.id + '-' + action.label"
                            type="button"
                            class="review-followup-action"
                            :class="{ 'review-followup-action--primary': action.id === 'apply_review_changes' }"
                            @click="handleReviewAction(action)"
                        >{{ action.label }}</button>
                        <button
                            type="button"
                            class="review-followup-bar__dismiss"
                            :title="activeReviewDismissLabel"
                            :aria-label="activeReviewDismissLabel"
                            @click="dismissActiveReview()"
                        ><i class="anticon anticon-close" aria-hidden="true"></i></button>
                    </div>
                </div>
                <form
                    class="chat-composer-form flex !gap-3"
                    data-testid="chat-composer"
                    @submit.prevent="sendMessage()"
                    v-if="!currentSession.is_call_log" 
                >
                    <button
                        type="button"
                        class="chat-files-shortcut"
                        data-testid="chat-files-shortcut"
                        title="Agregar archivos al chat"
                        aria-label="Agregar archivos al chat"
                        @click="openUserFilesShortcut"
                    >
                        <i class="anticon anticon-plus" aria-hidden="true"></i>
                    </button>
                    <div class="chat-composer-form__input">
                        <div
                            v-if="isCollaborativeChat && replyTarget"
                            class="collaboration-reply-composer"
                            data-testid="collaboration-reply-composer"
                        >
                            <button
                                type="button"
                                class="collaboration-reply-composer__preview"
                                :title="'Ir al mensaje citado de ' + (replyTarget.sender_label || 'Mensaje citado')"
                                @click="scrollToMessageReference(replyTarget)"
                            >
                                <span class="collaboration-reply-composer__meta">
                                    <i class="anticon anticon-message" aria-hidden="true"></i>
                                    <span>Respondiendo a</span>
                                    <strong>{{ replyTarget.sender_label || 'mensaje citado' }}</strong>
                                </span>
                                <span class="collaboration-reply-composer__snippet">{{ replyTarget.snippet }}</span>
                            </button>
                            <button
                                type="button"
                                class="collaboration-reply-composer__close"
                                title="Quitar respuesta"
                                aria-label="Quitar respuesta"
                                @click="clearReplyTarget"
                            >
                                <i class="anticon anticon-close"></i>
                            </button>
                        </div>
                        <div
                            v-if="showMentionSuggestions"
                            class="collaboration-mention-suggestions"
                            data-testid="collaboration-mention-suggestions"
                            role="listbox"
                            :aria-label="'Sugerencias para @' + mentionQuery"
                        >
                            <button
                                v-for="(suggestion, index) in mentionSuggestions"
                                :key="suggestion.id"
                                type="button"
                                class="collaboration-mention-suggestion"
                                :class="{
                                    'is-active': index === mentionActiveIndex,
                                    'is-lexius': suggestion.type === 'ai'
                                }"
                                role="option"
                                :aria-selected="index === mentionActiveIndex ? 'true' : 'false'"
                                :data-testid="'collaboration-mention-suggestion-' + suggestion.handle.toLowerCase()"
                                @mousedown.prevent
                                @click="selectMentionSuggestion(suggestion)"
                            >
                                <span class="collaboration-mention-suggestion__avatar">{{ suggestion.initial }}</span>
                                <span class="collaboration-mention-suggestion__body">
                                    <span class="collaboration-mention-suggestion__label">{{ suggestion.label }}</span>
                                    <span class="collaboration-mention-suggestion__detail">{{ suggestion.detail }}</span>
                                </span>
                                <span class="collaboration-mention-suggestion__handle">{{ suggestion.mention }}</span>
                            </button>
                        </div>
                        <textarea
                            name="message"
                            class="chat-input !px-4 !py-3 text-sm w-full border-fourth border rounded-lg focus:border-theme-color transition-colors"
                            data-testid="chat-input"
                            :placeholder="chatInputPlaceholder"
                            v-model="message"
                            @beforeinput="pauseDictationForEdit"
                            @compositionstart="pauseDictationForEdit"
                            @input="onComposerInput"
                            @select="rememberComposerSelection"
                            @keyup="rememberComposerSelection"
                            @click="updateMentionSuggestions"
                            @focus="updateMentionSuggestions"
                            @blur="scheduleHideMentionSuggestions(); rememberComposerSelection($event)"
                            @keydown="handleKeydown"
                            ref="messageInput"
                        ></textarea>
                    </div>
                    <button
                        v-if="dictationEnabled"
                        type="button"
                        class="chat-dictation-button"
                        :class="{ 'is-recording': dictationState === 'listening' }"
                        data-testid="chat-dictation-button"
                        :aria-label="dictationStarting ? 'Cancelar dictado' : (dictationBusy ? 'Detener dictado' : 'Dictar mensaje')"
                        :title="dictationStarting ? 'Cancelar dictado' : (dictationBusy ? 'Detener dictado' : 'Dictar mensaje')"
                        :aria-pressed="dictationState === 'listening' ? 'true' : 'false'"
                        :disabled="dictationState === 'finalizing' || (!dictationBusy && dictationUnavailable)"
                        @click="toggleDictation"
                    >
                        <svg v-if="dictationBusy" aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="5" width="14" height="14" rx="2" /></svg>
                        <svg v-else aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="9" y="2" width="6" height="13" rx="3"/><path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3M8 22h8"/></svg>
                    </button>
                    <button
                        class="chat-send !p-3 w-[75px] flex justify-center items-center rounded-lg !bg-theme-color"
                        data-testid="chat-send"
                        :aria-label="composerSendLabel"
                        :title="composerSendLabel"
                        :aria-busy="dictationSendPending ? 'true' : 'false'"
                        :class="{'opacity-70': !canSendCurrentMessage}"
                        :aria-disabled="!canSendCurrentMessage ? 'true' : 'false'"
                        :disabled="!canSendCurrentMessage"
                    >
                        <span v-if="dictationSendPending" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                        <svg 
                            xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-send" viewBox="0 0 16 16"
                            v-show="showSendIcon"
                        >
                            <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576 6.636 10.07Zm6.787-8.201L1.591 6.602l4.339 2.76 7.494-7.493Z"/>
                        </svg>
                        
                        <svg width="120" height="16" viewBox="0 0 120 30" xmlns="http://www.w3.org/2000/svg" class="fill-secondary" v-show="!showSendIcon && !dictationSendPending">
                            <circle cx="15" cy="15" r="15">
                                <animate attributeName="r" from="15" to="15"
                                        begin="0s" dur="0.8s"
                                        values="15;9;15" calcMode="linear"
                                        repeatCount="indefinite" />
                                <animate attributeName="fill-opacity" from="1" to="1"
                                        begin="0s" dur="0.8s"
                                        values="1;.5;1" calcMode="linear"
                                        repeatCount="indefinite" />
                            </circle>
                            <circle cx="60" cy="15" r="9" fill-opacity="0.3">
                                <animate attributeName="r" from="9" to="9"
                                        begin="0s" dur="0.8s"
                                        values="9;15;9" calcMode="linear"
                                        repeatCount="indefinite" />
                                <animate attributeName="fill-opacity" from="0.5" to="0.5"
                                        begin="0s" dur="0.8s"
                                        values=".5;1;.5" calcMode="linear"
                                        repeatCount="indefinite" />
                            </circle>
                            <circle cx="105" cy="15" r="15">
                                <animate attributeName="r" from="15" to="15"
                                        begin="0s" dur="0.8s"
                                        values="15;9;15" calcMode="linear"
                                        repeatCount="indefinite" />
                                <animate attributeName="fill-opacity" from="1" to="1"
                                        begin="0s" dur="0.8s"
                                        values="1;.5;1" calcMode="linear"
                                        repeatCount="indefinite" />
                            </circle>
                        </svg>

                    </button>
                    <div v-if="composerUnsavedDrafts.length" class="chat-composer-drafts">
                        <label for="chat-unsaved-drafts">Borradores sin enviar</label>
                        <select id="chat-unsaved-drafts" data-testid="chat-unsaved-drafts" @change="restoreUnsavedComposerDraft">
                            <option value="">Volver a un borrador…</option>
                            <option v-for="draft in composerUnsavedDrafts" :key="draft.id" :value="draft.id">{{ draft.label }}</option>
                        </select>
                    </div>
                    <div v-if="dictationEnabled" v-show="dictationStatusText || dictationRecovery" class="chat-dictation-feedback">
                        <div class="chat-dictation-status-row">
                            <span role="status" aria-live="polite" aria-atomic="true" data-testid="chat-dictation-status">
                                <span v-if="dictationState === 'listening'" class="chat-dictation-indicator" aria-hidden="true"></span>
                                {{ dictationStatusText }}
                            </span>
                            <button v-if="dictationStarting" type="button" data-testid="chat-dictation-cancel" @click="cancelDictation">Cancelar</button>
                            <button v-else-if="dictationSendPending" type="button" class="!border-0 !shadow-none !normal-case" style="background: transparent !important" data-testid="chat-dictation-cancel-send" @click="cancelDictation">Cancelar envío</button>
                            <button v-if="dictationState === 'error'" type="button" data-testid="chat-dictation-retry" :disabled="dictationUnavailable" @click="toggleDictation">Reintentar</button>
                        </div>
                        <details v-if="dictationError && (dictationError.code === 'permission_denied' || dictationError.code === 'permission' || dictationError.help)" class="chat-dictation-help">
                            <summary>Ayuda con el micrófono</summary>
                            <p>Revisa el permiso de micrófono en los controles del sitio del navegador y en los ajustes de privacidad del dispositivo. Si usas un dispositivo administrado, consulta a su administrador. Después, vuelve a intentarlo.</p>
                        </details>
                        <details v-if="dictationRecovery" class="chat-dictation-recovery" data-testid="chat-dictation-recovery">
                            <summary>Revisar último fragmento</summary>
                            <p>{{ dictationRecovery }}</p>
                            <button type="button" @click="copyDictationRecovery">{{ dictationCopied ? 'Copiado' : 'Copiar fragmento' }}</button>
                        </details>
                    </div>
                </form>
                <div v-if="currentSession.is_call_log" class="text-center text-gray-500 py-3 dynamic-color"
                    data-tour="chat-call-log-notice">
                    Este es el registro de una llamada. No puedes enviar mensajes en este chat.
                </div>
            </div>

            <transition name="fade">
                <div 
                    class="doc-details-popup" 
                    v-if="popupVisible" 
                    :style="popupStyles" 
                    v-html="popupContentText">
                </div>
            </transition>
        </div>`
}
